import { useState, useEffect, useRef } from "react";
import {
  Apple,
  Armchair,
  ArrowDown,
  ArrowUp,
  BarChart3,
  BatteryMedium,
  Beef,
  Bell,
  Bike,
  Brain,
  Cable,
  Calendar,
  Camera,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  Dices,
  Droplet,
  Dumbbell,
  Flame,
  Footprints,
  Hammer,
  HeartPulse,
  Infinity,
  Info,
  Leaf,
  Lightbulb,
  Moon,
  Package,
  Pause,
  Pencil,
  PersonStanding,
  Plus,
  RefreshCw,
  Sailboat,
  Save,
  Scale,
  Search,
  Settings,
  Shuffle,
  Sun,
  Sunrise,
  Target,
  Timer,
  Trash2,
  TrendingUp,
  Trophy,
  Upload,
  Utensils,
  Waves,
  Wheat,
  X,
  Zap,
} from "lucide-react";

const MUSCLE_GROUPS = [
  "Chest",
  "Back",
  "Legs",
  "Shoulders",
  "Arms",
  "Core",
  "Full Body",
];

const EXERCISE_LIBRARY = {
  Chest: [
    {
      name: "Bench Press", primaryMuscle: "Chest", secondaryMuscles: ["Arms", "Shoulders"], exerciseType: "compound", movementPattern: "horizontal_push",
      equipment: ["Barbell", "Dumbbell", "Machine", "Cable"],
    },
    {
      name: "Incline Press", primaryMuscle: "Chest", secondaryMuscles: ["Shoulders", "Arms"], exerciseType: "compound", movementPattern: "horizontal_push",
      equipment: ["Barbell", "Dumbbell", "Machine", "Cable"],
    },
    { name: "Decline Press", equipment: ["Barbell", "Dumbbell", "Machine"], primaryMuscle: "Chest", secondaryMuscles: ["Arms"], exerciseType: "compound", movementPattern: "horizontal_push", },
    { name: "Chest Fly", equipment: ["Dumbbell", "Cable", "Machine"], primaryMuscle: "Chest", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "horizontal_push", },
    { name: "Chest Press", equipment: ["Machine", "Cable"], primaryMuscle: "Chest", secondaryMuscles: ["Arms", "Shoulders"], exerciseType: "compound", movementPattern: "horizontal_push", },
    { name: "Push Up", equipment: ["Bodyweight"], primaryMuscle: "Chest", secondaryMuscles: ["Arms", "Shoulders"], exerciseType: "compound", movementPattern: "horizontal_push", },
    { name: "Dip", equipment: ["Bodyweight", "Machine"], primaryMuscle: "Chest", secondaryMuscles: ["Arms", "Shoulders"], exerciseType: "compound", movementPattern: "vertical_push", },
    { name: "Cable Crossover", equipment: ["Cable"], primaryMuscle: "Chest", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "horizontal_push", },
    { name: "Pec Deck", equipment: ["Machine"], primaryMuscle: "Chest", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "horizontal_push", },
  ],
  Back: [
    { name: "Deadlift", equipment: ["Barbell", "Dumbbell", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Legs"], exerciseType: "compound", movementPattern: "hinge", },
    { name: "Row", equipment: ["Barbell", "Dumbbell", "Cable", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Arms", "Shoulders"], exerciseType: "compound", movementPattern: "horizontal_pull", },
    { name: "Lat Pulldown", equipment: ["Cable", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Arms"], exerciseType: "compound", movementPattern: "vertical_pull", },
    { name: "Pull Up", equipment: ["Bodyweight", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Arms"], exerciseType: "compound", movementPattern: "vertical_pull", },
    { name: "Chin Up", equipment: ["Bodyweight", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Arms"], exerciseType: "compound", movementPattern: "vertical_pull", },
    { name: "Seated Cable Row", equipment: ["Cable", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Arms", "Shoulders"], exerciseType: "compound", movementPattern: "horizontal_pull", },
    { name: "Single Arm Row", equipment: ["Dumbbell", "Cable", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Arms"], exerciseType: "compound", movementPattern: "horizontal_pull", },
    { name: "Face Pull", equipment: ["Cable", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Shoulders"], exerciseType: "isolation", movementPattern: "horizontal_pull", },
    { name: "Shrug", equipment: ["Barbell", "Dumbbell", "Machine", "Cable"], primaryMuscle: "Back", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "other", },
    { name: "Hyperextension", equipment: ["Bodyweight", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Legs"], exerciseType: "isolation", movementPattern: "core_extension", },
  ],
  Legs: [
    {
      name: "Squat", primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "compound", movementPattern: "squat",
      equipment: ["Barbell", "Dumbbell", "Machine", "Bodyweight"],
    },
    { name: "Leg Press", equipment: ["Machine"], primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "compound", movementPattern: "squat", },
    { name: "Romanian Deadlift", equipment: ["Barbell", "Dumbbell", "Cable"], primaryMuscle: "Legs", secondaryMuscles: ["Back"], exerciseType: "compound", movementPattern: "hinge", },
    { name: "Leg Curl", equipment: ["Machine", "Cable", "Dumbbell"], primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "knee_flexion", },
    { name: "Leg Extension", equipment: ["Machine", "Cable"], primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "knee_extension", },
    {
      name: "Lunge", primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "compound", movementPattern: "lunge",
      equipment: ["Barbell", "Dumbbell", "Bodyweight", "Cable"],
    },
    {
      name: "Hip Thrust", primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "compound", movementPattern: "hinge",
      equipment: ["Barbell", "Dumbbell", "Machine", "Cable"],
    },
    {
      name: "Calf Raise", primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "calf",
      equipment: ["Machine", "Barbell", "Dumbbell", "Bodyweight"],
    },
    { name: "Hack Squat", equipment: ["Machine", "Barbell"], primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "compound", movementPattern: "squat", },
    { name: "Glute Kickback", equipment: ["Cable", "Machine", "Bodyweight"], primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "hinge", },
    { name: "Step Up", equipment: ["Barbell", "Dumbbell", "Bodyweight"], primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "compound", movementPattern: "lunge", },
  ],
  Shoulders: [
    {
      name: "Overhead Press", primaryMuscle: "Shoulders", secondaryMuscles: ["Arms"], exerciseType: "compound", movementPattern: "vertical_push",
      equipment: ["Barbell", "Dumbbell", "Machine", "Cable"],
    },
    { name: "Lateral Raise", equipment: ["Dumbbell", "Cable", "Machine"], primaryMuscle: "Shoulders", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "shoulder_abduction", },
    {
      name: "Front Raise", primaryMuscle: "Shoulders", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "shoulder_flexion",
      equipment: ["Barbell", "Dumbbell", "Cable", "Machine"],
    },
    { name: "Rear Delt Fly", equipment: ["Dumbbell", "Cable", "Machine"], primaryMuscle: "Shoulders", secondaryMuscles: ["Back"], exerciseType: "isolation", movementPattern: "horizontal_pull", },
    { name: "Arnold Press", equipment: ["Dumbbell"], primaryMuscle: "Shoulders", secondaryMuscles: ["Arms"], exerciseType: "compound", movementPattern: "vertical_push", },
    {
      name: "Upright Row", primaryMuscle: "Shoulders", secondaryMuscles: ["Back", "Arms"], exerciseType: "compound", movementPattern: "vertical_pull",
      equipment: ["Barbell", "Dumbbell", "Cable", "Machine"],
    },
    { name: "Face Pull", equipment: ["Cable", "Machine"], primaryMuscle: "Shoulders", secondaryMuscles: ["Back"], exerciseType: "isolation", movementPattern: "horizontal_pull", },
    { name: "Shrug", equipment: ["Barbell", "Dumbbell", "Machine", "Cable"], primaryMuscle: "Shoulders", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "other", },
  ],
  Arms: [
    {
      name: "Bicep Curl", primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_flexion",
      equipment: ["Barbell", "Dumbbell", "Cable", "Machine"],
    },
    { name: "Hammer Curl", equipment: ["Dumbbell", "Cable"], primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_flexion", },
    {
      name: "Preacher Curl", primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_flexion",
      equipment: ["Barbell", "Dumbbell", "Cable", "Machine"],
    },
    { name: "Concentration Curl", equipment: ["Dumbbell", "Cable"], primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_flexion", },
    { name: "Incline Curl", equipment: ["Dumbbell", "Cable"], primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_flexion", },
    { name: "Tricep Pushdown", equipment: ["Cable", "Machine"], primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_extension", },
    { name: "Skull Crusher", equipment: ["Barbell", "Dumbbell", "Cable"], primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_extension", },
    {
      name: "Overhead Tricep Ext", primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_extension",
      equipment: ["Barbell", "Dumbbell", "Cable", "Machine"],
    },
    { name: "Tricep Kickback", equipment: ["Dumbbell", "Cable"], primaryMuscle: "Arms", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "elbow_extension", },
    { name: "Close Grip Press", equipment: ["Barbell", "Machine"], primaryMuscle: "Arms", secondaryMuscles: ["Chest", "Shoulders"], exerciseType: "compound", movementPattern: "horizontal_push", },
    { name: "Dip", equipment: ["Bodyweight", "Machine"], primaryMuscle: "Arms", secondaryMuscles: ["Chest", "Shoulders"], exerciseType: "compound", movementPattern: "vertical_push", },
  ],
  Core: [
    { name: "Plank", equipment: ["Bodyweight"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "core_extension", },
    { name: "Crunch", equipment: ["Bodyweight", "Machine", "Cable"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "core_flexion", },
    { name: "Sit Up", equipment: ["Bodyweight"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "core_flexion", },
    { name: "Leg Raise", equipment: ["Bodyweight", "Machine", "Cable"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "core_flexion", },
    { name: "Russian Twist", equipment: ["Bodyweight", "Dumbbell", "Cable"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "core_rotation", },
    { name: "Cable Crunch", equipment: ["Cable"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "core_flexion", },
    { name: "Ab Rollout", equipment: ["Barbell", "Machine"], primaryMuscle: "Core", secondaryMuscles: ["Back"], exerciseType: "isolation", movementPattern: "core_extension", },
    { name: "Mountain Climber", equipment: ["Bodyweight"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "compound", movementPattern: "other", },
    { name: "Dead Bug", equipment: ["Bodyweight"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "core_flexion", },
    { name: "Hanging Knee Raise", equipment: ["Bodyweight"], primaryMuscle: "Core", secondaryMuscles: [], exerciseType: "isolation", movementPattern: "core_flexion", },
  ],
  "Full Body": [
    { name: "Deadlift", equipment: ["Barbell", "Dumbbell", "Machine"], primaryMuscle: "Back", secondaryMuscles: ["Legs"], exerciseType: "compound", movementPattern: "hinge", },
    { name: "Clean & Press", equipment: ["Barbell", "Dumbbell"], primaryMuscle: "Shoulders", secondaryMuscles: ["Legs", "Back"], exerciseType: "compound", movementPattern: "other", },
    { name: "Thruster", equipment: ["Barbell", "Dumbbell", "Machine"], primaryMuscle: "Legs", secondaryMuscles: ["Shoulders"], exerciseType: "compound", movementPattern: "other", },
    { name: "Kettlebell Swing", equipment: ["Dumbbell"], primaryMuscle: "Legs", secondaryMuscles: ["Back"], exerciseType: "compound", movementPattern: "hinge", },
    { name: "Burpee", equipment: ["Bodyweight"], primaryMuscle: "Legs", secondaryMuscles: ["Chest"], exerciseType: "compound", movementPattern: "other", },
    { name: "Box Jump", equipment: ["Bodyweight"], primaryMuscle: "Legs", secondaryMuscles: [], exerciseType: "compound", movementPattern: "other", },
    { name: "Farmer Carry", equipment: ["Barbell", "Dumbbell"], primaryMuscle: "Back", secondaryMuscles: [], exerciseType: "compound", movementPattern: "carry", },
    { name: "Battle Ropes", equipment: ["Machine"], primaryMuscle: "Shoulders", secondaryMuscles: ["Arms"], exerciseType: "compound", movementPattern: "other", },
  ],
};

// ── EXERCISE METADATA LOOKUP ────────────────────────────────────────
// Historical sessions store only { name, key, sets } — they were written
// before this metadata existed and are NOT migrated. Metadata is resolved
// centrally from the library at read time, keyed off the identity that
// history already carries, so every past session gains access to it without
// a single stored byte changing.
//
// Keys look like "Bench Press__Barbell" or "Bench Press__Cable__High": the
// base name is always the first segment. Four names appear in two groups
// (Dip, Deadlift, Face Pull, Shrug) and classify differently in each, so the
// session's muscle group is used to disambiguate when available.
const UNKNOWN_EXERCISE_META = {
  primaryMuscle: null,
  secondaryMuscles: [],
  exerciseType: "unknown",
  movementPattern: "other",
  isKnown: false,
};

function getExerciseMeta(keyOrName, sessionMuscleGroup) {
  if (!keyOrName) return { ...UNKNOWN_EXERCISE_META };
  // Strip the equipment/position suffixes and the "(Barbell)" display label.
  const base = String(keyOrName)
    .split("__")[0]
    .replace(/\s*\(.*\)\s*$/, "")
    .split(" — ")[0]
    .trim();

  const groupsToSearch = [];
  if (sessionMuscleGroup) {
    String(sessionMuscleGroup)
      .split(" + ")
      .forEach((g) => groupsToSearch.push(g.trim()));
  }
  Object.keys(EXERCISE_LIBRARY).forEach((g) => {
    if (!groupsToSearch.includes(g)) groupsToSearch.push(g);
  });

  for (const g of groupsToSearch) {
    const hit = (EXERCISE_LIBRARY[g] || []).find((e) => e.name === base);
    if (hit) {
      return {
        primaryMuscle: hit.primaryMuscle,
        secondaryMuscles: hit.secondaryMuscles || [],
        exerciseType: hit.exerciseType,
        movementPattern: hit.movementPattern,
        isKnown: true,
      };
    }
  }

  // Not in the library. Deliberately does NOT guess compound or isolation —
  // exerciseType stays "unknown" so no future model can silently treat an
  // unclassified movement as if it had been classified. primaryMuscle falls
  // back to the session's group only because that is genuinely the best
  // available signal about what was trained.
  return {
    ...UNKNOWN_EXERCISE_META,
    primaryMuscle: sessionMuscleGroup
      ? String(sessionMuscleGroup).split(" + ")[0].trim()
      : null,
  };
}


function isWorkingSet(set) {
  if (!set) return false;
  // Bodyweight movements store the user's bodyweight as `weight`, not 0 (see
  // the logger's isBodyweight branch), so load is never the test. The logger
  // already refuses reps <= 0, but historical or hand-edited data is not
  // guaranteed to have gone through it.
  const reps = Number(set.reps);
  return Number.isFinite(reps) && reps > 0;
}

// ── TRAINING DEMAND ENGINE (V1) ─────────────────────────────────────
// MEASUREMENT ONLY. Nothing downstream reads this score: nutrition, recovery
// and progression are all untouched. It exists so the ranking can be checked
// against real sessions before it is allowed to control anything.
//
// SOURCE OF TRUTH: getExerciseMeta() / EXERCISE_LIBRARY, exclusively.
// MOVEMENT_PATTERN_MAP disagrees with the library on Hip Thrust and Upright
// Row (it calls both isolation; the library calls both compound) and is
// deliberately NOT consulted here. It keeps its own separate job feeding
// selectAvoidingPatternOverlap in the workout builder.
//
// FORMULA — three bounded components, summing to at most 100:
//
//   setScore       0-60   the dominant signal, per the brief
//   repScore       0-20   metabolic cost, bounded
//   compoundScore  0-20   systemic involvement
//
// Every working set is converted into weighted "set units":
//
//   setUnits += regionFactor x typeFactor x involvementFactor
//
//   regionFactor       1.00  large region (Legs / Back / Chest)
//                      0.65  small region (Shoulders / Arms / Core)
//     A set of squats and a set of lateral raises are both one set, but they
//     are not the same amount of work. 0.65 is a coarse V1 distinction, not a
//     physiological claim.
//
//   typeFactor         1.15  compound
//                      1.00  isolation
//     Deliberately small. A compound set is not two isolation sets, and most
//     of its extra reach is already captured by its secondaryMuscles list
//     feeding involvementFactor below.
//
//   involvementFactor  1 + 0.05 per secondary muscle, capped at 1.10
//     This is what keeps secondary muscles from being double-counted. A bench
//     press with two secondaries earns 10% more demand, not a second set.
//     10 physical sets can never become 20.
//
// Load is NOT a multiplier anywhere. A 315lb set and a 135lb set are both one
// set. Tonnage is returned as a diagnostic only — see volumeLoad below.
const DEMAND_LARGE_REGIONS = ["Legs", "Back", "Chest"];
// Weighted units at which setScore reaches its 60-point ceiling. Roughly an
// 18-set session of large-muscle compounds — a genuinely hard workout.
const DEMAND_SET_UNIT_TARGET = 20;
const demandClamp01 = (v) => Math.max(0, Math.min(1, v));

function calculateTrainingDemand(session) {
  const direct = {};
  const indirect = {};
  let workingSets = 0;
  let totalReps = 0;
  let compoundSets = 0;
  let isolationSets = 0;
  let unknownSets = 0;
  let volumeLoad = 0;
  let setUnits = 0;
  let largeMuscleDirectSets = 0;
  let smallMuscleDirectSets = 0;

  (session?.exercises || []).forEach((ex) => {
    // Resolved from the library by exercise key, so historical sessions are
    // scored without being migrated.
    const meta = getExerciseMeta(ex.key || ex.name, session?.muscleGroup);
    const isLarge = DEMAND_LARGE_REGIONS.includes(meta.primaryMuscle);
    const regionFactor = isLarge ? 1.0 : 0.65;
    const typeFactor = meta.exerciseType === "compound" ? 1.15 : 1.0;
    const secondaries = meta.secondaryMuscles || [];
    const involvementFactor = Math.min(1.1, 1 + 0.05 * secondaries.length);

    (ex.sets || []).forEach((set) => {
      if (!isWorkingSet(set)) return;
      workingSets += 1;
      totalReps += Number(set.reps) || 0;
      volumeLoad += (Number(set.weight) || 0) * (Number(set.reps) || 0);

      // An unclassified exercise is counted as neither compound nor
      // isolation rather than being guessed into one. It still earns its
      // set units at the isolation rate, which is the conservative choice.
      if (!meta.isKnown) unknownSets += 1;
      else if (meta.exerciseType === "compound") compoundSets += 1;
      else isolationSets += 1;

      if (meta.primaryMuscle) {
        direct[meta.primaryMuscle] = (direct[meta.primaryMuscle] || 0) + 1;
        if (isLarge) largeMuscleDirectSets += 1;
        else smallMuscleDirectSets += 1;
      }
      // Same 1.0 / 0.5 split Step 10 established, reported for inspection.
      secondaries.forEach((m) => {
        indirect[m] = (indirect[m] || 0) + 0.5;
      });

      setUnits += regionFactor * typeFactor * involvementFactor;
    });
  });

  // ── SETS: the dominant term ──
  const setScore = 60 * demandClamp01(setUnits / DEMAND_SET_UNIT_TARGET);

  // ── REPS: bounded metabolic proxy ──
  // Scaled across an average of 3 to 18 reps per set, then gated by how many
  // sets were actually performed. The gate is what stops a single 50-rep set
  // from outweighing a whole workout: one set earns at most 1/12th of the
  // rep component no matter how many reps it contained.
  const avgReps = workingSets > 0 ? totalReps / workingSets : 0;
  const repIntensity = demandClamp01((avgReps - 3) / 15);
  const sessionSizeGate = demandClamp01(workingSets / 12);
  const repScore = 20 * repIntensity * sessionSizeGate;

  // ── COMPOUND SHARE ──
  // Gated the same way, so a 2-set session of nothing but squats cannot max
  // this component on proportion alone.
  const classified = compoundSets + isolationSets;
  const compoundShare = classified > 0 ? compoundSets / classified : 0;
  const compoundScore = 20 * compoundShare * sessionSizeGate;

  const raw = setScore + repScore + compoundScore;
  const score = Math.max(0, Math.min(100, Math.round(raw)));
  const level =
    score >= 80
      ? "Very High"
      : score >= 60
      ? "High"
      : score >= 40
      ? "Moderate"
      : score >= 20
      ? "Low"
      : "Very Low";

  const round1 = (o) =>
    Object.fromEntries(
      Object.entries(o).map(([k, v]) => [k, Math.round(v * 10) / 10])
    );

  return {
    score,
    level,
    components: {
      setScore: Math.round(setScore * 10) / 10,
      repScore: Math.round(repScore * 10) / 10,
      compoundScore: Math.round(compoundScore * 10) / 10,
    },
    workingSets,
    totalReps,
    avgReps: Math.round(avgReps * 10) / 10,
    compoundSets,
    isolationSets,
    unknownSets,
    setUnits: Math.round(setUnits * 100) / 100,
    directSetsByMuscle: round1(direct),
    indirectSetsByMuscle: round1(indirect),
    largeMuscleDirectSets,
    smallMuscleDirectSets,
    // Diagnostic only — NOT part of the score. Machine loading makes tonnage
    // incomparable across exercises: a leg press generates several times the
    // tonnage of a squat at similar effort, which would corrupt the ranking.
    volumeLoad,
    // Stored on the session but excluded from V1: it counts rest and idle
    // time, and a session resumed from auto-save can carry a distorted value.
    // Reported so it can be evaluated as a future signal.
    durationSecs: session?.duration ?? null,
  };
}


// ── MOVEMENT PATTERN CLASSIFICATION ──
// Backend-only data. Never displayed in the UI. Used purely to avoid stacking
// two compound exercises that share the same primary movement pattern back
// to back in an auto-suggested workout (e.g. Incline Barbell Press + Incline
// Dumbbell Press = same joint action, same primary mover, same angle —
// the second one is training an already-fatigued pattern, not a fresh one).
// Classification is based on joint action + primary mover overlap, not just
// muscle group, since that's what actually drives the interference effect.
//   pattern: shared movement signature (same angle/plane + same prime mover)
//   type: "compound" (multi-joint) or "isolation" (single-joint)
const MOVEMENT_PATTERN_MAP = {
  "Bench Press": { pattern: "flat-press", type: "compound" },
  "Incline Press": { pattern: "incline-press", type: "compound" },
  "Decline Press": { pattern: "decline-press", type: "compound" },
  "Chest Press": { pattern: "flat-press", type: "compound" },
  "Chest Fly": { pattern: "chest-fly", type: "isolation" },
  "Cable Crossover": { pattern: "chest-fly", type: "isolation" },
  "Pec Deck": { pattern: "chest-fly", type: "isolation" },
  "Push Up": { pattern: "flat-press", type: "compound" },
  Dip: { pattern: "decline-press", type: "compound" },
  Deadlift: { pattern: "hip-hinge", type: "compound" },
  Row: { pattern: "horizontal-pull", type: "compound" },
  "Seated Cable Row": { pattern: "horizontal-pull", type: "compound" },
  "Single Arm Row": { pattern: "horizontal-pull", type: "compound" },
  "Lat Pulldown": { pattern: "vertical-pull", type: "compound" },
  "Pull Up": { pattern: "vertical-pull", type: "compound" },
  "Chin Up": { pattern: "vertical-pull", type: "compound" },
  "Face Pull": { pattern: "rear-delt-pull", type: "isolation" },
  Shrug: { pattern: "shrug", type: "isolation" },
  Hyperextension: { pattern: "hip-hinge", type: "isolation" },
  Squat: { pattern: "squat", type: "compound" },
  "Hack Squat": { pattern: "squat", type: "compound" },
  "Leg Press": { pattern: "squat", type: "compound" },
  "Romanian Deadlift": { pattern: "hip-hinge", type: "compound" },
  "Hip Thrust": { pattern: "hip-hinge", type: "isolation" },
  Lunge: { pattern: "squat", type: "compound" },
  "Step Up": { pattern: "squat", type: "compound" },
  "Leg Curl": { pattern: "knee-flexion", type: "isolation" },
  "Leg Extension": { pattern: "knee-extension", type: "isolation" },
  "Calf Raise": { pattern: "calf-raise", type: "isolation" },
  "Glute Kickback": { pattern: "hip-extension", type: "isolation" },
  "Overhead Press": { pattern: "overhead-press", type: "compound" },
  "Arnold Press": { pattern: "overhead-press", type: "compound" },
  "Lateral Raise": { pattern: "lateral-raise", type: "isolation" },
  "Front Raise": { pattern: "front-raise", type: "isolation" },
  "Rear Delt Fly": { pattern: "rear-delt-pull", type: "isolation" },
  "Upright Row": { pattern: "upright-row", type: "isolation" },
  "Bicep Curl": { pattern: "elbow-flexion", type: "isolation" },
  "Hammer Curl": { pattern: "elbow-flexion", type: "isolation" },
  "Preacher Curl": { pattern: "elbow-flexion", type: "isolation" },
  "Concentration Curl": { pattern: "elbow-flexion", type: "isolation" },
  "Incline Curl": { pattern: "elbow-flexion", type: "isolation" },
  "Tricep Pushdown": { pattern: "elbow-extension", type: "isolation" },
  "Skull Crusher": { pattern: "elbow-extension", type: "isolation" },
  "Overhead Tricep Ext": { pattern: "elbow-extension", type: "isolation" },
  "Tricep Kickback": { pattern: "elbow-extension", type: "isolation" },
  "Close Grip Press": { pattern: "flat-press", type: "compound" },
  Plank: { pattern: "core-stability", type: "isolation" },
  Crunch: { pattern: "trunk-flexion", type: "isolation" },
  "Sit Up": { pattern: "trunk-flexion", type: "isolation" },
  "Leg Raise": { pattern: "hip-flexion", type: "isolation" },
  "Russian Twist": { pattern: "trunk-rotation", type: "isolation" },
  "Cable Crunch": { pattern: "trunk-flexion", type: "isolation" },
  "Ab Rollout": { pattern: "core-stability", type: "isolation" },
  "Mountain Climber": { pattern: "core-stability", type: "compound" },
  "Dead Bug": { pattern: "core-stability", type: "isolation" },
  "Hanging Knee Raise": { pattern: "hip-flexion", type: "isolation" },
  "Clean & Press": { pattern: "overhead-press", type: "compound" },
  Thruster: { pattern: "squat", type: "compound" },
  "Kettlebell Swing": { pattern: "hip-hinge", type: "compound" },
  Burpee: { pattern: "core-stability", type: "compound" },
  "Box Jump": { pattern: "squat", type: "compound" },
  "Farmer Carry": { pattern: "loaded-carry", type: "compound" },
  "Battle Ropes": { pattern: "core-stability", type: "compound" },
};

function getMovementPattern(exerciseKey) {
  const baseName = exerciseKey.split("__")[0];
  return (
    MOVEMENT_PATTERN_MAP[baseName] || {
      pattern: "unclassified",
      type: "isolation",
    }
  );
}

const GRIP_OPTIONS = {
  "Lat Pulldown": [
    "Wide Overhand",
    "Close Neutral",
    "Close Underhand",
    "Reverse",
  ],
  "Pull Up": ["Wide Overhand", "Close Neutral", "Shoulder Width"],
  "Chin Up": ["Supinated (underhand)", "Neutral"],
  Row: ["Overhand", "Underhand", "Neutral"],
  "Seated Cable Row": ["Wide", "Close Neutral", "Close Overhand"],
  "Single Arm Row": ["Neutral", "Supinated"],
  "Bicep Curl": ["Supinated (underhand)", "Neutral", "Overhand (reverse)"],
  "Preacher Curl": ["Supinated", "Neutral"],
  "Tricep Pushdown": ["Overhand", "Underhand (reverse)", "Neutral rope"],
  "Overhead Press": ["Overhand", "Neutral"],
  "Upright Row": ["Overhand", "Neutral"],
  Deadlift: ["Double Overhand", "Mixed", "Hook"],
};

const CABLE_POSITIONS = {
  "Chest Fly": ["High Cable", "Mid Cable", "Low Cable"],
  "Cable Crossover": ["High Cable", "Mid Cable", "Low Cable"],
  "Lat Pulldown": null, // grips only
  "Face Pull": ["High", "Mid"],
  "Lateral Raise": ["Low", "Mid"],
  "Rear Delt Fly": ["High", "Mid"],
  "Tricep Pushdown": null, // grips only
  "Bicep Curl": ["Low (standing)", "High (seated)"],
};

const GROUP_COLORS = {
  Chest: "var(--accent)",
  Back: "var(--accent)",
  Legs: "var(--accent)",
  Shoulders: "var(--accent)",
  Arms: "var(--accent)",
  Core: "var(--accent)",
  "Full Body": "var(--accent)",
};

const EQUIPMENT_ICONS = {
  Barbell: "Barbell",
  Dumbbell: "Dumbbell",
  Cable: "Cable",
  Machine: "Machine",
  Bodyweight: "Bodyweight",
};

// Returns the right Ic.* icon for each equipment type used in the exercise picker
function EquipIcon({ eq, s = 15 }) {
  const c = "var(--muted)";
  switch (eq) {
    case "Barbell":
      return <Ic.Barbell s={s} c={c} />;
    case "Dumbbell":
      return <Ic.Dumbbell s={s} c={c} />;
    case "Cable":
      return <Ic.Cable s={s} c={c} />;
    case "Machine":
      return <Ic.Gear s={s} c={c} />;
    case "Bodyweight":
      return <Ic.Muscle s={s} c={c} />;
    case "Kettlebell":
      return <Ic.Dumbbell s={s} c={c} />;
    default:
      return (
        <span style={{ fontSize: 11, color: "var(--muted)" }}>
          {eq.slice(0, 2).toUpperCase()}
        </span>
      );
  }
}

const GOALS = {
  strength: { label: "Strength", weightIncrease: 5, minReps: 3, maxReps: 6 },
  hypertrophy: {
    label: "Muscle Growth",
    weightIncrease: 5,
    minReps: 8,
    maxReps: 12,
  },
  endurance: {
    label: "Endurance",
    weightIncrease: 2.5,
    minReps: 12,
    maxReps: 20,
  },
};

const GROUP_CARB_FACTOR = {
  Legs: 1.35, // largest muscle mass, highest glycogen depletion
  Back: 1.2, // second largest group, significant glycogen use
  "Full Body": 1.3, // total body glycogen depletion
  Chest: 1.1, // moderate glycogen use
  Shoulders: 1.0, // moderate — similar to maintenance
  Arms: 0.8, // small muscle mass, minimal glycogen depletion
  Core: 0.75, // isolation, minimal systemic glycogen use
  Rest: 0.6, // no training — minimal carb need beyond baseline
};

const ACTIVITY_MULTIPLIER = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  very: 1.725,
  extreme: 1.9,
};

// Hard safety guardrail, NOT a recommendation. This does not assert that 1000
// kcal is an appropriate intake for anyone — it is the line below which the app
// refuses to issue a prescription at all, because a target of 445 or -140 kcal
// is a calculation artefact rather than a diet.
const ABSOLUTE_MIN_CALORIES = 1000;

// calAdjust is the user's manual ±250 kcal override from Settings. It is a
// parameter here rather than being added by each screen afterwards, because
// adding it downstream meant fat and carbs were still sized against the
// unadjusted number and different screens showed different targets.
// Resolves height from a profile in priority order. Exists because onboarding
// used to persist only the formatted display string ("6'2\"") and drop the raw
// heightFt/heightIn fields, so calcMacros fell through to its 5'10" default for
// every single user — BMR was computed at 5'10" no matter what anyone entered.
//
// Order: raw fields → parse the legacy display string → 5'10" as a last resort.
function resolveHeight(profile) {
  const ftRaw = profile?.heightFt;
  const inRaw = profile?.heightIn;
  const ft = Number(ftRaw);
  const inch = Number(inRaw);

  // Note the explicit empty/null checks rather than `Number(x) || fallback`.
  // A legitimate 0 inches is falsy, so the old `|| 10` silently turned every
  // exact-foot height (6'0") into 6'10".
  const ftOk = ftRaw !== "" && ftRaw != null && Number.isFinite(ft) && ft > 0;
  const inOk = inRaw !== "" && inRaw != null && Number.isFinite(inch) && inch >= 0 && inch < 12;
  if (ftOk && inOk) return { heightFt: ft, heightIn: inch };
  if (ftOk) return { heightFt: ft, heightIn: 0 };

  // Legacy profiles saved before this fix only have the display string.
  // Onboarding always wrote `${ft}'${in}"`, and the Settings text field is
  // free-form, so the parser is deliberately tolerant of spacing and a
  // missing trailing inch mark.
  const str = typeof profile?.height === "string" ? profile.height : "";
  const m = str.match(/(\d{1,2})\s*'\s*(\d{1,2})?/);
  if (m) {
    const pf = Number(m[1]);
    const pi = m[2] == null ? 0 : Number(m[2]);
    if (Number.isFinite(pf) && pf > 0 && Number.isFinite(pi) && pi >= 0 && pi < 12) {
      return { heightFt: pf, heightIn: pi };
    }
  }

  return { heightFt: 5, heightIn: 10 };
}

function calcMacros(profile, muscleGroup, calAdjust = 0) {
  // Defensive only, same as the age fallback below. A profile with an invalid
  // weight is intercepted by ProfileCompletionScreen before any nutrition
  // target is shown, so this should be unreachable — it exists so a malformed
  // profile cannot crash the calculation, not as a stand-in for a real weight.
  const weight = Number(profile.weight) || 180;
  // Defensive only. Age is now required by onboarding and legacy profiles are
  // intercepted by ProfileCompletionScreen before any nutrition target is shown,
  // so this fallback should be unreachable in practice — it exists so a
  // malformed profile cannot crash the calculation, not as a default.
  const age = Number(profile.age) || 25;
  const sex = profile.sex || "male";
  const { heightFt, heightIn } = resolveHeight(profile);
  const actMult = ACTIVITY_MULTIPLIER[profile.activity] || 1.55;
  const bodyGoal = profile.bodyGoal || "recomp";

  // Muscle-group detection is kept intact and still resolved, but its factor
  // is NO LONGER APPLIED to the macro prescription. See the note below.
  const primaryGroup = muscleGroup
    ?.split(" + ")
    .map((g) => g.trim())
    .reduce((best, g) => {
      const f = GROUP_CARB_FACTOR[g] || 0;
      return f > (GROUP_CARB_FACTOR[best] || 0) ? g : best;
    }, muscleGroup?.split(" + ")[0]?.trim() || "Rest");
  const carbFactor = GROUP_CARB_FACTOR[primaryGroup] || GROUP_CARB_FACTOR.Rest;

  const weightKg = weight * 0.453592;
  const heightCm = (heightFt * 12 + heightIn) * 2.54;
  const bmr =
    sex === "male"
      ? 10 * weightKg + 6.25 * heightCm - 5 * age + 5
      : 10 * weightKg + 6.25 * heightCm - 5 * age - 161;
  const tdee = Math.round(bmr * actMult);

  const calBonus = { bulk: 300, cut: -400, recomp: 0 }[bodyGoal] || 0;

  // AUTHORITATIVE TARGET. Everything below is a split of this number; nothing
  // is allowed to change it.
  //
  // rawCalories is kept separate so the UI can tell the user what their
  // settings actually produced, rather than silently presenting the floor as
  // if it were a normal calculated result.
  const rawCalories = tdee + calBonus + calAdjust;
  const baseCalories = Math.max(ABSOLUTE_MIN_CALORIES, rawCalories);
  const calorieFloorApplied = rawCalories < ABSOLUTE_MIN_CALORIES;

  // Protein per lb of TOTAL bodyweight. Lowered from 1.0/1.2/1.1 because
  // applying the old figures to total bodyweight produced impossible plans for
  // heavier users: a 200lb sedentary woman on a cut got 240g protein (960 kcal)
  // plus 35g fat (315 kcal) = 1275 kcal against a 1257 kcal target, before any
  // carbohydrate at all. The Step 1 clamp hid that by zeroing carbs; it did not
  // fix it. Cut stays highest because protein matters most under restriction.
  const proteinPerLb = { bulk: 0.8, cut: 1.0, recomp: 0.9 }[bodyGoal] || 0.9;
  const fatPct = { bulk: 0.25, cut: 0.25, recomp: 0.3 }[bodyGoal] || 0.28;

  let protein = Math.round(weight * proteinPerLb);
  // Math.max(0, ...) because baseCalories can go negative: calAdjust has no
  // lower bound, so repeated -250 taps eventually push the target below zero
  // and a percentage of a negative number is a negative fat target. A macro
  // gram count can never be negative. This is not a calorie floor — the target
  // itself is left alone, deliberately, since choosing a minimum is a separate
  // decision.
  const fat = Math.max(0, Math.round((baseCalories * fatPct) / 9));

  // ── GROUP_CARB_FACTOR IS INTENTIONALLY NOT APPLIED HERE ──
  // It used to multiply the carb remainder, after which total calories were
  // recomputed FROM the macros and returned as the target. Because the factor
  // ranged 0.60–1.35, it silently created or destroyed energy: a bulking user
  // on Legs asked for a +300 surplus and received roughly +993, while a
  // cutting user on a rest day was pushed hundreds of calories below their
  // intended deficit. The muscle group was changing total intake, not just its
  // composition.
  //
  // The constant, the group detection above, and the returned carbFactor are
  // all deliberately retained. Carbohydrate periodisation is a real feature
  // worth having, but it has to redistribute energy within a fixed target
  // rather than alter the target. That is a later step.
  // ── FEASIBILITY SAFEGUARD ──
  // Lowering the multipliers makes impossible plans rare, not impossible. A
  // very low target — a heavy user on a cut, or a large manual calAdjust —
  // can still leave protein + fat above the whole budget. This is a last
  // resort, not the normal path: protein is trimmed just enough to fit inside
  // what fat leaves behind, and the calorie target is never raised to
  // accommodate it.
  //
  // Carbs necessarily land at 0 here. Preserving any would require either an
  // arbitrary carb floor or cutting into fat, and both are deliberately out of
  // scope — so the honest result is zero rather than a number pulled from
  // nowhere.
  let feasibilityClamped = false;
  if (protein * 4 + fat * 9 > baseCalories) {
    feasibilityClamped = true;
    protein = Math.max(0, Math.floor(Math.max(0, baseCalories - fat * 9) / 4));
  }

  const carbCals = baseCalories - protein * 4 - fat * 9;
  const carbs = Math.max(0, Math.round(carbCals / 4));

  return {
    calories: baseCalories,
    protein,
    carbs,
    fat,
    tdee,
    carbFactor,
    feasibilityClamped,
    rawCalories,
    calorieFloorApplied,
  };
}

function smartRestSuggestion(goal, reps) {
  if (goal === "strength") return 180; // 3 min
  if (goal === "endurance") return 60; // 1 min
  if (reps && reps <= 6) return 180; // heavy set — more rest
  if (reps && reps >= 12) return 60; // lighter set — less rest
  return 90; // default hypertrophy: 90s
}

const MIN_SESSIONS_FOR_ADAPTIVE = 4;
const STALL_SESSIONS = 3;

function getEquipmentIncrement(exerciseName) {
  const name = (exerciseName || "").toLowerCase();
  // Isolation exercises with small load ranges — 2.5lb increment
  // These are movements where 5lbs is too large a jump relative to total load
  const isolationPatterns = [
    "curl",
    "fly",
    "flye",
    "lateral raise",
    "front raise",
    "rear delt",
    "kickback",
    "extension",
    "pushdown",
    "crunch",
    "raise",
    "shrug",
    "calf",
    "leg curl",
    "leg extension",
    "concentration",
    "preacher",
    "pec deck",
    "cable crossover",
    "face pull",
  ];
  if (isolationPatterns.some((p) => name.includes(p))) return 2.5;
  // Barbell compound movements — 5lb standard plate increment
  if (name.includes("barbell")) return 5;
  // Dumbbell compound (press, row, squat variants) — 5lbs
  if (
    name.includes("dumbbell") &&
    (name.includes("press") ||
      name.includes("row") ||
      name.includes("squat") ||
      name.includes("deadlift") ||
      name.includes("lunge"))
  )
    return 5;
  // Default: 2.5lbs — safer starting assumption, adaptive engine will correct
  return 2.5;
}

function roundToIncrement(weight, increment) {
  return Math.round(weight / increment) * increment;
}

function detectSetPattern(sets) {
  const working = sets.filter((s) => s.type !== "super");
  if (working.length < 2) return "insufficient";
  const weights = working.map((s) => s.weight);
  const allSame = weights.every((w) => w === weights[0]);
  if (allSame) return "straight";
  const isIncreasing = weights.every((w, i) => i === 0 || w >= weights[i - 1]);
  if (isIncreasing) return "pyramid";
  const isDecreasing = weights.every((w, i) => i === 0 || w <= weights[i - 1]);
  if (isDecreasing) return "reverse-pyramid";
  return "mixed";
}

function analyzeExerciseHistory(exerciseKey, sessions, goal) {
  const g = GOALS[goal] || GOALS.hypertrophy;
  const history = [];

  sessions.forEach((s) => {
    const ex = s.exercises.find((e) => e.key === exerciseKey);
    if (!ex) return;
    const validSets = ex.sets.filter((s) => s.type !== "super");
    if (validSets.length === 0) return;
    const bestSet = validSets.reduce(
      (best, s) => (s.weight > best.weight ? s : best),
      validSets[0]
    );
    history.push({
      date: s.date,
      timestamp: s.id || new Date(s.date).getTime(),
      weight: bestSet.weight,
      reps: bestSet.reps,
      e1RM: Math.round(bestSet.weight * (1 + bestSet.reps / 30)),
    });
  });

  history.sort((a, b) => a.timestamp - b.timestamp);
  const sessionCount = history.length;

  if (sessionCount < MIN_SESSIONS_FOR_ADAPTIVE) {
    return {
      strategy: "generic",
      sessionCount,
      remaining: MIN_SESSIONS_FOR_ADAPTIVE - sessionCount,
      confidence: "low",
      reason: `${MIN_SESSIONS_FOR_ADAPTIVE - sessionCount} more session${
        MIN_SESSIONS_FOR_ADAPTIVE - sessionCount !== 1 ? "s" : ""
      } needed for personalized targets`,
    };
  }

  const recent = history.slice(-STALL_SESSIONS);
  const recentWeights = recent.map((h) => h.weight);
  const recentReps = recent.map((h) => h.reps);
  const weightStalled = recentWeights.every((w) => w === recentWeights[0]);
  const repsStalled = recentReps.every((r) => r === recentReps[0]);
  const repsIncreasing =
    recentReps.length > 1 && recentReps[recentReps.length - 1] > recentReps[0];

  if (weightStalled && repsStalled) {
    return {
      strategy: "stall",
      sessionCount,
      confidence: "high",
      stalledWeight: recentWeights[0],
      stalledReps: recentReps[0],
      reason: `Weight and reps unchanged for ${STALL_SESSIONS} sessions — try a deload or form focus`,
    };
  }

  const lastTwo = history.slice(-2);
  if (lastTwo.length === 2 && lastTwo[1].weight < lastTwo[0].weight) {
    return {
      strategy: "regression",
      sessionCount,
      confidence: "high",
      lastWeight: lastTwo[0].weight,
      thisWeight: lastTwo[1].weight,
      reason: `Weight dropped ${lastTwo[0].weight}→${lastTwo[1].weight}lbs — return to last good weight`,
    };
  }

  const times = history.map(
    (h) => (h.timestamp - history[0].timestamp) / (1000 * 60 * 60 * 24 * 7)
  );
  const e1RMs = history.map((h) => h.e1RM);
  const n = history.length;
  const sumX = times.reduce((a, b) => a + b, 0);
  const sumY = e1RMs.reduce((a, b) => a + b, 0);
  const sumXY = times.reduce((a, t, i) => a + t * e1RMs[i], 0);
  const sumX2 = times.reduce((a, t) => a + t * t, 0);
  const denom = n * sumX2 - sumX * sumX;
  const ropRaw = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;

  // Cap at ±20 lbs/wk — physically impossible to exceed this naturally.
  // This prevents same-day test sessions (near-zero time diff) from
  // producing astronomical slopes while not affecting real users at all.
  const ropPerWeek = Math.max(-20, Math.min(20, ropRaw));
  const ropDisplay = Math.round(ropPerWeek * 10) / 10;
  const increment = getEquipmentIncrement(exerciseKey);

  let adaptiveJump, strategy, confidence;

  if (ropPerWeek >= 5) {
    adaptiveJump = roundToIncrement(Math.min(ropPerWeek * 1.5, 15), increment);
    strategy = "fast";
    confidence = "high";
  } else if (ropPerWeek >= 2) {
    adaptiveJump = roundToIncrement(
      Math.max(ropPerWeek * 1.2, increment),
      increment
    );
    strategy = "moderate";
    confidence = "medium";
  } else if (ropPerWeek > 0) {
    adaptiveJump = increment;
    strategy = "slow";
    confidence = "medium";
  } else {
    adaptiveJump = 0;
    strategy = "rep-focus";
    confidence = "medium";
  }

  return {
    strategy,
    sessionCount,
    confidence,
    ropPerWeek: ropDisplay,
    adaptiveJump: Math.max(adaptiveJump, 0),
    lastHistory: history[history.length - 1],
    reason:
      strategy === "fast"
        ? `Progressing +${ropDisplay}lbs/wk — larger jump applied`
        : strategy === "moderate"
        ? `Steady progress +${ropDisplay}lbs/wk — personalized`
        : strategy === "slow"
        ? `Slow progress — minimum increment`
        : `No weight gains — push reps before adding weight`,
  };
}

// getRecommendations produces the SINGLE summary target stored as ex.poTarget.
// It is used for: the muscle-options preview, hit/miss grading on the summary
// screens, and as the carrier of _goal that buildSetTargets reads.
//
// CRITICAL DESIGN RULE (per-set independence):
// The prescribed weight/reps ALWAYS come from applying the same per-set rule
// used in buildSetTargets to the supplied set, using the FIXED equipment
// increment. The cross-session regression in analyzeExerciseHistory is NEVER
// allowed to move the prescribed number — it is demoted to descriptive insight
// only. Its detections (stall / regression / rep-focus / rate of progress)
// contribute a sentence to `reason`, so the user still gets the coaching
// narrative ("+3.1 lbs/wk", "weight unchanged for 3 sessions — consider a
// deload"), but the actual target stays predictable and traceable to the set
// in front of them. This is what keeps the header summary and the per-set ramp
// strip from ever contradicting each other.
function getRecommendations(
  lastSet,
  goal,
  exerciseKey = null,
  allSessions = null
) {
  const g = GOALS[goal] || GOALS.hypertrophy;
  const { weight, reps } = lastSet;
  const increment = getEquipmentIncrement(exerciseKey || "");

  const analysis =
    exerciseKey && allSessions
      ? analyzeExerciseHistory(exerciseKey, allSessions, goal)
      : null;

  // ── INSIGHT TEXT (narrative only — never moves the numbers) ──
  // Pulled from the regression analysis when available. Prepended to the
  // prescription's own reason so the user sees the coaching context without
  // it ever overriding the per-set math.
  let insight = "";
  if (analysis) {
    if (analysis.strategy === "stall") {
      insight =
        "Weight and reps flat for 3 sessions — consider a deload or form focus. ";
    } else if (analysis.strategy === "regression") {
      insight = `Best-set weight dipped recently (${analysis.lastWeight}→${analysis.thisWeight}lbs). `;
    } else if (analysis.strategy === "rep-focus") {
      insight = "Trending toward reps over load lately. ";
    } else if (
      typeof analysis.ropPerWeek === "number" &&
      analysis.ropPerWeek !== 0
    ) {
      const dir = analysis.ropPerWeek > 0 ? "+" : "";
      insight = `Trending ${dir}${analysis.ropPerWeek}lbs/wk. `;
    }
  }
  const adaptive = !!analysis;

  // ── PRESCRIPTION (per-set rule on the supplied set, fixed increment) ──
  // Mirrors buildSetTargets exactly so the summary equals the top set of the
  // ramp. Three cases, one variable changed at a time:
  if (reps >= g.maxReps) {
    const newWeight = roundToIncrement(weight + increment, increment);
    return [
      {
        weight: newWeight,
        reps: g.minReps,
        label: adaptive ? "ADAPTIVE TARGET" : "INCREASE WEIGHT",
        reason: `${insight}Hit ${reps} reps — add weight to ${newWeight}lbs, reset to ${g.minReps} reps.`,
        adaptive,
        flag: "increase",
        _goal: g,
      },
    ];
  }

  if (reps < g.minReps) {
    return [
      {
        weight,
        reps: Math.min(reps + 1, g.minReps),
        label: adaptive ? "ADAPTIVE TARGET" : "BUILD REPS",
        reason: `${insight}Build toward ${g.minReps} reps at this weight before adding load.`,
        adaptive,
        flag: "build",
        _goal: g,
      },
    ];
  }

  // within range → push one rep, same weight
  return [
    {
      weight,
      reps: reps + 1,
      label: adaptive ? "ADAPTIVE TARGET" : "ADD REPS",
      reason: `${insight}Same weight — push for ${reps + 1} reps (goal: ${
        g.maxReps
      }).`,
      adaptive,
      flag: "reps",
      _goal: g,
    },
  ];
}

function buildSetTargets(lastSets, poRec, increment) {
  const working = lastSets.filter((s) => s.type !== "super");
  if (working.length === 0 || !poRec) return [];

  const inc = increment || 2.5;
  const g = poRec._goal || { minReps: 8, maxReps: 12, weightIncrease: inc };
  // Fixed, equipment-appropriate increment — NOT the regression's adaptiveJump.
  // The cross-session e1RM regression in analyzeExerciseHistory is noisy when a
  // user's top set weight varies session to session (e.g. pyramids where some
  // sessions include a heavy top set and others don't), which produced jumps as
  // large as 55lbs and targets detached from the sets actually performed. The
  // prescription must be predictable and per-set, so it always uses the fixed
  // increment. The adaptive regression survives only as descriptive insight
  // text (see getRecommendations) — it never moves the prescribed weight.
  const jump = inc;

  // Pure per-set independent progression.
  // Each set is evaluated solely on its own last-session reps vs the goal rep
  // range — no pyramid/pattern logic that could override individual set needs.
  // This correctly handles pyramids (and any other set structure) where
  // different sets may be at different stages of the progression:
  //   • Set at maxReps → add weight, reset to minReps
  //   • Set below minReps → push toward minReps, same weight
  //   • Set within range → add one rep, same weight
  return working.map((s, i) => {
    if (s.reps >= g.maxReps) {
      return {
        weight: roundToIncrement(s.weight + jump, inc),
        reps: g.minReps,
        flag: "increase",
        isTarget: true,
        setNum: i + 1,
      };
    } else if (s.reps < g.minReps) {
      return {
        weight: s.weight,
        reps: Math.min(s.reps + 1, g.minReps),
        flag: "build",
        isTarget: true,
        setNum: i + 1,
      };
    } else {
      return {
        weight: s.weight,
        reps: s.reps + 1,
        flag: "reps",
        isTarget: true,
        setNum: i + 1,
      };
    }
  });
}

// ── ICON LIBRARY ──
// All icons share the same visual language:
//   • strokeWidth: 1.8  (consistent optical weight)
//   • strokeLinecap: round, strokeLinejoin: round
//   • viewBox: 0 0 24 24, no fill
//   • Each icon fills ~75% of viewbox — never cramped, never overflowing
// Never add gradients, fills, or drop shadows to these icons.
// Color and size are always passed in as props.
const Ic = (() => {
  // 740 lines of hand-drawn SVG replaced by one icon family.
  //
  // The 22 originals were fine individually but drifted from each other, and
  // anything without a match had to fall back to emoji. Lucide draws every
  // icon on the same 24px grid at the same stroke weight, so they read as a
  // set — and there are ~6,200 of them, so there's no longer a reason to
  // reach for an emoji.
  //
  // The Ic.Name API and its { s, c } props are kept deliberately: 138 call
  // sites already use them, so nothing downstream has to change. This shim
  // translates to lucide's { size, color }.
  const wrap = (C) =>
    function Icon({ s = 22, c = "currentColor", filled = false, ...rest }) {
      return (
        <C
          size={s}
          color={c}
          strokeWidth={2}
          fill={filled ? "currentColor" : "none"}
          fillOpacity={filled ? 0.22 : 0}
          {...rest}
        />
      );
    };
  const m = {
    Apple: Apple, ArrowDown: ArrowDown, ArrowUp: ArrowUp,
    Barbell: Dumbbell, Battery: BatteryMedium, Bell: Bell,
    Brain: Brain, Cable: Cable, Calendar: Calendar, Camera: Camera,
    Chair: Armchair, ChartBar: BarChart3, Check: Check,
    ChevDown: ChevronDown, ChevUp: ChevronUp, Clipboard: ClipboardList,
    Clock: Clock, Dice: Dices, Droplet: Droplet, Dumbbell: Dumbbell,
    Food: Utensils, Gear: Settings, Hammer: Hammer, Info: Info,
    Leaf: Leaf, Meat: Beef, Moon: Moon, Muscle: Zap,
    Pause: Pause, Pencil: Pencil, Plus: Plus, Refresh: RefreshCw,
    Run: Footprints, Scale: Scale, Shuffle: Shuffle, Target: Target,
    Timer: Timer, Trash: Trash2, TrendUp: TrendingUp, Upload: Upload,
    Walk: Footprints, Yoga: PersonStanding,
    // Newly available — no emoji fallback needed for these any more.
    Flame: Flame, Trophy: Trophy, Search: Search, Sun: Sun,
    Save: Save, Bulb: Lightbulb, Close: X, Bike: Bike,
    Swim: Waves, Heart: HeartPulse, Rope: Infinity, Stairs: TrendingUp,
    Rowing: Sailboat, Sunrise: Sunrise, Rice: Wheat, Package: Package,
  };
  return Object.fromEntries(Object.entries(m).map(([k, C]) => [k, wrap(C)]));
})();

// ── SCREEN HEADER ───────────────────────────────────────────────────
// Every screen currently rolls its own title markup, which is why headings
// sit at different sizes and alignments as you navigate. One component means
// one shape: small accent eyebrow, big left-aligned title, grey subtitle.
//
// Left-aligned deliberately — centred headings are the single loudest
// "unfinished" signal, and a hard left edge is what makes consecutive screens
// feel like one app rather than a stack of unrelated pages.
function ScreenHeader({ eyebrow, title, sub, action }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 20,
      }}
    >
      <div style={{ textAlign: "left", minWidth: 0 }}>
        {eyebrow && (
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: "var(--accent)",
              marginBottom: 6,
            }}
          >
            {eyebrow}
          </p>
        )}
        <p
          style={{
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: -0.5,
            lineHeight: 1.15,
            color: "var(--text)",
          }}
        >
          {title}
        </p>
        {sub && (
          <p
            style={{
              fontSize: 13,
              color: "var(--muted)",
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            {sub}
          </p>
        )}
      </div>
      {action ? <div style={{ flexShrink: 0 }}>{action}</div> : null}
    </div>
  );
}

function load(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}
function save(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {}
}

// ── COUNT-UP NUMBER ──
// Animates a numeric value from its previous value up (or down) to the new
// target whenever it changes, using an ease-out curve so it settles smoothly
// rather than ticking linearly. Falls back to rendering instantly with no
// animation if the value isn't a clean finite number (e.g. pre-formatted
// strings like "4.2k" or "—") — animating those would look broken, so we
// only ever animate genuine numeric stats (Sets, Reps, streak counts, etc).
function CountUp({ value, duration = 600, decimals = 0 }) {
  const isAnimatable = typeof value === "number" && Number.isFinite(value);
  const [display, setDisplay] = useState(isAnimatable ? value : 0);
  const fromRef = useRef(isAnimatable ? value : 0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!isAnimatable) return; // non-numeric values render as-is below, skip animation
    const from = fromRef.current;
    const to = value;
    if (from === to) return;

    startRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const step = (ts) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const current = from + (to - from) * eased;
      setDisplay(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, isAnimatable, duration]);

  if (!isAnimatable) return <>{value}</>;

  const rounded =
    decimals > 0 ? display.toFixed(decimals) : Math.round(display);
  return <>{rounded}</>;
}

function MuscleIcon({ group, size = 28, color = "var(--accent)" }) {
  const letter =
    group === "Full Body"
      ? "FB"
      : group === "Core"
      ? "CR"
      : group === "Chest"
      ? "CH"
      : group[0];
  const fontSize = letter.length > 1 ? size * 0.38 : size * 0.46;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.28,
        background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.126)`,
        border: `1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.333)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontSize,
          fontWeight: 700,
          color: "var(--text)",
          fontFamily: "'DM Sans', sans-serif",
          letterSpacing: letter.length > 1 ? -0.5 : 0,
          lineHeight: 1,
        }}
      >
        {letter}
      </span>
    </div>
  );
}

export default function App() {
  const [showSplash, setShowSplash] = useState(() => {
    return !!load("wt_profile", null);
  });
  const [screen, setScreen] = useState(() =>
    load("wt_profile", null) ? "home" : "onboard"
  );
  const [tab, setTab] = useState("workout"); // "workout" | "nutrition"
  const [profile, setProfile] = useState(() =>
    load("wt_profile", {
      name: "",
      weight: "",
      goal: "hypertrophy",
      bodyGoal: "recomp",
    })
  );
  const [sessions, setSessions] = useState(() => load("wt_sessions", []));
  const [activeSession, setActiveSession] = useState(null);
  const [foodLogs, setFoodLogs] = useState(() => load("wt_food", {}));
  const [savedSession, setSavedSession] = useState(() => {
    try {
      const raw = localStorage.getItem("wt_active_session");
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (!s || !s.savedAt) return null;
      // Only offer resume if saved less than 6 hours ago
      if (Date.now() - s.savedAt > 6 * 60 * 60 * 1000) {
        localStorage.removeItem("wt_active_session");
        return null;
      }
      // Only offer if there are actual sets logged
      const totalSets = (s.session?.exercises || []).reduce(
        (a, ex) => a + ex.sets.length,
        0
      );
      if (totalSets === 0) return null;
      return s;
    } catch {
      return null;
    }
  });

  const saveProfile = (p) => {
    setProfile(p);
    save("wt_profile", p);
  };
  const saveSessions = (s) => {
    setSessions(s);
    save("wt_sessions", s);
  };

  const updateSession = (updatedSession) => {
    saveSessions(
      sessions.map((s) => (s.id === updatedSession.id ? updatedSession : s))
    );
  };
  const saveFoodLogs = (f) => {
    setFoodLogs(f);
    save("wt_food", f);
  };

  const [settings, setSettings] = useState(() =>
    load("wt_settings", {
      darkMode: true,
      timeFormat: "12hr",
      showExerciseInfo: true,
      autoTimer: true,
      manualRest: true,
      calAdjust: 0,
      vibration: true,
      showLastSession: true,
      showPOTargets: true,
      show1RM: true,
    })
  );

  const updateSetting = (key, val) => {
    const updated = { ...settings, [key]: val };
    setSettings(updated);
    save("wt_settings", updated);
    if (key === "darkMode") {
      document.body.className = val ? "theme-dark" : "theme-light";
    }
  };

  const bulkUpdateSettings = (newVals) => {
    const updated = { ...settings, ...newVals };
    setSettings(updated);
    save("wt_settings", updated);
  };

  useEffect(() => {
    const handler = () => switchTab("settings");
    window.addEventListener("overload-goto-settings", handler);
    return () => window.removeEventListener("overload-goto-settings", handler);
  }, []);

  const darkMode = settings.darkMode;
  const toggleTheme = () => updateSetting("darkMode", !darkMode);

  const [transitionClass, setTransitionClass] = useState("");
  const prevScreenRef = useRef("home");
  const prevTabRef = useRef("workout");

  const pushScreen = (newScreen) => {
    setTransitionClass("screen-push");
    prevScreenRef.current = screen;
    setScreen(newScreen);
  };

  const popScreen = (newScreen = "home") => {
    setTransitionClass("screen-pop");
    prevScreenRef.current = screen;
    setScreen(newScreen);
  };

  const switchTab = (newTab) => {
    setTransitionClass("screen-tab");
    prevTabRef.current = tab;
    setTab(newTab);
  };

  useEffect(() => {
    document.body.className = darkMode ? "theme-dark" : "theme-light";
  }, [darkMode]);

  const [cardioLogs, setCardioLogs] = useState(() => load("wt_cardio", []));
  const saveCardioLogs = (c) => {
    setCardioLogs(c);
    save("wt_cardio", c);
  };

  // Cardio used to be written into the food log as a negative-calorie "Snack",
  // which handed the user back their burn as extra food allowance. The activity
  // multiplier already accounts for the user's normal training routine, so
  // eating the burn back on top counted the same exercise twice.
  //
  // The cardio record and its calorie estimate are still saved in full — they
  // are just no longer part of the nutrition budget.
  const logCardio = (entry) => {
    const updated = [
      ...cardioLogs,
      { ...entry, id: Date.now(), date: todayKey },
    ];
    saveCardioLogs(updated);
  };

  const [restDays, setRestDays] = useState(() => load("wt_restdays", []));
  const saveRestDays = (r) => {
    setRestDays(r);
    save("wt_restdays", r);
  };
  const logRestDay = () => {
    const already = restDays.find((r) => r.date === todayKey);
    if (already) return;
    saveRestDays([...restDays, { id: Date.now(), date: todayKey }]);
  };

  const [checkins, setCheckins] = useState(() => load("wt_checkins", []));
  const saveCheckins = (c) => {
    setCheckins(c);
    save("wt_checkins", c);
  };

  const lastCheckin = checkins[checkins.length - 1];
  const daysSinceCheckin = lastCheckin
    ? Math.floor((Date.now() - lastCheckin.timestamp) / (1000 * 60 * 60 * 24))
    : 999;
  const checkinDue = daysSinceCheckin >= 7;

  const submitCheckin = (data) => {
    const updated = [
      ...checkins,
      { ...data, timestamp: Date.now(), date: new Date().toLocaleDateString() },
    ];
    saveCheckins(updated);
    // `if (data.weight)` accepted anything truthy, so "abc" or 5000 would have
    // been written straight into the profile and then into BMR. The check-in
    // record itself is kept either way; only the profile write is gated.
    if (isValidWeight(data.weight)) {
      saveProfile({ ...profile, weight: data.weight });
    }
  };

  const todayKey = new Date().toLocaleDateString();
  // Entries written by the old logCardio behaviour are still sitting in
  // wt_food. They are filtered out of nutrition calculations rather than
  // deleted, so nothing a user actually typed is ever at risk. The marker is
  // deliberately strict: negative calories AND all three macros at zero. The
  // food UI cannot produce that combination, so it cannot match real food.
  const isCardioBurnEntry = (e) =>
    e && e.cal < 0 && !e.p && !e.c && !e.f;
  const todayLog = (foodLogs[todayKey] || []).filter(
    (e) => !isCardioBurnEntry(e)
  );

  const addFoodEntry = (entry) => {
    const updated = {
      ...foodLogs,
      [todayKey]: [...todayLog, { ...entry, id: Date.now() }],
    };
    saveFoodLogs(updated);
  };
  const deleteFoodEntry = (id) => {
    const updated = {
      ...foodLogs,
      [todayKey]: todayLog.filter((e) => e.id !== id),
    };
    saveFoodLogs(updated);
  };

  const [favorites, setFavorites] = useState(() => load("wt_favorites", []));
  const saveFavorites = (f) => {
    setFavorites(f);
    save("wt_favorites", f);
  };

  const addFavorite = (food) => {
    if (favorites.find((f) => f.name === food.name)) return; // no duplicates
    saveFavorites([...favorites, food]);
  };
  const removeFavorite = (name) =>
    saveFavorites(favorites.filter((f) => f.name !== name));

  const [daysSummary, setDaysSummary] = useState(() =>
    load("wt_days_summary", {})
  );
  const saveDaysSummary = (d) => {
    setDaysSummary(d);
    save("wt_days_summary", d);
  };

  const finishDay = (summaryData) => {
    const updated = { ...daysSummary, [todayKey]: summaryData };
    saveDaysSummary(updated);
  };

  const todaySession = [...sessions].reverse().find((s) => s.date === todayKey);
  const todayMuscle = todaySession?.muscleGroup || "Rest";

  const getLastSessionForGroup = (muscleGroup) => {
    for (let i = sessions.length - 1; i >= 0; i--) {
      const s = sessions[i];
      const groups = (s.muscleGroup || "").split(" + ").map((g) => g.trim());
      if (groups.includes(muscleGroup) && s.exercises.length > 0) return s;
    }
    return null;
  };

  const [showDailyPrompt, setShowDailyPrompt] = useState(() => {
    const last = load("wt_last_prompt", null);
    return last !== new Date().toLocaleDateString();
  });

  const dismissDailyPrompt = () => {
    save("wt_last_prompt", todayKey);
    setShowDailyPrompt(false);
  };

  useEffect(() => {
    const needsMigration = sessions.some((s) =>
      s.exercises?.some((ex) => ex.key && ex.key.split("__").length > 2)
    );
    if (!needsMigration) return;

    const migrated = sessions.map((s) => ({
      ...s,
      exercises: (s.exercises || []).map((ex) => ({
        ...ex,
        key: ex.key ? ex.key.split("__").slice(0, 2).join("__") : ex.key,
      })),
    }));
    saveSessions(migrated);
  }, []); // runs once on mount
  const calcStreak = () => {
    if (sessions.length === 0 && cardioLogs.length === 0) return 0;
    const activeDates = new Set([
      ...sessions.map((s) => s.date),
      ...cardioLogs.map((c) => c.date),
    ]);
    let streak = 0;
    const today = new Date();
    for (let i = 0; i <= 365; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = d.toLocaleDateString();
      if (activeDates.has(dateStr)) {
        streak++;
      } else {
        if (i === 0) continue; // today might not have activity yet
        break;
      }
    }
    return streak;
  };

  const currentStreak = calcStreak();

  // ── EFFECTIVE SETS PER MUSCLE ───────────────────────────────────────
  // Tonnage (weight x reps) answers "how much did you move". It does not
  // answer "how much stimulus did each muscle receive", because a 315lb set
  // and a 135lb set are both one set of exposure. This counts sets instead:
  // 1.0 to the primary muscle, 0.5 to each secondary.
  //
  // Load and reps are deliberately NOT multipliers here, and exerciseType is
  // deliberately NOT one either — a compound set is one direct set, not 1.5.
  // Its extra reach is already expressed through its secondaryMuscles list.
  //
  // calcWeeklyVolume is untouched; this runs alongside it on the same week
  // window and the same session array.
  const calcWeeklyEffectiveSets = (weeksAgo = 0) => {
    // Same Monday-to-Monday window as calcWeeklyVolume, computed the same way
    // so the two figures always describe the same seven days.
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - daysSinceMonday - weeksAgo * 7);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    const weekSessions = (sessions || []).filter((s) => {
      const ts = s.id || new Date(s.date).getTime();
      return ts >= startOfWeek.getTime() && ts < endOfWeek.getTime();
    });

    const direct = {};
    const indirect = {};
    let totalPhysicalSets = 0;
    let unknownExerciseSets = 0;
    const unknownExercises = new Set();

    const add = (bucket, muscle, amount) => {
      if (!muscle) return;
      bucket[muscle] = (bucket[muscle] || 0) + amount;
    };

    weekSessions.forEach((session) => {
      (session.exercises || []).forEach((ex) => {
        // Metadata is resolved from the library at read time via the exercise
        // key, so historical sessions gain attribution without being migrated.
        const meta = getExerciseMeta(ex.key || ex.name, session.muscleGroup);
        (ex.sets || []).forEach((set) => {
          if (!isWorkingSet(set)) return;
          totalPhysicalSets += 1;
          if (!meta.isKnown) {
            unknownExerciseSets += 1;
            unknownExercises.add(ex.key || ex.name);
            // Unknown exercises get direct credit to the session's group only.
            // No secondaries are invented.
            add(direct, meta.primaryMuscle, 1);
            return;
          }
          add(direct, meta.primaryMuscle, 1);
          (meta.secondaryMuscles || []).forEach((m) => add(indirect, m, 0.5));
        });
      });
    });

    const effective = {};
    MUSCLE_GROUPS.forEach((m) => {
      effective[m] = (direct[m] || 0) + (indirect[m] || 0);
    });
    Object.keys({ ...direct, ...indirect }).forEach((m) => {
      if (effective[m] === undefined)
        effective[m] = (direct[m] || 0) + (indirect[m] || 0);
    });

    const round1 = (o) =>
      Object.fromEntries(
        Object.entries(o).map(([k, v]) => [k, Math.round(v * 10) / 10])
      );

    return {
      totalPhysicalSets,
      directSetsByMuscle: round1(direct),
      indirectSetsByMuscle: round1(indirect),
      effectiveSetsByMuscle: round1(effective),
      unknownExerciseSets,
      unknownExercises: [...unknownExercises],
      sessionCount: weekSessions.length,
    };
  };

  const calcWeeklyVolume = (weeksAgo = 0) => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon...
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - daysSinceMonday - weeksAgo * 7);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    const weekSessions = sessions.filter((s) => {
      const ts = s.id || new Date(s.date).getTime();
      return ts >= startOfWeek.getTime() && ts < endOfWeek.getTime();
    });

    let totalVolume = 0;
    let totalSets = 0; // working sets only
    let totalReps = 0;
    const byMuscle = {};

    weekSessions.forEach((session) => {
      const primaryGroup = session.muscleGroup?.split(" + ")[0] || "Other";
      if (!byMuscle[primaryGroup])
        byMuscle[primaryGroup] = { volume: 0, sets: 0, reps: 0 };

      session.exercises.forEach((ex) => {
        ex.sets.forEach((set) => {
          const vol = (set.weight || 0) * (set.reps || 0);
          totalVolume += vol;
          totalSets += 1;
          totalReps += set.reps || 0;
          byMuscle[primaryGroup].volume += vol;
          byMuscle[primaryGroup].sets += 1;
          byMuscle[primaryGroup].reps += set.reps || 0;
        });
      });
    });

    const weekCardio = (cardioLogs || []).filter((c) => {
      const ts = c.id || new Date(c.date).getTime();
      return ts >= startOfWeek.getTime() && ts < endOfWeek.getTime();
    });
    const cardioMins = weekCardio.reduce((a, c) => a + (c.duration || 0), 0);
    const cardioCal = weekCardio.reduce(
      (a, c) => a + (c.caloriesBurned || 0),
      0
    );

    const fmt = (d) =>
      d.toLocaleDateString([], { month: "short", day: "numeric" });
    const dateRange = `${fmt(startOfWeek)} – ${fmt(endOfWeek)}`;

    return {
      totalVolume,
      totalSets,
      totalReps,
      byMuscle,
      sessions: weekSessions.length,
      cardioMins,
      cardioCal,
      dateRange,
    };
  };

  const thisWeek = calcWeeklyVolume(0);
  const thisWeekSets = calcWeeklyEffectiveSets(0);
  const lastWeek = calcWeeklyVolume(1);
  const RECOVERY_HOURS = {
    Legs: 72, // largest muscle group, highest systemic fatigue
    Back: 60,
    "Full Body": 72,
    Chest: 48,
    Shoulders: 48,
    Arms: 36, // smaller muscles recover faster
    Core: 24, // can be trained more frequently
  };

  const getRecoveryStatus = () => {
    const now = Date.now();
    const statusMap = {};

    MUSCLE_GROUPS.forEach((group) => {
      const lastSession = [...sessions]
        .reverse()
        .find((s) =>
          s.muscleGroup?.split(" + ").some((g) => g.trim() === group)
        );

      if (!lastSession) {
        statusMap[group] = {
          status: "ready",
          hoursRested: null,
          hoursNeeded: RECOVERY_HOURS[group],
          lastDate: null,
        };
        return;
      }

      const sessionTimestamp =
        lastSession.id || new Date(lastSession.date).getTime();
      const hoursElapsed = (now - sessionTimestamp) / (1000 * 60 * 60);
      const hoursNeeded = RECOVERY_HOURS[group];
      const hoursRemaining = Math.max(0, hoursNeeded - hoursElapsed);
      const pctRecovered = Math.min(
        100,
        Math.round((hoursElapsed / hoursNeeded) * 100)
      );

      let status;
      if (pctRecovered >= 100) status = "ready";
      else if (pctRecovered >= 70) status = "almost";
      else status = "recovering";

      statusMap[group] = {
        status,
        hoursRested: Math.round(hoursElapsed),
        hoursRemaining: Math.round(hoursRemaining),
        hoursNeeded,
        pctRecovered,
        lastDate: lastSession.date,
      };
    });

    return statusMap;
  };

  const recoveryStatus = getRecoveryStatus();

  const suggestMuscleGroup = () => {
    const readyGroups = MUSCLE_GROUPS.filter(
      (g) => recoveryStatus[g]?.status === "ready"
    );
    if (readyGroups.length === 0) {
      return MUSCLE_GROUPS.sort(
        (a, b) =>
          (recoveryStatus[b]?.pctRecovered || 0) -
          (recoveryStatus[a]?.pctRecovered || 0)
      )[0];
    }
    const recentGroups = sessions
      .slice(-2)
      .map((s) => s.muscleGroup?.split(" + ")[0]);
    const preferred = readyGroups.filter((g) => !recentGroups.includes(g));
    return preferred.length > 0 ? preferred[0] : readyGroups[0];
  };

  const suggestedGroup = suggestMuscleGroup();

  const [timerMode, setTimerMode] = useState(null); // "auto" | "manual"
  const [showTimerPicker, setShowTimerPicker] = useState(false);
  const [workoutSummary, setWorkoutSummary] = useState(null); // summary screen data

  const repeatSession = (session) => {
    const poExercises = session.exercises.map((ex) => {
      const validSets = ex.sets;
      const bestSet =
        validSets.length > 0
          ? validSets.reduce(
              (best, s) => (s.weight > best.weight ? s : best),
              validSets[0]
            )
          : ex.sets[ex.sets.length - 1];
      const rec = bestSet
        ? getRecommendations(bestSet, profile.goal, ex.key, sessions)[0]
        : null;
      const workingSets = ex.sets;
      const loggedRest = workingSets.find((s) => s.rest)?.rest || null;
      const suggestedRest =
        loggedRest || smartRestSuggestion(profile.goal, bestSet?.reps);
      return {
        ...ex,
        sets: [],
        poTarget: rec,
        lastSets: ex.sets,
        lastRest: suggestedRest,
        restWasSuggested: !loggedRest,
      };
    });
    startSession(session.muscleGroup, poExercises);
  };

  // ── SHARED WORKOUT START LOGIC ──
  // Used by every entry point (DailyPrompt, HomeScreen picker, etc.).
  // Rather than blindly repeating the last session, this looks across ALL
  // sessions for the muscle group and offers two real choices:
  //   A) "Your Usual"  — your most frequently trained exercises for this muscle
  //   B) "Mix It Up"   — exercises you've done before for this muscle, but not
  //                       necessarily your most common ones; deliberately
  //                       excludes what you did last time so it feels different
  // Both use the same adaptive PO engine (analyzeExerciseHistory across all
  // sessions) for weight/rep targets — only the exercise SELECTION differs.
  const [muscleOptions, setMuscleOptions] = useState(null); // { muscleGroup, lastSession }

  // Gather every exercise/equipment key ever logged for this muscle group,
  // with frequency count and the most recent set data for PO calculation.
  const getExercisePoolForGroup = (muscleGroup) => {
    const pool = {}; // key -> { name, key, count, lastSets, lastDate, lastTimestamp }
    sessions.forEach((s) => {
      const groups = (s.muscleGroup || "").split(" + ").map((g) => g.trim());
      if (!groups.includes(muscleGroup)) return;
      // Single-muscle session: every exercise in it was trained under this
      // group, full stop — no ambiguity, no name-collision risk.
      // Combo session (e.g. "Back + Shoulders"): fall back to a library
      // lookup, but check the groups actually present in THIS session first
      // (in muscleGroup order) so exercises with duplicate names across
      // unrelated groups (e.g. "Shrug" in Back vs Shoulders, "Dip" in Chest
      // vs Arms) are matched against what was actually combined that day,
      // not silently misattributed to whichever group happens to be checked
      // first overall.
      const isSingleMuscleSession = groups.length === 1;

      s.exercises.forEach((ex) => {
        if (!ex.key || ex.sets.length === 0) return;

        let belongsTo = muscleGroup;
        if (!isSingleMuscleSession) {
          belongsTo = null;
          for (const g of groups) {
            const lib = EXERCISE_LIBRARY[g] || [];
            if (lib.some((e) => ex.key.startsWith(e.name))) {
              belongsTo = g;
              break;
            }
          }
        }
        if (belongsTo !== muscleGroup) return;

        const ts = s.id || new Date(s.date).getTime();
        if (!pool[ex.key]) {
          pool[ex.key] = {
            name: ex.name,
            key: ex.key,
            count: 0,
            lastSets: ex.sets,
            lastDate: s.date,
            lastTimestamp: ts,
          };
        }
        pool[ex.key].count += 1;
        if (ts >= pool[ex.key].lastTimestamp) {
          pool[ex.key].lastSets = ex.sets;
          pool[ex.key].lastDate = s.date;
          pool[ex.key].lastTimestamp = ts;
        }
      });
    });
    return Object.values(pool);
  };

  const buildPoExercisesFromPool = (poolExercises) => {
    return poolExercises.map((item) => {
      const validSets = item.lastSets;
      const bestSet =
        validSets.length > 0
          ? validSets.reduce(
              (best, s) => (s.weight > best.weight ? s : best),
              validSets[0]
            )
          : validSets[validSets.length - 1];
      const rec = bestSet
        ? getRecommendations(bestSet, profile.goal, item.key, sessions)[0]
        : null;
      const loggedRest = validSets.find((s) => s.rest)?.rest || null;
      const suggestedRest =
        loggedRest || smartRestSuggestion(profile.goal, bestSet?.reps);
      return {
        name: item.name,
        key: item.key,
        sets: [],
        poTarget: rec,
        lastSets: validSets,
        lastRest: suggestedRest,
        restWasSuggested: !loggedRest,
      };
    });
  };

  const TYPICAL_EXERCISE_COUNT = 4;

  // Greedily picks from a priority-ordered candidate list, skipping any
  // candidate that would stack a second compound exercise sharing the same
  // movement pattern as one already picked (e.g. Incline Barbell Press +
  // Incline Dumbbell Press). Isolation exercises and different patterns are
  // never restricted — this only prevents redundant compound-on-compound
  // fatigue stacking. Falls back to filling remaining slots from skipped
  // candidates if there aren't enough valid picks (never returns fewer
  // exercises than requested if candidates exist).
  const selectAvoidingPatternOverlap = (orderedCandidates, count) => {
    const picked = [];
    const skipped = [];
    const usedCompoundPatterns = new Set();

    for (const item of orderedCandidates) {
      if (picked.length >= count) break;
      const { pattern, type } = getMovementPattern(item.key);
      const conflicts =
        type === "compound" && usedCompoundPatterns.has(pattern);
      if (conflicts) {
        skipped.push(item);
        continue;
      }
      picked.push(item);
      if (type === "compound") usedCompoundPatterns.add(pattern);
    }
    // Fill any remaining slots from skipped candidates rather than leaving
    // the workout short — better to have the redundant pair than too few exercises.
    for (const item of skipped) {
      if (picked.length >= count) break;
      picked.push(item);
    }
    return picked;
  };

  // Option A — most frequently trained exercises for this muscle, all-time
  const buildUsualWorkout = (muscleGroup) => {
    const pool = getExercisePoolForGroup(muscleGroup);
    const sorted = [...pool].sort(
      (a, b) => b.count - a.count || b.lastTimestamp - a.lastTimestamp
    );
    return selectAvoidingPatternOverlap(sorted, TYPICAL_EXERCISE_COUNT);
  };

  // Option B — variety from history; excludes last session's exact exercises
  // so it actually feels different, but pulls from the FULL pool (not just
  // least-used), with light randomization so it's not the same every time.
  const buildMixItUpWorkout = (muscleGroup, lastSession) => {
    const pool = getExercisePoolForGroup(muscleGroup);
    const lastKeys = new Set(
      (lastSession?.exercises || []).map((ex) => ex.key)
    );
    const candidates = pool.filter((item) => !lastKeys.has(item.key));
    const usable = candidates.length > 0 ? candidates : pool; // fallback if everything was in last session

    // Shuffle deterministically-ish using current time as seed so each tap can differ
    const shuffled = [...usable].sort(() => Math.random() - 0.5);
    return selectAvoidingPatternOverlap(shuffled, TYPICAL_EXERCISE_COUNT);
  };

  const prepareWorkoutStart = (muscleGroup) => {
    const lastSession = getLastSessionForGroup(muscleGroup);
    const pool = getExercisePoolForGroup(muscleGroup);

    if (!lastSession || pool.length === 0) {
      startSession(muscleGroup);
      return;
    }

    setMuscleOptions({ muscleGroup, lastSession });
  };

  const startSession = (muscleGroup, preloadExercises = null, mode = null) => {
    if (!mode) {
      setActiveSession({
        id: Date.now(),
        date: todayKey,
        muscleGroup,
        exercises: preloadExercises || [],
      });
      setTimerMode(null);
      setShowTimerPicker(true);
      return;
    }
    setTimerMode(mode);
    setShowTimerPicker(false);
    pushScreen("log");
  };

  const confirmTimerMode = (mode) => {
    setTimerMode(mode);
    setShowTimerPicker(false);
    pushScreen("log");
  };

  const finishSession = (durationSecs, sessionData) => {
    const completed = { ...activeSession, duration: durationSecs };
    if (completed.exercises.length > 0) {
      saveSessions([...sessions, completed]);
    }
    setActiveSession(null);
    save("wt_active_session", null); // clear auto-save
    setWorkoutSummary({ session: completed, durationSecs });
    popScreen("home");
  };

  // Save partial session whenever it changes (auto-save safety net)
  useEffect(() => {
    if (activeSession && screen === "log") {
      save("wt_active_session", {
        session: activeSession,
        timerMode,
        savedAt: Date.now(),
      });
    }
  }, [activeSession, screen]);

  const submitWorkoutData = (calories, bpm, durationSecs, session) => {
    if (calories) {
      const updated = {
        ...foodLogs,
        [todayKey]: [
          ...(foodLogs[todayKey] || []),
          {
            id: Date.now(),
            name: `Calories Burned (${session.muscleGroup})`,
            cal: -Math.abs(Number(calories)),
            p: 0,
            c: 0,
            f: 0,
          },
        ],
      };
      saveFoodLogs(updated);
    }
    if (bpm) {
      // Save avg HR to session record
      const updatedSessions = sessions.map((s) =>
        s.id === session.id ? { ...s, avgBpm: Number(bpm) } : s
      );
      saveSessions(updatedSessions);
    }
    setWorkoutSummary(null);
    popScreen("home");
  };

  const getLastExercise = (key) => {
    for (let i = sessions.length - 1; i >= 0; i--) {
      const ex = sessions[i].exercises.find((e) => e.key === key);
      if (ex?.sets.length > 0) return ex;
    }
    return null;
  };

  if (screen === "cardio")
    return (
      <div className={`screen-push`} style={{ flex: 1 }}>
        <CardioScreen
          profile={profile}
          onLog={logCardio}
          onBack={() => popScreen("home")}
        />
      </div>
    );
  if (screen === "restday")
    return (
      <div className={`screen-push`} style={{ flex: 1 }}>
        <RestDayScreen
          profile={profile}
          settings={settings}
          recoveryStatus={recoveryStatus}
          sessions={sessions}
          restDays={restDays}
          todayKey={todayKey}
          onLog={logRestDay}
          onBack={() => popScreen("home")}
        />
      </div>
    );
  if (screen === "shock")
    return (
      <div className={`screen-push`} style={{ flex: 1 }}>
        <ShockWorkoutScreen
          profile={profile}
          sessions={sessions}
          recoveryStatus={recoveryStatus}
          onStart={(sess) => {
            setActiveSession(sess);
            setShowTimerPicker(true);
            pushScreen("timerPicker");
          }}
          onBack={() => popScreen("home")}
        />
      </div>
    );

  if (showSplash) return <SplashScreen onDone={() => setShowSplash(false)} />;

  // Profiles created before age was collected have age: "" and route straight
  // to Home, so they would never see the new onboarding question. Rather than
  // re-running onboarding (which would be a poor experience and risks their
  // existing answers), intercept once and ask for the single missing field.
  // saveProfile spreads the existing profile, so nothing else is touched and
  // no other localStorage key is involved.
  if (
    profile &&
    profile.name &&
    (!isValidAge(profile.age) || !isValidWeight(profile.weight))
  )
    return (
      <ProfileCompletionScreen
        needAge={!isValidAge(profile.age)}
        needWeight={!isValidWeight(profile.weight)}
        onSave={(patch) => saveProfile({ ...profile, ...patch })}
      />
    );

  if (screen === "tour")
    return (
      <TourScreen
        profile={profile}
        settings={settings}
        onUpdateSetting={updateSetting}
        onBulkUpdateSettings={bulkUpdateSettings}
        onDone={() => {
          save("wt_tour_done", true);
          popScreen("home");
        }}
      />
    );

  if (screen === "onboard")
    return (
      <OnboardScreen
        onDone={(p) => {
          saveProfile(p);
          const tourDone = load("wt_tour_done", false);
          setScreen(tourDone ? "home" : "tour");
        }}
      />
    );

  if (showTimerPicker && activeSession)
    return (
      <TimerModePicker
        muscleGroup={activeSession.muscleGroup}
        settings={settings}
        onSelect={confirmTimerMode}
        onBack={() => {
          setActiveSession(null);
          setShowTimerPicker(false);
        }}
      />
    );

  if (workoutSummary)
    return (
      <WorkoutSummary
        summary={workoutSummary}
        profile={profile}
        settings={settings}
        sessions={sessions}
        onDone={(cal, bpm) =>
          submitWorkoutData(
            cal,
            bpm,
            workoutSummary.durationSecs,
            workoutSummary.session
          )
        }
      />
    );

  if (screen === "log")
    return (
      <div
        className="screen-push"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <LogScreen
          session={activeSession}
          setSession={setActiveSession}
          profile={profile}
          getLastExercise={getLastExercise}
          onFinish={finishSession}
          onBack={(hasData) => {
            if (!hasData) {
              setActiveSession(null);
              save("wt_active_session", null);
              popScreen("home");
            }
            // If has data, LogScreen handles the confirm sheet internally
          }}
          timerMode={timerMode}
          settings={settings}
          sessions={sessions}
        />
      </div>
    );
  if (screen === "history")
    return (
      <div
        className="screen-push"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <HistoryScreen
          sessions={sessions}
          foodLogs={foodLogs}
          cardioLogs={cardioLogs}
          restDays={restDays}
          onBack={() => popScreen("home")}
          onUpdateSession={updateSession}
          onRepeatSession={repeatSession}
          profile={profile}
        />
      </div>
    );

  if (muscleOptions) {
    const { muscleGroup, lastSession } = muscleOptions;
    const pool = getExercisePoolForGroup(muscleGroup);
    const usualCount = Math.min(TYPICAL_EXERCISE_COUNT, pool.length);

    // If a choice has been made, show the confirm screen with built exercises
    if (muscleOptions.choice) {
      const poExercises =
        muscleOptions.choice === "usual"
          ? buildPoExercisesFromPool(buildUsualWorkout(muscleGroup))
          : buildPoExercisesFromPool(
              buildMixItUpWorkout(muscleGroup, lastSession)
            );

      return (
        <div
          className="screen"
          style={{
                        gap: 16,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              className="btn-ghost sm"
              onClick={() => setMuscleOptions({ muscleGroup, lastSession })}
            >
              ← Back
            </button>
          </div>

          <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
            <p
              style={{
                fontSize: 13,
                color: "var(--accent)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {muscleOptions.choice === "usual" ? "Your Usual" : "Mix It Up"}
            </p>
            <p style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
              {muscleGroup} Day
            </p>
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 6 }}>
              {muscleOptions.choice === "usual"
                ? `Your ${poExercises.length} most-trained exercises for ${muscleGroup}`
                : `${poExercises.length} exercises from your history — something different today`}
            </p>
          </div>

          <div className="card" style={{ gap: 14 }}>
            <p
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--accent)",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              <Ic.TrendUp s={22} c="currentColor" /> Progressive Overload
              Targets
            </p>
            {poExercises.map((ex, i) => (
              <div
                key={i}
                style={{
                  borderBottom:
                    i < poExercises.length - 1
                      ? "1px solid var(--border)"
                      : "none",
                  paddingBottom: i < poExercises.length - 1 ? 12 : 0,
                }}
              >
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--text)",
                  }}
                >
                  {ex.name}
                </p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  {ex.lastSets.map((s, si) => (
                    <span key={si} className="hint-chip">
                      Last Set {si + 1}: {s.weight}lbs × {s.reps}
                    </span>
                  ))}
                </div>
                {ex.poTarget && (
                  <div
                    style={{
                      marginTop: 8,
                      background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
                      border: "1px solid var(--accent)",
                      borderRadius: 8,
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--accent)",
                        fontWeight: 700,
                      }}
                    >
                      TARGET →
                    </span>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "var(--text)",
                      }}
                    >
                      {ex.poTarget.weight} lbs × {ex.poTarget.reps} reps
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginLeft: "auto",
                      }}
                    >
                      {ex.poTarget.reason}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            className="btn-primary"
            style={{ padding: 16, fontSize: 15 }}
            onClick={() => {
              setMuscleOptions(null);
              startSession(muscleGroup, poExercises);
            }}
          >
            Start This Workout
          </button>
          {muscleOptions.choice === "mix" && (
            <button
              className="btn-ghost"
              onClick={() =>
                setMuscleOptions({ muscleGroup, lastSession, choice: "mix" })
              }
            >
              <Ic.Shuffle s={22} c="currentColor" /> Shuffle Again
            </button>
          )}
          <button
            className="btn-ghost"
            onClick={() => {
              setMuscleOptions(null);
              startSession(muscleGroup);
            }}
          >
            Start Fresh Instead
          </button>
          <style>{CSS}</style>
        </div>
      );
    }

    // Choice screen — let the user pick their intent
    return (
      <div
        className="screen"
        style={{
                    gap: 16,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            className="btn-ghost sm"
            onClick={() => setMuscleOptions(null)}
          >
            ← Back
          </button>
        </div>

        <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
          <p
            style={{
              fontSize: 13,
              color: "var(--accent)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            {muscleGroup}
          </p>
          <ScreenHeader
            title="How do you want to train today?"
            sub={`${pool.length} exercise${
              pool.length !== 1 ? "s" : ""
            } logged for ${muscleGroup} historically`}
          />
        </div>

        <button
          onClick={() =>
            setMuscleOptions({ muscleGroup, lastSession, choice: "usual" })
          }
          style={{
            background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
            border: "1.5px solid var(--accent)",
            borderRadius: "var(--r-md)",
            padding: "18px 16px",
            textAlign: "left",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <span style={{ fontSize: 28, flexShrink: 0 }}>
            <Ic.Target s={22} c="currentColor" />
          </span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              Your Usual
            </p>
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                marginTop: 3,
                lineHeight: 1.5,
              }}
            >
              Your {usualCount} most-trained {muscleGroup} exercises, with
              adaptive weight targets based on your full history.
            </p>
          </div>
        </button>

        <button
          onClick={() =>
            setMuscleOptions({ muscleGroup, lastSession, choice: "mix" })
          }
          style={{
            background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
            border: "1.5px solid var(--purple)",
            borderRadius: "var(--r-md)",
            padding: "18px 16px",
            textAlign: "left",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <span style={{ fontSize: 28, flexShrink: 0 }}>
            <Ic.Shuffle s={22} c="currentColor" />
          </span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              Mix It Up
            </p>
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                marginTop: 3,
                lineHeight: 1.5,
              }}
            >
              Exercises you've done before for {muscleGroup}, different from
              last time — keeps things from feeling stale.
            </p>
          </div>
        </button>

        <button
          className="btn-ghost"
          onClick={() => {
            setMuscleOptions(null);
            startSession(muscleGroup);
          }}
        >
          Start Fresh — Pick Exercises Myself
        </button>
        <style>{CSS}</style>
      </div>
    );
  }

  if (showDailyPrompt && sessions.length > 0) {
    return (
      <DailyPrompt
        profile={profile}
        suggestedGroup={suggestedGroup}
        recoveryStatus={recoveryStatus}
        onTrain={(group) => {
          dismissDailyPrompt();
          prepareWorkoutStart(group);
        }}
        onChoose={(group) => {
          dismissDailyPrompt();
          prepareWorkoutStart(group);
        }}
        onRest={() => dismissDailyPrompt()}
        onSkip={() => dismissDailyPrompt()}
        muscleGroups={MUSCLE_GROUPS}
        getLastSessionForGroup={getLastSessionForGroup}
      />
    );
  }

  return (
    <div
      className={darkMode ? "theme-dark" : "theme-light"}
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        maxWidth: 480,
        margin: "0 auto",
        background: "var(--bg)",
      }}
    >
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div key={tab} className="screen-tab">
          {tab === "workout" ? (
            <HomeScreen
              profile={profile}
              sessions={sessions}
              onStart={startSession}
              onMuscleSelect={prepareWorkoutStart}
              getLastSessionForGroup={getLastSessionForGroup}
              onHistory={() => pushScreen("history")}
              checkinDue={checkinDue}
              onSubmitCheckin={submitCheckin}
              darkMode={darkMode}
              onToggleTheme={toggleTheme}
              recoveryStatus={recoveryStatus}
              onStartCardio={() => pushScreen("cardio")}
              onRestDay={() => pushScreen("restday")}
              onShockWorkout={() => pushScreen("shock")}
              cardioLogs={cardioLogs}
              currentStreak={currentStreak}
              thisWeek={thisWeek}
              thisWeekSets={thisWeekSets}
              lastWeek={lastWeek}
              settings={settings}
              savedSession={savedSession}
              onResumeSession={() => {
                setActiveSession(savedSession.session);
                setSavedSession(null);
                setTimerMode(savedSession.timerMode || "auto");
                pushScreen("log");
              }}
              onDiscardSaved={() => {
                setSavedSession(null);
                save("wt_active_session", null);
              }}
            />
          ) : tab === "nutrition" ? (
            <NutritionScreen
              profile={profile}
              todayMuscle={todayMuscle}
              todayLog={todayLog}
              onAddFood={addFoodEntry}
              onDeleteFood={deleteFoodEntry}
              favorites={favorites}
              onAddFavorite={addFavorite}
              onRemoveFavorite={removeFavorite}
              onFinishDay={finishDay}
              daySummary={daysSummary[todayKey]}
              settings={settings}
            />
          ) : (
            <SettingsScreen
              profile={profile}
              settings={settings}
              onUpdate={updateSetting}
              onSaveProfile={saveProfile}
              sessions={sessions}
            />
          )}
        </div>
      </div>

      <div className="bottom-nav">
        <button
          className={`nav-btn ${tab === "workout" ? "active" : ""}`}
          onClick={() => switchTab("workout")}
        >
          <div className="nav-icon-wrap">
            <Ic.Barbell s={22} filled={tab === "workout"} />
          </div>
          <span className="nav-label">Workout</span>
        </button>
        <button
          className={`nav-btn ${tab === "nutrition" ? "active" : ""}`}
          onClick={() => switchTab("nutrition")}
        >
          <div className="nav-icon-wrap">
            <Ic.Apple s={22} filled={tab === "nutrition"} />
          </div>
          <span className="nav-label">Nutrition</span>
        </button>
        <button className="nav-btn" onClick={() => pushScreen("history")}>
          <div className="nav-icon-wrap">
            <Ic.Clock s={22} />
          </div>
          <span className="nav-label">History</span>
        </button>
        <button
          className={`nav-btn ${tab === "settings" ? "active" : ""}`}
          onClick={() => switchTab("settings")}
        >
          <div className="nav-icon-wrap">
            <Ic.Gear s={22} filled={tab === "settings"} />
          </div>
          <span className="nav-label">Settings</span>
        </button>
      </div>
      <style>{CSS}</style>
    </div>
  );
}

// ── WHEEL SELECT ──
// A bounded dropdown that renders as the native iOS scroll-wheel ("rolodex")
// picker on mobile Safari, and a normal dropdown elsewhere. Used for body
// stats so impossible values (e.g. 10ft tall, 5lb bodyweight) can't be entered
// at all — the choice is constrained by construction rather than validated
// after the fact. Styled to match the dark onboarding surface.
function WheelSelect({
  value,
  onChange,
  options,
  unit,
  placeholder = "Select",
  flex = 1,
}) {
  return (
    <div
      style={{
        flex,
        position: "relative",
        display: "flex",
        alignItems: "center",
      }}
    >
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          background: "var(--surface2)",
          color: value ? "var(--text)" : "var(--muted)",
          border: "2px solid var(--border)",
          borderRadius: 12,
          padding: "14px 38px 14px 14px",
          fontSize: 22,
          fontWeight: 700,
          fontFamily: "'DM Sans',sans-serif",
          outline: "none",
          cursor: "pointer",
          caretColor: "var(--accent)",
        }}
      >
        {/* `hidden` as well as `disabled`: a disabled-only placeholder still
            renders inside iOS Safari's select popover, where it appears as a
            checked row sitting above the real range — which reads as a
            duplicate of the first value rather than as a prompt. */}
        <option value="" disabled hidden>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {/* unit label + chevron, non-interactive */}
      <div
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 6,
          pointerEvents: "none",
        }}
      >
        {unit && (
          <span
            style={{ fontSize: 13, color: "var(--muted)", fontWeight: 700 }}
          >
            {unit}
          </span>
        )}
        <Ic.ChevDown s={16} c="var(--muted)" />
      </div>
    </div>
  );
}

// Bounded option ranges — these caps are what make absurd inputs impossible.
// Input-sanity bounds, not a claim about what weights are possible. Decimals
// are allowed because scales report them and check-ins record them.
const MIN_WEIGHT = 70;
const MAX_WEIGHT = 700;
const isValidWeight = (v) => {
  if (v === "" || v == null) return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= MIN_WEIGHT && n <= MAX_WEIGHT;
};
// Picker caps at 400 for list length; the validator accepts up to MAX_WEIGHT
// so a typed Settings value above 400 is still allowed.
const WEIGHT_PICKER_MAX = 400;
const WEIGHT_OPTIONS = Array.from(
  { length: WEIGHT_PICKER_MAX - MIN_WEIGHT + 1 },
  (_, i) => {
    const v = MIN_WEIGHT + i;
    return { value: String(v), label: String(v) };
  }
);
// 18 is the lower bound because the nutrition engine is Mifflin-St Jeor with
// adult activity multipliers — there are no pediatric formulas here, and
// applying adult targets to a minor would be wrong rather than merely
// imprecise. 120 is an input sanity limit, not a recommendation.
const MIN_AGE = 18;
const MAX_AGE = 120;
const AGE_OPTIONS = Array.from({ length: MAX_AGE - MIN_AGE + 1 }, (_, i) => {
  const v = MIN_AGE + i;
  return { value: String(v), label: String(v) };
});
const isValidAge = (v) => {
  const n = Number(v);
  return (
    v !== "" && v != null && Number.isInteger(n) && n >= MIN_AGE && n <= MAX_AGE
  );
};

const HEIGHT_FT_OPTIONS = [3, 4, 5, 6, 7].map((v) => ({
  value: String(v),
  label: String(v),
}));
const HEIGHT_IN_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

function OnboardScreen({ onDone }) {
  const [phase, setPhase] = useState("hook"); // hook | setup | payoff
  const [hookStep, setHookStep] = useState(0); // 0 or 1
  const [setupStep, setSetupStep] = useState(0); // 0..4
  const [form, setForm] = useState({
    name: "",
    sex: "male",
    age: "",
    heightFt: "",
    heightIn: "",
    weight: "",
    activity: "moderate",
    goal: "",
    bodyGoal: "",
    experience: "intermediate",
  });

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // ── OPTION CARD ──
  const OptionCard = ({
    selected,
    onClick,
    emoji,
    title,
    sub,
    color = "var(--accent)",
  }) => (
    <button
      onClick={onClick}
      style={{
        background: selected ? `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.08)` : "var(--surface2)",
        border: `2px solid ${selected ? "var(--accent)" : "var(--border)"}`,
        borderRadius: 16,
        padding: "16px",
        cursor: "pointer",
        textAlign: "left",
        width: "100%",
        transition: "all .15s",
        fontFamily: "'DM Sans',sans-serif",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          flexShrink: 0,
          background: selected ? "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.15)" : "var(--surface)",
          border: `1.5px solid ${selected ? "var(--accent)" : "var(--border)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
        }}
      >
        {emoji}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: selected ? "var(--accent)" : "var(--text)",
          }}
        >
          {title}
        </p>
        {sub && (
          <p
            style={{
              fontSize: 11,
              color: "var(--muted)",
              marginTop: 3,
              lineHeight: 1.4,
            }}
          >
            {sub}
          </p>
        )}
      </div>
      {selected && (
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            flexShrink: 0,
            background: "var(--accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 11, color: "var(--bg)", fontWeight: 700 }}>
            ✓
          </span>
        </div>
      )}
    </button>
  );

  // ── PHASE 1: HOOK ──────────────────────────────────────────────────────────
  if (phase === "hook") {
    const hookScreens = [
      {
        emoji: <Ic.Barbell s={22} c="currentColor" />,
        headline: "Train smarter.\nNot just harder.",
        body: "Most people repeat the same workout forever and wonder why they stop making progress. Overload ensures every session is scientifically harder than the last.",
        cta: "Show me how →",
      },
      {
        features: [
          {
            icon: <Ic.ChartBar s={22} c="currentColor" />,
            title: "Track every set",
            sub: "Log weight and reps in seconds. Your history is always there.",
          },
          {
            icon: <Ic.TrendUp s={22} c="currentColor" />,
            title: "Follow your progression",
            sub: "The PO engine tells you exactly what to lift next session — no guessing.",
          },
          {
            icon: null,
            title: "Hit new PRs",
            sub: "Adaptive targets that learn your pace. Bigger jumps when you're hot, smaller when you're not.",
          },
        ],
        cta: "Let's build your profile →",
      },
    ];

    const screen = hookScreens[hookStep];

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(160deg, var(--bg) 0%, var(--surface2) 60%, var(--surface) 100%)",
          padding: "48px 20px 100px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Ambient blobs */}
        <div
          style={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 250,
            height: 250,
            borderRadius: "50%",
            background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.06)",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -40,
            left: -40,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
            filter: "blur(50px)",
            pointerEvents: "none",
          }}
        />

        {/* Progress dots */}
        <div style={{ display: "flex", gap: 6, marginBottom: 48 }}>
          {[0, 1].map((i) => (
            <div
              key={i}
              style={{
                width: i === hookStep ? 24 : 8,
                height: 8,
                borderRadius: 99,
                background:
                  i <= hookStep ? "var(--accent)" : "rgba(255,255,255,.15)",
                transition: "all .3s",
              }}
            />
          ))}
        </div>

        {hookStep === 0 ? (
          /* Hook screen 1 — headline */
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 32, lineHeight: 1 }}>
              {screen.emoji}
            </div>
            <p
              style={{
                fontFamily: "'Bebas Neue',sans-serif",
                fontSize: 40,
                color: "var(--text)",
                lineHeight: 1,
                letterSpacing: 1,
                marginBottom: 20,
                whiteSpace: "pre-line",
              }}
            >
              {screen.headline}
            </p>
            <p
              style={{
                fontSize: 15,
                color: "var(--text)",
                lineHeight: 1.7,
                marginBottom: 48,
              }}
            >
              {screen.body}
            </p>
          </div>
        ) : (
          /* Hook screen 2 — features */
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <p
              style={{
                fontFamily: "'Bebas Neue',sans-serif",
                fontSize: 40,
                color: "var(--text)",
                letterSpacing: 1,
                marginBottom: 8,
              }}
            >
              WHAT OVERLOAD DOES
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--muted)",
                marginBottom: 32,
              }}
            >
              Three things. No fluff.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {screen.features.map((f, i) => (
                <div
                  key={i}
                  style={{
                    background: "rgba(255,255,255,.05)",
                    border: "1px solid rgba(255,255,255,.08)",
                    borderRadius: 16,
                    padding: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      flexShrink: 0,
                      background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)",
                      border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                    }}
                  >
                    {f.icon}
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "var(--text)",
                      }}
                    >
                      {f.title}
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginTop: 3,
                        lineHeight: 1.4,
                      }}
                    >
                      {f.sub}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => (hookStep === 0 ? setHookStep(1) : setPhase("setup"))}
          style={{
            background: "var(--accent)",
            border: "none",
            borderRadius: 16,
            padding: "18px",
            fontSize: 15,
            fontWeight: 700,
            color: "var(--bg)",
            cursor: "pointer",
            fontFamily: "'DM Sans',sans-serif",
            boxShadow: "0 4px 24px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35)",
            letterSpacing: 0.3,
            marginTop: 32,
            flexShrink: 0,
          }}
        >
          {hookStep === 0 ? hookScreens[0].cta : hookScreens[1].cta}
        </button>
        <style>{CSS}</style>
      </div>
    );
  }

  // ── PHASE 2: SETUP ─────────────────────────────────────────────────────────
  const setupScreens = [
    {
      emoji: null,
      title: "What's your name?",
      sub: "We'll use this to personalize your experience.",
      valid: form.name.trim().length > 0,
      body: (
        <div>
          <input
            autoFocus
            placeholder="Your first name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            style={{
              width: "100%",
              fontSize: 28,
              fontWeight: 700,
              background: "none",
              border: "none",
              outline: "none",
              color: "var(--text)",
              borderBottom: "2px solid var(--border)",
              paddingBottom: 12,
              fontFamily: "'DM Sans',sans-serif",
              boxSizing: "border-box",
              caretColor: "var(--accent)",
            }}
          />
          {form.name.trim() && (
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 12 }}>
              Hey {form.name.split(" ")[0]}
            </p>
          )}
        </div>
      ),
    },
    {
      emoji: <Ic.Target s={22} c="currentColor" />,
      title: "What are you training for?",
      sub: "This shapes your training. You'll set your nutrition goal next.",
      // Was `valid: true` with a pre-selected default, which let the user skip
      // the question entirely and still end up with a goal they never chose.
      valid: !!form.goal,
      body: (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <OptionCard
            selected={form.goal === "strength"}
            onClick={() => set("goal", "strength")}
            emoji={<Ic.Barbell s={22} c="currentColor" />}
            title="Strength"
            sub="Get stronger and prioritize heavier lifting"
          />
          <OptionCard
            selected={form.goal === "hypertrophy"}
            onClick={() => set("goal", "hypertrophy")}
            emoji={<Ic.TrendUp s={22} c="currentColor" />}
            title="Muscle Growth"
            sub="Prioritize building muscle size"
          />
          <OptionCard
            selected={form.goal === "endurance"}
            onClick={() => set("goal", "endurance")}
            emoji={<Ic.Battery s={22} c="currentColor" />}
            title="Muscular Endurance"
            sub="Perform more reps and sustain muscular effort"
          />
        </div>
      ),
    },
    {
      // ── BODY GOAL ──
      // Previously initialised to "recomp" and only reachable from Settings, so
      // a user could pick Muscle Growth as their training goal and silently
      // receive maintenance calories. Training goal and body goal are genuinely
      // separate concepts (Strength + Cut is a valid combination), so this is a
      // second question rather than a merge.
      emoji: <Ic.Scale s={22} c="currentColor" />,
      title: "What's your body goal?",
      sub: "Your training goal shapes your workouts. This sets your calorie and nutrition targets.",
      valid: !!form.bodyGoal,
      body: (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <OptionCard
            selected={form.bodyGoal === "bulk"}
            onClick={() => set("bodyGoal", "bulk")}
            emoji={<Ic.ArrowUp s={22} c="currentColor" />}
            title="Gain"
            sub="Increase bodyweight to support muscle growth"
          />
          <OptionCard
            selected={form.bodyGoal === "cut"}
            onClick={() => set("bodyGoal", "cut")}
            emoji={<Ic.ArrowDown s={22} c="currentColor" />}
            title="Lose Fat"
            sub="Reduce body fat while preserving muscle and performance"
          />
          <OptionCard
            selected={form.bodyGoal === "recomp"}
            onClick={() => set("bodyGoal", "recomp")}
            emoji={<Ic.Refresh s={22} c="currentColor" />}
            title="Maintain / Recomp"
            sub="Hold your bodyweight while improving composition"
          />
        </div>
      ),
    },
    {
      emoji: <Ic.Calendar s={22} c="currentColor" />,
      title: "How experienced are you?",
      sub: "This calibrates how much weight Overload adds each session.",
      valid: true,
      body: (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <OptionCard
            selected={form.experience === "beginner"}
            onClick={() => set("experience", "beginner")}
            emoji={<Ic.Leaf s={22} c="currentColor" />}
            title="Beginner"
            sub="Under 1 year · still learning the movements"
          />
          <OptionCard
            selected={form.experience === "intermediate"}
            onClick={() => set("experience", "intermediate")}
            emoji="⚡"
            title="Intermediate"
            sub="1–3 years · consistent training history"
          />
          <OptionCard
            selected={form.experience === "advanced"}
            onClick={() => set("experience", "advanced")}
            emoji={<Ic.Apple s={22} c="currentColor" />}
            title="Advanced"
            sub="3+ years · close to your genetic ceiling"
          />
        </div>
      ),
    },
    {
      emoji: <Ic.Scale s={20} c="currentColor" />,
      title: "Your body stats",
      sub: "Used for calorie and macro calculations.",
      valid:
        isValidWeight(form.weight) &&
        Number(form.heightFt) > 0 &&
        isValidAge(form.age),
      body: (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                marginBottom: 8,
              }}
            >
              Age
            </p>
            {/* Age was in the form state and saved, but no screen ever asked
                for it — so every profile stored "" and calcMacros fell through
                to its age-25 default. Mifflin-St Jeor subtracts 5 kcal per
                year, so a 60-year-old was being handed a 25-year-old's BMR. */}
            <WheelSelect
              value={form.age}
              onChange={(v) => set("age", v)}
              options={AGE_OPTIONS}
              unit="yrs"
              placeholder="Age"
            />
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              Used to estimate your calorie needs. OverLoad's nutrition
              recommendations are designed for adults 18 and older.
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                marginBottom: 8,
              }}
            >
              Weight
            </p>
            <WheelSelect
              value={form.weight}
              onChange={(v) => set("weight", v)}
              options={WEIGHT_OPTIONS}
              unit="lbs"
              placeholder="Weight"
            />
          </div>
          <div>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                marginBottom: 8,
              }}
            >
              Height
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <WheelSelect
                value={form.heightFt}
                onChange={(v) => set("heightFt", v)}
                options={HEIGHT_FT_OPTIONS}
                unit="ft"
                placeholder="Feet"
              />
              <WheelSelect
                value={form.heightIn}
                onChange={(v) => set("heightIn", v)}
                options={HEIGHT_IN_OPTIONS}
                unit="in"
                placeholder="Inches"
              />
            </div>
          </div>
          <div>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                marginBottom: 8,
              }}
            >
              Sex
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              {[
                ["male", "Male"],
                ["female", "Female"],
              ].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => set("sex", val)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    borderRadius: 12,
                    cursor: "pointer",
                    background:
                      form.sex === val
                        ? "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)"
                        : "var(--surface2)",
                    border: `2px solid ${
                      form.sex === val ? "var(--accent)" : "var(--border)"
                    }`,
                    fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  <p
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: form.sex === val ? "var(--accent)" : "var(--text)",
                    }}
                  >
                    {label}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      emoji: <Ic.Run s={22} c="currentColor" />,
      title: "How active are you overall?",
      sub: "Include your job, daily movement, and your usual weekly exercise.",
      valid: true,
      body: (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            {
              key: "sedentary",
              emoji: <Ic.Chair s={22} c="currentColor" />,
              title: "Sedentary",
              sub: "Mostly seated, little regular exercise",
            },
            {
              key: "light",
              emoji: <Ic.Walk s={22} c="currentColor" />,
              title: "Lightly Active",
              sub: "Some daily movement or light exercise a few times a week",
            },
            {
              key: "moderate",
              emoji: <Ic.Run s={22} c="currentColor" />,
              title: "Moderately Active",
              sub: "Exercise several days a week, or a job that keeps you moving",
            },
            {
              key: "very",
              emoji: <Ic.Hammer s={22} c="currentColor" />,
              title: "Very Active",
              sub: "Hard exercise most days, or physically demanding work",
            },
            {
              key: "extreme",
              emoji: null,
              title: "Extremely Active",
              sub: "Intense training, physical labor, or two sessions a day",
            },
          ].map((o) => (
            <OptionCard
              key={o.key}
              selected={form.activity === o.key}
              onClick={() => set("activity", o.key)}
              emoji={o.emoji}
              title={o.title}
              sub={o.sub}
            />
          ))}
        </div>
      ),
    },
  ];

  if (phase === "setup") {
    const cur = setupScreens[setupStep];
    const totalSetupSteps = setupScreens.length;
    const progress = setupStep / totalSetupSteps; // 0 to 1

    return (
      <div
        className="screen"
        style={{
                    display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        {/* Progress bar only — no step counter text */}
        <div
          style={{
            height: 3,
            background: "var(--border)",
            borderRadius: 99,
            marginBottom: 36,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${(setupStep / (totalSetupSteps - 1)) * 100}%`,
              height: "100%",
              background: "var(--accent)",
              borderRadius: 99,
              transition: "width .4s cubic-bezier(.4,0,.2,1)",
            }}
          />
        </div>

        {/* Question */}
        <div style={{ marginBottom: 28, flex: "0 0 auto" }}>
          <p style={{ fontSize: 40, lineHeight: 1, marginBottom: 16 }}>
            {cur.emoji}
          </p>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "var(--text)",
              letterSpacing: -0.3,
              lineHeight: 1.2,
              marginBottom: 8,
            }}
          >
            {cur.title}
          </h2>
          <p style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>
            {cur.sub}
          </p>
        </div>

        {/* Body — scrollable if needed */}
        <div style={{ flex: 1, overflowY: "auto", marginBottom: 20 }}>
          {cur.body}
        </div>

        {/* CTA */}
        <div style={{ flexShrink: 0 }}>
          <button
            disabled={!cur.valid}
            onClick={() =>
              setupStep < totalSetupSteps - 1
                ? setSetupStep((s) => s + 1)
                : setPhase("payoff")
            }
            style={{
              background: cur.valid ? "var(--accent)" : "var(--surface2)",
              border: "none",
              borderRadius: 14,
              padding: "16px",
              fontSize: 15,
              fontWeight: 700,
              color: cur.valid ? "var(--bg)" : "var(--muted)",
              cursor: cur.valid ? "pointer" : "not-allowed",
              fontFamily: "'DM Sans',sans-serif",
              width: "100%",
              transition: "all .15s",
            }}
          >
            {setupStep < totalSetupSteps - 1 ? "Continue →" : "Almost done →"}
          </button>
          <button
            onClick={() =>
              setupStep === 0 ? setPhase("hook") : setSetupStep((s) => s - 1)
            }
            style={{
              background: "none",
              border: "none",
              color: "var(--muted)",
              fontSize: 13,
              cursor: "pointer",
              marginTop: 12,
              textAlign: "center",
              width: "100%",
              fontFamily: "'DM Sans',sans-serif",
              padding: "8px",
            }}
          >
            ← Back
          </button>
        </div>

        <style>{CSS}</style>
      </div>
    );
  }

  // ── PHASE 3: PAYOFF ────────────────────────────────────────────────────────
  if (phase === "payoff") {
    const goalLabels = {
      strength: {
        label: "Strength",
        reps: "3–6 reps",
        jump: "+5lbs per milestone",
      },
      hypertrophy: {
        label: "Hypertrophy",
        reps: "8–12 reps",
        jump: "+5lbs per milestone",
      },
      endurance: {
        label: "Endurance",
        reps: "12–20 reps",
        jump: "+2.5lbs per milestone",
      },
    };
    const g = goalLabels[form.goal] || goalLabels.hypertrophy;
    const expText =
      {
        beginner: "We'll start conservative and build your foundation safely.",
        intermediate:
          "We'll push you steadily — larger jumps when you're progressing fast.",
        advanced:
          "We'll be precise. Small increments, science-backed periodization.",
      }[form.experience] || "";

    const name = form.name.split(" ")[0];

    const completeOnboard = () => {
      const height = form.heightFt
        ? `${form.heightFt}'${form.heightIn || "0"}"`
        : "";
      onDone({
        name: form.name.trim(),
        sex: form.sex,
        age: form.age,
        height,
        weight: form.weight,
        activity: form.activity,
        // The formatted string is kept for display; the raw fields are what
        // the BMR calculation actually reads. Saving only the string is what
        // pinned every user to 5'10".
        heightFt: form.heightFt,
        heightIn: form.heightIn === "" ? "0" : form.heightIn,
        goal: form.goal,
        bodyGoal: form.bodyGoal,
        experience: form.experience,
      });
    };

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(160deg, var(--bg) 0%, var(--surface2) 60%, var(--surface) 100%)",
          padding: "48px 20px 100px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)",
            filter: "blur(50px)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* Personalized summary */}
          <div style={{ fontSize: 40, marginBottom: 24, lineHeight: 1 }}>
            <Ic.Target s={22} c="currentColor" />
          </div>

          <p
            style={{
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: 40,
              color: "var(--accent)",
              letterSpacing: 1,
              lineHeight: 0.95,
              marginBottom: 6,
            }}
          >
            YOU'RE SET,
          </p>
          <p
            style={{
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: 40,
              color: "var(--text)",
              letterSpacing: 1,
              lineHeight: 0.95,
              marginBottom: 28,
            }}
          >
            {name.toUpperCase()}.
          </p>

          {/* Summary cards */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 36,
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,.05)",
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 14,
                padding: "14px 16px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                Your Training Mode
              </p>
              <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
                {g.label} · {g.reps}
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {expText}
              </p>
            </div>

            <div
              style={{
                background: "rgba(255,255,255,.05)",
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 14,
                padding: "14px 16px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                How Overload Works for You
              </p>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text)",
                  lineHeight: 1.6,
                }}
              >
                When you hit{" "}
                <strong style={{ color: "var(--accent)" }}>{g.reps}</strong> on
                all your sets, Overload adds weight next session ({g.jump}). If
                you're not there yet, it pushes reps first. One variable at a
                time — the science-backed way.
              </p>
            </div>
          </div>
        </div>

        <div style={{ flexShrink: 0 }}>
          <button
            onClick={completeOnboard}
            style={{
              background: "var(--accent)",
              border: "none",
              borderRadius: 16,
              padding: "18px",
              fontSize: 18,
              fontWeight: 700,
              color: "var(--bg)",
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
              boxShadow: "0 4px 24px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.4)",
              letterSpacing: 0.3,
              width: "100%",
              marginBottom: 12,
            }}
          >
            Start My First Workout
          </button>
          <button
            onClick={() => setPhase("setup")}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted)",
              fontSize: 13,
              cursor: "pointer",
              width: "100%",
              textAlign: "center",
              fontFamily: "'DM Sans',sans-serif",
              padding: "8px",
            }}
          >
            ← Edit my profile
          </button>
        </div>

        <style>{CSS}</style>
      </div>
    );
  }

  return null;
}

function LastSessionSummary({ session, onClose, profile, settings }) {
  const [showShareCard, setShowShareCard] = useState(false);

  const color =
    GROUP_COLORS[session.muscleGroup?.split(" + ")[0]] || "var(--accent)";
  const workingSets = session.exercises.reduce(
    (a, ex) => a + ex.sets.length,
    0
  );
  const warmupSets = 0;
  const totalSets = workingSets;
  const totalReps = session.exercises.reduce(
    (a, ex) => a + ex.sets.reduce((b, s) => b + s.reps, 0),
    0
  );
  const totalVol = session.exercises.reduce(
    (a, ex) => a + ex.sets.reduce((b, s) => b + (s.weight || 0) * s.reps, 0),
    0
  );
  const durSecs = session.duration || 0;
  const mins = Math.floor(durSecs / 60);
  const secs = durSecs % 60;
  const calories = session.calories || "";
  const bpm = session.bpm || "";
  const timeRange = fmtSessionTimeRange(
    session,
    settings?.timeFormat || "12hr"
  );

  return (
    <div style={{ padding: "0 0 80px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "48px 16px 14px",
          background: "var(--surface)",
          borderBottom: "1px solid var(--border)",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button className="btn-ghost sm" onClick={onClose}>
          ← Back
        </button>
        <p style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>
          Session Summary
        </p>
        <div style={{ width: 60 }} />
      </div>

      <div
        style={{
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div>
          <p
            style={{
              fontSize: 11,
              color: "var(--muted)",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            {session.date}
          </p>
          <p style={{ fontSize: 28, fontWeight: 700, color: "var(--text)" }}>
            {session.muscleGroup}
          </p>
          {durSecs > 0 && (
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 2 }}>
              <Ic.Timer s={22} c="currentColor" /> {mins}m {secs}s
            </p>
          )}
          {timeRange && (
            <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              {timeRange}
            </p>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 10,
          }}
        >
          {[
            { val: totalSets, label: "Working Sets" },
            { val: totalReps, label: "Total Reps" },
            {
              val:
                totalVol >= 1000
                  ? `${(totalVol / 1000).toFixed(1)}k`
                  : totalVol,
              label: "Volume (lbs)",
            },
          ].map((s, i) => (
            <div key={i} className="stat-box">
              <span className="stat-num" style={{ fontSize: 18 }}>
                <CountUp value={s.val} />
              </span>
              <span className="stat-lbl">{s.label}</span>
            </div>
          ))}
        </div>

        {(calories || bpm) && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2,1fr)",
              gap: 10,
            }}
          >
            {calories && (
              <div className="stat-box">
                <span
                  className="stat-num"
                  style={{ fontSize: 18, color: "var(--danger)" }}
                >
                  <CountUp value={Number(calories)} />
                </span>
                <span className="stat-lbl">kcal burned</span>
              </div>
            )}
            {bpm && (
              <div className="stat-box">
                <span
                  className="stat-num"
                  style={{ fontSize: 18, color: "var(--accent)" }}
                >
                  <CountUp value={Number(bpm)} />
                </span>
                <span className="stat-lbl">avg BPM</span>
              </div>
            )}
          </div>
        )}

{(() => {
          // MEASUREMENT ONLY — deliberately no calorie or carb language here.
          // The score is shown so it can be sanity-checked against how the
          // session actually felt, before anything downstream consumes it.
          const demand = calculateTrainingDemand(session);
          if (demand.workingSets === 0) return null;
          return (
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: "14px 16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 8,
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--muted)",
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                  }}
                >
                  Training Demand
                </p>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--accent)",
                  }}
                >
                  {demand.level}
                </p>
              </div>
              <p
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: "var(--text)",
                  lineHeight: 1,
                }}
              >
                {demand.score}
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 400,
                    color: "var(--muted)",
                    marginLeft: 4,
                  }}
                >
                  / 100
                </span>
              </p>
              <div
                style={{
                  height: 5,
                  background: "var(--surface2)",
                  borderRadius: 99,
                  overflow: "hidden",
                  margin: "10px 0 8px",
                }}
              >
                <div
                  style={{
                    width: `${demand.score}%`,
                    height: "100%",
                    background: "var(--accent)",
                    borderRadius: 99,
                  }}
                />
              </div>
              <p style={{ fontSize: 11, color: "var(--muted)" }}>
                {demand.workingSets} working set
                {demand.workingSets !== 1 ? "s" : ""} · {demand.totalReps} reps
                · {demand.compoundSets} compound
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 8,
                  lineHeight: 1.5,
                }}
              >
                An estimate of how much work this session represented, from
                sets, reps and the muscles involved.
              </p>
            </div>
          );
        })()}

        {(() => {
          const poExs = session.exercises.filter(
            (ex) => ex.poTarget && ex.sets.length > 0
          );
          if (!poExs.length) return null;
          const results = poExs.map((ex) => {
            const ws = ex.sets;
            const best = ws.reduce(
              (a, s) => (s.weight > a.weight ? s : a),
              ws[0]
            );
            const hit =
              best.weight >= ex.poTarget.weight &&
              best.reps >= ex.poTarget.reps;
            const exc = best.weight > ex.poTarget.weight;
            return {
              ex,
              best,
              status: exc ? "exceeded" : hit ? "hit" : "missed",
            };
          });
          const hitCount = results.filter((r) => r.status !== "missed").length;
          return (
            <div
              style={{
                background: "var(--surface)",
                border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
                borderRadius: 16,
                padding: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: "var(--text)",
                  }}
                >
                  Progressive Overload
                </p>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color:
                      hitCount === results.length
                        ? "var(--success)"
                        : "var(--accent)",
                  }}
                >
                  {hitCount}/{results.length} hit
                </p>
              </div>
              {results.map((r, i) => {
                const icon =
                  r.status === "exceeded"
                    ? "▲"
                    : r.status === "hit"
                    ? "✓"
                    : "✕";
                const tc =
                  r.status === "exceeded"
                    ? "var(--accent)"
                    : r.status === "hit"
                    ? "var(--success)"
                    : "var(--danger)";
                return (
                  <div
                    key={i}
                    style={{
                      background: "var(--surface2)",
                      border: "1px solid var(--border)",
                      borderLeft: `3px solid ${tc}`,
                      borderRadius: 10,
                      padding: "8px 12px",
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <p style={{ fontSize: 13, fontWeight: 700 }}>
                        {icon} {r.ex.name.split(" (")[0]}
                      </p>
                      <p style={{ fontSize: 11, color: tc, fontWeight: 700 }}>
                        {r.best.weight}×{r.best.reps}
                      </p>
                    </div>
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginTop: 2,
                      }}
                    >
                      Target: {r.ex.poTarget.weight}×{r.ex.poTarget.reps}
                    </p>
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: 16,
          }}
        >
          <p
            style={{
              fontWeight: 700,
              fontSize: 13,
              marginBottom: 10,
              color: "var(--text)",
            }}
          >
            Session Recap
          </p>
          {session.exercises.map((ex, i) => {
            const working = ex.sets;
            const best =
              working.length > 0
                ? working.reduce(
                    (a, s) => (s.weight > a.weight ? s : a),
                    working[0]
                  )
                : null;
            return (
              <div
                key={i}
                style={{
                  borderBottom: "1px solid var(--border)",
                  padding: "8px 0",
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <p style={{ fontSize: 13, fontWeight: 700 }}>{ex.name}</p>
                  {best && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--text)",
                        fontWeight: 700,
                      }}
                    >
                      Best: {best.weight}×{best.reps}
                    </p>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    marginTop: 4,
                  }}
                >
                  {working.map((s, si) => (
                    <span key={si} className="chip">
                      {s.weight}×{s.reps}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={() => setShowShareCard(true)}
          style={{
            background: "linear-gradient(135deg, var(--surface2), var(--surface))",
            border: `2px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.333)`,
            borderRadius: 16,
            padding: "16px",
            cursor: "pointer",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ textAlign: "left" }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
              <Ic.Camera s={22} c="currentColor" /> Share This Workout
            </p>
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                marginTop: 2,
              }}
            >
              Open full-screen share card
            </p>
          </div>
          <span style={{ fontSize: 18, color: "var(--text)" }}>→</span>
        </button>

        {showShareCard &&
          (() => {
            const durSecs2 = session.duration || 0;
            const mins2 = Math.floor(durSecs2 / 60);
            const secs2 = durSecs2 % 60;
            return (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 999,
                  background: "var(--bg)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                    background: `linear-gradient(160deg, var(--bg) 0%, var(--surface2) 50%, var(--surface) 100%)`,
                    position: "relative",
                  }}
                >
                  <button
                    onClick={() => setShowShareCard(false)}
                    style={{
                      position: "sticky",
                      top: 10,
                      float: "right",
                      marginRight: 12,
                      marginTop: 10,
                      zIndex: 1000,
                      background: "rgba(255,255,255,.15)",
                      border: "1px solid rgba(255,255,255,.2)",
                      borderRadius: "50%",
                      width: 34,
                      height: 34,
                      color: "var(--text)",
                      fontSize: 15,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ✕
                  </button>
                  <div
                    style={{
                      position: "absolute",
                      top: -60,
                      right: -60,
                      width: 200,
                      height: 200,
                      borderRadius: "50%",
                      background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.094)`,
                      filter: "blur(40px)",
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      padding: "10px 18px 24px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 50,
                      }}
                    >
                      <div
                        style={{
                          background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.126)`,
                          border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
                          borderRadius: 20,
                          padding: "3px 10px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        
                        <span
                          style={{
                            fontSize: 11,
                            color,
                            fontWeight: 700,
                            letterSpacing: 2,
                            textTransform: "uppercase",
                          }}
                        >
                          OverLoad
                        </span>
                      </div>
                      <p
                        style={{ fontSize: 11, color: "var(--muted)" }}
                      >
                        {session.date}
                        {timeRange ? ` · ${timeRange}` : ""}
                      </p>
                    </div>
                    <div>
                      <p
                        style={{
                          fontSize: 11,
                          color: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.6)`,
                          textTransform: "uppercase",
                          letterSpacing: 2,
                          fontWeight: 700,
                        }}
                      >
                        {session.muscleGroup}
                      </p>
                      <p
                        style={{
                          fontSize: 28,
                          fontWeight: 700,
                          fontFamily: "'Bebas Neue',sans-serif",
                          color: "var(--text)",
                          letterSpacing: 2,
                          lineHeight: 1,
                          marginTop: 2,
                        }}
                      >
                        WORKOUT{" "}
                        <span style={{ color }}>
                          {session.muscleGroup.toUpperCase()}
                        </span>
                      </p>
                    </div>
                    <div
                      style={{
                        background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.082)`,
                        border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.188)`,
                        borderRadius: 10,
                        padding: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 15 }}>
                        <Ic.Timer s={22} c="currentColor" />
                      </span>
                      <div>
                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            fontFamily: "'Bebas Neue',sans-serif",
                            color: "var(--text)",
                            lineHeight: 1,
                          }}
                        >
                          {mins2}:{String(secs2).padStart(2, "0")}
                        </p>
                        <p
                          style={{
                            fontSize: 11,
                            color: "var(--muted)",
                            textTransform: "uppercase",
                          }}
                        >
                          Duration
                        </p>
                      </div>
                      {Number(calories) > 0 && (
                        <>
                          <div
                            style={{
                              width: 1,
                              height: 26,
                              background: "rgba(255,255,255,.1)",
                            }}
                          />
                          <div>
                            <p
                              style={{
                                fontSize: 18,
                                fontWeight: 700,
                                fontFamily: "'Bebas Neue',sans-serif",
                                color: "var(--danger)",
                                lineHeight: 1,
                              }}
                            >
                              {calories}
                            </p>
                            <p
                              style={{
                                fontSize: 11,
                                color: "var(--muted)",
                                textTransform: "uppercase",
                              }}
                            >
                              kcal
                            </p>
                          </div>
                        </>
                      )}
                      {Number(bpm) > 0 && (
                        <>
                          <div
                            style={{
                              width: 1,
                              height: 26,
                              background: "rgba(255,255,255,.1)",
                            }}
                          />
                          <div>
                            <p
                              style={{
                                fontSize: 18,
                                fontWeight: 700,
                                fontFamily: "'Bebas Neue',sans-serif",
                                color: "var(--accent)",
                                lineHeight: 1,
                              }}
                            >
                              {bpm}
                            </p>
                            <p
                              style={{
                                fontSize: 11,
                                color: "var(--muted)",
                                textTransform: "uppercase",
                              }}
                            >
                              BPM
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3,1fr)",
                        gap: 6,
                      }}
                    >
                      {[
                        { val: totalSets, label: "Working\nSets", c: color },
                        {
                          val: totalReps,
                          label: "Total\nReps",
                          c: "var(--purple)",
                        },
                        {
                          val:
                            totalVol >= 1000
                              ? `${(totalVol / 1000).toFixed(1)}k`
                              : totalVol,
                          label: "Volume\n(lbs)",
                          c: "var(--info)",
                        },
                      ].map((s, i) => (
                        <div
                          key={i}
                          style={{
                            background: "rgba(255,255,255,.04)",
                            border: "1px solid rgba(255,255,255,.08)",
                            borderRadius: 10,
                            padding: "8px 6px",
                            textAlign: "center",
                          }}
                        >
                          <p
                            style={{
                              fontSize: 22,
                              fontWeight: 700,
                              fontFamily: "'Bebas Neue',sans-serif",
                              color: s.c,
                              lineHeight: 1,
                            }}
                          >
                            {s.val}
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 2,
                              textTransform: "uppercase",
                              letterSpacing: 0.8,
                              whiteSpace: "pre",
                            }}
                          >
                            {s.label}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        background: "rgba(255,255,255,.03)",
                        border: "1px solid rgba(255,255,255,.08)",
                        borderRadius: 10,
                        padding: "8px 12px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 11,
                          color: "var(--muted)",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          marginBottom: 6,
                        }}
                      >
                        Best Sets
                      </p>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "3px 10px",
                        }}
                      >
                        {session.exercises
                          .filter((ex) => ex.sets.length > 0)
                          .slice(0, 8)
                          .map((ex, i) => {
                            const ws = ex.sets;
                            const best = ws.reduce(
                              (a, s) =>
                                s.weight * s.reps > a.weight * a.reps ? s : a,
                              ws[0]
                            );
                            return (
                              <div
                                key={i}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: 11,
                                    color: "var(--text)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 100,
                                  }}
                                >
                                  {ex.name.split(" (")[0]}
                                </p>
                                <p
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "var(--text)",
                                    flexShrink: 0,
                                    marginLeft: 3,
                                  }}
                                >
                                  {best.weight}×{best.reps}
                                </p>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    <div
                      style={{
                        paddingTop: 8,
                        borderTop: "1px solid rgba(255,255,255,.06)",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 11,
                          color: "var(--muted)",
                          letterSpacing: 1,
                        }}
                      >
                        OVERLOAD · PROGRESSIVE OVERLOAD TRACKER
                      </p>
                      <p
                        style={{ fontSize: 11, color: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.333)` }}
                      >
                        overload.app
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    background: "var(--bg)",
                    padding: "10px 24px 16px",
                    textAlign: "center",
                    borderTop: "1px solid rgba(255,255,255,.06)",
                    flexShrink: 0,
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--muted)",
                      marginBottom: 8,
                    }}
                  >
                    <Ic.Camera s={22} c="currentColor" /> Take a screenshot to
                    save & share
                  </p>
                  <button
                    onClick={() => setShowShareCard(false)}
                    style={{
                      background: "rgba(255,255,255,.08)",
                      border: "1px solid rgba(255,255,255,.15)",
                      borderRadius: 10,
                      padding: "8px 24px",
                      color: "var(--text)",
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
}

// ── EXPANDED CARD CONTENT ─────────────────────────────────────────────────────
// Full-screen version of each carousel card
function ExpandedCardContent({
  cardId,
  last,
  thisWeek,
  lastWeek,
  recoveryStatus,
  sessions,
  profile,
  cardioLogs,
  thisWeekSets,
  onViewLastSession,
}) {
  switch (cardId) {
    case "last": {
      if (!last)
        return (
          <p
            style={{ color: "var(--muted)", textAlign: "center", padding: 40 }}
          >
            No sessions yet
          </p>
        );
      const color =
        GROUP_COLORS[last.muscleGroup?.split(" + ")[0]] || "var(--accent)";
      const totalVol = last.exercises.reduce(
        (a, ex) =>
          a + ex.sets.reduce((b, s) => b + (s.weight || 0) * s.reps, 0),
        0
      );
      const totalSets = last.exercises.reduce((a, ex) => a + ex.sets.length, 0);
      const totalReps = last.exercises.reduce(
        (a, ex) => a + ex.sets.reduce((b, s) => b + s.reps, 0),
        0
      );
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <p style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }}>
              {last.muscleGroup}
            </p>
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
              {last.date}
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 10,
            }}
          >
            {[
              { val: totalSets, label: "Sets" },
              { val: totalReps, label: "Reps" },
              {
                val:
                  totalVol >= 1000
                    ? `${(totalVol / 1000).toFixed(1)}k`
                    : totalVol,
                label: "Volume",
              },
            ].map((s, i) => (
              <div key={i} className="stat-box">
                <span className="stat-num" style={{ fontSize: 28 }}>
                  <CountUp value={s.val} />
                </span>
                <span className="stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>
          {last.note && (
            <div
              style={{
                background: "var(--surface2)",
                borderRadius: 12,
                padding: "12px 14px",
                borderLeft: "3px solid var(--border)",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  fontStyle: "italic",
                }}
              >
                "{last.note}"
              </p>
            </div>
          )}
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            {last.exercises.map((ex, i) => {
              const best =
                ex.sets.length > 0
                  ? ex.sets.reduce(
                      (a, s) => (s.weight > a.weight ? s : a),
                      ex.sets[0]
                    )
                  : null;
              return (
                <div
                  key={i}
                  style={{
                    padding: "12px 16px",
                    borderBottom:
                      i < last.exercises.length - 1
                        ? "1px solid var(--border)"
                        : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      marginBottom: 6,
                    }}
                  >
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--text)",
                      }}
                    >
                      {ex.name}
                    </p>
                    {best && (
                      <p
                        style={{
                          fontSize: 11,
                          color: "var(--text)",
                          fontWeight: 700,
                        }}
                      >
                        {best.weight}×{best.reps}
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {ex.sets.map((s, si) => (
                      <span
                        key={si}
                        style={{
                          fontSize: 11,
                          background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.082)`,
                          border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.2)`,
                          borderRadius: 20,
                          padding: "3px 10px",
                          color: "var(--text)",
                          fontWeight: 700,
                        }}
                      >
                        {s.weight}×{s.reps}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            onClick={onViewLastSession}
            style={{
              background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.082)`,
              border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
              borderRadius: 12,
              padding: "14px",
              fontSize: 13,
              fontWeight: 700,
              color: "var(--text)",
              cursor: "pointer",
            }}
          >
            <Ic.ChartBar s={22} c="currentColor" /> Full Summary & Share →
          </button>
        </div>
      );
    }

    case "week": {
      if (!thisWeek) return null;
      const lastWeekSessions = sessions.filter((s) => {
        const d = new Date(s.id || s.date);
        const now = new Date();
        const diffDays = (now - d) / (1000 * 60 * 60 * 24);
        return diffDays >= 7 && diffDays < 14;
      });
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const weekDays = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - d.getDay() + i);
        const ds = d.toLocaleDateString();
        const sess = sessions.filter((s) => s.date === ds);
        return {
          day: dayNames[i],
          date: ds,
          sessions: sess,
          sets: sess.reduce(
            (a, s) => a + s.exercises.reduce((b, ex) => b + ex.sets.length, 0),
            0
          ),
        };
      });
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 10,
            }}
          >
            {[
              { val: thisWeek.totalSets, label: "Sets" },
              { val: thisWeek.sessions, label: "Workouts" },
              {
                val:
                  thisWeek.totalVolume >= 1000
                    ? `${(thisWeek.totalVolume / 1000).toFixed(1)}k`
                    : thisWeek.totalVolume,
                label: "Volume",
              },
            ].map((s, i) => (
              <div key={i} className="stat-box">
                <span className="stat-num" style={{ fontSize: 28 }}>
                  <CountUp value={s.val} />
                </span>
                <span className="stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>
          {thisWeekSets && thisWeekSets.totalPhysicalSets > 0 && (
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: "14px 16px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  marginBottom: 12,
                }}
              >
                Effective Sets This Week
              </p>
              {MUSCLE_GROUPS.filter(
                (m) => (thisWeekSets.effectiveSetsByMuscle[m] || 0) > 0
              ).map((m) => (
                <div
                  key={m}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    padding: "5px 0",
                  }}
                >
                  <span style={{ fontSize: 13, color: "var(--text)" }}>{m}</span>
                  <span
                    style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}
                  >
                    {thisWeekSets.effectiveSetsByMuscle[m]}
                  </span>
                </div>
              ))}
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 10,
                  lineHeight: 1.5,
                }}
              >
                Direct sets plus partial credit for secondary-muscle work. An
                estimate of training exposure, not a measurement.
              </p>
            </div>
          )}

          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: "14px 16px",
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                marginBottom: 12,
              }}
            >
              This Week
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                gap: 4,
              }}
            >
              {weekDays.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: 60,
                      borderRadius: 8,
                      position: "relative",
                      background: "var(--surface2)",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "flex-end",
                    }}
                  >
                    {d.sets > 0 && (
                      <div
                        style={{
                          width: "100%",
                          height: `${Math.min(100, (d.sets / 25) * 100)}%`,
                          background: "var(--accent)",
                          borderRadius: "4px 4px 0 0",
                        }}
                      />
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: 11,
                      color:
                        d.sessions.length > 0 ? "var(--text)" : "var(--muted)",
                      fontWeight: d.sessions.length > 0 ? 700 : 400,
                    }}
                  >
                    {d.day}
                  </p>
                  {d.sets > 0 && (
                    <p style={{ fontSize: 11, color: "var(--muted)" }}>
                      {d.sets}s
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
          {lastWeekSessions.length > 0 && (
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: "14px 16px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  marginBottom: 8,
                }}
              >
                Last Week
              </p>
              {[
                {
                  label: "Sets",
                  this: thisWeek.totalSets,
                  last: lastWeek?.totalSets || 0,
                },
                {
                  label: "Volume",
                  this: thisWeek.totalVolume,
                  last: lastWeek?.totalVolume || 0,
                },
                {
                  label: "Workouts",
                  this: thisWeek.sessions,
                  last: lastWeek?.sessions || 0,
                },
              ].map((m, i) => {
                const diff = m.this - m.last;
                const pct =
                  m.last > 0 ? Math.round((diff / m.last) * 100) : null;
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      borderBottom: i < 2 ? "1px solid var(--border)" : "none",
                    }}
                  >
                    <p style={{ fontSize: 13, color: "var(--muted)" }}>
                      {m.label}
                    </p>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "var(--text)",
                        }}
                      >
                        {m.this}
                      </p>
                      {pct !== null && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color:
                              diff >= 0 ? "var(--success)" : "var(--danger)",
                          }}
                        >
                          {diff >= 0 ? "▲" : "▼"} {Math.abs(pct)}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    case "recovery": {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {MUSCLE_GROUPS.map((g) => {
            const r = recoveryStatus?.[g];
            if (!r?.lastDate)
              return (
                <div
                  key={g}
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: 14,
                    padding: "14px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                    }}
                  >
                    {g}
                  </p>
                  <p style={{ fontSize: 11, color: "var(--muted)" }}>
                    Never trained
                  </p>
                </div>
              );
            const statusColor =
              r.status === "ready"
                ? "var(--success)"
                : r.status === "almost"
                ? "var(--accent)"
                : "var(--danger)";
            const statusLabel =
              r.status === "ready"
                ? "✓ Ready"
                : r.status === "almost"
                ? `${r.hoursRemaining}h to go`
                : "Needs rest";
            const pct =
              r.status === "ready"
                ? 100
                : r.status === "almost"
                ? Math.round((1 - r.hoursRemaining / 48) * 100)
                : 30;
            return (
              <div
                key={g}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: 14,
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                    }}
                  >
                    {g}
                  </p>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: statusColor,
                    }}
                  >
                    {statusLabel}
                  </span>
                </div>
                <div
                  style={{
                    height: 5,
                    background: "var(--surface2)",
                    borderRadius: 99,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      background: statusColor,
                      borderRadius: 99,
                      transition: "width .5s",
                    }}
                  />
                </div>
                <p
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 5 }}
                >
                  Last trained {r.lastDate}
                </p>
              </div>
            );
          })}
        </div>
      );
    }

    default:
      return (
        <p style={{ color: "var(--muted)", textAlign: "center", padding: 40 }}>
          No data available
        </p>
      );
  }
}

function HomeCarousel({
  last,
  settings,
  thisWeek,
  lastWeek,
  recoveryStatus,
  sessions,
  profile,
  cardioLogs,
  onViewLastSession,
  onCardTap,
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const startXRef = useRef(null);
  const containerRef = useRef(null);

  const cards = [
    { id: "last", label: "Last Session" },
    { id: "week", label: "This Week" },
    { id: "recovery", label: "Recovery" },
    { id: "weight", label: "Body Weight" },
    { id: "nutrition", label: "Nutrition" },
    { id: "milestones", label: "All-Time" },
  ].filter((c) => {
    if (c.id === "last" && !last) return false;
    if (c.id === "week" && (!thisWeek || thisWeek.totalSets === 0))
      return false;
    if (c.id === "recovery" && !recoveryStatus) return false;
    return true;
  });

  const total = cards.length;

  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (startXRef.current === null) return;
    const diff = startXRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) setActiveIdx((i) => Math.min(i + 1, total - 1));
      else setActiveIdx((i) => Math.max(i - 1, 0));
    }
    startXRef.current = null;
  };

  const card = cards[activeIdx];

  const renderCard = () => {
    switch (card?.id) {
      case "last": {
        const color =
          GROUP_COLORS[last.muscleGroup?.split(" + ")[0]] || "var(--accent)";
        const workingSets = last.exercises.reduce(
          (a, ex) => a + ex.sets.length,
          0
        );
        const totalVol = last.exercises.reduce(
          (a, ex) =>
            a + ex.sets.reduce((b, s) => b + (s.weight || 0) * s.reps, 0),
          0
        );
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Last Session — {last.date}
              </p>
              {last.duration && (
                <p style={{ fontSize: 11, color: "var(--muted)" }}>
                  <Ic.Timer s={22} c="currentColor" />{" "}
                  {Math.floor(last.duration / 60)}m
                </p>
              )}
            </div>
            <p style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }}>
              {last.muscleGroup}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: 8,
              }}
            >
              {[
                { val: workingSets, label: "Sets" },
                {
                  val: last.exercises.reduce(
                    (a, ex) => a + ex.sets.reduce((b, s) => b + s.reps, 0),
                    0
                  ),
                  label: "Reps",
                },
                {
                  val:
                    totalVol >= 1000
                      ? `${(totalVol / 1000).toFixed(1)}k`
                      : totalVol,
                  label: "Volume",
                },
              ].map((s, i) => (
                <div
                  key={i}
                  className="stat-box"
                  style={{ padding: "8px 6px" }}
                >
                  <span className="stat-num" style={{ fontSize: 15 }}>
                    <CountUp value={s.val} />
                  </span>
                  <span className="stat-lbl">{s.label}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {last.exercises.map((e, i) => (
                <span key={i} className="chip" style={{ fontSize: 11 }}>
                  {e.name.split(" (")[0]}
                </span>
              ))}
            </div>
            <button
              onClick={onViewLastSession}
              style={{
                background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.082)`,
                border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
                borderRadius: 10,
                padding: "10px",
                fontSize: 13,
                fontWeight: 700,
                color: "var(--text)",
                cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
                marginTop: 4,
              }}
            >
              <Ic.ChartBar s={22} c="currentColor" /> View Full Summary & Share
              →
            </button>
          </div>
        );
      }

      case "week": {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--accent)",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                <Ic.ChartBar s={22} c="currentColor" /> This Week
              </p>
              <p style={{ fontSize: 11, color: "var(--muted)" }}>
                {thisWeek.dateRange}
              </p>
            </div>
            <p style={{ fontSize: 11, color: "var(--muted)" }}>
              {thisWeek.sessions} session{thisWeek.sessions !== 1 ? "s" : ""}
              {thisWeek.cardioMins > 0
                ? ` · ${thisWeek.cardioMins}min cardio`
                : ""}
              {" · working sets only"}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: 8,
              }}
            >
              {[
                {
                  label: "Volume",
                  raw: thisWeek.totalVolume,
                  prev: lastWeek.totalVolume,
                  val:
                    thisWeek.totalVolume >= 1000
                      ? `${(thisWeek.totalVolume / 1000).toFixed(1)}k`
                      : thisWeek.totalVolume,
                },
                {
                  label: "Sets",
                  raw: thisWeek.totalSets,
                  prev: lastWeek.totalSets,
                  val: thisWeek.totalSets,
                },
                {
                  label: "Reps",
                  raw: thisWeek.totalReps,
                  prev: lastWeek.totalReps,
                  val: thisWeek.totalReps,
                },
              ].map((s, i) => {
                const pct =
                  s.prev > 0
                    ? Math.round(((s.raw - s.prev) / s.prev) * 100)
                    : null;
                const up = pct !== null && pct >= 0;
                return (
                  <div
                    key={i}
                    className="stat-box"
                    style={{ padding: "8px 6px" }}
                  >
                    <span
                      className="stat-num"
                      style={{ fontSize: 15, color: "var(--text)" }}
                    >
                      <CountUp value={s.val} />
                    </span>
                    <span className="stat-lbl">{s.label}</span>
                    {pct !== null && (
                      <span
                        style={{
                          fontSize: 11,
                          color: up ? "var(--success)" : "var(--danger)",
                        }}
                      >
                        {up ? "▲" : "▼"}
                        {Math.abs(pct)}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {Object.keys(thisWeek.byMuscle).length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {Object.entries(thisWeek.byMuscle)
                  .sort((a, b) => b[1].volume - a[1].volume)
                  .map(([group, data]) => {
                    const color = GROUP_COLORS[group] || "var(--accent)";
                    const maxVol = Math.max(
                      ...Object.values(thisWeek.byMuscle).map((d) => d.volume)
                    );
                    const pct =
                      maxVol > 0 ? Math.round((data.volume / maxVol) * 100) : 0;
                    return (
                      <div key={group}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 2,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "var(--text)",
                            }}
                          >
                            {group}{" "}
                            <span
                              style={{ color: "var(--muted)", fontWeight: 400 }}
                            >
                              {data.sets} sets
                            </span>
                          </span>
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>
                            {data.volume >= 1000
                              ? `${(data.volume / 1000).toFixed(1)}k`
                              : data.volume}{" "}
                            lbs
                          </span>
                        </div>
                        <div
                          style={{
                            height: 4,
                            background: "var(--surface2)",
                            borderRadius: 99,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${pct}%`,
                              height: "100%",
                              background: color,
                              borderRadius: 99,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      }

      case "recovery": {
        const trained = MUSCLE_GROUPS.filter(
          (g) => recoveryStatus[g]?.lastDate
        );
        if (!trained.length)
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "28px 16px",
                gap: 10,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "rgba(61,220,132,.08)",
                  border: "1px solid rgba(61,220,132,.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                }}
              >
                <Ic.Barbell s={22} c="currentColor" />
              </div>
              <div>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--text)",
                    marginBottom: 4,
                  }}
                >
                  No recovery data yet
                </p>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    lineHeight: 1.6,
                  }}
                >
                  Complete your first workout to start tracking muscle recovery
                </p>
              </div>
            </div>
          );
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--accent)",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Recovery Status
            </p>
            {trained.map((g) => {
              const r = recoveryStatus[g];
              const color =
                r.status === "ready"
                  ? "var(--success)"
                  : r.status === "almost"
                  ? "var(--accent)"
                  : "var(--muted)";
              const label =
                r.status === "ready" ? "Ready ✓" : `${r.hoursRemaining}h left`;
              return (
                <div
                  key={g}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--text)",
                      width: 85,
                      flexShrink: 0,
                    }}
                  >
                    {g}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 6,
                      background: "var(--surface2)",
                      borderRadius: 99,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${r.pctRecovered}%`,
                        height: "100%",
                        background: color,
                        borderRadius: 99,
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--text)",
                      fontWeight: 700,
                      width: 68,
                      textAlign: "right",
                      flexShrink: 0,
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        );
      }

      case "weight": {
        const bw = Number(profile.weight) || 0;
        const bwData = sessions
          .filter((s) => s.bodyWeight)
          .slice(-8)
          .map((s) => ({ d: s.date, w: Number(s.bodyWeight) }));
        const startW = bwData.length > 1 ? bwData[0].w : bw;
        const diff = bw - startW;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--accent)",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              <Ic.Scale s={20} c="currentColor" /> Body Weight
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <div>
                <p style={{ fontSize: 40, fontWeight: 700, lineHeight: 1 }}>
                  {bw}
                </p>
                <p
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}
                >
                  lbs current
                </p>
              </div>
              {bwData.length > 1 && (
                <p
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color:
                      diff < 0
                        ? "var(--success)"
                        : diff > 0
                        ? "var(--danger)"
                        : "var(--muted)",
                  }}
                >
                  {diff > 0 ? "+" : ""}
                  {diff.toFixed(1)} lbs since start
                </p>
              )}
            </div>
            {bwData.length > 1 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 3,
                  height: 50,
                }}
              >
                {bwData.map((d, i) => {
                  const min = Math.min(...bwData.map((x) => x.w));
                  const max = Math.max(...bwData.map((x) => x.w));
                  const h =
                    Math.round(((d.w - min) / (max - min || 1)) * 36) + 8;
                  return (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: h,
                          background: "var(--accent)",
                          borderRadius: "3px 3px 0 0",
                          opacity: 0.6 + i * 0.05,
                        }}
                      />
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>
                        {d.d.split("/")[1]}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ fontSize: 11, color: "var(--muted)" }}>
                Update your weight in Settings to track history.
              </p>
            )}
          </div>
        );
      }

      case "nutrition": {
        const macros = calcMacros(profile, "Rest", settings?.calAdjust || 0);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--accent)",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              <Ic.Food s={22} c="currentColor" /> Daily Targets
            </p>
            <p style={{ fontSize: 11, color: "var(--muted)" }}>
              Based on your profile · log food in the Nutrition tab
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: 8,
              }}
            >
              {[
                {
                  val: `${macros.calories}`,
                  label: "Calories kcal",
                  color: "var(--text)",
                },
                {
                  val: `${macros.protein}g`,
                  label: "Protein",
                  color: "var(--success)",
                },
                { val: `${macros.carbs}g`, label: "Carbs", color: "var(--accent)" },
                { val: `${macros.fat}g`, label: "Fat", color: "var(--warn)" },
              ].map((m, i) => (
                <div
                  key={i}
                  className="stat-box"
                  style={{ padding: "10px 8px", borderColor: "var(--border)" }}
                >
                  <span
                    className="stat-num"
                    style={{ fontSize: 18, color: m.color }}
                  >
                    <CountUp value={m.val} />
                  </span>
                  <span className="stat-lbl">{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case "milestones": {
        const totalVol = sessions.reduce(
          (a, s) =>
            a +
            s.exercises.reduce(
              (b, ex) =>
                b + ex.sets.reduce((c, s) => c + (s.weight || 0) * s.reps, 0),
              0
            ),
          0
        );
        const totalSets = sessions.reduce(
          (a, s) => a + s.exercises.reduce((b, ex) => b + ex.sets.length, 0),
          0
        );
        const cardioMin = (cardioLogs || []).reduce(
          (a, c) => a + (c.duration || 0),
          0
        );
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--accent)",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              All-Time Stats
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: 8,
              }}
            >
              {[
                {
                  icon: <Ic.Barbell s={22} c="currentColor" />,
                  val: sessions.length,
                  label: "Sessions",
                },
                {
                  icon: null,
                  val:
                    totalVol >= 1000
                      ? `${(totalVol / 1000).toFixed(0)}k lbs`
                      : `${totalVol} lbs`,
                  label: "Total Volume",
                },
                { icon: null, val: totalSets, label: "Working Sets" },
                {
                  icon: <Ic.Run s={22} c="currentColor" />,
                  val: `${cardioMin}m`,
                  label: "Cardio",
                },
              ].map((m, i) => (
                <div
                  key={i}
                  className="stat-box"
                  style={{ padding: "10px 8px" }}
                >
                  <span style={{ fontSize: 18 }}>{m.icon}</span>
                  <span
                    className="stat-num"
                    style={{
                      fontSize: 15,
                      color: "var(--accent)",
                      marginTop: 4,
                    }}
                  >
                    <CountUp value={m.val} />
                  </span>
                  <span className="stat-lbl">{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div>
      <div
        className="card"
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          minHeight: 160,
          userSelect: "none",
          WebkitUserSelect: "none",
          cursor: "pointer",
          position: "relative",
        }}
        onClick={() => card && onCardTap && onCardTap(card.id, card.label)}
      >
        {renderCard()}
        {/* Tap hint */}
        <div
          style={{
            position: "absolute",
            bottom: 8,
            right: 10,
            fontSize: 11,
            color: "var(--muted)",
            fontWeight: 400,
            opacity: 0.6,
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          <span>tap to expand</span>
          <span style={{ fontSize: 11 }}>↗</span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 6,
          marginTop: 10,
        }}
      >
        {cards.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveIdx(i)}
            style={{
              width: i === activeIdx ? 20 : 6,
              height: 6,
              borderRadius: 99,
              border: "none",
              cursor: "pointer",
              padding: 0,
              background: i === activeIdx ? "var(--accent)" : "var(--border)",
              transition: "width .2s, background .2s",
            }}
          />
        ))}
      </div>
      <p
        style={{
          textAlign: "center",
          fontSize: 11,
          color: "var(--muted)",
          marginTop: 5,
        }}
      >
        {cards[activeIdx]?.label} · swipe to see more
      </p>
    </div>
  );
}

function HomeScreen({
  profile,
  thisWeekSets,
  sessions,
  onStart,
  onMuscleSelect,
  getLastSessionForGroup,
  onHistory,
  checkinDue,
  onSubmitCheckin,
  darkMode,
  onToggleTheme,
  recoveryStatus,
  onStartCardio,
  onRestDay,
  onShockWorkout,
  cardioLogs,
  currentStreak,
  thisWeek,
  lastWeek,
  settings,
  savedSession,
  onResumeSession,
  onDiscardSaved,
}) {
  const [picking, setPicking] = useState(false);
  const [showLastSession, setShowLastSession] = useState(false);
  const [showLastSessionFull, setShowLastSessionFull] = useState(false);
  const [showCheckin, setShowCheckin] = useState(checkinDue);
  const [checkinForm, setCheckinForm] = useState({ weight: "", photo: null });
  const [expandedCard, setExpandedCard] = useState(null); // full-screen card view
  const last = sessions[sessions.length - 1];

  const handleCheckinPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) =>
      setCheckinForm((f) => ({ ...f, photo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  if (showCheckin) {
    return (
      <div
        style={{
          padding: "48px 20px 100px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          minHeight: "100vh",
        }}
      >
        <div style={{ textAlign: "left", color: "var(--accent)" }}>
          <p style={{ fontSize: 28, marginBottom: 4 }}>
            <Ic.Calendar s={26} c="currentColor" />
          </p>
          <ScreenHeader
            title="Weekly Check-In"
            sub="It's been 7 days. Time to log your progress."
          />
        </div>

        <div className="card" style={{ gap: 14 }}>
          <div className="input-group">
            <label className="input-label">Current Body Weight (lbs)</label>
            <input
              className="num-input"
              type="number"
              inputMode="decimal"
              placeholder={profile.weight}
              value={checkinForm.weight}
              onChange={(e) =>
                setCheckinForm((f) => ({ ...f, weight: e.target.value }))
              }
              style={{ marginTop: 4 }}
            />
          </div>

          <div>
            <label
              className="input-label"
              style={{ display: "block", marginBottom: 8 }}
            >
              Progress Photo
            </label>
            {checkinForm.photo ? (
              <img
                src={checkinForm.photo}
                alt="progress"
                style={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "cover",
                  borderRadius: 12,
                  border: "2px solid var(--accent)",
                }}
              />
            ) : (
              <div
                style={{
                  height: 120,
                  background: "var(--surface2)",
                  borderRadius: 12,
                  border: "2px dashed var(--border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--muted)",
                  fontSize: 13,
                }}
              >
                No photo yet
              </div>
            )}
            <label style={{ display: "block", marginTop: 8 }}>
              <div
                className="btn-add-exercise"
                style={{ textAlign: "center", cursor: "pointer" }}
              >
                {checkinForm.photo ? "Retake" : "Take / Choose Photo"}
              </div>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCheckinPhoto}
                style={{ display: "none" }}
              />
            </label>
          </div>
        </div>

        <div className="screen-action">
          {checkinForm.weight !== "" &&
            !isValidWeight(checkinForm.weight) && (
              <p
                style={{
                  fontSize: 11,
                  color: "var(--danger)",
                  lineHeight: 1.5,
                }}
              >
                Enter a valid weight between 70 and 700 lb, or leave it blank to
                skip.
              </p>
            )}
          <button
            className="btn-primary"
            // A blank weight is allowed — the check-in still records the photo
            // and the date. An invalid one is blocked so it cannot reach the
            // profile write.
            disabled={
              checkinForm.weight !== "" && !isValidWeight(checkinForm.weight)
            }
            onClick={() => {
              if (
                checkinForm.weight !== "" &&
                !isValidWeight(checkinForm.weight)
              )
                return;
              onSubmitCheckin(checkinForm);
              setShowCheckin(false);
            }}
          >
            Save Check-In
          </button>
          <button className="btn-ghost" onClick={() => setShowCheckin(false)}>
            Remind Me Later
          </button>
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Good night";
  };

  const todayStr = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  const trainedToday =
    sessions.some((s) => s.date === new Date().toLocaleDateString()) ||
    (cardioLogs || []).some((c) => c.date === new Date().toLocaleDateString());

  return (
    <div
      className="screen"
      style={{ padding: "0 0 100px", display: "flex", flexDirection: "column" }}
    >
      {/* ── RESUME BANNER ── */}
      {savedSession && (
        <div
          style={{
            background: "var(--surface2)",
            borderBottom: "2px solid var(--accent)",
            borderLeft: "4px solid var(--accent)",
            padding: "14px 16px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--accent)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                ⚡ Unfinished Workout
              </p>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--text)",
                  marginTop: 2,
                }}
              >
                {savedSession.session?.muscleGroup || "Workout"}
              </p>
              <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                {savedSession.session?.exercises?.reduce(
                  (a, ex) => a + ex.sets.length,
                  0
                ) || 0}{" "}
                sets ·{" "}
                {new Date(savedSession.savedAt).toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button
                onClick={onDiscardSaved}
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  color: "var(--muted)",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Discard
              </button>
              <button
                onClick={onResumeSession}
                style={{
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: "var(--accent)",
                  border: "none",
                  color: "var(--bg)",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Resume →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FULL SCREEN LAST SESSION OVERLAY ── */}
      {showLastSessionFull && last && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 300,
            background: "var(--bg)",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LastSessionSummary
            session={last}
            onClose={() => setShowLastSessionFull(false)}
            profile={profile}
            settings={settings}
          />
        </div>
      )}

      {/* ── FULL SCREEN CARD OVERLAY ── */}
      {expandedCard && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 290,
            background: "var(--bg)",
            display: "flex",
            flexDirection: "column",
            animation: "slideInRight .25s cubic-bezier(.32,.72,0,1)",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "52px 16px 12px",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "var(--surface)",
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => setExpandedCard(null)}
              style={{
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 700,
                color: "var(--text)",
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              {expandedCard.label}
            </p>
          </div>
          {/* Full card content */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px" }}>
            <ExpandedCardContent
              cardId={expandedCard.id}
              last={last}
              thisWeek={thisWeek}
              thisWeekSets={thisWeekSets}
              lastWeek={lastWeek}
              recoveryStatus={recoveryStatus}
              sessions={sessions}
              profile={profile}
              cardioLogs={cardioLogs}
              onViewLastSession={() => {
                setExpandedCard(null);
                setShowLastSessionFull(true);
              }}
            />
          </div>
          <style>{CSS}</style>
        </div>
      )}

      {/* ── HERO HEADER ── */}
      <div
        style={{
          background:
            "linear-gradient(160deg, var(--bg) 0%, var(--surface2) 60%, var(--surface) 100%)",
          padding: "48px 20px 28px",
          position: "relative",
          overflow: "hidden",
          borderRadius: "0 0 28px 28px",
          boxShadow: darkMode
            ? "0 8px 32px rgba(0,0,0,.25)"
            : "0 8px 32px rgba(0,0,0,.08)",
          flexShrink: 0,
        }}
      >
        {/* Ambient blobs */}
        <div
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
            filter: "blur(50px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -20,
            left: -20,
            width: 140,
            height: 140,
            borderRadius: "50%",
            background:
              "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.05)",
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />

        {/* Theme toggle */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            marginBottom: 20,
          }}
        >
          <button
            onClick={onToggleTheme}
            style={{
              background: darkMode
                ? "rgba(255,255,255,.08)"
                : "rgba(0,0,0,.06)",
              border: darkMode
                ? "1px solid rgba(255,255,255,.12)"
                : "1px solid rgba(0,0,0,.1)",
              borderRadius: 12,
              padding: "8px 12px",
              fontSize: 18,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            {darkMode ? "Dark" : "Light"}
          </button>
        </div>

        {/* Greeting + name */}
        <p
          style={{
            fontSize: 13,
            color: "var(--muted)",
            fontWeight: 400,
            letterSpacing: 0.5,
            marginBottom: 4,
          }}
        >
          {getGreeting()},
        </p>
        <p
          style={{
            fontSize: 40,
            fontWeight: 700,
            color: "var(--text)",
            letterSpacing: -0.5,
            lineHeight: 1.05,
            marginBottom: 10,
          }}
        >
          {profile.name}
        </p>

        {/* Date + weight row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <p
            style={{
              fontSize: 13,
              color: "var(--muted)",
              fontWeight: 400,
            }}
          >
            {todayStr}
          </p>
          {profile.weight && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: darkMode
                  ? "rgba(255,255,255,.06)"
                  : "rgba(0,0,0,.05)",
                borderRadius: 20,
                padding: "4px 12px",
              }}
            >
              <span style={{ fontSize: 11, color: "var(--muted)" }}>
                <Ic.Scale s={20} c="currentColor" />
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--text)",
                }}
              >
                {profile.weight} lbs
              </span>
            </div>
          )}
        </div>

        {/* Streak banner inside hero */}
        {currentStreak >= 3 && (
          <div
            style={{
              marginTop: 14,
              background: "rgba(255,140,0,.15)",
              border: "1px solid rgba(255,140,0,.35)",
              borderRadius: 12,
              padding: "8px 14px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            
            <div>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--warn)",
                  lineHeight: 1,
                }}
              >
                {currentStreak} Day Streak
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 2,
                }}
              >
                {currentStreak >= 30
                  ? "Elite consistency."
                  : currentStreak >= 14
                  ? "Two weeks strong."
                  : currentStreak >= 7
                  ? "One full week."
                  : "Don't break the chain!"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── MAIN ACTIONS ── */}
      <div
        style={{
          padding: "20px 16px 0",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {/* Primary CTA — Training */}
        {!picking ? (
          <button
            onClick={() => setPicking(true)}
            style={{
              background: "var(--accent)",
              border: "none",
              borderRadius: 16,
              padding: "18px 20px",
              fontSize: 18,
              fontWeight: 700,
              color: "var(--bg)",
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
              letterSpacing: 0.3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 4px 24px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35)",
              animation: "workoutPulse 2.5s ease-in-out infinite",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 22 }}>
                <Ic.Barbell s={22} c="currentColor" />
              </span>
              <div style={{ textAlign: "left" }}>
                <p style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>
                  Training
                </p>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 400,
                    opacity: 0.6,
                    marginTop: 2,
                  }}
                >
                  Start or build a workout
                </p>
              </div>
            </div>
            <span style={{ fontSize: 18, opacity: 0.7 }}>›</span>
          </button>
        ) : (
          /* Muscle group picker — inline */
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "14px 16px 10px",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "var(--text)",
                  }}
                >
                  What are you training?
                </p>
                <p
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}
                >
                  Select a muscle group
                </p>
              </div>
              <button
                onClick={() => setPicking(false)}
                style={{
                  background: "var(--surface2)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "5px 10px",
                  fontSize: 11,
                  color: "var(--muted)",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
            <div style={{ padding: "8px 8px 8px" }}>
              {MUSCLE_GROUPS.map((g) => {
                const r = recoveryStatus?.[g];
                const color = GROUP_COLORS[g] || "var(--accent)";
                const statusColor = !r?.lastDate
                  ? "var(--muted)"
                  : r.status === "ready"
                  ? "var(--success)"
                  : r.status === "almost"
                  ? "var(--accent)"
                  : "var(--danger)";
                const statusText = !r?.lastDate
                  ? "Never trained"
                  : r.status === "ready"
                  ? "✓ Ready"
                  : `${r.hoursRemaining}h to go`;
                return (
                  <button
                    key={g}
                    onClick={() => {
                      setPicking(false);
                      onMuscleSelect(g);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0,
                      width: "100%",
                      background: "transparent",
                      border: "none",
                      borderBottom: "1px solid var(--border)",
                      cursor: "pointer",
                      padding: "10px 8px",
                      transition: "background .1s",
                    }}
                    onTouchStart={(e) =>
                      (e.currentTarget.style.background = "var(--surface2)")
                    }
                    onTouchEnd={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <MuscleIcon group={g} size={32} color={color} />
                    <div style={{ flex: 1, textAlign: "left", marginLeft: 12 }}>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 15,
                          color: "var(--text)",
                        }}
                      >
                        {g}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        color: statusColor,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {statusText}
                    </span>
                  </button>
                );
              })}

              {/* Build Workout option */}
              <button
                onClick={() => {
                  setPicking(false);
                  onShockWorkout();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "12px 8px",
                  marginTop: 2,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: "rgba(255,159,67,.12)",
                    border: "1.5px solid rgba(255,159,67,.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 15,
                  }}
                >
                  <Ic.Dice s={22} c="currentColor" />
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: "var(--text)",
                    }}
                  >
                    Build Workout
                  </span>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      marginTop: 1,
                    }}
                  >
                    AI picks exercises for you
                  </p>
                </div>
                <span style={{ fontSize: 15, color: "var(--muted)" }}>›</span>
              </button>
            </div>
          </div>
        )}

        {/* Secondary CTAs — Cardio + Rest */}
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
        >
          <button
            onClick={onStartCardio}
            style={{
              background: "var(--surface2)",
              border: "1.5px solid var(--border)",
              borderRadius: 14,
              padding: "16px 12px",
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
            <span style={{ fontSize: 22 }}>
              <Ic.Run s={22} c="currentColor" />
            </span>
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              Cardio
            </p>
            <p style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.3 }}>
              Log a cardio session
            </p>
          </button>

          <button
            onClick={trainedToday ? undefined : onRestDay}
            disabled={trainedToday}
            style={{
              background: "var(--surface2)",
              border: "1.5px solid var(--border)",
              borderRadius: 14,
              padding: "16px 12px",
              cursor: trainedToday ? "not-allowed" : "pointer",
              fontFamily: "'DM Sans',sans-serif",
              opacity: trainedToday ? 0.5 : 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
            <span style={{ fontSize: 22 }}>
              <Ic.Moon s={22} c="currentColor" />
            </span>
            <p
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: trainedToday ? "var(--muted)" : "var(--purple)",
              }}
            >
              Rest Day
            </p>
            <p style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.3 }}>
              {trainedToday ? "Already trained today" : "Log recovery day"}
            </p>
          </button>
        </div>
      </div>

      {/* ── STATS CAROUSEL ── */}
      <div style={{ padding: "20px 16px 0" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--muted)",
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            Your Stats
          </p>
          <button
            onClick={onHistory}
            style={{
              fontSize: 11,
              color: "var(--accent)",
              fontWeight: 700,
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            Full History →
          </button>
        </div>
        <HomeCarousel
          last={last}
          settings={settings}
          thisWeek={thisWeek}
          lastWeek={lastWeek}
          recoveryStatus={recoveryStatus}
          sessions={sessions}
          profile={profile}
          cardioLogs={cardioLogs}
          onViewLastSession={() => setShowLastSessionFull(true)}
          onCardTap={(cardId, label) => setExpandedCard({ id: cardId, label })}
        />
      </div>

      <style>{`
        @keyframes workoutPulse {
          0%, 100% { box-shadow: 0 4px 24px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35); }
          50%       { box-shadow: 0 4px 36px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.6), 0 0 0 6px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08); }
        }
      `}</style>
      <style>{CSS}</style>
    </div>
  );
}

function TimerModePicker({ muscleGroup, settings, onSelect, onBack }) {
  const showAuto = settings?.autoTimer !== false;
  const showRest = settings?.manualRest !== false;

  useEffect(() => {
    if (showAuto && !showRest) onSelect("auto");
    if (!showAuto && showRest) onSelect("manual");
    if (!showAuto && !showRest) onSelect("auto"); // fallback
  }, []);

  return (
    <div
      className="screen"
      style={{
                display: "flex",
        flexDirection: "column",
        gap: 24,
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <div>
        <p
          style={{
            fontSize: 13,
            color: "var(--accent)",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 2,
          }}
        >
          Starting Workout
        </p>
        <p style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>
          {muscleGroup}
        </p>
        <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 8 }}>
          How do you want to time this session?
        </p>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          width: "100%",
        }}
      >
        {showAuto && (
          <button
            onClick={() => onSelect("auto")}
            style={{
              background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)",
              border: "2px solid var(--accent)",
              borderRadius: "var(--r-lg)",
              padding: "22px 20px",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <p style={{ fontSize: 18, marginBottom: 6 }}>
              <Ic.Timer s={22} c="currentColor" />
            </p>
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              Session Timer
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--muted)",
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              Tracks how long your whole workout takes. Tap to pause. Rest
              between sets is up to you.
            </p>
          </button>
        )}

        {showRest && (
          <button
            onClick={() => onSelect("manual")}
            style={{
              background: "var(--surface)",
              border: "2px solid var(--border)",
              borderRadius: "var(--r-lg)",
              padding: "22px 20px",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <p style={{ fontSize: 18, marginBottom: 6 }}>
              <Ic.Pause s={22} c="currentColor" />
            </p>
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              Rest Timer
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--muted)",
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              Automatically starts a countdown after each set, based on your
              goal and how hard the set was. No tapping required.
            </p>
          </button>
        )}
      </div>

      <button className="btn-ghost" style={{ width: "100%" }} onClick={onBack}>
        ← Back
      </button>
      <style>{CSS}</style>
    </div>
  );
}

function WorkoutSummary({ summary, profile, settings, sessions, onDone }) {
  const [calories, setCalories] = useState("");
  const [bpm, setBpm] = useState("");
  const [phase, setPhase] = useState("celebrate"); // celebrate | stats | recap
  const [showShareCard, setShowShareCard] = useState(false);
  const [confettiDone, setConfettiDone] = useState(false);

  const { session, durationSecs } = summary;
  const mins = Math.floor(durationSecs / 60);
  const secs = durationSecs % 60;
  const timeRange = fmtSessionTimeRange(
    session,
    settings?.timeFormat || "12hr"
  );
  const color =
    GROUP_COLORS[session.muscleGroup?.split(" + ")[0]] || "var(--accent)";

  const totalSets = session.exercises.reduce((a, ex) => a + ex.sets.length, 0);
  const totalReps = session.exercises.reduce(
    (a, ex) => a + ex.sets.reduce((b, s) => b + s.reps, 0),
    0
  );
  const totalVol = session.exercises.reduce(
    (a, ex) => a + ex.sets.reduce((b, s) => b + (s.weight || 0) * s.reps, 0),
    0
  );

  // ── PR DETECTION ──
  // Compare today's best set per exercise against all previous sessions
  const newPRs = session.exercises.flatMap((ex) => {
    if (ex.sets.length === 0) return [];
    const todayBest = ex.sets.reduce(
      (a, s) => {
        const rm = Math.round((s.weight || 0) * (1 + s.reps / 30));
        return rm > a.rm ? { weight: s.weight, reps: s.reps, rm } : a;
      },
      { weight: 0, reps: 0, rm: 0 }
    );
    if (todayBest.weight === 0) return [];

    // Find previous best from past sessions (exclude today)
    const prevSessions = sessions.filter((s) => s.id !== session.id);
    let prevBest = 0;
    prevSessions.forEach((s) => {
      const prevEx = s.exercises.find((e) => e.key === ex.key);
      if (!prevEx) return;
      prevEx.sets.forEach((set) => {
        const rm = Math.round((set.weight || 0) * (1 + set.reps / 30));
        if (rm > prevBest) prevBest = rm;
      });
    });

    if (todayBest.rm > prevBest && prevBest > 0) {
      return [
        {
          name: ex.name.split(" (")[0].split(" —")[0],
          weight: todayBest.weight,
          reps: todayBest.reps,
          e1RM: todayBest.rm,
          prevBest,
        },
      ];
    }
    return [];
  });

  // ── MOTIVATIONAL LINE ──
  const getMotivation = () => {
    if (newPRs.length > 0)
      return `${newPRs.length} new personal record${
        newPRs.length > 1 ? "s" : ""
      }. That's what overloading looks like.`;
    if (mins >= 60)
      return `${mins} minutes of work. Most people never even started.`;
    if (totalSets >= 20)
      return `${totalSets} sets. High volume session — recovery is key tonight.`;
    if (totalVol >= 10000)
      return `${Math.round(
        totalVol / 1000
      )}k lbs moved. Volume like that builds muscle.`;
    return "Another session in the books. Consistency beats intensity every time.";
  };

  // ── CONFETTI ──
  const Confetti = () => {
    const pieces = Array.from({ length: 28 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.8,
      dur: 1.8 + Math.random() * 1.2,
      color: [
        "var(--accent)",
        "var(--success)",
        "var(--info)",
        "var(--warn)",
        "var(--purple)",
        "var(--orange)",
      ][i % 6],
      size: 6 + Math.random() * 8,
      rotate: Math.random() * 360,
    }));
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 300,
          pointerEvents: "none",
          overflow: "hidden",
          zIndex: 10,
        }}
      >
        {pieces.map((p) => (
          <div
            key={p.id}
            style={{
              position: "absolute",
              left: `${p.x}%`,
              top: -20,
              width: p.size,
              height: p.size * (Math.random() > 0.5 ? 1 : 2.5),
              background: p.color,
              borderRadius: Math.random() > 0.5 ? "50%" : "2px",
              transform: `rotate(${p.rotate}deg)`,
              animation: `confettiFall ${p.dur}s ${p.delay}s ease-in forwards`,
              opacity: 0.9,
            }}
          />
        ))}
      </div>
    );
  };

  // ── PHASE: CELEBRATE ──
  if (phase === "celebrate") {
    return (
      <div
        className="screen"
        style={{
          padding: 0,
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(160deg, var(--bg) 0%, #111820 50%, #0d1a10 100%)`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!confettiDone && <Confetti />}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 20px 100px",
            gap: 0,
            textAlign: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* Big emoji burst */}
          <div
            style={{
              fontSize: 40,
              lineHeight: 1,
              marginBottom: 20,
              animation: "celebBounce .6s cubic-bezier(.36,.07,.19,.97) both",
            }}
          >
            
          </div>

          {/* Headline */}
          <p
            style={{
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: 40,
              letterSpacing: 2,
              color: "var(--text)",
              lineHeight: 0.95,
              marginBottom: 8,
            }}
          >
            WORKOUT
            <br />
            <span style={{ color: "var(--accent)" }}>DONE</span>
          </p>

          {/* Muscle group + time */}
          <p
            style={{
              fontSize: 13,
              color: "var(--muted)",
              marginBottom: 28,
              letterSpacing: 0.5,
            }}
          >
            {session.muscleGroup} · {mins}m {secs > 0 ? `${secs}s` : ""}
          </p>

          {/* PR callout */}
          {newPRs.length > 0 && (
            <div
              style={{
                background: "rgba(255,215,0,.12)",
                border: "1.5px solid rgba(255,215,0,.4)",
                borderRadius: 16,
                padding: "14px 20px",
                marginBottom: 24,
                width: "100%",
                animation: "fadeSlideUp .5s .3s both",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--warn)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  marginBottom: 8,
                }}
              >
                New Personal Record{newPRs.length > 1 ? "s" : ""}
              </p>
              {newPRs.map((pr, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: i > 0 ? 8 : 0,
                    paddingTop: i > 0 ? 8 : 0,
                    borderTop: i > 0 ? "1px solid rgba(255,215,0,.2)" : "none",
                  }}
                >
                  <p
                    style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}
                  >
                    {pr.name}
                  </p>
                  <div style={{ textAlign: "right" }}>
                    <p
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "var(--warn)",
                      }}
                    >
                      {pr.weight} lbs × {pr.reps}
                    </p>
                    <p style={{ fontSize: 11, color: "var(--warn)" }}>
                      est. 1RM {pr.e1RM} lbs (+{pr.e1RM - pr.prevBest} lbs)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 3 key stats */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 10,
              width: "100%",
              marginBottom: 24,
              animation: "fadeSlideUp .5s .15s both",
            }}
          >
            {[
              { val: totalSets, label: "Sets", color: "var(--text)" },
              { val: totalReps, label: "Reps", color: "var(--info)" },
              {
                val:
                  totalVol >= 1000
                    ? `${(totalVol / 1000).toFixed(1)}k`
                    : totalVol,
                label: "Volume",
                color: "var(--success)",
              },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  background: "rgba(255,255,255,.06)",
                  border: "1px solid rgba(255,255,255,.1)",
                  borderRadius: 14,
                  padding: "14px 8px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontFamily: "'Bebas Neue',sans-serif",
                    fontSize: 28,
                    color: s.color,
                    lineHeight: 1,
                  }}
                >
                  {s.val}
                </p>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    marginTop: 4,
                  }}
                >
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          {/* Motivation line */}
          <p
            style={{
              fontSize: 13,
              color: "var(--muted)",
              lineHeight: 1.6,
              marginBottom: 32,
              fontStyle: "italic",
              animation: "fadeSlideUp .5s .25s both",
            }}
          >
            "{getMotivation()}"
          </p>

          {/* Duration stat */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginBottom: 32,
              background: "rgba(255,255,255,.04)",
              borderRadius: 12,
              padding: "10px 20px",
              animation: "fadeSlideUp .5s .35s both",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontFamily: "'Bebas Neue',sans-serif",
                  fontSize: 28,
                  color: "var(--text)",
                  lineHeight: 1,
                }}
              >
                {mins}:{String(secs).padStart(2, "0")}
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                Duration
              </p>
            </div>
            {timeRange && (
              <>
                <div
                  style={{
                    width: 1,
                    height: 32,
                    background: "rgba(255,255,255,.1)",
                  }}
                />
                <div style={{ textAlign: "center" }}>
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                      lineHeight: 1,
                    }}
                  >
                    {timeRange}
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                      marginTop: 4,
                    }}
                  >
                    Time
                  </p>
                </div>
              </>
            )}
          </div>

          {/* CTA buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              width: "100%",
              animation: "fadeSlideUp .5s .4s both",
            }}
          >
            <button
              onClick={() => setPhase("stats")}
              style={{
                background: "var(--accent)",
                border: "none",
                borderRadius: 14,
                padding: "15px",
                fontSize: 15,
                fontWeight: 700,
                color: "var(--bg)",
                cursor: "pointer",
                letterSpacing: 0.3,
              }}
            >
              See Full Summary →
            </button>
            <button
              onClick={() => onDone("", "")}
              style={{
                background: "rgba(255,255,255,.06)",
                border: "1px solid rgba(255,255,255,.12)",
                borderRadius: 14,
                padding: "13px",
                fontSize: 13,
                fontWeight: 700,
                color: "var(--text)",
                cursor: "pointer",
              }}
            >
              Done — Back to Home
            </button>{" "}
          </div>
        </div>

        <style>{`
          @keyframes confettiFall {
            0%   { transform: translateY(0) rotate(0deg); opacity:.9; }
            100% { transform: translateY(320px) rotate(720deg); opacity:0; }
          }
          @keyframes celebBounce {
            0%   { transform: scale(0) rotate(-10deg); opacity:0; }
            60%  { transform: scale(1.2) rotate(5deg); }
            100% { transform: scale(1) rotate(0deg); opacity:1; }
          }
          @keyframes fadeSlideUp {
            from { opacity:0; transform:translateY(16px); }
            to   { opacity:1; transform:translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ── PHASE: STATS (calories / HR input) ──
  if (phase === "stats") {
    return (
      <div
        className="screen"
        style={{
                    display: "flex",
          flexDirection: "column",
          gap: 16,
          overflowY: "auto",
        }}
      >
        <button
          className="btn-ghost sm"
          style={{ alignSelf: "flex-start" }}
          onClick={() => setPhase("celebrate")}
        >
          ← Back
        </button>

        <ScreenHeader
          title="Log Your Stats"
          sub="Optional — helps calibrate nutrition and gives you accurate feedback."
        />

        <div className="card" style={{ gap: 14 }}>
          {[
            {
              label: "Calories Burned",
              key: "cal",
              val: calories,
              set: setCalories,
              unit: "kcal",
              hint: "Check your watch or heart rate monitor",
            },
            {
              label: "Average Heart Rate",
              key: "bpm",
              val: bpm,
              set: setBpm,
              unit: "BPM",
              hint: "From your fitness tracker",
            },
          ].map((f) => (
            <div key={f.key} className="input-group">
              <label className="input-label">
                {f.label}{" "}
                <span style={{ color: "var(--muted)", fontWeight: 400 }}>
                  (optional)
                </span>
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 4,
                }}
              >
                <input
                  className="num-input"
                  type="number"
                  inputMode="numeric"
                  placeholder={f.key === "cal" ? "e.g. 420" : "e.g. 138"}
                  value={f.val}
                  onChange={(e) => f.set(e.target.value)}
                  style={{ flex: 1 }}
                />
                <span
                  style={{ color: "var(--muted)", fontSize: 13, flexShrink: 0 }}
                >
                  {f.unit}
                </span>
              </div>
              <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                {f.hint}
              </p>
            </div>
          ))}
        </div>

        {/* PO results */}
        {(() => {
          const poExercises = session.exercises.filter(
            (ex) => ex.poTarget && ex.sets.length > 0
          );
          if (poExercises.length === 0) return null;
          const results = poExercises.map((ex) => {
            const best = ex.sets.reduce(
              (a, s) =>
                s.weight > a.weight ||
                (s.weight === a.weight && s.reps > a.reps)
                  ? s
                  : a,
              ex.sets[0]
            );
            const target = ex.poTarget;
            const exceeded =
              best.weight > target.weight ||
              (best.weight === target.weight && best.reps > target.reps);
            const hit =
              best.weight >= target.weight && best.reps >= target.reps;
            const status = exceeded ? "exceeded" : hit ? "hit" : "missed";
            return { ex, status, best, target };
          });
          const hitCount = results.filter((r) => r.status !== "missed").length;
          return (
            <div
              style={{
                background: "var(--surface)",
                border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
                borderRadius: 16,
                padding: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 12,
                }}
              >
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: "var(--text)",
                  }}
                >
                  PO Results
                </p>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color:
                      hitCount === results.length
                        ? "var(--success)"
                        : hitCount >= results.length / 2
                        ? "var(--accent)"
                        : "var(--muted)",
                  }}
                >
                  {hitCount}/{results.length} hit
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {results.map((r, i) => {
                  const icon =
                    r.status === "exceeded"
                      ? "▲"
                      : r.status === "hit"
                      ? "✓"
                      : "✕";
                  const tc =
                    r.status === "exceeded"
                      ? "var(--warn)"
                      : r.status === "hit"
                      ? "var(--success)"
                      : "var(--danger)";
                  return (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: `var(--surface2)`,
                        borderRadius: 10,
                        padding: "10px 12px",
                        borderLeft: `3px solid ${tc}`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "var(--text)",
                        }}
                      >
                        {icon} {r.ex.name.split(" (")[0].split(" —")[0]}
                      </p>
                      <div style={{ textAlign: "right" }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: tc }}>
                          {r.best.weight}lbs × {r.best.reps}
                        </p>
                        <p style={{ fontSize: 11, color: "var(--muted)" }}>
                          target {r.target.weight}×{r.target.reps}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

{(() => {
          // MEASUREMENT ONLY — deliberately no calorie or carb language here.
          // The score is shown so it can be sanity-checked against how the
          // session actually felt, before anything downstream consumes it.
          const demand = calculateTrainingDemand(session);
          if (demand.workingSets === 0) return null;
          return (
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: "14px 16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 8,
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--muted)",
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                  }}
                >
                  Training Demand
                </p>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--accent)",
                  }}
                >
                  {demand.level}
                </p>
              </div>
              <p
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: "var(--text)",
                  lineHeight: 1,
                }}
              >
                {demand.score}
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 400,
                    color: "var(--muted)",
                    marginLeft: 4,
                  }}
                >
                  / 100
                </span>
              </p>
              <div
                style={{
                  height: 5,
                  background: "var(--surface2)",
                  borderRadius: 99,
                  overflow: "hidden",
                  margin: "10px 0 8px",
                }}
              >
                <div
                  style={{
                    width: `${demand.score}%`,
                    height: "100%",
                    background: "var(--accent)",
                    borderRadius: 99,
                  }}
                />
              </div>
              <p style={{ fontSize: 11, color: "var(--muted)" }}>
                {demand.workingSets} working set
                {demand.workingSets !== 1 ? "s" : ""} · {demand.totalReps} reps
                · {demand.compoundSets} compound
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 8,
                  lineHeight: 1.5,
                }}
              >
                An estimate of how much work this session represented, from
                sets, reps and the muscles involved.
              </p>
            </div>
          );
        })()}

        {/* Session recap */}
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: 16,
          }}
        >
          <p
            style={{
              fontWeight: 700,
              fontSize: 13,
              marginBottom: 10,
              color: "var(--text)",
            }}
          >
            Session Recap
          </p>
          {session.exercises.map((ex, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <p
                style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}
              >
                {ex.name}
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 5,
                  marginTop: 4,
                }}
              >
                {ex.sets.map((s, si) => (
                  <span key={si} className="chip">
                    {s.type === "super" ? "↕ " : ""}
                    {s.weight}×{s.reps}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Share card button */}
        <button
          onClick={() => setShowShareCard(true)}
          style={{
            background: "var(--surface2)",
            border: `1px solid var(--border)`,
            borderRadius: 14,
            padding: "13px 16px",
            cursor: "pointer",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ textAlign: "left" }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
              <Ic.Camera s={22} c="currentColor" /> Share Card
            </p>
            <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              Screenshot & share your workout
            </p>
          </div>
          <span style={{ fontSize: 18, color: "var(--accent)" }}>→</span>
        </button>

        <div className="screen-action">
          <button className="btn-primary" onClick={() => onDone(calories, bpm)}>
            Done — Back to Home
          </button>
        </div>

        {/* Share card overlay */}
        {showShareCard && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 999,
              background: "var(--bg)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                background: `linear-gradient(160deg, var(--bg) 0%, var(--surface2) 50%, var(--surface) 100%)`,
                position: "relative",
              }}
            >
              <button
                onClick={() => setShowShareCard(false)}
                style={{
                  position: "sticky",
                  top: 10,
                  float: "right",
                  marginRight: 12,
                  marginTop: 10,
                  background: "rgba(255,255,255,.15)",
                  border: "1px solid rgba(255,255,255,.2)",
                  borderRadius: "50%",
                  width: 34,
                  height: 34,
                  color: "var(--text)",
                  fontSize: 15,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
              <div
                style={{
                  padding: "10px 18px 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div style={{ marginTop: 50 }}>
                  <p
                    style={{
                      fontFamily: "'Bebas Neue',sans-serif",
                      fontSize: 40,
                      letterSpacing: 2,
                      color: "var(--text)",
                      lineHeight: 0.95,
                    }}
                  >
                    {session.muscleGroup.toUpperCase()}
                    <br />
                    <span style={{ color: "var(--accent)" }}>COMPLETE</span>
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      marginTop: 4,
                    }}
                  >
                    {new Date().toLocaleDateString([], {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 6,
                  }}
                >
                  {[
                    {
                      val: `${mins}:${String(secs).padStart(2, "0")}`,
                      label: "Duration",
                      c: "var(--accent)",
                    },
                    { val: totalSets, label: "Sets", c: "var(--info)" },
                    {
                      val:
                        totalVol >= 1000
                          ? `${(totalVol / 1000).toFixed(1)}k`
                          : totalVol,
                      label: "Volume",
                      c: "var(--success)",
                    },
                  ].map((s, i) => (
                    <div
                      key={i}
                      style={{
                        background: "rgba(255,255,255,.05)",
                        borderRadius: 10,
                        padding: "8px 6px",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontFamily: "'Bebas Neue',sans-serif",
                          fontSize: 22,
                          color: s.c,
                          lineHeight: 1,
                        }}
                      >
                        {s.val}
                      </p>
                      <p
                        style={{
                          fontSize: 11,
                          color: "var(--muted)",
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          marginTop: 2,
                        }}
                      >
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
                {newPRs.length > 0 && (
                  <div
                    style={{
                      background: "rgba(255,215,0,.1)",
                      border: "1px solid rgba(255,215,0,.3)",
                      borderRadius: 10,
                      padding: "10px 12px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--warn)",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        marginBottom: 6,
                      }}
                    >
                      New PRs
                    </p>
                    {newPRs.map((pr, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <p
                          style={{
                            fontSize: 11,
                            color: "var(--text)",
                          }}
                        >
                          {pr.name}
                        </p>
                        <p
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "var(--warn)",
                          }}
                        >
                          {pr.weight}×{pr.reps}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  style={{
                    background: "rgba(255,255,255,.03)",
                    borderRadius: 10,
                    padding: "8px 12px",
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      marginBottom: 5,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Best Sets
                  </p>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "3px 10px",
                    }}
                  >
                    {session.exercises
                      .filter((ex) => ex.sets.length > 0)
                      .slice(0, 8)
                      .map((ex, i) => {
                        const best = ex.sets.reduce(
                          (a, s) =>
                            s.weight * s.reps > a.weight * a.reps ? s : a,
                          ex.sets[0]
                        );
                        return (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <p
                              style={{
                                fontSize: 11,
                                color: "var(--muted)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 100,
                              }}
                            >
                              {ex.name.split(" (")[0].split(" —")[0]}
                            </p>
                            <p
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "var(--text)",
                                flexShrink: 0,
                                marginLeft: 3,
                              }}
                            >
                              {best.weight}×{best.reps}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                </div>
                <div
                  style={{
                    paddingTop: 8,
                    borderTop: "1px solid rgba(255,255,255,.06)",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      letterSpacing: 1,
                    }}
                  >
                    OVERLOAD · PROGRESSIVE OVERLOAD TRACKER
                  </p>
                  <p style={{ fontSize: 11, color: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.333)` }}>
                    overload.app
                  </p>
                </div>
              </div>
            </div>
            <div
              style={{
                background: "var(--bg)",
                padding: "10px 24px 16px",
                textAlign: "center",
                borderTop: "1px solid rgba(255,255,255,.06)",
                flexShrink: 0,
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  color: "var(--muted)",
                  marginBottom: 8,
                }}
              >
                <Ic.Camera s={22} c="currentColor" /> Take a screenshot to save
                & share
              </p>
              <button
                onClick={() => setShowShareCard(false)}
                style={{
                  background: "rgba(255,255,255,.08)",
                  border: "1px solid rgba(255,255,255,.15)",
                  borderRadius: 10,
                  padding: "8px 24px",
                  color: "var(--text)",
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}

        <style>{CSS}</style>
      </div>
    );
  }
}

function SupersetPicker({ exercises, supersetIdx, onAdd, onCancel }) {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedEx, setSelectedEx] = useState(null);
  const primaryEx = exercises[supersetIdx];

  if (selectedGroup && selectedEx) {
    return (
      <div className="picker-modal">
        <p className="picker-title">{selectedEx.name}</p>
        <p className="picker-sub">Choose equipment</p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 4,
          }}
        >
          {selectedEx.equipment.map((eq) => (
            <button
              key={eq}
              className="equip-btn"
              onClick={() =>
                onAdd({
                  name: `${selectedEx.name} (${eq})`,
                  key: `${selectedEx.name}__${eq}`,
                  group: selectedGroup,
                })
              }
            >
              <span className="equip-name">{eq}</span>
              <span className="equip-arrow">→</span>
            </button>
          ))}
        </div>
        <button
          className="btn-ghost"
          style={{ marginTop: 12 }}
          onClick={() => setSelectedEx(null)}
        >
          ← Back
        </button>
      </div>
    );
  }

  if (selectedGroup) {
    return (
      <div className="picker-modal">
        <p className="picker-title">Super Set With...</p>
        <p className="picker-sub">{selectedGroup}</p>
        <div
          style={{
            maxHeight: 360,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 8,
          }}
        >
          {(EXERCISE_LIBRARY[selectedGroup] || []).map((ex, i) => (
            <button
              key={i}
              className="ex-pick-btn"
              onClick={() => setSelectedEx(ex)}
            >
              <span className="ex-pick-name">{ex.name}</span>
              <div className="ex-pick-equip"></div>
            </button>
          ))}
        </div>
        <button
          className="btn-ghost"
          style={{ marginTop: 12 }}
          onClick={() => setSelectedGroup(null)}
        >
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="picker-modal">
      <p className="picker-title">Super Set With...</p>
      <p className="picker-sub">
        Pairing with: {primaryEx?.name?.split(" (")[0]}
      </p>
      <p style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>
        Choose a muscle group
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {MUSCLE_GROUPS.map((g) => {
          const color = GROUP_COLORS[g] || "var(--accent)";
          return (
            <button
              key={g}
              className="equip-btn"
              onClick={() => setSelectedGroup(g)}
            >
              <MuscleIcon group={g} size={22} color={color} />
              <span className="equip-name">{g}</span>
              <span className="equip-arrow">→</span>
            </button>
          );
        })}
      </div>
      <button
        className="btn-ghost"
        style={{ marginTop: 12 }}
        onClick={onCancel}
      >
        Cancel
      </button>
    </div>
  );
}

function ExercisePicker({ muscleGroup, existingKeys, onAdd, onCancel }) {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedGrip, setSelectedGrip] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef(null);

  const exercises = EXERCISE_LIBRARY[muscleGroup] || [];
  // Show ALL exercises and ALL equipment variants — never hide anything.
  // Users can add the same exercise multiple times (different positions, angles, or intentionally).
  const available = exercises;

  const searchResults =
    searchQuery.trim().length > 0
      ? MUSCLE_GROUPS.flatMap((group) =>
          (EXERCISE_LIBRARY[group] || [])
            .filter((ex) =>
              ex.name.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((ex) => ({ ...ex, group }))
        )
      : [];

  const buildExercise = () => {
    const parts = [selectedExercise.name];
    if (selectedPosition) parts.push(selectedPosition);
    if (selectedGrip) parts.push(selectedGrip);
    const label = `${parts.join(" — ")} (${selectedEquipment})`;
    // Include position in key so High Cable and Low Cable are separate exercises
    const keyParts = [selectedExercise.name, selectedEquipment];
    if (selectedPosition) keyParts.push(selectedPosition);
    const key = keyParts.join("__");
    return { name: label, key, sets: [] };
  };

  const grips = selectedExercise
    ? GRIP_OPTIONS[selectedExercise.name] || null
    : null;
  const positions =
    selectedExercise && selectedEquipment === "Cable"
      ? CABLE_POSITIONS[selectedExercise.name] || null
      : null;

  const needsGrip = grips && !selectedGrip;
  const needsPosition = positions && !selectedPosition;

  const afterEquipment = () => {
    if (positions) return; // show position step next
    if (grips) return; // show grip step next
    onAdd(buildExercise());
  };

  if (selectedExercise && selectedEquipment && !needsPosition && !needsGrip) {
    return (
      <div className="picker-modal">
        <p className="picker-title">Confirm Exercise</p>
        <div className="confirm-box">
          <p className="confirm-name">{selectedExercise.name}</p>
          {selectedPosition && (
            <p style={{ fontSize: 13, color: "var(--accent)", marginTop: 4 }}>
              {selectedPosition}
            </p>
          )}
          {selectedGrip && (
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 2 }}>
              {selectedGrip}
            </p>
          )}
          <p className="confirm-equip">{selectedEquipment}</p>
        </div>
        <div className="screen-action">
          <button className="btn-primary" onClick={() => onAdd(buildExercise())}>
            Add to Workout
          </button>
          <button
            className="btn-ghost"
            onClick={() => {
              setSelectedGrip(null);
              setSelectedPosition(null);
            }}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  if (selectedExercise && selectedEquipment && needsGrip && !needsPosition) {
    return (
      <div className="picker-modal">
        <p className="picker-title">{selectedExercise.name}</p>
        <p className="picker-sub">Choose grip</p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 4,
          }}
        >
          {grips.map((g) => (
            <button
              key={g}
              className="equip-btn"
              onClick={() => setSelectedGrip(g)}
            >
              <span className="equip-name">{g}</span>
              <span className="equip-arrow">→</span>
            </button>
          ))}
          <button
            className="equip-btn"
            onClick={() => setSelectedGrip("Standard")}
          >
            <span className="equip-name">Standard (no preference)</span>
            <span className="equip-arrow">→</span>
          </button>
        </div>
        <button
          className="btn-ghost"
          style={{ marginTop: 12 }}
          onClick={() => setSelectedEquipment(null)}
        >
          ← Back
        </button>
      </div>
    );
  }

  if (selectedExercise && selectedEquipment && needsPosition) {
    return (
      <div className="picker-modal">
        <p className="picker-title">{selectedExercise.name}</p>
        <p className="picker-sub">Choose cable position</p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 4,
          }}
        >
          {positions.map((pos) => (
            <button
              key={pos}
              className="equip-btn"
              onClick={() => setSelectedPosition(pos)}
            >
              <span className="equip-name">{pos}</span>
              <span className="equip-arrow">→</span>
            </button>
          ))}
        </div>
        <button
          className="btn-ghost"
          style={{ marginTop: 12 }}
          onClick={() => setSelectedEquipment(null)}
        >
          ← Back
        </button>
      </div>
    );
  }

  if (selectedExercise) {
    const availableEquip = selectedExercise.equipment; // show all equipment always
    return (
      <div className="picker-modal">
        <p className="picker-title">{selectedExercise.name}</p>
        <p className="picker-sub">Choose equipment</p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 4,
          }}
        >
          {availableEquip.map((eq) => (
            <button
              key={eq}
              className="equip-btn"
              onClick={() => {
                setSelectedEquipment(eq);
                const pos =
                  eq === "Cable"
                    ? CABLE_POSITIONS[selectedExercise.name] || null
                    : null;
                const grp = GRIP_OPTIONS[selectedExercise.name] || null;
                if (!pos && !grp) {
                  const label = `${selectedExercise.name} (${eq})`;
                  const key = `${selectedExercise.name}__${eq}`;
                  onAdd({ name: label, key, sets: [] });
                }
              }}
            >
              <span className="equip-name">{eq}</span>
              <span className="equip-arrow">→</span>
            </button>
          ))}
        </div>
        <button
          className="btn-ghost"
          style={{ marginTop: 12 }}
          onClick={() => setSelectedExercise(null)}
        >
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="picker-modal">
      <p className="picker-title">Add Exercise</p>

      <div style={{ position: "relative", marginBottom: 10 }}>
        <span
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 13,
            color: "var(--muted)",
            pointerEvents: "none",
          }}
        >
          <Ic.Target s={15} c="currentColor" />
        </span>
        <input
          ref={searchRef}
          type="text"
          placeholder="Search all exercises..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "100%",
            padding: "10px 10px 10px 32px",
            borderRadius: 10,
            border: "1px solid var(--border)",
            background: "var(--surface2)",
            color: "var(--text)",
            fontSize: 13,
            fontFamily: "'DM Sans',sans-serif",
            boxSizing: "border-box",
            outline: "none",
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              fontSize: 15,
              padding: 0,
            }}
          >
            ✕
          </button>
        )}
      </div>

      {searchQuery.trim().length > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            maxHeight: 360,
            overflowY: "auto",
          }}
        >
          {searchResults.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                color: "var(--muted)",
                padding: "24px 0",
                fontSize: 13,
              }}
            >
              No exercises found for "{searchQuery}"
            </p>
          ) : (
            searchResults.map((ex, i) => (
              <button
                key={i}
                className="ex-pick-btn"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedExercise(ex);
                }}
              >
                <div style={{ textAlign: "left" }}>
                  <span className="ex-pick-name">{ex.name}</span>
                  <p
                    style={{
                      fontSize: 11,
                      color: GROUP_COLORS[ex.group] || "var(--muted)",
                      marginTop: 2,
                    }}
                  >
                    {ex.group}
                  </p>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 4,
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {GRIP_OPTIONS[ex.name] && (
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--accent)",
                        background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)",
                        borderRadius: 4,
                        padding: "1px 5px",
                      }}
                    >
                      grips
                    </span>
                  )}
                  <div className="ex-pick-equip"></div>
                </div>
              </button>
            ))
          )}
        </div>
      ) : (
        <>
          <p className="picker-sub">{muscleGroup}</p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginTop: 4,
              maxHeight: 360,
              overflowY: "auto",
            }}
          >
            {available.map((ex, i) => (
              <button
                key={i}
                className="ex-pick-btn"
                onClick={() => setSelectedExercise(ex)}
              >
                <span className="ex-pick-name">{ex.name}</span>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  {GRIP_OPTIONS[ex.name] && (
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--accent)",
                        background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)",
                        borderRadius: 4,
                        padding: "1px 5px",
                      }}
                    >
                      grips
                    </span>
                  )}
                  <div className="ex-pick-equip"></div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
      <button
        className="btn-ghost"
        style={{ marginTop: 12 }}
        onClick={onCancel}
      >
        Cancel
      </button>
    </div>
  );
}

function StepperInput({
  label,
  value,
  onChange,
  step = 1,
  min = 0,
  max = 999,
  placeholder = "0",
  unit = "",
  accent = false,
  filled = false,
}) {
  const numVal = parseFloat(value) || 0;
  const isEmpty = value === "" || value === undefined || value === null;

  const decrement = () => {
    const next = Math.max(min, parseFloat((numVal - step).toFixed(2)));
    onChange(String(next));
  };
  const increment = () => {
    const next = Math.min(max, parseFloat((numVal + step).toFixed(2)));
    onChange(String(next));
  };

  const borderColor = filled
    ? "var(--success)"
    : accent
    ? "var(--accent)"
    : "var(--border)";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        flex: 1,
        minWidth: 0,
      }}
    >
      <label
        style={{
          fontSize: 11,
          color: "var(--muted)",
          fontWeight: 700,
          letterSpacing: 0.3,
        }}
      >
        {label}
      </label>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: "var(--surface2)",
          border: `1.5px solid ${borderColor}`,
          borderRadius: 12,
          overflow: "hidden",
          transition: "border-color .15s",
        }}
      >
        <button
          onClick={decrement}
          disabled={numVal <= min && !isEmpty}
          style={{
            width: 44,
            minHeight: 52,
            background: "none",
            border: "none",
            borderRight: "1px solid var(--border)",
            cursor: "pointer",
            color: "var(--muted)",
            fontSize: 18,
            fontWeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background .1s",
            fontFamily: "'DM Sans',sans-serif",
          }}
          onTouchStart={(e) =>
            (e.currentTarget.style.background = "var(--border)")
          }
          onTouchEnd={(e) => (e.currentTarget.style.background = "none")}
        >
          −
        </button>

        <input
          type="number"
          inputMode="decimal"
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1,
            background: "none",
            border: "none",
            outline: "none",
            textAlign: "center",
            fontSize: 22,
            fontWeight: 700,
            color: isEmpty ? "var(--muted)" : "var(--text)",
            fontFamily: "'DM Sans',sans-serif",
            padding: "0 4px",
            minWidth: 0,
            minHeight: 52,
            lineHeight: "52px",
          }}
        />

        {unit && !isEmpty && (
          <span
            style={{
              fontSize: 11,
              color: "var(--muted)",
              paddingRight: 8,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {unit}
          </span>
        )}

        <button
          onClick={increment}
          style={{
            width: 44,
            minHeight: 52,
            background: "none",
            border: "none",
            borderLeft: "1px solid var(--border)",
            cursor: "pointer",
            color: filled ? "var(--success)" : "var(--accent)",
            fontSize: 18,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background .1s",
            fontFamily: "'DM Sans',sans-serif",
          }}
          onTouchStart={(e) =>
            (e.currentTarget.style.background = "var(--border)")
          }
          onTouchEnd={(e) => (e.currentTarget.style.background = "none")}
        >
          +
        </button>
      </div>
    </div>
  );
}

// ── EXERCISE DEMO IMAGE ──
// Cycles between two still frames (start/end position) to simulate movement.
// Must be a top-level component — not defined inline inside a render — so
// React's reconciler treats it as a stable identity and never remounts it
// mid-cycle, which would reset state and kill the interval.
function ExerciseDemoImage({ frames, name }) {
  const [frame, setFrame] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setFrame((f) => (f === 0 ? 1 : 0)), 900);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      style={{
        borderRadius: 10,
        overflow: "hidden",
        background: "var(--surface2)",
        textAlign: "center",
        position: "relative",
        minHeight: loaded ? 0 : 120,
      }}
    >
      {!loaded && (
        <div style={{ padding: "40px 0" }}>
          <p style={{ color: "var(--muted)", fontSize: 11 }}>Loading demo...</p>
        </div>
      )}
      {frames.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`${name} — frame ${i + 1}`}
          onLoad={() => setLoaded(true)}
          style={{
            width: "100%",
            maxHeight: 280,
            objectFit: "contain",
            display: "block",
            opacity: frame === i && loaded ? 1 : 0,
            position: i === 0 ? "relative" : "absolute",
            top: i === 0 ? "auto" : 0,
            left: 0,
            transition: "opacity .15s ease",
          }}
        />
      ))}
      {loaded && (
        <div
          style={{
            position: "absolute",
            bottom: 6,
            right: 8,
            fontSize: 11,
            color: "var(--muted)",
            fontWeight: 700,
            pointerEvents: "none",
          }}
        >
          Demo
        </div>
      )}
    </div>
  );
}

function LogScreen({
  session,
  setSession,
  profile,
  getLastExercise,
  onFinish,
  onBack,
  timerMode,
  settings,
  sessions,
}) {
  const [tab, setTab] = useState("log");
  const [openIdx, setOpenIdx] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [inputs, setInputs] = useState({});
  const [picking, setPicking] = useState(false);
  const [pickingGroup, setPickingGroup] = useState(null);
  const [addingMuscle, setAddingMuscle] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [swapExIdx, setSwapExIdx] = useState(null); // exIdx being swapped
  const [swapPickedExercise, setSwapPickedExercise] = useState(null); // step 2: chosen exercise, picking equipment
  const [swapUndo, setSwapUndo] = useState(null); // { exIdx, prevEx, prevInputs } for undo
  const [swapUndoVisible, setSwapUndoVisible] = useState(false);

  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState(0); // accumulated paused seconds
  const [startTime] = useState(Date.now());

  useEffect(() => {
    if (timerMode !== "auto") return;
    const t = setInterval(() => {
      if (!paused)
        setElapsed(Math.floor((Date.now() - startTime) / 1000) - pausedAt);
    }, 1000);
    return () => clearInterval(t);
  }, [timerMode, paused, pausedAt, startTime]);

  useEffect(() => {
    if (openIdx === null || openIdx === undefined) return;
    const ex = session?.exercises?.[openIdx];
    if (!ex) return;
    const currentInp = inputs[openIdx] || {};
    if (currentInp.weight !== "" && currentInp.weight !== undefined) return;
    const setsLogged = ex.sets.length;
    const inc = getEquipmentIncrement(ex.key);
    const setTargets = ex.lastSets
      ? buildSetTargets(ex.lastSets, ex.poTarget, inc)
      : [];
    const target = setTargets[setsLogged] || null;
    if (target) {
      setInputs((prev) => ({
        ...prev,
        [openIdx]: {
          ...(prev[openIdx] || {}),
          weight: String(target.weight),
          reps: String(target.reps),
          isTarget: true,
          selectedType: "working",
        },
      }));
      return;
    }
    const lastEx = getLastExercise(ex.key);
    if (!lastEx) return;
    const setData = lastEx.sets[setsLogged] || lastEx.sets[0];
    if (!setData) return;
    setInputs((prev) => ({
      ...prev,
      [openIdx]: {
        ...(prev[openIdx] || {}),
        weight: String(setData.weight),
        reps: String(setData.reps),
        isCarryForward: true,
        selectedType: "working",
      },
    }));
  }, [openIdx]);

  const [pauseStart, setPauseStart] = useState(null);
  const togglePause = () => {
    if (!paused) {
      setPauseStart(Date.now());
      setPaused(true);
    } else {
      const pausedSecs = Math.floor((Date.now() - pauseStart) / 1000);
      setPausedAt((p) => p + pausedSecs);
      setPaused(false);
    }
  };

  const [manualRestTotal, setManualRestTotal] = useState(0);
  const [manualStart] = useState(Date.now());

  const restEndRef = useRef(null);
  const [restTimer, setRestTimer] = useState(null);
  const [restActive, setRestActive] = useState(false);
  const [restActiveExIdx, setRestActiveExIdx] = useState(null); // which exercise card shows inline countdown
  const [flashGreen, setFlashGreen] = useState(false);
  const flashTimerRef = useRef(null);

  useEffect(() => {
    if (!restActive) return;

    const vibrate = (pattern) => {
      if (settings?.vibration !== false && navigator?.vibrate) {
        navigator.vibrate(pattern);
      }
    };

    const tick = () => {
      const remaining = Math.ceil((restEndRef.current - Date.now()) / 1000);
      if (remaining <= 0) {
        setRestTimer(0);
        setRestActive(false);
        setRestActiveExIdx(null);
        setFlashGreen(true);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlashGreen(false), 1200);
        vibrate([200, 100, 200, 100, 400]);
      } else {
        setRestTimer(remaining);
        if (remaining <= 5) {
          setFlashGreen(true);
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => setFlashGreen(false), 300);
          vibrate(80);
        }
      }
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [restActive]);

  const startRest = (secs, exIdx = null) => {
    setManualRestTotal((r) => r + secs);
    restEndRef.current = Date.now() + secs * 1000;
    setRestTimer(secs);
    setRestActive(true);
    setRestActiveExIdx(exIdx);
  };

  const REST_OPTIONS = [
    { label: "No Rest", secs: 0 },
    { label: "45s", secs: 45 },
    { label: "60s", secs: 60 },
    { label: "2min", secs: 120 },
    { label: "3min", secs: 180 },
  ];

  const fmtTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(
      2,
      "0"
    )}`;

  const getDuration = () => {
    if (timerMode === "auto") return elapsed;
    return Math.floor((Date.now() - manualStart) / 1000);
  };

  const [finishedExercises, setFinishedExercises] = useState({});
  const [finishWarning, setFinishWarning] = useState(null); // exIdx that triggered warning
  const [undoExercise, setUndoExercise] = useState(null); // { exercise, idx }
  const [undoSet, setUndoSet] = useState(null); // { exIdx, set, idx }
  const [undoVisible, setUndoVisible] = useState(false);
  const undoTimerRef = useRef(null);

  const showUndo = (data, type) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (type === "exercise") setUndoExercise(data);
    if (type === "set") setUndoSet(data);
    setUndoVisible(true);
    undoTimerRef.current = setTimeout(() => {
      setUndoVisible(false);
      setUndoExercise(null);
      setUndoSet(null);
    }, 5000);
  };

  const doUndo = () => {
    if (undoExercise) {
      setSession((prev) => {
        const exs = [...prev.exercises];
        exs.splice(undoExercise.idx, 0, undoExercise.exercise);
        return { ...prev, exercises: exs };
      });
    }
    if (undoSet) {
      setSession((prev) => ({
        ...prev,
        exercises: prev.exercises.map((ex, i) => {
          if (i !== undoSet.exIdx) return ex;
          const sets = [...ex.sets];
          sets.splice(undoSet.idx, 0, undoSet.set);
          return { ...ex, sets };
        }),
      }));
    }
    setUndoVisible(false);
    setUndoExercise(null);
    setUndoSet(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  const finishExercise = (exIdx) => {
    const exKey = exercises[exIdx]?.key;
    if (!exKey) return;
    setFinishedExercises((prev) => ({ ...prev, [exKey]: true }));
    setOpenIdx(null);
  };

  const [exerciseInfo, setExerciseInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(false);

  const exercises = session?.exercises || [];

  const EXERCISE_INSTRUCTIONS = {};

  const getBuiltInInfo = () => null;

  const CLOUD = "dqujyvob3";
  const FETCH = `https://res.cloudinary.com/${CLOUD}/image/fetch/`;
  const BASE_IMG =
    "https://raw.githubusercontent.com/yuhonas/free-exercise-db/main/exercises";
  const f = (path) => `${FETCH}${BASE_IMG}/${path}`;

  // 101 exercise+equipment combos mapped to verified Free Exercise DB images
  // served via Cloudinary fetch CDN (sets proper CORS headers automatically).
  // Keyed as "Exercise Base Name|Equipment" matching ex.key format.
  // Entries are only included where the match is correct for that specific
  // equipment variant — never a misleading substitute.
  const EXERCISE_DEMO_IMAGES = {
    "Ab Rollout|Bodyweight": [
      f("Barbell_Ab_Rollout/0.jpg"),
      f("Barbell_Ab_Rollout/1.jpg"),
    ],
    "Arnold Press|Dumbbell": [
      f("Arnold_Dumbbell_Press/0.jpg"),
      f("Arnold_Dumbbell_Press/1.jpg"),
    ],
    "Bench Press|Barbell": [
      f("Barbell_Bench_Press_-_Medium_Grip/0.jpg"),
      f("Barbell_Bench_Press_-_Medium_Grip/1.jpg"),
    ],
    "Bench Press|Dumbbell": [
      f("Dumbbell_Bench_Press/0.jpg"),
      f("Dumbbell_Bench_Press/1.jpg"),
    ],
    "Bench Press|Machine": [
      f("Machine_Bench_Press/0.jpg"),
      f("Machine_Bench_Press/1.jpg"),
    ],
    "Bicep Curl|Barbell": [f("Barbell_Curl/0.jpg"), f("Barbell_Curl/1.jpg")],
    "Bicep Curl|Cable": [
      f("High_Cable_Curls/0.jpg"),
      f("High_Cable_Curls/1.jpg"),
    ],
    "Bicep Curl|Dumbbell": [
      f("Dumbbell_Bicep_Curl/0.jpg"),
      f("Dumbbell_Bicep_Curl/1.jpg"),
    ],
    "Bicep Curl|Machine": [
      f("Machine_Bicep_Curl/0.jpg"),
      f("Machine_Bicep_Curl/1.jpg"),
    ],
    "Box Jump|Bodyweight": [
      f("Front_Box_Jump/0.jpg"),
      f("Front_Box_Jump/1.jpg"),
    ],
    "Cable Crossover|Cable": [
      f("Cable_Crossover/0.jpg"),
      f("Cable_Crossover/1.jpg"),
    ],
    "Cable Crunch|Cable": [f("Cable_Crunch/0.jpg"), f("Cable_Crunch/1.jpg")],
    "Calf Raise|Barbell": [
      f("Standing_Barbell_Calf_Raise/0.jpg"),
      f("Standing_Barbell_Calf_Raise/1.jpg"),
    ],
    "Calf Raise|Dumbbell": [
      f("Standing_Dumbbell_Calf_Raise/0.jpg"),
      f("Standing_Dumbbell_Calf_Raise/1.jpg"),
    ],
    "Calf Raise|Machine": [
      f("Seated_Calf_Raise/0.jpg"),
      f("Seated_Calf_Raise/1.jpg"),
    ],
    "Chest Fly|Cable": [f("Cable_Crossover/0.jpg"), f("Cable_Crossover/1.jpg")],
    "Chest Fly|Dumbbell": [
      f("Dumbbell_Flyes/0.jpg"),
      f("Dumbbell_Flyes/1.jpg"),
    ],
    "Chest Fly|Machine": [
      f("Reverse_Machine_Flyes/0.jpg"),
      f("Reverse_Machine_Flyes/1.jpg"),
    ],
    "Chest Press|Cable": [
      f("Cable_Chest_Press/0.jpg"),
      f("Cable_Chest_Press/1.jpg"),
    ],
    "Chest Press|Machine": [
      f("Leverage_Chest_Press/0.jpg"),
      f("Leverage_Chest_Press/1.jpg"),
    ],
    "Chin Up|Bodyweight": [f("Chin-Up/0.jpg"), f("Chin-Up/1.jpg")],
    "Clean & Press|Barbell": [
      f("Clean_and_Press/0.jpg"),
      f("Clean_and_Press/1.jpg"),
    ],
    "Close Grip Press|Barbell": [
      f("Smith_Machine_Close-Grip_Bench_Press/0.jpg"),
      f("Smith_Machine_Close-Grip_Bench_Press/1.jpg"),
    ],
    "Concentration Curl|Dumbbell": [
      f("Concentration_Curls/0.jpg"),
      f("Concentration_Curls/1.jpg"),
    ],
    "Crunch|Bodyweight": [f("Crunches/0.jpg"), f("Crunches/1.jpg")],
    "Deadlift|Barbell": [
      f("Barbell_Deadlift/0.jpg"),
      f("Barbell_Deadlift/1.jpg"),
    ],
    "Deadlift|Dumbbell": [
      f("Stiff-Legged_Dumbbell_Deadlift/0.jpg"),
      f("Stiff-Legged_Dumbbell_Deadlift/1.jpg"),
    ],
    "Deadlift|Machine": [
      f("Rack_Pull_with_Bands/0.jpg"),
      f("Rack_Pull_with_Bands/1.jpg"),
    ],
    "Decline Press|Barbell": [
      f("Decline_Barbell_Bench_Press/0.jpg"),
      f("Decline_Barbell_Bench_Press/1.jpg"),
    ],
    "Decline Press|Dumbbell": [
      f("Decline_Dumbbell_Bench_Press/0.jpg"),
      f("Decline_Dumbbell_Bench_Press/1.jpg"),
    ],
    "Dip|Bodyweight": [f("Bench_Dips/0.jpg"), f("Bench_Dips/1.jpg")],
    "Dip|Machine": [f("Dip_Machine/0.jpg"), f("Dip_Machine/1.jpg")],
    "Face Pull|Cable": [f("Face_Pull/0.jpg"), f("Face_Pull/1.jpg")],
    "Farmer Carry|Dumbbell": [f("Farmers_Walk/0.jpg"), f("Farmers_Walk/1.jpg")],
    "Front Raise|Dumbbell": [
      f("Side_Laterals_to_Front_Raise/0.jpg"),
      f("Side_Laterals_to_Front_Raise/1.jpg"),
    ],
    "Glute Kickback|Cable": [
      f("One-Legged_Cable_Kickback/0.jpg"),
      f("One-Legged_Cable_Kickback/1.jpg"),
    ],
    "Hack Squat|Barbell": [
      f("Barbell_Hack_Squat/0.jpg"),
      f("Barbell_Hack_Squat/1.jpg"),
    ],
    "Hack Squat|Machine": [f("Hack_Squat/0.jpg"), f("Hack_Squat/1.jpg")],
    "Hammer Curl|Cable": [
      f("Cable_Hammer_Curls_-_Rope_Attachment/0.jpg"),
      f("Cable_Hammer_Curls_-_Rope_Attachment/1.jpg"),
    ],
    "Hammer Curl|Dumbbell": [f("Hammer_Curls/0.jpg"), f("Hammer_Curls/1.jpg")],
    "Hanging Knee Raise|Bodyweight": [
      f("Step-up_with_Knee_Raise/0.jpg"),
      f("Step-up_with_Knee_Raise/1.jpg"),
    ],
    "Hip Thrust|Barbell": [
      f("Barbell_Hip_Thrust/0.jpg"),
      f("Barbell_Hip_Thrust/1.jpg"),
    ],
    "Hip Thrust|Bodyweight": [
      f("Single_Leg_Glute_Bridge/0.jpg"),
      f("Single_Leg_Glute_Bridge/1.jpg"),
    ],
    "Hyperextension|Bodyweight": [
      f("Hyperextensions_With_No_Hyperextension_Bench/0.jpg"),
      f("Hyperextensions_With_No_Hyperextension_Bench/1.jpg"),
    ],
    "Incline Curl|Dumbbell": [
      f("Incline_Dumbbell_Curl/0.jpg"),
      f("Incline_Dumbbell_Curl/1.jpg"),
    ],
    "Incline Press|Barbell": [
      f("Barbell_Incline_Bench_Press_-_Medium_Grip/0.jpg"),
      f("Barbell_Incline_Bench_Press_-_Medium_Grip/1.jpg"),
    ],
    "Incline Press|Cable": [
      f("Cable_Incline_Pushdown/0.jpg"),
      f("Cable_Incline_Pushdown/1.jpg"),
    ],
    "Incline Press|Dumbbell": [
      f("Incline_Dumbbell_Press/0.jpg"),
      f("Incline_Dumbbell_Press/1.jpg"),
    ],
    "Incline Press|Machine": [
      f("Smith_Machine_Incline_Bench_Press/0.jpg"),
      f("Smith_Machine_Incline_Bench_Press/1.jpg"),
    ],
    "Kettlebell Swing|Dumbbell": [
      f("One-Arm_Kettlebell_Swings/0.jpg"),
      f("One-Arm_Kettlebell_Swings/1.jpg"),
    ],
    "Lat Pulldown|Cable": [
      f("One_Arm_Lat_Pulldown/0.jpg"),
      f("One_Arm_Lat_Pulldown/1.jpg"),
    ],
    "Lat Pulldown|Machine": [
      f("Close-Grip_Front_Lat_Pulldown/0.jpg"),
      f("Close-Grip_Front_Lat_Pulldown/1.jpg"),
    ],
    "Lateral Raise|Dumbbell": [
      f("One-Arm_Side_Laterals/0.jpg"),
      f("One-Arm_Side_Laterals/1.jpg"),
    ],
    "Leg Curl|Machine": [
      f("Lying_Leg_Curls/0.jpg"),
      f("Lying_Leg_Curls/1.jpg"),
    ],
    "Leg Extension|Machine": [
      f("Leg_Extensions/0.jpg"),
      f("Leg_Extensions/1.jpg"),
    ],
    "Leg Press|Machine": [f("Leg_Press/0.jpg"), f("Leg_Press/1.jpg")],
    "Leg Raise|Bodyweight": [
      f("Rear_Leg_Raises/0.jpg"),
      f("Rear_Leg_Raises/1.jpg"),
    ],
    "Lunge|Barbell": [f("Barbell_Lunge/0.jpg"), f("Barbell_Lunge/1.jpg")],
    "Lunge|Bodyweight": [
      f("Barbell_Walking_Lunge/0.jpg"),
      f("Barbell_Walking_Lunge/1.jpg"),
    ],
    "Lunge|Dumbbell": [f("Dumbbell_Lunges/0.jpg"), f("Dumbbell_Lunges/1.jpg")],
    "Overhead Press|Barbell": [
      f("Barbell_Shoulder_Press/0.jpg"),
      f("Barbell_Shoulder_Press/1.jpg"),
    ],
    "Overhead Press|Cable": [
      f("Cable_Shoulder_Press/0.jpg"),
      f("Cable_Shoulder_Press/1.jpg"),
    ],
    "Overhead Press|Dumbbell": [
      f("Seated_Dumbbell_Press/0.jpg"),
      f("Seated_Dumbbell_Press/1.jpg"),
    ],
    "Overhead Tricep Ext|Barbell": [
      f("Standing_Overhead_Barbell_Triceps_Extension/0.jpg"),
      f("Standing_Overhead_Barbell_Triceps_Extension/1.jpg"),
    ],
    "Overhead Tricep Ext|Cable": [
      f("Cable_Rope_Overhead_Triceps_Extension/0.jpg"),
      f("Cable_Rope_Overhead_Triceps_Extension/1.jpg"),
    ],
    "Overhead Tricep Ext|Dumbbell": [
      f("Dumbbell_Tricep_Extension_-Pronated_Grip/0.jpg"),
      f("Dumbbell_Tricep_Extension_-Pronated_Grip/1.jpg"),
    ],
    "Pec Deck|Machine": [f("Butterfly/0.jpg"), f("Butterfly/1.jpg")],
    "Plank|Bodyweight": [f("Plank/0.jpg"), f("Plank/1.jpg")],
    "Preacher Curl|Barbell": [
      f("Reverse_Barbell_Preacher_Curls/0.jpg"),
      f("Reverse_Barbell_Preacher_Curls/1.jpg"),
    ],
    "Preacher Curl|Cable": [
      f("Cable_Preacher_Curl/0.jpg"),
      f("Cable_Preacher_Curl/1.jpg"),
    ],
    "Preacher Curl|Dumbbell": [
      f("One_Arm_Dumbbell_Preacher_Curl/0.jpg"),
      f("One_Arm_Dumbbell_Preacher_Curl/1.jpg"),
    ],
    "Pull Up|Bodyweight": [f("Pullups/0.jpg"), f("Pullups/1.jpg")],
    "Pull Up|Machine": [
      f("Band_Assisted_Pull-Up/0.jpg"),
      f("Band_Assisted_Pull-Up/1.jpg"),
    ],
    "Push Up|Bodyweight": [f("Pushups/0.jpg"), f("Pushups/1.jpg")],
    "Rear Delt Fly|Cable": [
      f("Cable_Rear_Delt_Fly/0.jpg"),
      f("Cable_Rear_Delt_Fly/1.jpg"),
    ],
    "Rear Delt Fly|Dumbbell": [
      f("Bent_Over_Dumbbell_Rear_Delt_Raise_With_Head_On_Bench/0.jpg"),
      f("Bent_Over_Dumbbell_Rear_Delt_Raise_With_Head_On_Bench/1.jpg"),
    ],
    "Rear Delt Fly|Machine": [
      f("Reverse_Machine_Flyes/0.jpg"),
      f("Reverse_Machine_Flyes/1.jpg"),
    ],
    "Romanian Deadlift|Barbell": [
      f("Romanian_Deadlift/0.jpg"),
      f("Romanian_Deadlift/1.jpg"),
    ],
    "Romanian Deadlift|Dumbbell": [
      f("Stiff-Legged_Dumbbell_Deadlift/0.jpg"),
      f("Stiff-Legged_Dumbbell_Deadlift/1.jpg"),
    ],
    "Row|Barbell": [
      f("Bent_Over_Barbell_Row/0.jpg"),
      f("Bent_Over_Barbell_Row/1.jpg"),
    ],
    "Row|Cable": [f("Seated_Cable_Rows/0.jpg"), f("Seated_Cable_Rows/1.jpg")],
    "Row|Dumbbell": [
      f("One-Arm_Dumbbell_Row/0.jpg"),
      f("One-Arm_Dumbbell_Row/1.jpg"),
    ],
    "Row|Machine": [f("Lying_T-Bar_Row/0.jpg"), f("Lying_T-Bar_Row/1.jpg")],
    "Russian Twist|Bodyweight": [
      f("Russian_Twist/0.jpg"),
      f("Russian_Twist/1.jpg"),
    ],
    "Seated Cable Row|Cable": [
      f("Seated_Cable_Rows/0.jpg"),
      f("Seated_Cable_Rows/1.jpg"),
    ],
    "Shrug|Barbell": [f("Barbell_Shrug/0.jpg"), f("Barbell_Shrug/1.jpg")],
    "Shrug|Dumbbell": [f("Dumbbell_Shrug/0.jpg"), f("Dumbbell_Shrug/1.jpg")],
    "Single Arm Row|Dumbbell": [
      f("One-Arm_Dumbbell_Row/0.jpg"),
      f("One-Arm_Dumbbell_Row/1.jpg"),
    ],
    "Sit Up|Bodyweight": [f("Sit-Up/0.jpg"), f("Sit-Up/1.jpg")],
    "Skull Crusher|Barbell": [
      f("Decline_Close-Grip_Bench_To_Skull_Crusher/0.jpg"),
      f("Decline_Close-Grip_Bench_To_Skull_Crusher/1.jpg"),
    ],
    "Squat|Barbell": [f("Barbell_Squat/0.jpg"), f("Barbell_Squat/1.jpg")],
    "Squat|Dumbbell": [f("Dumbbell_Squat/0.jpg"), f("Dumbbell_Squat/1.jpg")],
    "Squat|Machine": [
      f("Smith_Machine_Squat/0.jpg"),
      f("Smith_Machine_Squat/1.jpg"),
    ],
    "Step Up|Barbell": [
      f("Barbell_Step_Ups/0.jpg"),
      f("Barbell_Step_Ups/1.jpg"),
    ],
    "Step Up|Dumbbell": [
      f("Dumbbell_Step_Ups/0.jpg"),
      f("Dumbbell_Step_Ups/1.jpg"),
    ],
    "Thruster|Barbell": [
      f("Kettlebell_Thruster/0.jpg"),
      f("Kettlebell_Thruster/1.jpg"),
    ],
    "Tricep Kickback|Dumbbell": [
      f("Tricep_Dumbbell_Kickback/0.jpg"),
      f("Tricep_Dumbbell_Kickback/1.jpg"),
    ],
    "Tricep Pushdown|Cable": [
      f("Triceps_Pushdown/0.jpg"),
      f("Triceps_Pushdown/1.jpg"),
    ],
    "Upright Row|Barbell": [
      f("Upright_Barbell_Row/0.jpg"),
      f("Upright_Barbell_Row/1.jpg"),
    ],
    "Upright Row|Cable": [
      f("Upright_Cable_Row/0.jpg"),
      f("Upright_Cable_Row/1.jpg"),
    ],
    "Upright Row|Dumbbell": [
      f("Standing_Dumbbell_Upright_Row/0.jpg"),
      f("Standing_Dumbbell_Upright_Row/1.jpg"),
    ],
  };

  // Extract exercise name and equipment from ex.key (format: "Exercise Name__Equipment__Position?")
  // and look up in EXERCISE_DEMO_IMAGES keyed as "Exercise Name|Equipment"
  const getExerciseBaseForDemo = (exerciseName, exerciseKey) => {
    if (exerciseKey) {
      const parts = exerciseKey.split("__");
      if (parts.length >= 2) {
        const lookupKey = `${parts[0]}|${parts[1]}`;
        if (EXERCISE_DEMO_IMAGES[lookupKey]) return lookupKey;
      }
    }
    // Fallback: try matching base name against any equipment variant
    const baseName = exerciseName?.split(" (")[0].split(" —")[0].trim();
    return (
      Object.keys(EXERCISE_DEMO_IMAGES).find((k) =>
        k.startsWith(baseName + "|")
      ) || null
    );
  };

  const fetchExerciseInfo = async (exerciseName, exerciseKey) => {
    setInfoLoading(true);
    setExerciseInfo({
      name: exerciseName,
      gifUrl: null,
      instructions: [],
      muscles: [],
    });

    // Check our verified local lookup first — equipment-specific, guaranteed to work
    const demoKey = getExerciseBaseForDemo(exerciseName, exerciseKey);
    const demoFrames = demoKey ? EXERCISE_DEMO_IMAGES[demoKey] : null;

    try {
      const cleanName = exerciseName
        .split(" (")[0]
        .split(" —")[0]
        .toLowerCase();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(
        `https://exercisedb-api.vercel.app/api/v1/exercises/search?name=${encodeURIComponent(
          cleanName
        )}&limit=5`,
        { signal: controller.signal }
      );
      clearTimeout(timeout);
      if (!res.ok) throw new Error("API error");
      const data = await res.json();
      const results = data?.data?.exercises || data?.exercises || [];
      if (results.length > 0) {
        const ex = results[0];
        setExerciseInfo({
          name: exerciseName,
          gifUrl: ex.gifUrl || ex.imageUrl || null,
          demoFrames, // attach our local frames regardless so panel can use either
          instructions: ex.instructions?.length > 0 ? ex.instructions : [],
          muscles: [
            ...(ex.targetMuscles || []),
            ...(ex.secondaryMuscles || []),
          ].slice(0, 6),
        });
        setInfoLoading(false);
        return;
      }
    } catch (e) {
      // External API failed — fall through to local data
    }

    setExerciseInfo({
      name: exerciseName,
      gifUrl: null,
      demoFrames,
      instructions: ["No additional instructions available."],
      muscles: [],
      usingFallback: true,
    });
    setInfoLoading(false);
  };

  const addSpecialSet = (exIdx, type) => {
    const inp = inputs[exIdx] || {};
    const weight = parseFloat(inp.weight);
    const reps = parseInt(inp.reps);
    if (!weight || !reps || weight <= 0 || reps <= 0) return;
    setSession((prev) => ({
      ...prev,
      exercises: prev.exercises.map((ex, i) =>
        i !== exIdx
          ? ex
          : {
              ...ex,
              sets: [
                ...ex.sets,
                { weight, reps, type, rest: inp.rest || null },
              ],
            }
      ),
    }));
    setInputs((prev) => ({ ...prev, [exIdx]: { weight: "", reps: "" } }));
    if (inp.rest) startRest(inp.rest);
  };

  const [supersetPicking, setSupersetPicking] = useState(null); // exIdx | null

  const addSupersetExercise = (exIdx, pairedEx) => {
    const alreadyAddedIdx = exercises.findIndex((e) => e.key === pairedEx.key);
    if (alreadyAddedIdx >= 0) {
      setSession((prev) => ({
        ...prev,
        exercises: prev.exercises.map((e, i) =>
          i === alreadyAddedIdx ? { ...e, supersetChild: exIdx } : e
        ),
      }));
    } else {
      setSession((prev) => ({
        ...prev,
        exercises: [
          ...prev.exercises,
          { ...pairedEx, sets: [], supersetChild: exIdx },
        ],
      }));
    }
    setSupersetPicking(null);
    setOpenIdx(exIdx);
  };

  const addExercise = (ex) => {
    const nextIdx = exercises.length;
    setSession((prev) => ({ ...prev, exercises: [...prev.exercises, ex] }));
    setOpenIdx(nextIdx);
    setPicking(false);
    setPickingGroup(null);
  };

  // ── SWAP EXERCISE ──
  const swapExercise = (exIdx, newEx) => {
    const prevEx = exercises[exIdx];
    const prevInputs = inputs[exIdx];

    // Build poTarget and lastSets for the new exercise
    const lastSession = getLastExercise(newEx.key);
    const lastSets = lastSession?.sets || [];
    const bestSet =
      lastSets.length > 0
        ? lastSets.reduce((a, s) => (s.weight > a.weight ? s : a), lastSets[0])
        : null;
    const poTarget = bestSet
      ? getRecommendations(bestSet, profile.goal, newEx.key, sessions)[0]
      : null;

    // Replace exercise in session, preserve position
    setSession((prev) => ({
      ...prev,
      exercises: prev.exercises.map((ex, i) =>
        i !== exIdx
          ? ex
          : {
              ...newEx,
              sets: [],
              poTarget,
              lastSets,
              swappedFrom: prevEx.name, // track for display
            }
      ),
    }));

    // Clear inputs for this slot
    setInputs((prev) => ({
      ...prev,
      [exIdx]: { weight: "", reps: "", rest: null, selectedType: null },
    }));

    // Store undo data
    setSwapUndo({ exIdx, prevEx, prevInputs: prevInputs || {} });
    setSwapUndoVisible(true);
    setSwapExIdx(null);
    setSwapPickedExercise(null);

    // Auto-dismiss undo after 5 seconds
    setTimeout(() => setSwapUndoVisible(false), 5000);
  };

  const undoSwap = () => {
    if (!swapUndo) return;
    const { exIdx, prevEx, prevInputs } = swapUndo;
    setSession((prev) => ({
      ...prev,
      exercises: prev.exercises.map((ex, i) => (i !== exIdx ? ex : prevEx)),
    }));
    setInputs((prev) => ({ ...prev, [exIdx]: prevInputs }));
    setSwapUndo(null);
    setSwapUndoVisible(false);
  };

  const addSet = (exIdx) => {
    const inp = inputs[exIdx] || {};
    const ex = exercises[exIdx];
    const isBodyweight =
      ex?.key?.includes("__Bodyweight") || ex?.name?.includes("(Bodyweight)");
    const rawWeight = isBodyweight ? Number(profile?.weight) || 0 : inp.weight;
    const weight = parseFloat(rawWeight);
    const reps = parseInt(inp.reps);
    if (!isBodyweight && (inp.weight === "" || inp.weight === undefined))
      return;
    if (!reps || reps <= 0) return;

    // Resolve rest: manual chip choice always wins. Otherwise auto-compute —
    // same priority order the UI hint already uses: exact match from the same
    // set position last time you did this exercise, then a nearby set's rest,
    // then the goal+reps-based smartRestSuggestion formula as the fallback.
    // This applies whether timerMode is "manual" or "auto" so a rest interval
    // is always attached to the logged set; only "auto" mode skips starting
    // the visible countdown UI (it relies on the session's running clock instead).
    let rest = inp.rest || null;
    if (!rest) {
      const workingLastSets = ex.lastSets || [];
      const nextSetIdx = ex.sets.length;
      const exactMatch = workingLastSets[nextSetIdx]?.rest || null;
      const nearbyMatch = !exactMatch
        ? [0, 1, -1, 2, -2]
            .map((offset) => workingLastSets[nextSetIdx + offset]?.rest)
            .find(Boolean)
        : null;
      rest =
        exactMatch ||
        nearbyMatch ||
        ex.lastRest ||
        smartRestSuggestion(profile?.goal, reps);
    }

    setSession((prev) => ({
      ...prev,
      exercises: prev.exercises.map((ex, i) =>
        i !== exIdx ? ex : { ...ex, sets: [...ex.sets, { weight, reps, rest }] }
      ),
    }));

    // nextWorkingIdx = index of the NEXT set after the one just logged
    // ex.sets.length is the count BEFORE this set is committed (React async)
    // so ex.sets.length = index of set just logged, +1 = next set index
    const nextWorkingIdx = ex.sets.length + 1;
    const setTargets = ex.lastSets
      ? buildSetTargets(ex.lastSets, ex.poTarget, getEquipmentIncrement(ex.key))
      : [];
    const nextTarget = setTargets[nextWorkingIdx] || null;

    const totalTargets = setTargets.length;
    const hasTargets = totalTargets > 0;
    const allDone = hasTargets && nextWorkingIdx >= totalTargets;

    if (allDone) {
      setInputs((prev) => ({
        ...prev,
        [exIdx]: {
          weight: "",
          reps: "",
          rest: null,
          selectedType: null,
          isTarget: false,
          isCarryForward: false,
        },
      }));
    } else if (nextTarget) {
      setInputs((prev) => ({
        ...prev,
        [exIdx]: {
          weight: String(nextTarget.weight),
          reps: String(nextTarget.reps),
          rest: null,
          selectedType: "working",
          isTarget: true,
          isCarryForward: false,
        },
      }));
    } else {
      setInputs((prev) => ({
        ...prev,
        [exIdx]: {
          weight: String(weight),
          reps: "",
          rest: null,
          selectedType: "working",
          isTarget: false,
          isCarryForward: true,
        },
      }));
    }

    // Auto-start the manual rest countdown using the resolved value, unless
    // the person explicitly chose "No Rest" (0s) — that's a deliberate signal
    // to skip resting entirely (e.g. supersetting straight into another move),
    // not a missing value to be overridden.
    if (timerMode === "manual" && rest > 0) startRest(rest, exIdx);
  };

  const deleteSet = (exIdx, setIdx) => {
    const deletedSet = exercises[exIdx]?.sets[setIdx];
    setSession((prev) => ({
      ...prev,
      exercises: prev.exercises.map((ex, i) =>
        i !== exIdx
          ? ex
          : { ...ex, sets: ex.sets.filter((_, si) => si !== setIdx) }
      ),
    }));
    if (deletedSet) showUndo({ exIdx, set: deletedSet, idx: setIdx }, "set");
  };

  const updateInput = (exIdx, field, value) =>
    setInputs((prev) => ({
      ...prev,
      [exIdx]: {
        ...(prev[exIdx] || {}),
        [field]: value,
        ...(field === "weight" || field === "reps"
          ? { isTarget: false, isCarryForward: false }
          : {}),
      },
    }));

  const totalSetsLogged = exercises.reduce((a, ex) => a + ex.sets.length, 0);
  const totalVolLogged = exercises.reduce(
    (a, ex) => a + ex.sets.reduce((b, s) => b + (s.weight || 0) * s.reps, 0),
    0
  );
  const doneCount = exercises.filter(
    (ex) =>
      (ex.supersetChild === undefined || ex.supersetChild === null) &&
      finishedExercises[ex.key]
  ).length;
  const muscleColor =
    GROUP_COLORS[session?.muscleGroup?.split(" + ")[0]] || "var(--accent)";

  return (
    <div className="screen" style={{ padding: 0 }}>
      {/* ── WORKOUT HEADER ── */}
      <div
        style={{
          background: "var(--surface)",
          borderBottom: "1px solid var(--border)",
          paddingTop: 48,
          boxShadow: "var(--shadow-md)",
        }}
      >
        {/* Top row — back / title / done */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 16px 12px",
            gap: 8,
          }}
        >
          <button
            className="btn-ghost sm"
            onClick={() => {
              const totalSets = (session?.exercises || []).reduce(
                (a, ex) => a + ex.sets.length,
                0
              );
              if (totalSets > 0) setShowExitConfirm(true);
              else onBack(false);
            }}
            style={{
              color: "var(--muted)",
              borderColor: "var(--border)",
              flexShrink: 0,
            }}
          >
            ← Back
          </button>

          <div style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontWeight: 700,
                fontSize: 18,
                color: "var(--text)",
                letterSpacing: -0.5,
                lineHeight: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {session?.muscleGroup}
            </p>
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                marginTop: 3,
                textTransform: "uppercase",
                letterSpacing: 1,
                fontWeight: 700,
              }}
            >
              {timerMode === "manual"
                ? "Rest Timer"
                : paused
                ? "⏸ Paused"
                : "In progress"}
            </p>
          </div>

          {!confirmFinish ? (
            <button
              onClick={() => setConfirmFinish(true)}
              style={{
                background: "var(--accent)",
                border: "none",
                borderRadius: "var(--r-sm)",
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 700,
                color: "var(--bg)",
                cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
                flexShrink: 0,
                whiteSpace: "nowrap",
                boxShadow: "0 2px 12px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
              }}
            >
              Finish
            </button>
          ) : (
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button
                style={{
                  background: "var(--success)",
                  color: "var(--bg)",
                  border: "none",
                  borderRadius: "var(--r-sm)",
                  padding: "8px 14px",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                }}
                onClick={() => onFinish(getDuration())}
              >
                ✓ Yes
              </button>
              <button
                style={{
                  background: "var(--surface2)",
                  color: "var(--muted)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--r-sm)",
                  padding: "8px 10px",
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                }}
                onClick={() => setConfirmFinish(false)}
              >
                No
              </button>
            </div>
          )}
        </div>

        {/* Stats row — clean, no neon background */}
        <div style={{ display: "flex", borderTop: "1px solid var(--border)" }}>
          {timerMode === "auto" && (
            <button
              onClick={togglePause}
              style={{
                flex: 1,
                padding: "10px 0",
                background: "none",
                border: "none",
                borderRight: "1px solid var(--border)",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <p
                style={{
                  fontFamily: "'Bebas Neue',sans-serif",
                  fontSize: 22,
                  color: paused ? "var(--muted)" : "var(--accent)",
                  letterSpacing: 1,
                  lineHeight: 1,
                }}
              >
                {fmtTime(elapsed)}
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontWeight: 700,
                }}
              >
                {paused ? "paused" : "time"}
              </p>
            </button>
          )}

          {[
            {
              val: totalSetsLogged || "—",
              label: "Sets",
              color: totalSetsLogged > 0 ? "var(--text)" : "var(--muted)",
            },
            {
              val:
                totalVolLogged >= 1000
                  ? `${(totalVolLogged / 1000).toFixed(1)}k`
                  : totalVolLogged || "—",
              label: "Volume",
              color: totalVolLogged > 0 ? "var(--text)" : "var(--muted)",
            },
          ].map((s, i, arr) => (
            <div
              key={i}
              style={{
                flex: 1,
                padding: "10px 0",
                borderRight:
                  i < arr.length - 1 || doneCount > 0
                    ? "1px solid var(--border)"
                    : "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <p
                style={{
                  fontFamily: "'Bebas Neue',sans-serif",
                  fontSize: 22,
                  color: s.color,
                  lineHeight: 1,
                }}
              >
                {s.val}
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontWeight: 700,
                }}
              >
                {s.label}
              </p>
            </div>
          ))}

          {doneCount > 0 && (
            <div
              style={{
                flex: 1,
                padding: "10px 0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <p
                style={{
                  fontFamily: "'Bebas Neue',sans-serif",
                  fontSize: 22,
                  color: "var(--success)",
                  lineHeight: 1,
                }}
              >
                {doneCount}/
                {
                  exercises.filter(
                    (e) =>
                      e.supersetChild === undefined || e.supersetChild === null
                  ).length
                }
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontWeight: 700,
                }}
              >
                Done
              </p>
            </div>
          )}
        </div>
      </div>

      {flashGreen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background:
              restTimer === 0
                ? "rgba(61,220,132,.35)" // bright full flash on completion
                : "rgba(61,220,132,.18)", // subtle pulse during countdown
            pointerEvents: "none",
            transition: "opacity .1s",
          }}
        />
      )}

      {undoVisible && (
        <div
          style={{
            background: "var(--surface2)",
            borderBottom: "1px solid var(--border)",
            padding: "10px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 49,
          }}
        >
          <p style={{ fontSize: 13, color: "var(--text)" }}>
            {undoExercise ? "Exercise deleted" : "Set deleted"}
          </p>
          <button
            onClick={doUndo}
            style={{
              background: "var(--accent)",
              color: "var(--bg)",
              border: "none",
              borderRadius: 8,
              padding: "6px 14px",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Undo
          </button>
        </div>
      )}

      <div className="tab-bar">
        <button
          className={`tab ${tab === "log" ? "active" : ""}`}
          onClick={() => setTab("log")}
        >
          Log Sets
        </button>
        <button
          className={`tab ${tab === "next" ? "active" : ""}`}
          onClick={() => setTab("next")}
        >
          Next Session
        </button>
      </div>

      {tab === "log" && (
        <div
          style={{
            padding: "12px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {exercises.length === 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "48px 20px",
                gap: 14,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)",
                  border: "1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ic.Plus s={28} c="var(--accent)" />
              </div>
              <div>
                <p
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "var(--text)",
                    marginBottom: 6,
                  }}
                >
                  No exercises added yet
                </p>
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--muted)",
                    lineHeight: 1.6,
                  }}
                >
                  Tap{" "}
                  <strong style={{ color: "var(--accent)" }}>
                    + Add Exercise
                  </strong>{" "}
                  below to build your session
                </p>
              </div>
            </div>
          )}

          {exercises.map((ex, exIdx) => {
            if (ex.supersetChild !== undefined && ex.supersetChild !== null)
              return null;

            const lastEx = getLastExercise(ex.key);
            const inp = inputs[exIdx] || {};
            const isOpen = openIdx === exIdx;
            const isDone = finishedExercises[ex.key];

            const workingSetsLogged = ex.sets.length;
            const setTargets = ex.lastSets
              ? buildSetTargets(
                  ex.lastSets,
                  ex.poTarget,
                  getEquipmentIncrement(ex.key)
                )
              : [];
            const currentTarget = setTargets[workingSetsLogged] || null;

            const superChild = exercises.find(
              (e, i) => i !== exIdx && e.supersetChild === exIdx
            );
            const superChildIdx = superChild
              ? exercises.indexOf(superChild)
              : null;
            const superChildInp =
              superChildIdx !== null ? inputs[superChildIdx] || {} : {};
            const superChildLast = superChild
              ? getLastExercise(superChild.key)
              : null;

            // PR flash: check if any logged set beats all-time best e1RM from previous sessions
            const allTimeBest = sessions.reduce((best, s) => {
              const prevEx = s.exercises?.find((e) => e.key === ex.key);
              if (!prevEx) return best;
              prevEx.sets?.forEach((set) => {
                const rm = Math.round((set.weight || 0) * (1 + set.reps / 30));
                if (rm > best) best = rm;
              });
              return best;
            }, 0);
            const prSetIndices = new Set(
              ex.sets
                .map((s, si) => {
                  const rm = Math.round((s.weight || 0) * (1 + s.reps / 30));
                  return rm > allTimeBest ? si : -1;
                })
                .filter((i) => i >= 0)
            );

            return (
              <div
                key={exIdx}
                className={`ex-card ${isOpen ? "open" : ""} ${
                  isDone ? "finished" : ""
                }`}
                style={{
                  // Active spotlight — slightly different background when open
                  background: isOpen
                    ? "var(--surface)"
                    : isDone
                    ? "var(--surface)"
                    : "var(--surface)",
                  // Opacity dim for exercises that aren't active yet (not started, not open)
                  opacity:
                    !isOpen &&
                    !isDone &&
                    totalSetsLogged > 0 &&
                    ex.sets.length === 0
                      ? 0.6
                      : 1,
                  transition: "opacity .2s, box-shadow .2s, border-color .2s",
                }}
              >
                {/* ── COMPLETED (collapsed) ── */}
                {isDone && !isOpen ? (
                  <div
                    onClick={() => setOpenIdx(exIdx)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "11px 14px",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        flexShrink: 0,
                        background: "rgba(61,220,132,.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Ic.Check s={14} c="var(--success)" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "var(--text)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ex.name}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: 4,
                          marginTop: 3,
                          flexWrap: "wrap",
                        }}
                      >
                        {ex.sets.map((s, si) => (
                          <span
                            key={si}
                            style={{
                              fontSize: 11,
                              color: "var(--success)",
                              background: "rgba(61,220,132,.08)",
                              borderRadius: 4,
                              padding: "2px 7px",
                              fontWeight: 700,
                            }}
                          >
                            {s.weight}×{s.reps}
                          </span>
                        ))}
                      </div>
                    </div>
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="var(--muted)"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 5l4 4 4-4" />
                    </svg>
                  </div>
                ) : (
                  <>
                    {/* ── EXERCISE HEADER ── */}
                    <div
                      className="ex-header"
                      onClick={() => setOpenIdx(isOpen ? null : exIdx)}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          className="ex-name"
                          style={{
                            fontSize: isOpen ? 16 : 15,
                            transition: "font-size .15s",
                          }}
                        >
                          {ex.name}
                        </p>
                        {ex.swappedFrom && (
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 2,
                              fontStyle: "italic",
                            }}
                          >
                            ⇄ from {ex.swappedFrom}
                          </p>
                        )}
                        {superChild && (
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--purple)",
                              marginTop: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                            }}
                          >
                            <Ic.ChevUp s={12} c="currentColor" />
                            Superset · {superChild.name}
                          </p>
                        )}
                        {ex.sets.length > 0 && !isOpen && (
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 3,
                            }}
                          >
                            {ex.sets.length} set
                            {ex.sets.length !== 1 ? "s" : ""} logged
                          </p>
                        )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          flexShrink: 0,
                        }}
                      >
                        {ex.sets.length === 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSwapExIdx(exIdx);
                            }}
                            style={{
                              background: "none",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--r-sm)",
                              padding: "4px 8px",
                              fontSize: 11,
                              color: "var(--muted)",
                              cursor: "pointer",
                            }}
                          >
                            ⇄
                          </button>
                        )}
                        {settings?.showExerciseInfo !== false && (
                          <button
                            style={{
                              background: "none",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--r-sm)",
                              padding: "4px 8px",
                              fontSize: 11,
                              color: "var(--muted)",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchExerciseInfo(ex.name, ex.key);
                            }}
                          >
                            <Ic.Info s={14} c="currentColor" />
                          </button>
                        )}
                        {/* Delete always visible — not gated by open/closed state */}
                        <button
                          style={{
                            fontSize: 11,
                            color: "var(--muted)",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px 6px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const deletedEx = exercises[exIdx];
                            setSession((prev) => ({
                              ...prev,
                              exercises: prev.exercises.filter(
                                (_, i) => i !== exIdx && i !== superChildIdx
                              ),
                            }));
                            showUndo(
                              { exercise: deletedEx, idx: exIdx },
                              "exercise"
                            );
                          }}
                        >
                          <Ic.Trash s={14} c="currentColor" />
                        </button>
                        {isDone ? (
                          <span
                            style={{ color: "var(--success)", fontSize: 18 }}
                          >
                            ✓
                          </span>
                        ) : (
                          <span
                            style={{
                              color: isOpen ? "var(--accent)" : "var(--subtle)",
                              fontSize: 15,
                              transition: "color .15s",
                            }}
                          >
                            {isOpen ? "▲" : "▼"}
                          </span>
                        )}
                      </div>
                    </div>

                    {isOpen && (
                      <div className="ex-body">
                        {/* ── PO TARGET — per-set ramp strip (collapses to one
                         number when every set's target is identical, e.g.
                         straight sets; shows the ramp when they differ, e.g.
                         pyramids / reverse-pyramids). Built from the SAME
                         buildSetTargets array the inputs pre-fill from, so the
                         header can never disagree with what gets typed in. ── */}
                        {ex.poTarget &&
                          settings?.showPOTargets !== false &&
                          (() => {
                            // Does the ramp have more than one distinct target?
                            const ramp = setTargets || [];
                            const uniform =
                              ramp.length > 0 &&
                              ramp.every(
                                (t) =>
                                  t.weight === ramp[0].weight &&
                                  t.reps === ramp[0].reps
                              );
                            const showRamp = ramp.length > 1 && !uniform;

                            return (
                              <div
                                style={{
                                  background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.05)",
                                  border: "1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35)",
                                  borderRadius: "var(--r-sm)",
                                  padding: "12px 14px",
                                }}
                              >
                                {/* Label row */}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 8,
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                      letterSpacing: 1.2,
                                      color: "var(--accent)",
                                    }}
                                  >
                                    {ex.poTarget.adaptive
                                      ? "Adaptive"
                                      : "Target"}
                                  </p>
                                  {ex.poTarget.adaptive && (
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: "var(--muted)",
                                        background: "var(--surface2)",
                                        borderRadius: 4,
                                        padding: "2px 6px",
                                        fontWeight: 700,
                                      }}
                                    >
                                      personalized
                                    </span>
                                  )}
                                </div>

                                {showRamp ? (
                                  /* ── RAMP STRIP — one chip per set ── */
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 6,
                                    }}
                                  >
                                    {ramp.map((t, ti) => {
                                      const isCurrent =
                                        ti === workingSetsLogged;
                                      return (
                                        <div
                                          key={ti}
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            background: isCurrent
                                              ? "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.14)"
                                              : "var(--surface2)",
                                            border: `1px solid ${
                                              isCurrent
                                                ? "var(--accent)"
                                                : "var(--border)"
                                            }`,
                                            borderRadius: 8,
                                            padding: "6px 10px",
                                            minWidth: 62,
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: 11,
                                              fontWeight: 700,
                                              color: isCurrent
                                                ? "var(--accent)"
                                                : "var(--muted)",
                                              textTransform: "uppercase",
                                              letterSpacing: 0.5,
                                            }}
                                          >
                                            S{t.setNum ?? ti + 1}
                                          </span>
                                          <span
                                            style={{
                                              fontFamily:
                                                "'Bebas Neue',sans-serif",
                                              fontSize: 18,
                                              lineHeight: 1.1,
                                              color: isCurrent
                                                ? "var(--text)"
                                                : "var(--muted)",
                                            }}
                                          >
                                            {t.weight}×{t.reps}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  /* ── SINGLE NUMBER — uniform sets / straight sets ── */
                                  <p
                                    style={{
                                      fontFamily: "'Bebas Neue',sans-serif",
                                      fontSize: 28,
                                      lineHeight: 1,
                                      letterSpacing: 0.5,
                                      color: "var(--text)",
                                    }}
                                  >
                                    {ramp[0]?.weight ?? ex.poTarget.weight}
                                    <span
                                      style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        color: "var(--muted)",
                                        marginLeft: 4,
                                      }}
                                    >
                                      lbs
                                    </span>
                                    <span
                                      style={{
                                        color: "var(--muted)",
                                        margin: "0 8px",
                                        fontSize: 18,
                                      }}
                                    >
                                      ×
                                    </span>
                                    {ramp[0]?.reps ?? ex.poTarget.reps}
                                    <span
                                      style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        color: "var(--muted)",
                                        marginLeft: 4,
                                      }}
                                    >
                                      reps
                                    </span>
                                  </p>
                                )}

                                {/* Reason */}
                                <p
                                  style={{
                                    fontSize: 11,
                                    color: "var(--muted)",
                                    marginTop: showRamp ? 8 : 5,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {showRamp
                                    ? "Each set progresses on its own — beat the highlighted target for your next set."
                                    : ex.poTarget.reason}
                                </p>
                              </div>
                            );
                          })()}

                        {/* ── LAST SESSION ── */}
                        {lastEx && settings?.showLastSession !== false && (
                          <div className="hint-box">
                            <p className="hint-title">Last session</p>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 5,
                                marginTop: 5,
                              }}
                            >
                              {lastEx.sets.map((s, i) => (
                                <span key={i} className="hint-chip">
                                  {i + 1}. {s.weight}lbs × {s.reps}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {ex.sets.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {ex.sets.map((s, si) => (
                              <div
                                key={si}
                                className={`set-row${
                                  prSetIndices.has(si) ? " pr-flash" : ""
                                }`}
                              >
                                <span className="set-num-label">
                                  {`S${si + 1}`}
                                </span>
                                {s.type === "super" && (
                                  <span className="set-type-badge super">
                                    SS
                                  </span>
                                )}
                                {prSetIndices.has(si) && (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: "var(--warn)",
                                      fontWeight: 700,
                                      background: "rgba(255,215,0,.15)",
                                      borderRadius: 4,
                                      padding: "1px 5px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    PR
                                  </span>
                                )}
                                <span className="set-detail">
                                  {s.weight} lbs × {s.reps} reps
                                </span>
                                {s.rest && s.rest > 0 && (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: "var(--muted)",
                                    }}
                                  >
                                    {REST_OPTIONS.find((r) => r.secs === s.rest)
                                      ?.label || `${s.rest}s`}
                                  </span>
                                )}
                                <button
                                  className="del-btn"
                                  onClick={() => deleteSet(exIdx, si)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {(() => {
                          const isBodyweight =
                            ex.key?.includes("__Bodyweight") ||
                            ex.name?.includes("(Bodyweight)");
                          const bw = Number(profile?.weight) || 0;
                          // selectedType is auto-set to "working" as soon as weight or
                          // reps are entered — no button tap required from the user.
                          const effectiveType =
                            inp.selectedType ||
                            ((inp.weight !== "" && inp.weight !== undefined) ||
                            inp.reps
                              ? "working"
                              : null);
                          const canLog =
                            effectiveType &&
                            inp.reps &&
                            (isBodyweight ||
                              (inp.weight !== "" && inp.weight !== undefined));

                          // Is the rest countdown active for THIS specific exercise?
                          // We track which exIdx started the last rest so only that
                          // card shows the inline countdown.
                          const isResting =
                            timerMode === "manual" &&
                            restTimer !== null &&
                            restActiveExIdx === exIdx;

                          return (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                              }}
                            >
                              {effectiveType !== "super" && (
                                <div className="input-row">
                                  {isBodyweight ? (
                                    <div
                                      style={{
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 5,
                                      }}
                                    >
                                      <label
                                        style={{
                                          fontSize: 11,
                                          color: "var(--muted)",
                                          fontWeight: 700,
                                          letterSpacing: 0.3,
                                        }}
                                      >
                                        Weight
                                      </label>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          padding: "12px 14px",
                                          background: "var(--surface2)",
                                          border: "1.5px solid var(--success)",
                                          borderRadius: 12,
                                          minHeight: 52,
                                        }}
                                      >
                                        
                                        <div>
                                          <p
                                            style={{
                                              fontSize: 13,
                                              fontWeight: 700,
                                              color: "var(--success)",
                                            }}
                                          >
                                            Bodyweight
                                            {bw > 0 ? ` (${bw} lbs)` : ""}
                                          </p>
                                          <p
                                            style={{
                                              fontSize: 11,
                                              color: "var(--muted)",
                                            }}
                                          >
                                            auto-logged
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 5,
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <label
                                          style={{
                                            fontSize: 11,
                                            color: "var(--muted)",
                                            fontWeight: 700,
                                            letterSpacing: 0.3,
                                          }}
                                        >
                                          Weight (lbs)
                                        </label>
                                        {inp.isTarget && (
                                          <span
                                            style={{
                                              fontSize: 11,
                                              fontWeight: 700,
                                              color: "var(--accent)",
                                              background:
                                                "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.15)",
                                              borderRadius: 4,
                                              padding: "2px 6px",
                                              letterSpacing: 0.5,
                                            }}
                                          >
                                            TARGET
                                          </span>
                                        )}
                                        {inp.isCarryForward &&
                                          !inp.isTarget && (
                                            <span
                                              style={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                color: "var(--muted)",
                                                background: "var(--surface2)",
                                                borderRadius: 4,
                                                padding: "2px 6px",
                                                letterSpacing: 0.3,
                                              }}
                                            >
                                              LAST SET
                                            </span>
                                          )}
                                      </div>
                                      <StepperInput
                                        label=""
                                        value={inp.weight}
                                        onChange={(v) =>
                                          updateInput(exIdx, "weight", v)
                                        }
                                        step={getEquipmentIncrement(ex.key)}
                                        min={0}
                                        max={999}
                                        placeholder="135"
                                        filled={
                                          inp.weight !== "" &&
                                          inp.weight !== undefined
                                        }
                                        accent={inp.isTarget}
                                      />
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 5,
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <label
                                        style={{
                                          fontSize: 11,
                                          color: "var(--muted)",
                                          fontWeight: 700,
                                          letterSpacing: 0.3,
                                        }}
                                      >
                                        Reps
                                      </label>
                                      {inp.isTarget && (
                                        <span
                                          style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: "var(--accent)",
                                            background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.15)",
                                            borderRadius: 4,
                                            padding: "2px 6px",
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          TARGET
                                        </span>
                                      )}
                                    </div>
                                    <StepperInput
                                      label=""
                                      value={inp.reps}
                                      onChange={(v) =>
                                        updateInput(exIdx, "reps", v)
                                      }
                                      step={1}
                                      min={1}
                                      max={100}
                                      placeholder="10"
                                      filled={!!inp.reps}
                                      accent={inp.isTarget}
                                    />
                                  </div>
                                </div>
                              )}

                              {settings?.show1RM !== false &&
                                effectiveType &&
                                effectiveType !== "super" &&
                                (() => {
                                  const isBodyweight =
                                    ex.key?.includes("__Bodyweight") ||
                                    ex.name?.includes("(Bodyweight)");
                                  const w = isBodyweight
                                    ? Number(profile?.weight) || 0
                                    : parseFloat(inp.weight);
                                  const r = parseInt(inp.reps);
                                  if (!w || !r || r <= 0) return null;
                                  const est1RM = Math.round(w * (1 + r / 30));
                                  const prevBest1RM =
                                    lastEx?.sets?.reduce((best, s) => {
                                      const rm = Math.round(
                                        (s.weight || 0) * (1 + s.reps / 30)
                                      );
                                      return rm > best ? rm : best;
                                    }, 0) || 0;
                                  const isPR =
                                    prevBest1RM > 0 && est1RM > prevBest1RM;
                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        background: isPR
                                          ? "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)"
                                          : "var(--surface2)",
                                        border: `1px solid ${
                                          isPR
                                            ? "var(--accent)"
                                            : "var(--border)"
                                        }`,
                                        borderRadius: 10,
                                        padding: "8px 12px",
                                      }}
                                    >
                                      <div>
                                        <p
                                          style={{
                                            fontSize: 11,
                                            color: isPR
                                              ? "var(--accent)"
                                              : "var(--muted)",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.8,
                                          }}
                                        >
                                          {isPR ? "New 1RM PR" : "Est. 1RM"}
                                        </p>
                                        <p
                                          style={{
                                            fontSize: 18,
                                            fontWeight: 700,
                                            color: isPR
                                              ? "var(--accent)"
                                              : "var(--text)",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {est1RM} lbs
                                        </p>
                                      </div>
                                      {prevBest1RM > 0 && (
                                        <div style={{ textAlign: "right" }}>
                                          <p
                                            style={{
                                              fontSize: 11,
                                              color: "var(--muted)",
                                            }}
                                          >
                                            Previous best
                                          </p>
                                          <p
                                            style={{
                                              fontSize: 13,
                                              color: "var(--muted)",
                                              fontWeight: 700,
                                            }}
                                          >
                                            {prevBest1RM} lbs
                                          </p>
                                          {isPR && (
                                            <p
                                              style={{
                                                fontSize: 11,
                                                color: "var(--accent)",
                                                fontWeight: 700,
                                              }}
                                            >
                                              +{est1RM - prevBest1RM} lbs ↑
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                              {/* ── LOG BUTTON or INLINE REST COUNTDOWN ── */}
                              {isResting ? (
                                /* Rest countdown replaces the log button while resting */
                                <div
                                  style={{
                                    background:
                                      restTimer <= 5
                                        ? "rgba(61,220,132,.1)"
                                        : "var(--surface2)",
                                    border: `1.5px solid ${
                                      restTimer <= 5
                                        ? "var(--success)"
                                        : "var(--border)"
                                    }`,
                                    borderRadius: "var(--r-md)",
                                    padding: "16px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    transition:
                                      "background .3s, border-color .3s",
                                    animation: "slideUp .2s ease",
                                  }}
                                >
                                  <div>
                                    <p
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: 1,
                                        marginBottom: 4,
                                        color:
                                          restTimer === 0
                                            ? "var(--success)"
                                            : restTimer <= 5
                                            ? "var(--success)"
                                            : "var(--muted)",
                                      }}
                                    >
                                      {restTimer === 0
                                        ? "✓ Rest done — go!"
                                        : restTimer <= 5
                                        ? `⚡ Almost...`
                                        : "⏸ Resting"}
                                    </p>
                                    <p
                                      style={{
                                        fontFamily: "'Bebas Neue',sans-serif",
                                        fontSize: restTimer <= 5 ? 42 : 32,
                                        color:
                                          restTimer === 0
                                            ? "var(--success)"
                                            : restTimer <= 5
                                            ? "var(--success)"
                                            : "var(--text)",
                                        lineHeight: 1,
                                        letterSpacing: 1,
                                        transition: "font-size .15s, color .2s",
                                      }}
                                    >
                                      {restTimer === 0
                                        ? "GO!"
                                        : fmtTime(restTimer)}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setRestTimer(null);
                                      setRestActive(false);
                                      setRestActiveExIdx(null);
                                    }}
                                    style={{
                                      background: "var(--surface)",
                                      border: "1px solid var(--border)",
                                      borderRadius: "var(--r-sm)",
                                      padding: "8px 14px",
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: "var(--muted)",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Skip
                                  </button>
                                </div>
                              ) : (
                                <button
                                  disabled={!canLog}
                                  onClick={() => addSet(exIdx)}
                                  style={{
                                    width: "100%",
                                    padding: "16px",
                                    borderRadius: "var(--r-md)",
                                    border: canLog
                                      ? "1.5px solid var(--accent)"
                                      : "1px solid var(--border)",
                                    background: canLog
                                      ? "var(--accent)"
                                      : "var(--surface2)",
                                    color: canLog
                                      ? "var(--bg)"
                                      : "var(--muted)",
                                    fontSize: 15,
                                    fontWeight: 700,
                                    cursor: canLog ? "pointer" : "not-allowed",
                                    fontFamily: "'DM Sans',sans-serif",
                                    transition: "all .15s",
                                  }}
                                >
                                  {canLog ? "Log Set +" : "Enter weight & reps"}
                                </button>
                              )}
                            </div>
                          );
                        })()}

                        {(superChild ||
                          ((inp.selectedType === "working" ||
                            inp.weight ||
                            inp.reps) &&
                            ex.sets.length === 0)) &&
                          (superChild ? (
                            <div
                              style={{
                                background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
                                border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
                                borderRadius: 12,
                                padding: "12px 12px 10px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 8,
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "var(--purple)",
                                  }}
                                >
                                  ↕ {superChild.name}
                                </p>
                                <p
                                  style={{
                                    fontSize: 11,
                                    color: "var(--muted)",
                                  }}
                                >
                                  superset partner
                                </p>
                              </div>

                              {superChildLast &&
                                settings?.showLastSession !== false && (
                                  <div
                                    className="hint-box"
                                    style={{ marginBottom: 8 }}
                                  >
                                    <p className="hint-title">
                                      Last session —{" "}
                                      {superChild.name.split(" (")[0]}:
                                    </p>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 6,
                                        marginTop: 4,
                                      }}
                                    >
                                      {superChildLast.sets.map((s, i) => (
                                        <span key={i} className="hint-chip">
                                          Set {i + 1}: {s.weight}lbs × {s.reps}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                              {superChild.sets.length > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    marginBottom: 8,
                                  }}
                                >
                                  {superChild.sets.map((s, si) => (
                                    <div key={si} className="set-row">
                                      <span
                                        className="set-num-label"
                                        style={{ color: "var(--purple)" }}
                                      >
                                        S{si + 1}
                                      </span>
                                      <span className="set-detail">
                                        {s.weight} lbs × {s.reps} reps
                                      </span>
                                      <button
                                        className="del-btn"
                                        onClick={() =>
                                          deleteSet(superChildIdx, si)
                                        }
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="input-row">
                                <StepperInput
                                  label="Weight (lbs)"
                                  value={superChildInp.weight}
                                  onChange={(v) =>
                                    updateInput(superChildIdx, "weight", v)
                                  }
                                  step={getEquipmentIncrement(
                                    superChild?.key || ""
                                  )}
                                  min={0}
                                  max={999}
                                  placeholder="135"
                                  filled={!!superChildInp.weight}
                                />
                                <StepperInput
                                  label="Reps"
                                  value={superChildInp.reps}
                                  onChange={(v) =>
                                    updateInput(superChildIdx, "reps", v)
                                  }
                                  step={1}
                                  min={1}
                                  max={100}
                                  placeholder="10"
                                  filled={!!superChildInp.reps}
                                />
                              </div>

                              <button
                                disabled={
                                  !superChildInp.weight || !superChildInp.reps
                                }
                                onClick={() => {
                                  const w = parseFloat(superChildInp.weight);
                                  const r = parseInt(superChildInp.reps);
                                  if (!w || !r) return;
                                  setSession((prev) => ({
                                    ...prev,
                                    exercises: prev.exercises.map((e, i) =>
                                      i !== superChildIdx
                                        ? e
                                        : {
                                            ...e,
                                            sets: [
                                              ...e.sets,
                                              {
                                                weight: w,
                                                reps: r,
                                                type: "super",
                                              },
                                            ],
                                          }
                                    ),
                                  }));
                                  setInputs((prev) => ({
                                    ...prev,
                                    [superChildIdx]: { weight: "", reps: "" },
                                  }));
                                }}
                                style={{
                                  width: "100%",
                                  marginTop: 8,
                                  padding: "10px",
                                  borderRadius: 10,
                                  background:
                                    superChildInp.weight && superChildInp.reps
                                      ? "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.2)"
                                      : "var(--surface2)",
                                  border: `1px solid ${
                                    superChildInp.weight && superChildInp.reps
                                      ? "var(--purple)"
                                      : "var(--border)"
                                  }`,
                                  color:
                                    superChildInp.weight && superChildInp.reps
                                      ? "var(--purple)"
                                      : "var(--muted)",
                                  fontSize: 13,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  fontFamily: "'DM Sans',sans-serif",
                                }}
                              >
                                Log Set for {superChild.name.split(" (")[0]}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setSupersetPicking(exIdx)}
                              style={{
                                background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.06)",
                                border: "1px dashed rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
                                borderRadius: 10,
                                padding: "10px",
                                fontSize: 11,
                                fontWeight: 700,
                                color: "var(--purple)",
                                cursor: "pointer",
                                width: "100%",
                                fontFamily: "'DM Sans',sans-serif",
                              }}
                            >
                              <Ic.ChevUp
                                s={12}
                                c="currentColor"
                                style={{
                                  display: "inline",
                                  verticalAlign: "middle",
                                  marginRight: 4,
                                }}
                              />
                              Add Superset Partner
                            </button>
                          ))}

                        {ex.sets.length > 0 &&
                          (finishWarning === exIdx ? (
                            <div
                              style={{
                                background: "rgba(255,213,0,.08)",
                                border: "1px solid rgba(255,213,0,.3)",
                                borderRadius: 10,
                                padding: "10px 12px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: 11,
                                  color: "var(--warn)",
                                  fontWeight: 700,
                                }}
                              >
                                ⚠️ You have unsaved inputs — did you mean to log
                                a set first?
                              </p>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  marginTop: 8,
                                }}
                              >
                                <button
                                  className="btn-set-action normal"
                                  style={{ flex: 1, fontSize: 11 }}
                                  onClick={() => {
                                    addSet(exIdx);
                                    updateInput(exIdx, "selectedType", null);
                                    setFinishWarning(null);
                                    finishExercise(exIdx);
                                  }}
                                >
                                  Log & Finish
                                </button>
                                <button
                                  onClick={() => {
                                    setFinishWarning(null);
                                    finishExercise(exIdx);
                                  }}
                                  style={{
                                    flex: 1,
                                    fontSize: 11,
                                    background: "none",
                                    border: "1px solid var(--border)",
                                    borderRadius: 8,
                                    color: "var(--muted)",
                                    cursor: "pointer",
                                    padding: "6px 10px",
                                  }}
                                >
                                  Finish Anyway
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              className="btn-finish-exercise"
                              onClick={() => {
                                const isBodyweight =
                                  ex.key?.includes("__Bodyweight") ||
                                  ex.name?.includes("(Bodyweight)");
                                const hasUnsaved =
                                  inp.selectedType &&
                                  inp.reps &&
                                  (isBodyweight ||
                                    (inp.weight !== "" &&
                                      inp.weight !== undefined));
                                if (hasUnsaved) {
                                  setFinishWarning(exIdx);
                                } else {
                                  finishExercise(exIdx);
                                }
                              }}
                            >
                              ✓ Finish Exercise — Move to Next
                            </button>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {!addingMuscle &&
            !pickingGroup &&
            (() => {
              const groups = session?.muscleGroup?.split(" + ") || [];
              if (groups.length > 1) {
                return (
                  <div
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: 16,
                      padding: 16,
                    }}
                  >
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        marginBottom: 10,
                        color: "var(--text)",
                      }}
                    >
                      Add exercise for which group?
                    </p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {groups.map((g) => (
                        <button
                          key={g}
                          className="muscle-btn"
                          style={{ flex: 1 }}
                          onClick={() => {
                            setPickingGroup(g);
                            setPicking(true);
                          }}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <button
                  className="btn-add-exercise"
                  onClick={() => {
                    setPickingGroup(groups[0] || session?.muscleGroup);
                    setPicking(true);
                  }}
                >
                  + Add Exercise
                </button>
              );
            })()}

          {!addingMuscle ? (
            <button
              onClick={() => setAddingMuscle(true)}
              style={{
                background: "var(--surface2)",
                border: "1px dashed var(--border)",
                borderRadius: 16,
                padding: "11px",
                color: "var(--muted)",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                width: "100%",
              }}
            >
              + Add Another Muscle Group
            </button>
          ) : (
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 16,
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  marginBottom: 10,
                  color: "var(--text)",
                }}
              >
                Add muscle group to this session
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2,1fr)",
                  gap: 8,
                }}
              >
                {MUSCLE_GROUPS.filter(
                  (g) => !session?.muscleGroup?.includes(g)
                ).map((g) => {
                  const color = GROUP_COLORS[g] || "var(--accent)";
                  return (
                    <button
                      key={g}
                      style={{
                        background: "var(--surface2)",
                        border: `1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.2)`,
                        borderRadius: 12,
                        padding: "12px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        cursor: "pointer",
                        transition: "all .12s",
                      }}
                      onClick={() => {
                        setSession((prev) => ({
                          ...prev,
                          muscleGroup: prev.muscleGroup + " + " + g,
                        }));
                        setPickingGroup(g);
                        setPicking(true);
                        setAddingMuscle(false);
                      }}
                    >
                      <MuscleIcon group={g} size={22} color={color} />
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "var(--text)",
                        }}
                      >
                        {g}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                className="btn-ghost"
                style={{ marginTop: 10 }}
                onClick={() => setAddingMuscle(false)}
              >
                Cancel
              </button>
            </div>
          )}

          {/* ── SESSION NOTE ── */}
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 14px",
                borderBottom: session?.note
                  ? "1px solid var(--border)"
                  : "none",
              }}
            >
              <Ic.Pencil s={14} c="var(--muted)" />
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                Session Note
              </p>
              {session?.note && (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--accent)",
                    fontWeight: 700,
                    background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)",
                    borderRadius: 4,
                    padding: "1px 6px",
                    marginLeft: "auto",
                  }}
                >
                  saved
                </span>
              )}
            </div>
            <textarea
              value={session?.note || ""}
              onChange={(e) =>
                setSession((prev) => ({ ...prev, note: e.target.value }))
              }
              placeholder="How did this session feel? Any notes for next time..."
              rows={
                session?.note
                  ? Math.max(2, (session.note.match(/\n/g) || []).length + 2)
                  : 2
              }
              style={{
                width: "100%",
                padding: "12px 14px",
                background: "transparent",
                border: "none",
                outline: "none",
                resize: "none",
                boxSizing: "border-box",
                color: "var(--text)",
                fontSize: 13,
                lineHeight: 1.6,
                fontFamily: "'DM Sans',sans-serif",
                "::placeholder": { color: "var(--muted)" },
              }}
            />
          </div>
        </div>
      )}

      {tab === "next" && (
        <div
          style={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div>
            <p style={{ fontSize: 18, fontWeight: 700 }}>
              Next Session Targets
            </p>
            <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
              Based on today's sets · Goal: {GOALS[profile.goal]?.label}
            </p>
          </div>
          {exercises.length === 0 && (
            <div
              style={{
                textAlign: "center",
                color: "var(--muted)",
                padding: "32px 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "32px 20px",
                  gap: 12,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)",
                    border: "1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                  }}
                >
                  <Ic.ChartBar s={22} c="currentColor" />
                </div>
                <div>
                  <p
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--text)",
                      marginBottom: 6,
                    }}
                  >
                    No data yet
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      lineHeight: 1.6,
                    }}
                  >
                    Log your first set on the left tab and your next session
                    targets will appear here
                  </p>
                </div>
              </div>
            </div>
          )}
          {exercises.map((ex, i) => {
            const lastSet = ex.sets[ex.sets.length - 1];
            const recs = lastSet
              ? getRecommendations(lastSet, profile.goal, ex.key, sessions)
              : null;
            return (
              <div key={i} className="rec-card">
                <p className="rec-ex-name">{ex.name}</p>
                {!lastSet && (
                  <p style={{ fontSize: 13, color: "var(--muted)" }}>
                    No sets logged yet
                  </p>
                )}
                {lastSet && (
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      marginBottom: 4,
                    }}
                  >
                    Today's last set:{" "}
                    <strong style={{ color: "var(--text)" }}>
                      {lastSet.weight} lbs × {lastSet.reps} reps
                    </strong>
                  </p>
                )}
                {recs &&
                  recs.map((r, ri) => (
                    <div
                      key={ri}
                      className={`rec-option ${
                        ri === 0 ? "primary" : "secondary"
                      }`}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        <span className="rec-big">{r.weight}</span>
                        <span style={{ color: "var(--muted)", fontSize: 13 }}>
                          lbs ×
                        </span>
                        <span className="rec-big">{r.reps}</span>
                        <span style={{ color: "var(--muted)", fontSize: 13 }}>
                          reps
                        </span>
                        <span className="rec-badge">{r.label}</span>
                      </div>
                      <p
                        style={{
                          fontSize: 11,
                          color: "var(--muted)",
                          marginTop: 4,
                        }}
                      >
                        {r.reason}
                      </p>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      )}

      {exerciseInfo && (
        <div className="overlay" onClick={() => setExerciseInfo(null)}>
          <div
            className="picker-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: "85vh", overflowY: "auto" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <p className="picker-title" style={{ flex: 1, paddingRight: 12 }}>
                {exerciseInfo.name}
              </p>
              <button
                onClick={() => setExerciseInfo(null)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 18,
                  cursor: "pointer",
                  color: "var(--muted)",
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>

            {infoLoading && (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <p style={{ color: "var(--muted)", fontSize: 13 }}>
                  Loading exercise info...
                </p>
              </div>
            )}

            {/* Cycling demo image — two frames alternating to simulate movement */}
            {!infoLoading &&
              exerciseInfo.demoFrames &&
              (() => {
                const frames = exerciseInfo.demoFrames;
                return (
                  <ExerciseDemoImage frames={frames} name={exerciseInfo.name} />
                );
              })()}

            {/* External API gif — only shown if no demoFrames but API returned one */}
            {!infoLoading &&
              !exerciseInfo.demoFrames &&
              exerciseInfo.gifUrl && (
                <div
                  style={{
                    borderRadius: "var(--r-sm)",
                    overflow: "hidden",
                    background: "var(--surface2)",
                    textAlign: "center",
                  }}
                >
                  <img
                    src={exerciseInfo.gifUrl}
                    alt={exerciseInfo.name}
                    style={{
                      width: "100%",
                      maxHeight: 280,
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

            {/* No image available */}
            {!infoLoading &&
              !exerciseInfo.demoFrames &&
              !exerciseInfo.gifUrl &&
              !exerciseInfo.error && (
                <div
                  style={{
                    background: "var(--surface2)",
                    borderRadius: "var(--r-sm)",
                    padding: "16px",
                    textAlign: "center",
                  }}
                >
                  <p style={{ fontSize: 13, color: "var(--muted)" }}>
                    No demo available for this exercise yet
                  </p>
                </div>
              )}

            {!infoLoading && exerciseInfo.muscles?.length > 0 && (
              <div>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--accent)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 6,
                  }}
                >
                  Muscles Worked
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {exerciseInfo.muscles.map((m, i) => (
                    <span
                      key={i}
                      className="chip"
                      style={{ color: "var(--text)" }}
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!infoLoading && exerciseInfo.instructions?.length > 0 && (
              <div>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--accent)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  How to Perform
                </p>
                {exerciseInfo.instructions.map((step, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", gap: 10, marginBottom: 10 }}
                  >
                    <span
                      style={{
                        background: "var(--accent)",
                        color: "var(--bg)",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </span>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--text)",
                        lineHeight: 1.5,
                      }}
                    >
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {!infoLoading && (
              <div
                style={{
                  background: "var(--surface2)",
                  borderRadius: 12,
                  padding: "12px 14px",
                  display: "flex",
                  gap: 16,
                }}
              >
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--accent)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Eccentric Phase
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--text)",
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    The lowering / lengthening portion. Control the weight
                    slowly — this is where most muscle damage (growth stimulus)
                    occurs.
                  </p>
                </div>
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--success)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Concentric Phase
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--text)",
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    The lifting / shortening portion. Drive through the movement
                    with power and control.
                  </p>
                </div>
              </div>
            )}

            <button className="btn-ghost" onClick={() => setExerciseInfo(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {picking && (
        <div className="overlay">
          <ExercisePicker
            muscleGroup={pickingGroup || session?.muscleGroup}
            existingKeys={exercises.map((e) => e.key)}
            onAdd={addExercise}
            onCancel={() => {
              setPicking(false);
              setPickingGroup(null);
            }}
          />
        </div>
      )}

      {supersetPicking !== null && (
        <div className="overlay">
          <SupersetPicker
            exercises={exercises}
            supersetIdx={supersetPicking}
            onAdd={(ex) => {
              addSupersetExercise(supersetPicking, { ...ex, sets: [] });
            }}
            onCancel={() => setSupersetPicking(null)}
          />
        </div>
      )}

      {/* ── SWAP EXERCISE SHEET ── */}
      {swapExIdx !== null && (
        <>
          <div
            onClick={() => {
              setSwapExIdx(null);
              setSwapPickedExercise(null);
            }}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,.5)",
              zIndex: 200,
              backdropFilter: "blur(2px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 210,
              background: "var(--surface)",
              borderRadius: "20px 20px 0 0",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 -8px 40px rgba(0,0,0,.4)",
              animation: "slideUpSheet .22s cubic-bezier(.32,.72,0,1)",
            }}
          >
            {/* Handle + header */}
            <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 99,
                  background: "var(--border)",
                  margin: "0 auto 14px",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                    }}
                  >
                    {swapPickedExercise ? "Choose Equipment" : "Swap Exercise"}
                  </p>
                  <p
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "var(--text)",
                      marginTop: 3,
                    }}
                  >
                    {swapPickedExercise
                      ? swapPickedExercise.name
                      : exercises[swapExIdx]?.name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSwapExIdx(null);
                    setSwapPickedExercise(null);
                  }}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    color: "var(--muted)",
                    fontSize: 15,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div
              style={{
                overflowY: "auto",
                padding: "8px 16px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {(() => {
                const curEx = exercises[swapExIdx];
                const sessionGroups = (session?.muscleGroup || "")
                  .split(" + ")
                  .map((g) => g.trim());
                let exMuscle = sessionGroups[0] || "";
                // If this is a combo session, narrow down using the library —
                // but only check the groups actually present in this session,
                // not the entire app, to avoid misattributing exercises with
                // duplicate names across unrelated groups (e.g. "Shrug" exists
                // in both Back and Shoulders).
                if (sessionGroups.length > 1) {
                  for (const g of sessionGroups) {
                    if (
                      (EXERCISE_LIBRARY[g] || []).some((e) =>
                        curEx?.key?.startsWith(e.name)
                      )
                    ) {
                      exMuscle = g;
                      break;
                    }
                  }
                }
                const libExercises = EXERCISE_LIBRARY[exMuscle] || [];
                const currentBaseName = curEx?.name
                  ?.split(" (")[0]
                  .split(" —")[0];
                const suggestions = libExercises.filter(
                  (e) => e.name !== currentBaseName
                );

                // ── STEP 2: equipment picker for the chosen exercise ──
                if (swapPickedExercise) {
                  const sugEx = swapPickedExercise;
                  return (
                    <>
                      <button
                        onClick={() => setSwapPickedExercise(null)}
                        style={{
                          alignSelf: "flex-start",
                          background: "none",
                          border: "none",
                          color: "var(--muted)",
                          fontSize: 13,
                          cursor: "pointer",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        ← Back to exercises
                      </button>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {sugEx.equipment.map((eq) => {
                          const newKey = `${sugEx.name}__${eq}`;
                          const hasHist = getLastExercise(newKey) !== null;
                          return (
                            <button
                              key={eq}
                              onClick={() => {
                                swapExercise(swapExIdx, {
                                  name: `${sugEx.name} (${eq})`,
                                  key: newKey,
                                  group: exMuscle,
                                  sets: [],
                                });
                                setSwapPickedExercise(null);
                              }}
                              style={{
                                width: "100%",
                                textAlign: "left",
                                padding: "14px 16px",
                                borderRadius: "var(--r-sm)",
                                cursor: "pointer",
                                border: hasHist
                                  ? "1.5px solid var(--accent)"
                                  : "1px solid var(--border)",
                                background: hasHist
                                  ? "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)"
                                  : "var(--surface2)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 15,
                                  fontWeight: 700,
                                  color: hasHist
                                    ? "var(--accent)"
                                    : "var(--text)",
                                }}
                              >
                                {eq}
                              </span>
                              {hasHist ? (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "var(--accent)",
                                    fontWeight: 700,
                                    background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.15)",
                                    borderRadius: 6,
                                    padding: "3px 8px",
                                  }}
                                >
                                  Has history
                                </span>
                              ) : (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "var(--muted)",
                                  }}
                                >
                                  New
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  );
                }

                // ── STEP 1: pick an exercise — clean single-column list ──
                return (
                  <>
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                      }}
                    >
                      {exMuscle} exercises
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {suggestions.map((sugEx, i) => {
                        const anyHistory = sugEx.equipment.some(
                          (eq) =>
                            getLastExercise(`${sugEx.name}__${eq}`) !== null
                        );
                        const singleEquip = sugEx.equipment.length === 1;
                        return (
                          <button
                            key={i}
                            onClick={() => {
                              if (singleEquip) {
                                const eq = sugEx.equipment[0];
                                swapExercise(swapExIdx, {
                                  name: `${sugEx.name} (${eq})`,
                                  key: `${sugEx.name}__${eq}`,
                                  group: exMuscle,
                                  sets: [],
                                });
                              } else {
                                setSwapPickedExercise(sugEx);
                              }
                            }}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              padding: "14px 16px",
                              borderRadius: "var(--r-sm)",
                              cursor: "pointer",
                              border: "1px solid var(--border)",
                              background: "var(--surface2)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <p
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: "var(--text)",
                                }}
                              >
                                {sugEx.name}
                              </p>
                              <p
                                style={{
                                  fontSize: 11,
                                  color: "var(--muted)",
                                  marginTop: 2,
                                }}
                              >
                                {sugEx.equipment.join(" · ")}
                              </p>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                flexShrink: 0,
                              }}
                            >
                              {anyHistory && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "var(--accent)",
                                    fontWeight: 700,
                                    background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.15)",
                                    borderRadius: 5,
                                    padding: "2px 6px",
                                  }}
                                >
                                  history
                                </span>
                              )}
                              <span
                                style={{ color: "var(--muted)", fontSize: 15 }}
                              >
                                ›
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Browse all option */}
                    <button
                      onClick={() => {
                        setSwapExIdx(null);
                        setSwapPickedExercise(null);
                        setPickingGroup(exMuscle);
                        setPicking(true);
                      }}
                      style={{
                        background: "var(--surface2)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--r-sm)",
                        padding: "12px 16px",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--text)",
                        cursor: "pointer",
                        textAlign: "left",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      Browse all {exMuscle} exercises
                      <span style={{ color: "var(--muted)", fontSize: 15 }}>
                        →
                      </span>
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* ── SWAP UNDO TOAST ── */}
      {swapUndoVisible && swapUndo && (
        <div
          style={{
            position: "absolute",
            bottom: 90,
            left: 16,
            right: 16,
            zIndex: 220,
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            borderRadius: 14,
            padding: "12px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,.3)",
            animation: "fadeSlideUp .2s ease",
          }}
        >
          <p style={{ fontSize: 13, color: "var(--text)", fontWeight: 700 }}>
            ⇄ Swapped to {exercises[swapUndo.exIdx]?.name?.split(" (")[0]}
          </p>
          <button
            onClick={undoSwap}
            style={{
              background: "var(--accent)",
              border: "none",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--bg)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Undo
          </button>
        </div>
      )}

      {/* ── EXIT CONFIRMATION SHEET ── */}
      {showExitConfirm && (
        <>
          <div
            onClick={() => setShowExitConfirm(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,.5)",
              zIndex: 200,
              backdropFilter: "blur(2px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 210,
              background: "var(--surface)",
              borderRadius: "20px 20px 0 0",
              padding: "20px 20px 40px",
              boxShadow: "0 -8px 40px rgba(0,0,0,.4)",
              animation: "slideUpSheet .22s cubic-bezier(.32,.72,0,1)",
            }}
          >
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 99,
                background: "var(--border)",
                margin: "0 auto 20px",
              }}
            />

            <p
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "var(--text)",
                marginBottom: 6,
              }}
            >
              End workout?
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--muted)",
                marginBottom: 24,
                lineHeight: 1.5,
              }}
            >
              You have{" "}
              {(session?.exercises || []).reduce(
                (a, ex) => a + ex.sets.length,
                0
              )}{" "}
              sets logged. What would you like to do?
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* Save & Exit */}
              <button
                onClick={() => {
                  setShowExitConfirm(false);
                  onFinish(getDuration());
                }}
                style={{
                  background: "var(--accent)",
                  border: "none",
                  borderRadius: 14,
                  padding: "15px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--bg)",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <p style={{ fontSize: 15, fontWeight: 700 }}>Save & Exit</p>
                  <p
                    style={{
                      fontSize: 11,
                      fontWeight: 400,
                      marginTop: 2,
                      opacity: 0.7,
                    }}
                  >
                    Save this partial session to your history
                  </p>
                </div>
                <Ic.Upload s={18} c="currentColor" />
              </button>

              {/* Keep Going */}
              <button
                onClick={() => setShowExitConfirm(false)}
                style={{
                  background: "var(--surface2)",
                  border: "1px solid var(--border)",
                  borderRadius: 14,
                  padding: "15px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--text)",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <p style={{ fontSize: 15, fontWeight: 700 }}>Keep Going</p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      marginTop: 2,
                    }}
                  >
                    Go back to your workout
                  </p>
                </div>
                <Ic.Barbell s={18} c="currentColor" />
              </button>

              {/* Discard */}
              <button
                onClick={() => {
                  setShowExitConfirm(false);
                  onBack(false);
                }}
                style={{
                  background: "none",
                  border: "1px solid rgba(255,85,102,.3)",
                  borderRadius: 14,
                  padding: "13px 15px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--danger)",
                  cursor: "pointer",
                }}
              >
                Discard Workout
              </button>
            </div>
          </div>
        </>
      )}

      <style>{CSS}</style>
    </div>
  );
}
function DailyPrompt({
  profile,
  suggestedGroup,
  recoveryStatus,
  onTrain,
  onChoose,
  onRest,
  onSkip,
  muscleGroups,
  getLastSessionForGroup,
}) {
  const [choosing, setChoosing] = useState(false);

  if (choosing) {
    return (
      <div
        className="screen"
        style={{
                    gap: 16,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ScreenHeader title="What are you training?" />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {muscleGroups.map((g) => {
            const last = getLastSessionForGroup(g);
            const r = recoveryStatus?.[g];
            const color = GROUP_COLORS[g] || "var(--accent)";
            const statusColor = !r?.lastDate
              ? "var(--muted)"
              : r.status === "ready"
              ? "var(--success)"
              : r.status === "almost"
              ? "var(--accent)"
              : "var(--danger)";
            const statusText = !r?.lastDate
              ? ""
              : r.status === "ready"
              ? "✓ Ready"
              : `${r.hoursRemaining}h left`;
            return (
              <button
                key={g}
                onClick={() => onChoose(g)}
                style={{
                  background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.094)`,
                  border: `1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.333)`,
                  borderRadius: 16,
                  padding: "12px 16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <div style={{ textAlign: "left" }}>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: "var(--text)",
                    }}
                  >
                    {g}
                  </span>
                  {last && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginTop: 2,
                      }}
                    >
                      Last: {last.date}
                    </p>
                  )}
                </div>
                {statusText && (
                  <span
                    style={{
                      fontSize: 11,
                      color: statusColor,
                      fontWeight: 700,
                    }}
                  >
                    {statusText}
                  </span>
                )}
              </button>
            );
          })}
          <button
            onClick={onRest}
            style={{
              background: "var(--surface2)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: "14px 16px",
              color: "var(--muted)",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <Ic.Moon s={22} c="currentColor" /> Rest Day
          </button>
        </div>
        <button className="btn-ghost" onClick={onSkip}>
          Skip for now
        </button>
        <style>{CSS}</style>
      </div>
    );
  }

  return (
    <div
      className="screen"
      style={{
                gap: 24,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <div>
        <p
          style={{
            fontSize: 13,
            color: "var(--accent)",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 2,
          }}
        >
          Good{" "}
          {new Date().getHours() < 12
            ? "Morning"
            : new Date().getHours() < 17
            ? "Afternoon"
            : "Evening"}
        </p>
        <p style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>
          {profile.name}
        </p>
        <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 8 }}>
          Ready to train today?
        </p>
      </div>

      <div
        style={{
          width: "100%",
          background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
          border: "1px solid var(--accent)",
          borderRadius: 20,
          padding: "24px 20px",
        }}
      >
        <p
          style={{
            fontSize: 11,
            color: "var(--accent)",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          Suggested Today
        </p>
        <p
          style={{
            fontSize: 40,
            fontWeight: 700,
            fontFamily: "'Bebas Neue',sans-serif",
            letterSpacing: 2,
            marginTop: 8,
          }}
        >
          {suggestedGroup}
        </p>
        <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
          {recoveryStatus?.[suggestedGroup]?.status === "ready"
            ? `Fully recovered — ${recoveryStatus[suggestedGroup].hoursRested}h since last session ✓`
            : "Based on your training pattern"}
        </p>
        <button
          className="btn-primary"
          style={{ marginTop: 16 }}
          onClick={() => onTrain(suggestedGroup)}
        >
          Train {suggestedGroup} Today
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          width: "100%",
        }}
      >
        <button
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: "16px",
            color: "var(--text)",
            fontWeight: 700,
            cursor: "pointer",
            fontSize: 13,
          }}
          onClick={() => setChoosing(true)}
        >
          Choose a Different Muscle Group
        </button>
        <button
          style={{
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: "16px",
            color: "var(--muted)",
            fontWeight: 700,
            cursor: "pointer",
            fontSize: 13,
          }}
          onClick={onRest}
        >
          <Ic.Moon s={22} c="currentColor" /> Rest Day
        </button>
        <button className="btn-ghost" onClick={onSkip}>
          Skip for now
        </button>
      </div>
      <style>{CSS}</style>
    </div>
  );
}

function HistoryScreen({
  sessions,
  foodLogs,
  cardioLogs,
  restDays,
  onBack,
  onUpdateSession,
  onRepeatSession,
  profile,
}) {
  const [viewMode, setViewMode] = useState("calendar");
  const [selectedDate, setSelectedDate] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date());
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState("weight");
  const [editingSession, setEditingSession] = useState(null);
  const [editInputs, setEditInputs] = useState({});
  const [undoStack, setUndoStack] = useState(null);
  const [undoVisible, setUndoVisible] = useState(false);
  const [repeatConfirm, setRepeatConfirm] = useState(null);
  const [filterGroup, setFilterGroup] = useState("All");

  const sessionsByDate = {};
  sessions.forEach((s) => {
    if (!sessionsByDate[s.date]) sessionsByDate[s.date] = [];
    sessionsByDate[s.date].push(s);
  });

  const cardioByDate = {};
  (cardioLogs || []).forEach((c) => {
    if (!cardioByDate[c.date]) cardioByDate[c.date] = [];
    cardioByDate[c.date].push(c);
  });

  const restDaySet = new Set((restDays || []).map((r) => r.date));

  const year = calMonth.getFullYear();
  const month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = calMonth.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const prevMonth = () => setCalMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCalMonth(new Date(year, month + 1, 1));

  const todayStr = new Date().toLocaleDateString();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dateStr = (d) => new Date(year, month, d).toLocaleDateString();

  const selectedSessions = selectedDate
    ? sessionsByDate[selectedDate] || []
    : [];
  // Same filter as the Nutrition screen — historical cardio burn entries must
  // not skew past-day totals in History either.
  const selectedFood = selectedDate
    ? (foodLogs[selectedDate] || []).filter(
        (e) => !(e && e.cal < 0 && !e.p && !e.c && !e.f)
      )
    : [];
  const selectedCardio = selectedDate ? cardioByDate[selectedDate] || [] : [];
  const selectedIsRest = selectedDate ? restDaySet.has(selectedDate) : false;
  const foodTotals = selectedFood.reduce(
    (a, e) => ({
      cal: a.cal + e.cal,
      p: a.p + e.p,
      c: a.c + e.c,
      f: a.f + e.f,
    }),
    { cal: 0, p: 0, c: 0, f: 0 }
  );

  // ── EDIT SESSION ──
  const startEdit = (session) => {
    setEditingSession(JSON.parse(JSON.stringify(session)));
    setEditInputs({});
  };

  const saveEdit = () => {
    onUpdateSession(editingSession);
    setEditingSession(null);
  };

  const deleteSetFromEdit = (exIdx, setIdx) => {
    const prev = JSON.parse(JSON.stringify(editingSession));
    const updated = { ...editingSession };
    updated.exercises = updated.exercises.map((ex, i) =>
      i !== exIdx
        ? ex
        : { ...ex, sets: ex.sets.filter((_, si) => si !== setIdx) }
    );
    setEditingSession(updated);
    setUndoStack(prev);
    setUndoVisible(true);
    setTimeout(() => setUndoVisible(false), 5000);
  };

  const addSetToEdit = (exIdx) => {
    const inp = editInputs[exIdx] || {};
    const weight = parseFloat(inp.weight);
    const reps = parseInt(inp.reps);
    if (!weight || !reps) return;
    const updated = { ...editingSession };
    updated.exercises = updated.exercises.map((ex, i) =>
      i !== exIdx ? ex : { ...ex, sets: [...ex.sets, { weight, reps }] }
    );
    setEditingSession(updated);
    setEditInputs((prev) => ({ ...prev, [exIdx]: { weight: "", reps: "" } }));
  };

  const doUndo = () => {
    if (undoStack) {
      setEditingSession(undoStack);
      setUndoStack(null);
      setUndoVisible(false);
    }
  };

  // ── EDIT VIEW ──
  if (editingSession) {
    return (
      <div className="screen" style={{ padding: 0 }}>
        <div className="log-header">
          <button
            className="btn-ghost sm"
            onClick={() => setEditingSession(null)}
          >
            ← Cancel
          </button>
          <p style={{ fontWeight: 700, fontSize: 15 }}>
            Edit — {editingSession.muscleGroup}
          </p>
          <button className="btn-finish" onClick={saveEdit}>
            Save ✓
          </button>
        </div>
        {undoVisible && (
          <div
            style={{
              background: "var(--surface2)",
              borderBottom: "1px solid var(--border)",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <p style={{ fontSize: 13, color: "var(--text)" }}>Set deleted</p>
            <button
              onClick={doUndo}
              style={{
                background: "var(--accent)",
                color: "var(--bg)",
                border: "none",
                borderRadius: 8,
                padding: "6px 14px",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Undo
            </button>
          </div>
        )}
        <div
          style={{
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            overflowY: "auto",
          }}
        >
          {editingSession.exercises.map((ex, exIdx) => (
            <div key={exIdx} className="ex-card open">
              <div className="ex-header">
                <p className="ex-name">{ex.name}</p>
                <p style={{ fontSize: 11, color: "var(--muted)" }}>
                  {ex.sets.length} sets
                </p>
              </div>
              <div className="ex-body">
                {ex.sets.map((s, si) => (
                  <div key={si} className="set-row">
                    <span className="set-num-label">S{si + 1}</span>
                    <span className="set-detail">
                      {s.weight} lbs × {s.reps} reps
                    </span>
                    <button
                      className="del-btn"
                      onClick={() => deleteSetFromEdit(exIdx, si)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div className="input-row" style={{ marginTop: 8 }}>
                  <div className="input-group">
                    <label className="input-label">Weight (lbs)</label>
                    <input
                      className="num-input"
                      type="number"
                      inputMode="decimal"
                      placeholder="135"
                      value={(editInputs[exIdx] || {}).weight || ""}
                      onChange={(e) =>
                        setEditInputs((p) => ({
                          ...p,
                          [exIdx]: {
                            ...(p[exIdx] || {}),
                            weight: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">Reps</label>
                    <input
                      className="num-input"
                      type="number"
                      inputMode="numeric"
                      placeholder="10"
                      value={(editInputs[exIdx] || {}).reps || ""}
                      onChange={(e) =>
                        setEditInputs((p) => ({
                          ...p,
                          [exIdx]: {
                            ...(p[exIdx] || {}),
                            reps: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                  <button
                    className="btn-add-set"
                    disabled={
                      !(editInputs[exIdx] || {}).weight ||
                      !(editInputs[exIdx] || {}).reps
                    }
                    onClick={() => addSetToEdit(exIdx)}
                  >
                    + Set
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  // ── REPEAT CONFIRM VIEW ──
  if (repeatConfirm) {
    const s = repeatConfirm;
    const color =
      GROUP_COLORS[s.muscleGroup?.split(" + ")[0]] || "var(--accent)";
    return (
      <div
        className="screen"
        style={{
                    display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <button
          className="btn-ghost sm"
          style={{ alignSelf: "flex-start" }}
          onClick={() => setRepeatConfirm(null)}
        >
          ← Back
        </button>
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <p
            style={{
              fontSize: 13,
              color: "var(--text)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Repeat Workout
          </p>
          <p style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>
            {s.muscleGroup}
          </p>
          <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
            Originally done {s.date}
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {s.exercises.map((ex, i) => {
            const validSets = ex.sets;
            const bestSet =
              validSets.length > 0
                ? validSets.reduce(
                    (best, s) => (s.weight > best.weight ? s : best),
                    validSets[0]
                  )
                : null;
            return (
              <div
                key={i}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 12,
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                    }}
                  >
                    {ex.name}
                  </p>
                  {bestSet && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--text)",
                        fontWeight: 700,
                      }}
                    >
                      {bestSet.weight}×{bestSet.reps}
                    </p>
                  )}
                </div>
                <p
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}
                >
                  {validSets.length} sets
                </p>
              </div>
            );
          })}
        </div>
        <button
          onClick={() => {
            onRepeatSession(s);
            setRepeatConfirm(null);
          }}
          style={{
            background: "var(--accent)",
            border: "none",
            borderRadius: 14,
            padding: "16px",
            fontSize: 15,
            fontWeight: 700,
            color: "var(--bg)",
            cursor: "pointer",
          }}
        >
          Let's Go — Start This Workout
        </button>
        <button className="btn-ghost" onClick={() => setRepeatConfirm(null)}>
          Cancel
        </button>
        <style>{CSS}</style>
      </div>
    );
  }

  // ── MONTH STATS (used in calendar header) ──
  const monthSessions = sessions.filter((s) => {
    const d = new Date(s.id || s.date);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  const monthSets = monthSessions.reduce(
    (a, s) => a + s.exercises.reduce((b, ex) => b + ex.sets.length, 0),
    0
  );
  const monthWorkouts = monthSessions.length;

  // ── WEEK BUILDER ──
  const weeks = [];
  let week = [];
  cells.forEach((d) => {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  });
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  // ── MAIN LAYOUT ──
  return (
    <div className="screen" style={{ padding: 0, position: "relative" }}>
      <div
        className="log-header"
        style={{
          flexDirection: "column",
          gap: 0,
          padding: "48px 16px 0",
          alignItems: "stretch",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <button className="btn-ghost sm" onClick={onBack}>
            ← Back
          </button>
          <p style={{ fontWeight: 700, fontSize: 18 }}>History</p>
          <div style={{ width: 60 }} />
        </div>
        <div
          style={{ display: "flex", borderBottom: "1px solid var(--border)" }}
        >
          {[
            { key: "calendar", label: "Calendar" },
            { key: "list", label: "List" },
            { key: "progress", label: "Progress" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setViewMode(t.key);
                setSelectedDate(null);
                setSelectedExercise(null);
              }}
              style={{
                flex: 1,
                padding: "10px 4px",
                background: "none",
                border: "none",
                borderBottom:
                  viewMode === t.key
                    ? "2px solid var(--accent)"
                    : "2px solid transparent",
                color: viewMode === t.key ? "var(--accent)" : "var(--muted)",
                fontSize: 13,
                fontWeight: viewMode === t.key ? 700 : 500,
                cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
                marginBottom: -1,
                transition: "color .15s",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ════════════════════════════════
          CALENDAR VIEW
          ════════════════════════════════ */}
      {viewMode === "calendar" && (
        <div style={{ padding: "16px 12px 100px", overflowY: "auto" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <button
              onClick={prevMonth}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                color: "var(--text)",
                fontSize: 18,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ‹
            </button>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  color: "var(--text)",
                  letterSpacing: -0.3,
                }}
              >
                {monthName}
              </p>
              <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                {monthWorkouts > 0
                  ? `${monthWorkouts} workout${
                      monthWorkouts !== 1 ? "s" : ""
                    } · ${monthSets} sets`
                  : "No workouts this month"}
              </p>
            </div>
            <button
              onClick={nextMonth}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                color: "var(--text)",
                fontSize: 18,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ›
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7,1fr)",
              marginBottom: 8,
            }}
          >
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <p
                key={d}
                style={{
                  textAlign: "center",
                  fontSize: 11,
                  color: "var(--muted)",
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                {d}
              </p>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {weeks.map((wk, wi) => {
              // Week training count for the strip
              const wkSets = wk.reduce((total, d) => {
                if (!d) return total;
                const ds = dateStr(d);
                const sess = sessionsByDate[ds] || [];
                return (
                  total +
                  sess.reduce(
                    (a, s) =>
                      a + s.exercises.reduce((b, ex) => b + ex.sets.length, 0),
                    0
                  )
                );
              }, 0);
              const wkColor =
                wkSets === 0
                  ? "transparent"
                  : wkSets >= 20
                  ? "var(--success)"
                  : wkSets >= 10
                  ? "var(--accent)"
                  : "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35)";

              return (
                <div key={wi}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7,1fr)",
                      gap: 4,
                    }}
                  >
                    {wk.map((d, di) => {
                      if (!d) return <div key={di} />;
                      const ds = dateStr(d);
                      const hasSess = (sessionsByDate[ds] || []).length > 0;
                      const hasCard = (cardioByDate[ds] || []).length > 0;
                      const isRest = restDaySet.has(ds);
                      const isToday = ds === todayStr;
                      const isSelected = ds === selectedDate;

                      // Simplified: one color per day, one dot
                      const dotColor = hasSess
                        ? "var(--accent)"
                        : hasCard
                        ? "var(--info)"
                        : isRest
                        ? "var(--purple)"
                        : null;

                      const hasActivity = hasSess || hasCard || isRest;

                      return (
                        <button
                          key={di}
                          onClick={() =>
                            setSelectedDate(ds === selectedDate ? null : ds)
                          }
                          style={{
                            aspectRatio: "1",
                            borderRadius: 12,
                            cursor: "pointer",
                            background: isSelected
                              ? "var(--accent)"
                              : hasActivity
                              ? "var(--surface2)"
                              : "var(--surface)",
                            border:
                              isToday && !isSelected
                                ? "2px solid var(--accent)"
                                : isSelected
                                ? "2px solid var(--accent)"
                                : "1px solid var(--border)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                            padding: "4px 2px",
                            transition: "transform .1s, background .15s",
                          }}
                          onTouchStart={(e) =>
                            (e.currentTarget.style.transform = "scale(.88)")
                          }
                          onTouchEnd={(e) =>
                            (e.currentTarget.style.transform = "scale(1)")
                          }
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: isToday || hasActivity ? 700 : 400,
                              lineHeight: 1,
                              color: isSelected
                                ? "var(--bg)"
                                : isToday
                                ? "var(--accent)"
                                : hasActivity
                                ? "var(--text)"
                                : "var(--muted)",
                            }}
                          >
                            {d}
                          </span>

                          {dotColor && !isSelected && (
                            <span
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                background: dotColor,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {isSelected && hasActivity && (
                            <span
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                background: "var(--bg)",
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 3,
                      marginBottom: 2,
                      paddingLeft: 2,
                      paddingRight: 2,
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: 2.5,
                        borderRadius: 99,
                        background: "var(--border)",
                      }}
                    >
                      <div
                        style={{
                          width:
                            wkSets > 0
                              ? `${Math.min(100, (wkSets / 25) * 100)}%`
                              : "0%",
                          height: "100%",
                          borderRadius: 99,
                          background: wkColor,
                          transition: "width .3s",
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        minWidth: 44,
                        textAlign: "right",
                        flexShrink: 0,
                      }}
                    >
                      {wkSets > 0 ? `${wkSets} sets` : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              gap: "8px 16px",
              flexWrap: "wrap",
              padding: "10px 12px",
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 12,
            }}
          >
            {[
              ["var(--accent)", "Workout"],
              ["var(--info)", "Cardio"],
              ["var(--purple)", "Rest Day"],
            ].map(([color, label]) => (
              <div
                key={label}
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: color,
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    fontWeight: 400,
                  }}
                >
                  {label}
                </span>
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div
                style={{
                  width: 22,
                  height: 3,
                  borderRadius: 99,
                  background: "var(--accent)",
                }}
              />
              <span
                style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}
              >
                Weekly volume
              </span>
            </div>
          </div>

          {monthWorkouts > 0 &&
            (() => {
              const freq = {};
              monthSessions.forEach((s) => {
                s.muscleGroup?.split(" + ").forEach((g) => {
                  const key = g.trim();
                  freq[key] = (freq[key] || 0) + 1;
                });
              });
              const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]);
              const missing = MUSCLE_GROUPS.filter(
                (g) => !freq[g] && g !== "Full Body"
              );
              return (
                <div
                  style={{
                    marginTop: 12,
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    padding: "12px",
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                      marginBottom: 10,
                    }}
                  >
                    This Month
                  </p>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 7 }}
                  >
                    {sorted.map(([g, count]) => {
                      const maxCount = sorted[0][1];
                      return (
                        <div
                          key={g}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "var(--text)",
                              width: 78,
                              flexShrink: 0,
                            }}
                          >
                            {g}
                          </span>
                          <div
                            style={{
                              flex: 1,
                              height: 5,
                              background: "var(--surface2)",
                              borderRadius: 99,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${(count / maxCount) * 100}%`,
                                height: "100%",
                                background: "var(--accent)",
                                borderRadius: 99,
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              flexShrink: 0,
                              width: 42,
                              textAlign: "right",
                            }}
                          >
                            {count}×
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {missing.length > 0 && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginTop: 10,
                        lineHeight: 1.5,
                      }}
                    >
                      Not trained: {missing.join(", ")}
                    </p>
                  )}
                  {missing.length === 0 && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--success)",
                        marginTop: 10,
                      }}
                    >
                      ✓ All muscle groups trained this month
                    </p>
                  )}
                </div>
              );
            })()}
        </div>
      )}

      {/* ════════════════════════════════
          LIST VIEW
          ════════════════════════════════ */}
      {viewMode === "list" && (
        <div
          style={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <p
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--text)",
                flexShrink: 0,
              }}
            >
              Filter:
            </p>
            <div
              style={{
                display: "flex",
                gap: 6,
                overflowX: "auto",
                paddingBottom: 4,
                flex: 1,
              }}
            >
              {["All", ...MUSCLE_GROUPS, "Cardio", "Rest Day"].map((g) => (
                <button
                  key={g}
                  onClick={() => setFilterGroup(g)}
                  style={{
                    flexShrink: 0,
                    padding: "5px 12px",
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    border: "1px solid",
                    background:
                      filterGroup === g
                        ? g === "Cardio"
                          ? "var(--info)"
                          : g === "Rest Day"
                          ? "var(--purple)"
                          : "var(--accent)"
                        : "var(--surface2)",
                    color: filterGroup === g ? "var(--bg)" : "var(--muted)",
                    borderColor:
                      filterGroup === g
                        ? g === "Cardio"
                          ? "var(--info)"
                          : g === "Rest Day"
                          ? "var(--purple)"
                          : "var(--accent)"
                        : "var(--border)",
                  }}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {(() => {
            const allItems = [
              ...[...sessions].map((s) => ({
                type: "workout",
                data: s,
                ts: s.id || new Date(s.date).getTime(),
              })),
              ...(cardioLogs || []).map((c) => ({
                type: "cardio",
                data: c,
                ts: c.id || new Date(c.date).getTime(),
              })),
              ...(restDays || []).map((r) => ({
                type: "rest",
                data: r,
                ts: r.id || new Date(r.date).getTime(),
              })),
            ].sort((a, b) => b.ts - a.ts);

            const filtered = allItems.filter((item) => {
              if (filterGroup === "All") return true;
              if (filterGroup === "Cardio") return item.type === "cardio";
              if (filterGroup === "Rest Day") return item.type === "rest";
              if (item.type === "workout")
                return item.data.muscleGroup
                  ?.split(" + ")
                  .some((g) => g.trim() === filterGroup);
              return false;
            });

            if (filtered.length === 0)
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "56px 24px",
                    gap: 16,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background: "var(--surface2)",
                      border: "1px solid var(--border)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 28,
                    }}
                  >
                    {filterGroup === "Cardio" ? (
                      <Ic.Run s={16} c="currentColor" />
                    ) : filterGroup === "Rest Day" ? (
                      <Ic.Moon s={16} c="currentColor" />
                    ) : (
                      <Ic.Barbell s={16} c="currentColor" />
                    )}
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "var(--text)",
                        marginBottom: 8,
                      }}
                    >
                      {filterGroup === "All"
                        ? "No sessions logged yet"
                        : `No ${filterGroup} logged yet`}
                    </p>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--muted)",
                        lineHeight: 1.7,
                      }}
                    >
                      {filterGroup === "All"
                        ? "Your workout history will appear here after your first session"
                        : filterGroup === "Cardio"
                        ? "Start a cardio session from the home screen"
                        : filterGroup === "Rest Day"
                        ? "Log a rest day from the home screen"
                        : `Complete a ${filterGroup} workout to see it here`}
                    </p>
                  </div>
                </div>
              );

            return filtered.map((item, i) => {
              if (item.type === "rest") {
                const r = item.data;
                return (
                  <div
                    key={i}
                    style={{
                      background: "rgba(162,155,254,.08)",
                      border: "1px solid rgba(162,155,254,.25)",
                      borderRadius: 16,
                      padding: 16,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <span style={{ fontSize: 22 }}>
                        <Ic.Moon s={22} c="currentColor" />
                      </span>
                      <div>
                        <p
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: "var(--purple)",
                          }}
                        >
                          Rest Day
                        </p>
                        <p
                          style={{
                            fontSize: 11,
                            color: "var(--muted)",
                            marginTop: 2,
                          }}
                        >
                          {r.date}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }

              if (item.type === "cardio") {
                const c = item.data;
                return (
                  <div
                    key={i}
                    style={{
                      background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)",
                      border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
                      borderRadius: 16,
                      padding: 16,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <p
                        style={{
                          fontWeight: 700,
                          fontSize: 15,
                          color: "var(--info)",
                        }}
                      >
                        <Ic.Run s={22} c="currentColor" /> {c.activity}
                      </p>
                      <p style={{ fontSize: 11, color: "var(--muted)" }}>
                        {c.date}
                      </p>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <div className="stat-box" style={{ flex: 1 }}>
                        <span
                          className="stat-num"
                          style={{ fontSize: 18, color: "var(--info)" }}
                        >
                          <CountUp value={c.duration} />m
                        </span>
                        <span className="stat-lbl">Duration</span>
                      </div>
                      <div className="stat-box" style={{ flex: 1 }}>
                        <span
                          className="stat-num"
                          style={{ fontSize: 18, color: "var(--danger)" }}
                        >
                          ~<CountUp value={c.caloriesBurned} />
                        </span>
                        <span className="stat-lbl">kcal burned</span>
                      </div>
                    </div>
                  </div>
                );
              }

              const s = item.data;
              const color =
                GROUP_COLORS[s.muscleGroup?.split(" + ")[0]] || "var(--accent)";
              const timeRange = fmtSessionTimeRange(
                s,
                profile?.settings?.timeFormat || "12hr"
              );
              return (
                <div
                  key={i}
                  style={{
                    background: "var(--surface)",
                    border: `1px solid var(--border)`,
                    borderLeft: `3px solid ${color}`,
                    borderRadius: 16,
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <p
                      style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: "var(--text)",
                      }}
                    >
                      {s.muscleGroup}
                    </p>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontSize: 11, color: "var(--muted)" }}>
                        {s.date}
                      </p>
                      {s.duration && (
                        <p style={{ fontSize: 11, color: "var(--muted)" }}>
                          <Ic.Timer s={22} c="currentColor" />{" "}
                          {Math.floor(s.duration / 60)}m
                        </p>
                      )}
                      {timeRange && (
                        <p style={{ fontSize: 11, color: "var(--muted)" }}>
                          {timeRange}
                        </p>
                      )}
                    </div>
                  </div>
                  {s.exercises.map((ex, ei) => (
                    <div key={ei} style={{ marginTop: 6 }}>
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "var(--text)",
                        }}
                      >
                        {ex.name}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 4,
                          marginTop: 4,
                        }}
                      >
                        {ex.sets.map((set, si) => (
                          <span key={si} className="chip">
                            {set.weight}×{set.reps}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {s.note && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "8px 10px",
                        background: "var(--surface2)",
                        borderRadius: 10,
                        borderLeft: "2px solid var(--border)",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 11,
                          color: "var(--muted)",
                          fontStyle: "italic",
                          lineHeight: 1.5,
                        }}
                      >
                        "{s.note}"
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => setRepeatConfirm(s)}
                    style={{
                      marginTop: 12,
                      width: "100%",
                      background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.082)`,
                      border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
                      borderRadius: 10,
                      padding: "10px",
                      fontSize: 13,
                      color: "var(--text)",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    ↺ Repeat This Workout
                  </button>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ════════════════════════════════
          PROGRESS VIEW
          ════════════════════════════════ */}
      {viewMode === "progress" && (
        <ProgressView
          sessions={sessions}
          selectedExercise={selectedExercise}
          setSelectedExercise={setSelectedExercise}
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
        />
      )}

      {/* ════════════════════════════════
          DAY DETAIL — BOTTOM SHEET
          Slides up over the calendar
          ════════════════════════════════ */}
      {selectedDate && viewMode === "calendar" && (
        <>
          <div
            onClick={() => setSelectedDate(null)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,.45)",
              zIndex: 40,
              backdropFilter: "blur(2px)",
            }}
          />

          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 50,
              background: "var(--surface)",
              borderRadius: "20px 20px 0 0",
              boxShadow: "0 -8px 40px rgba(0,0,0,.4)",
              maxHeight: "75vh",
              display: "flex",
              flexDirection: "column",
              animation: "slideUpSheet .25s cubic-bezier(.32,.72,0,1)",
            }}
          >
            <div style={{ padding: "12px 16px 0", flexShrink: 0 }}>
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 99,
                  background: "var(--border)",
                  margin: "0 auto 14px",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 12,
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                    }}
                  >
                    {new Date(selectedDate).toLocaleDateString("default", {
                      weekday: "long",
                    })}
                  </p>
                  <p
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "var(--text)",
                      letterSpacing: -0.3,
                    }}
                  >
                    {new Date(selectedDate).toLocaleDateString("default", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedDate(null)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    color: "var(--muted)",
                    fontSize: 15,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>

              {(selectedSessions.length > 0 || selectedCardio.length > 0) &&
                (() => {
                  const totalSets = selectedSessions.reduce(
                    (a, s) =>
                      a + s.exercises.reduce((b, ex) => b + ex.sets.length, 0),
                    0
                  );
                  const totalVol = selectedSessions.reduce(
                    (a, s) =>
                      a +
                      s.exercises.reduce(
                        (b, ex) =>
                          b +
                          ex.sets.reduce(
                            (c, s) => c + (s.weight || 0) * s.reps,
                            0
                          ),
                        0
                      ),
                    0
                  );
                  return (
                    <div
                      style={{
                        display: "flex",
                        gap: 16,
                        paddingBottom: 12,
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      {totalSets > 0 && (
                        <div>
                          <p
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: "var(--accent)",
                              lineHeight: 1,
                            }}
                          >
                            {totalSets}
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 2,
                            }}
                          >
                            sets
                          </p>
                        </div>
                      )}
                      {totalVol > 0 && (
                        <div>
                          <p
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: "var(--accent)",
                              lineHeight: 1,
                            }}
                          >
                            {totalVol >= 1000
                              ? `${(totalVol / 1000).toFixed(1)}k`
                              : totalVol}
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 2,
                            }}
                          >
                            lbs volume
                          </p>
                        </div>
                      )}
                      {selectedCardio.length > 0 && (
                        <div>
                          <p
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: "var(--info)",
                              lineHeight: 1,
                            }}
                          >
                            {selectedCardio.reduce((a, c) => a + c.duration, 0)}
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 2,
                            }}
                          >
                            min cardio
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>

            <div
              style={{
                overflowY: "auto",
                padding: "12px 16px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {selectedSessions.length === 0 &&
                selectedFood.length === 0 &&
                selectedCardio.length === 0 &&
                !selectedIsRest && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px 0",
                      color: "var(--muted)",
                    }}
                  >
                    <p style={{ fontSize: 28, marginBottom: 10 }}>
                      <Ic.Calendar s={22} c="currentColor" />
                    </p>
                    <p
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "var(--text)",
                      }}
                    >
                      No activity
                    </p>
                    <p style={{ fontSize: 11, marginTop: 6 }}>
                      Rest is training too
                    </p>
                  </div>
                )}

              {selectedIsRest &&
                selectedSessions.length === 0 &&
                selectedCardio.length === 0 && (
                  <div
                    style={{
                      background: "rgba(162,155,254,.1)",
                      border: "1.5px solid rgba(162,155,254,.3)",
                      borderRadius: 16,
                      padding: 20,
                      textAlign: "center",
                    }}
                  >
                    <p style={{ fontSize: 28 }}>
                      <Ic.Moon s={22} c="currentColor" />
                    </p>
                    <p
                      style={{
                        fontWeight: 700,
                        fontSize: 18,
                        color: "var(--purple)",
                        marginTop: 8,
                      }}
                    >
                      Rest Day
                    </p>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--muted)",
                        marginTop: 6,
                        lineHeight: 1.6,
                      }}
                    >
                      Recovery is where growth happens.
                    </p>
                  </div>
                )}

              {selectedCardio.map((c, i) => (
                <div
                  key={i}
                  style={{
                    background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)",
                    border: "1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35)",
                    borderRadius: 16,
                    padding: 16,
                  }}
                >
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: "var(--info)",
                      marginBottom: 10,
                    }}
                  >
                    <Ic.Run s={22} c="currentColor" /> {c.activity}
                  </p>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3,1fr)",
                      gap: 8,
                    }}
                  >
                    {[
                      {
                        label: "Duration",
                        val: `${c.duration}m`,
                        color: "var(--info)",
                      },
                      {
                        label: "Calories",
                        val: `~${c.caloriesBurned}`,
                        color: "var(--danger)",
                      },
                      {
                        label: "Intensity",
                        val: c.intensity || "Moderate",
                        color: "var(--accent)",
                      },
                    ].map((m) => (
                      <div
                        key={m.label}
                        className="stat-box"
                        style={{ padding: "8px 6px" }}
                      >
                        <span
                          className="stat-num"
                          style={{ fontSize: 15, color: m.color }}
                        >
                          <CountUp value={m.val} />
                        </span>
                        <span className="stat-lbl">{m.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {selectedSessions.map((s, i) => {
                const color =
                  GROUP_COLORS[s.muscleGroup?.split(" + ")[0]] ||
                  "var(--accent)";
                const working = s.exercises.reduce(
                  (a, ex) => a + ex.sets.length,
                  0
                );
                const vol = s.exercises.reduce(
                  (a, ex) =>
                    a +
                    ex.sets.reduce((b, s) => b + (s.weight || 0) * s.reps, 0),
                  0
                );
                const timeRange = fmtSessionTimeRange(
                  s,
                  profile?.settings?.timeFormat || "12hr"
                );
                return (
                  <div
                    key={i}
                    style={{
                      background: "var(--surface2)",
                      border: `1px solid var(--border)`,
                      borderLeft: `3px solid ${color}`,
                      borderRadius: 16,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "14px 16px 10px",
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div>
                          <p
                            style={{
                              fontWeight: 700,
                              fontSize: 18,
                              color: "var(--text)",
                              letterSpacing: -0.3,
                            }}
                          >
                            {s.muscleGroup}
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 2,
                            }}
                          >
                            {s.duration
                              ? `<Ic.Timer s={22} c="currentColor" /> ${Math.floor(
                                  s.duration / 60
                                )}m ${s.duration % 60}s`
                              : ""}
                            {timeRange ? `  ${timeRange}` : ""}
                          </p>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => startEdit(s)}
                            style={{
                              background: "var(--surface)",
                              border: "1px solid var(--border)",
                              borderRadius: 8,
                              padding: "5px 10px",
                              fontSize: 11,
                              color: "var(--text)",
                              cursor: "pointer",
                              fontWeight: 700,
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setRepeatConfirm(s)}
                            style={{
                              background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.1)`,
                              border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.3)`,
                              borderRadius: 8,
                              padding: "5px 10px",
                              fontSize: 11,
                              color,
                              cursor: "pointer",
                              fontWeight: 700,
                            }}
                          >
                            Repeat
                          </button>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3,1fr)",
                          gap: 8,
                          marginTop: 10,
                        }}
                      >
                        {[
                          { val: working, label: "Sets" },
                          {
                            val: s.exercises.reduce(
                              (a, ex) =>
                                a + ex.sets.reduce((b, s) => b + s.reps, 0),
                              0
                            ),
                            label: "Reps",
                          },
                          {
                            val:
                              vol >= 1000 ? `${(vol / 1000).toFixed(1)}k` : vol,
                            label: "Volume",
                          },
                        ].map((m, mi) => (
                          <div
                            key={mi}
                            className="stat-box"
                            style={{ padding: "7px 4px" }}
                          >
                            <span className="stat-num" style={{ fontSize: 15 }}>
                              <CountUp value={m.val} />
                            </span>
                            <span className="stat-lbl">{m.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ padding: "10px 16px 14px" }}>
                      {s.exercises
                        .filter(
                          (ex) =>
                            ex.supersetChild === undefined ||
                            ex.supersetChild === null
                        )
                        .map((ex, ei) => {
                          const ws = ex.sets;
                          const best =
                            ws.length > 0
                              ? ws.reduce(
                                  (a, s) => (s.weight > a.weight ? s : a),
                                  ws[0]
                                )
                              : null;
                          return (
                            <div
                              key={ei}
                              style={{
                                paddingBottom: 8,
                                marginBottom: 8,
                                borderBottom:
                                  ei <
                                  s.exercises.filter(
                                    (e) =>
                                      e.supersetChild == null ||
                                      e.supersetChild === undefined
                                  ).length -
                                    1
                                    ? "1px solid var(--border)"
                                    : "none",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "baseline",
                                  marginBottom: 5,
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "var(--text)",
                                  }}
                                >
                                  {ex.name}
                                </p>
                                {best && (
                                  <p
                                    style={{
                                      fontSize: 11,
                                      color: "var(--text)",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {best.weight}×{best.reps}
                                  </p>
                                )}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 4,
                                }}
                              >
                                {ws.map((s, si) => (
                                  <span
                                    key={si}
                                    style={{
                                      fontSize: 11,
                                      background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.082)`,
                                      border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.2)`,
                                      borderRadius: 20,
                                      padding: "2px 8px",
                                      color: "var(--text)",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {s.weight}×{s.reps}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                      {/* Note — display + inline edit */}
                      <div
                        style={{
                          marginTop: 8,
                          borderTop: "1px solid var(--border)",
                          paddingTop: 10,
                        }}
                      >
                        {s.note ? (
                          <div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 8,
                              }}
                            >
                              <p
                                style={{
                                  fontSize: 11,
                                  color: "var(--muted)",
                                  fontStyle: "italic",
                                  lineHeight: 1.6,
                                  flex: 1,
                                }}
                              >
                                "{s.note}"
                              </p>
                              <button
                                onClick={() =>
                                  onUpdateSession({ ...s, note: "" })
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "var(--muted)",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  padding: "2px 6px",
                                }}
                              >
                                clear
                              </button>
                            </div>
                          </div>
                        ) : (
                          <textarea
                            placeholder="Add a note..."
                            defaultValue=""
                            onBlur={(e) => {
                              if (e.target.value.trim()) {
                                onUpdateSession({
                                  ...s,
                                  note: e.target.value.trim(),
                                });
                                e.target.value = "";
                              }
                            }}
                            rows={1}
                            style={{
                              width: "100%",
                              padding: "7px 10px",
                              background: "var(--surface)",
                              border: "1px solid var(--border)",
                              borderRadius: 8,
                              color: "var(--text)",
                              fontSize: 11,
                              lineHeight: 1.5,
                              resize: "none",
                              outline: "none",
                              fontFamily: "'DM Sans',sans-serif",
                              boxSizing: "border-box",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {selectedFood.length > 0 && (
                <div
                  style={{
                    background: "rgba(61,220,132,.06)",
                    border: "1.5px solid rgba(61,220,132,.25)",
                    borderRadius: 16,
                    padding: 16,
                  }}
                >
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: 13,
                      color: "var(--success)",
                      marginBottom: 10,
                    }}
                  >
                    <Ic.Food s={22} c="currentColor" /> Nutrition
                  </p>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4,1fr)",
                      gap: 6,
                      marginBottom: 10,
                    }}
                  >
                    {[
                      {
                        label: "Cal",
                        val: Math.round(foodTotals.cal),
                        color: "var(--accent)",
                      },
                      {
                        label: "Protein",
                        val: Math.round(foodTotals.p),
                        color: "var(--success)",
                      },
                      {
                        label: "Carbs",
                        val: Math.round(foodTotals.c),
                        color: "var(--warn)",
                      },
                      {
                        label: "Fat",
                        val: Math.round(foodTotals.f),
                        color: "var(--orange)",
                      },
                    ].map((m) => (
                      <div
                        key={m.label}
                        className="stat-box"
                        style={{ padding: "6px 4px" }}
                      >
                        <span
                          className="stat-num"
                          style={{ fontSize: 13, color: m.color }}
                        >
                          <CountUp value={m.val} />
                        </span>
                        <span className="stat-lbl">{m.label}</span>
                      </div>
                    ))}
                  </div>
                  {selectedFood.slice(0, 4).map((f, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "5px 0",
                        borderTop: "1px solid var(--border)",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <p style={{ fontSize: 11, color: "var(--text)" }}>
                        {f.name}
                      </p>
                      <p style={{ fontSize: 11, color: "var(--muted)" }}>
                        {f.cal}kcal
                      </p>
                    </div>
                  ))}
                  {selectedFood.length > 4 && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        textAlign: "center",
                        marginTop: 6,
                      }}
                    >
                      +{selectedFood.length - 4} more
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <style>{CSS}</style>
    </div>
  );
}

function fmtSessionTimeRange(session, timeFormat) {
  if (!session?.id) return null;
  const start = new Date(session.id);
  const end = new Date(session.id + (session.duration || 0) * 1000);
  const opts =
    timeFormat === "24hr"
      ? { hour: "2-digit", minute: "2-digit", hour12: false }
      : { hour: "numeric", minute: "2-digit", hour12: true };
  return `${start.toLocaleTimeString([], opts)} – ${end.toLocaleTimeString(
    [],
    opts
  )}`;
}

const SUB_MUSCLE_MAP = {};

const BALANCE_BENCHMARKS = {};

function getSubMuscle(exerciseName) {
  const clean = exerciseName.split(" (")[0].split(" —")[0];
  if (SUB_MUSCLE_MAP[clean]) return SUB_MUSCLE_MAP[clean];
  const key = Object.keys(SUB_MUSCLE_MAP).find((k) =>
    clean.toLowerCase().includes(k.toLowerCase())
  );
  return key ? SUB_MUSCLE_MAP[key] : null;
}

function MuscleBalanceAnalysis({ sessions, muscleGroup }) {
  const benchmark = BALANCE_BENCHMARKS[muscleGroup];
  if (!benchmark) return null;

  const cutoff = Date.now() - 28 * 24 * 60 * 60 * 1000;
  const relevantSessions = sessions.filter((s) => {
    const ts = s.id || new Date(s.date).getTime();
    return (
      ts >= cutoff &&
      s.muscleGroup?.split(" + ").some((g) => g.trim() === muscleGroup)
    );
  });

  if (relevantSessions.length === 0) return null;

  const subMuscleSets = {};
  relevantSessions.forEach((session) => {
    session.exercises.forEach((ex) => {
      const sub = getSubMuscle(ex.name);
      if (sub) {
        subMuscleSets[sub] = (subMuscleSets[sub] || 0) + ex.sets.length;
      }
    });
  });

  if (Object.keys(subMuscleSets).length === 0) return null;

  const maxSets = Math.max(...Object.values(subMuscleSets));
  const minSets = Math.min(...Object.values(subMuscleSets));
  const color = GROUP_COLORS[muscleGroup] || "var(--accent)";

  const imbalances = benchmark.groups.filter((sub) => {
    const sets = subMuscleSets[sub] || 0;
    return sets < maxSets * 0.4;
  });

  const hasData = benchmark.groups.some((sub) => subMuscleSets[sub] > 0);
  if (!hasData) return null;

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div>
        <p
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "var(--text)",
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          <Ic.Scale s={20} c="currentColor" /> Muscle Balance — Last 28 Days
        </p>
        <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
          {benchmark.note}
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {benchmark.groups.map((sub) => {
          const sets = subMuscleSets[sub] || 0;
          const pct = maxSets > 0 ? Math.round((sets / maxSets) * 100) : 0;
          const isLow = sets < maxSets * 0.4;
          const barColor = isLow
            ? "var(--danger)"
            : sets === maxSets
            ? color
            : "var(--accent)";

          return (
            <div key={sub}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: isLow ? "var(--danger)" : "var(--text)",
                  }}
                >
                  {sub} {isLow && sets === 0 ? "— Not trained" : ""}
                </span>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>
                  {sets} set{sets !== 1 ? "s" : ""}
                </span>
              </div>
              <div
                style={{
                  height: 8,
                  background: "var(--surface2)",
                  borderRadius: 99,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.max(pct, sets > 0 ? 4 : 0)}%`,
                    height: "100%",
                    background: barColor,
                    borderRadius: 99,
                    transition: "width .4s",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {imbalances.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <p
            style={{
              fontSize: 11,
              color: "var(--danger)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            ⚠️ Imbalance Detected
          </p>
          {imbalances.map((sub) => (
            <div
              key={sub}
              style={{
                background: "rgba(255,85,102,.07)",
                border: "1px solid rgba(255,85,102,.25)",
                borderRadius: 10,
                padding: "10px 12px",
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--danger)",
                }}
              >
                {sub} is undertrained
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--text)",
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {benchmark.recs[sub]}
              </p>
              {subMuscleSets[sub] > 0 && (
                <p
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}
                >
                  {subMuscleSets[sub]} sets vs {maxSets} for your most-trained
                  sub-muscle — aim for no less than 40% ratio
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {imbalances.length === 0 && (
        <div
          style={{
            background: "rgba(61,220,132,.07)",
            border: "1px solid rgba(61,220,132,.3)",
            borderRadius: 10,
            padding: "10px 12px",
          }}
        >
          <p style={{ fontSize: 13, color: "var(--success)", fontWeight: 700 }}>
            ✓ Good balance across all sub-muscles
          </p>
          <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
            Keep rotating your exercises to maintain this.
          </p>
        </div>
      )}
    </div>
  );
}

function ProgressView({
  sessions,
  selectedExercise,
  setSelectedExercise,
  selectedMetric,
  setSelectedMetric,
}) {
  const [filterMuscle, setFilterMuscle] = useState("All");
  const [filterExercise, setFilterExercise] = useState("All");
  const [sortBy, setSortBy] = useState("progression");

  const exerciseMap = {};
  sessions.forEach((session) => {
    session.exercises.forEach((ex) => {
      if (!ex.key || ex.sets.length === 0) return;
      if (!exerciseMap[ex.key])
        exerciseMap[ex.key] = { name: ex.name, points: [], muscle: "Other" };
      const bestWeight = Math.max(...ex.sets.map((s) => s.weight));
      const bestReps = Math.max(...ex.sets.map((s) => s.reps));
      const totalVolume = ex.sets.reduce((a, s) => a + s.weight * s.reps, 0);
      const e1RM = Math.round(bestWeight * (1 + bestReps / 30));
      exerciseMap[ex.key].points.push({
        date: session.date,
        bestWeight,
        bestReps,
        totalVolume,
        e1RM,
        timestamp: new Date(session.date).getTime(),
      });
    });
  });

  // Assign muscle group to each exercise
  Object.entries(exerciseMap).forEach(([key, ex]) => {
    for (const group of MUSCLE_GROUPS) {
      const lib = EXERCISE_LIBRARY[group] || [];
      if (lib.some((e) => key.startsWith(e.name))) {
        ex.muscle = group;
        break;
      }
    }
  });

  Object.values(exerciseMap).forEach((ex) => {
    ex.points.sort((a, b) => a.timestamp - b.timestamp);
  });

  const exercises = Object.entries(exerciseMap);
  const activeEx = selectedExercise ? exerciseMap[selectedExercise] : null;
  const points = activeEx?.points || [];

  const computeROP = (pts) => {
    if (pts.length < 2) return null;
    const weights = pts.map((p) => p.bestWeight);
    const times = pts.map(
      (p) => (p.timestamp - pts[0].timestamp) / (1000 * 60 * 60 * 24 * 7)
    );
    const n = pts.length;
    const sumX = times.reduce((a, b) => a + b, 0);
    const sumY = weights.reduce((a, b) => a + b, 0);
    const sumXY = times.reduce((a, t, i) => a + t * weights[i], 0);
    const sumX2 = times.reduce((a, t) => a + t * t, 0);
    const denom = n * sumX2 - sumX * sumX;
    if (denom === 0) return 0;
    return Math.round(((n * sumXY - sumX * sumY) / denom) * 10) / 10;
  };

  // PR detection: is the latest session the all-time best weight?
  const isPR = (pts) => {
    if (pts.length < 2) return false;
    const latest = pts[pts.length - 1].bestWeight;
    const allBest = Math.max(...pts.map((p) => p.bestWeight));
    return latest >= allBest;
  };

  // Plateau detection: weight AND reps unchanged for last 3 sessions
  const isPlateaued = (pts) => {
    if (pts.length < 3) return false;
    const recent = pts.slice(-3);
    const wSame = recent.every((p) => p.bestWeight === recent[0].bestWeight);
    const rSame = recent.every((p) => p.bestReps === recent[0].bestReps);
    return wSame && rSame;
  };

  // Date range string: "Mar 12 – May 28"
  const dateRange = (pts) => {
    if (pts.length < 2) return null;
    const fmt = (d) => {
      const parts = d.split("/");
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${months[parseInt(parts[0]) - 1]} ${parseInt(parts[1])}`;
    };
    return `${fmt(pts[0].date)} – ${fmt(pts[pts.length - 1].date)}`;
  };

  const rop = points.length >= 2 ? computeROP(points) : null;

  // ── GRAPH LINE (line chart for weight/1RM/reps) ──
  const GraphLine = ({ pts, metric }) => {
    if (pts.length === 0) return null;
    const values = pts.map((p) =>
      metric === "weight"
        ? p.bestWeight
        : metric === "volume"
        ? p.totalVolume
        : metric === "e1rm"
        ? p.e1RM
        : p.bestReps
    );
    const minV = Math.min(...values);
    const maxV = Math.max(...values);
    const range = maxV - minV || 1;
    const W = 320,
      H = 140,
      PAD = 20;
    const x = (i) => PAD + (i / Math.max(pts.length - 1, 1)) * (W - PAD * 2);
    const y = (v) => H - PAD - ((v - minV) / range) * (H - PAD * 2);
    const pathD = pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(values[i])}`)
      .join(" ");
    const areaD = `${pathD} L ${x(pts.length - 1)} ${H - PAD} L ${x(0)} ${
      H - PAD
    } Z`;
    return (
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ overflow: "visible" }}
      >
        <defs>
          <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line
            key={i}
            x1={PAD}
            x2={W - PAD}
            y1={PAD + t * (H - PAD * 2)}
            y2={PAD + t * (H - PAD * 2)}
            stroke="var(--border)"
            strokeWidth="1"
          />
        ))}
        {[0, 0.5, 1].map((t, i) => (
          <text
            key={i}
            x={PAD - 4}
            y={PAD + t * (H - PAD * 2) + 4}
            textAnchor="end"
            fontSize="9"
            fill="var(--muted)"
          >
            {Math.round(minV + (1 - t) * range)}
          </text>
        ))}
        <path d={areaD} fill="url(#grad)" />
        <path
          d={pathD}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {pts.map((p, i) => (
          <circle
            key={i}
            cx={x(i)}
            cy={y(values[i])}
            r="4"
            fill={
              i === pts.length - 1 && isPR(pts) && metric === "weight"
                ? "var(--warn)"
                : "var(--bg)"
            }
            stroke={
              i === pts.length - 1 && isPR(pts) && metric === "weight"
                ? "var(--warn)"
                : "var(--accent)"
            }
            strokeWidth="2"
          />
        ))}
        {[0, Math.floor(pts.length / 2), pts.length - 1]
          .filter((v, i, a) => a.indexOf(v) === i && pts[v])
          .map((i) => (
            <text
              key={i}
              x={x(i)}
              y={H - 2}
              textAnchor="middle"
              fontSize="8"
              fill="var(--muted)"
            >
              {pts[i].date.split("/").slice(0, 2).join("/")}
            </text>
          ))}
      </svg>
    );
  };

  // ── VOLUME BAR CHART ──
  const VolumeBarChart = ({ pts }) => {
    if (pts.length === 0) return null;
    const values = pts.map((p) => p.totalVolume);
    const maxV = Math.max(...values) || 1;
    const W = 320,
      H = 120,
      PAD = 20;
    const barW = Math.max(4, (W - PAD * 2) / pts.length - 3);
    const x = (i) => PAD + i * ((W - PAD * 2) / pts.length) + barW * 0.1;
    const barH = (v) => (v / maxV) * (H - PAD - 16);
    return (
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ overflow: "visible" }}
      >
        {[0, 0.5, 1].map((t, i) => (
          <line
            key={i}
            x1={PAD}
            x2={W - PAD}
            y1={16 + t * (H - PAD - 16)}
            y2={16 + t * (H - PAD - 16)}
            stroke="var(--border)"
            strokeWidth="1"
          />
        ))}
        {pts.map((p, i) => {
          const h = barH(p.totalVolume);
          const bx = x(i);
          const by = H - PAD - h;
          const isLatest = i === pts.length - 1;
          return (
            <g key={i}>
              <rect
                x={bx}
                y={by}
                width={barW}
                height={h}
                rx="3"
                ry="3"
                fill={isLatest ? "var(--accent)" : "rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.3)"}
              />
            </g>
          );
        })}
        {[0, Math.floor(pts.length / 2), pts.length - 1]
          .filter((v, i, a) => a.indexOf(v) === i && pts[v])
          .map((i) => (
            <text
              key={i}
              x={x(i) + barW / 2}
              y={H - 2}
              textAnchor="middle"
              fontSize="8"
              fill="var(--muted)"
            >
              {pts[i].date.split("/").slice(0, 2).join("/")}
            </text>
          ))}
        {[0, 0.5, 1].map((t, i) => (
          <text
            key={i}
            x={PAD - 4}
            y={16 + t * (H - PAD - 16) + 4}
            textAnchor="end"
            fontSize="9"
            fill="var(--muted)"
          >
            {Math.round((maxV * (1 - t)) / 1000)}k
          </text>
        ))}
      </svg>
    );
  };

  const METRICS = [
    { key: "weight", label: "Best Weight" },
    { key: "e1rm", label: "Est. 1RM" },
    { key: "volume", label: "Volume" },
    { key: "reps", label: "Best Reps" },
  ];

  // ── EXERCISE DETAIL VIEW ──
  if (activeEx && points.length > 0) {
    const latest = points[points.length - 1];
    const first = points[0];
    const totalGain = latest.bestWeight - first.bestWeight;
    const exIsPR = isPR(points);
    const exIsPlat = isPlateaued(points);
    const range = dateRange(points);

    return (
      <div style={{ padding: 0, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            background: "var(--surface)",
            borderBottom: "1px solid var(--border)",
            padding: "16px 16px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <button
            className="btn-ghost sm"
            style={{ alignSelf: "flex-start" }}
            onClick={() => setSelectedExercise(null)}
          >
            ← All Exercises
          </button>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "var(--text)",
                  letterSpacing: -0.4,
                }}
              >
                {activeEx.name}
              </p>
              <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                {points.length} session{points.length !== 1 ? "s" : ""}
                {range ? ` · ${range}` : ""}
              </p>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              {exIsPR && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--warn)",
                    background: "rgba(255,215,0,.12)",
                    border: "1px solid rgba(255,215,0,.35)",
                    borderRadius: 8,
                    padding: "4px 8px",
                  }}
                >
                  PR
                </span>
              )}
              {exIsPlat && !exIsPR && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--orange)",
                    background: "rgba(255,159,67,.1)",
                    border: "1px solid rgba(255,159,67,.3)",
                    borderRadius: 8,
                    padding: "4px 8px",
                  }}
                >
                  ⚠️ Plateau
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "14px 16px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 8,
            }}
          >
            {[
              { label: "Current Best", val: `${latest.bestWeight} lbs` },
              { label: "Est. 1RM", val: `${latest.e1RM} lbs` },
              {
                label: "Total Gain",
                val: `${totalGain >= 0 ? "+" : ""}${totalGain} lbs`,
              },
            ].map((s, i) => (
              <div key={i} className="stat-box">
                <span
                  className="stat-num"
                  style={{
                    fontSize: 15,
                    color:
                      i === 2
                        ? totalGain > 0
                          ? "var(--success)"
                          : totalGain < 0
                          ? "var(--danger)"
                          : "var(--muted)"
                        : "var(--accent)",
                  }}
                >
                  {s.val}
                </span>
                <span className="stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>

          {exIsPlat && (
            <div
              style={{
                background: "rgba(255,159,67,.08)",
                border: "1px solid rgba(255,159,67,.3)",
                borderRadius: 12,
                padding: "11px 14px",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--orange)",
                  }}
                >
                  Plateau detected
                </p>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    marginTop: 3,
                    lineHeight: 1.5,
                  }}
                >
                  Weight and reps unchanged for 3+ sessions. Try adding a rep,
                  microloading (+2.5lbs), or a deload week.
                </p>
              </div>
            </div>
          )}

          {rop !== null && (
            <div
              style={{
                background:
                  rop > 0 ? "rgba(61,220,132,.07)" : "rgba(255,85,85,.07)",
                border: `1px solid ${
                  rop > 0 ? "rgba(61,220,132,.3)" : "rgba(255,85,85,.3)"
                }`,
                borderRadius: 12,
                padding: "12px 14px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Rate of Progression
              </p>
              <p
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color:
                    rop > 0
                      ? "var(--success)"
                      : rop < 0
                      ? "var(--danger)"
                      : "var(--muted)",
                  marginTop: 4,
                }}
              >
                {rop > 0 ? "+" : ""}
                {rop} lbs / week
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {rop > 2
                  ? "Strong progression — you're gaining rapidly."
                  : rop > 0
                  ? "Steady progression — keep going."
                  : rop === 0
                  ? "Plateau — try adjusting volume or intensity."
                  : "Regression — check recovery, sleep, and nutrition."}{" "}
                Across {points.length} sessions.
              </p>
            </div>
          )}

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {METRICS.map((m) => (
              <button
                key={m.key}
                className={`rest-chip ${
                  selectedMetric === m.key ? "active" : ""
                }`}
                onClick={() => setSelectedMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: "16px 8px 8px",
            }}
          >
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                textAlign: "center",
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {METRICS.find((m) => m.key === selectedMetric)?.label} Over Time
            </p>
            {selectedMetric === "volume" ? (
              <VolumeBarChart pts={points} />
            ) : (
              <GraphLine pts={points} metric={selectedMetric} />
            )}
          </div>

          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid var(--border)",
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: 4,
              }}
            >
              {["Date", "Best Wt", "Est 1RM", "Volume"].map((h) => (
                <p
                  key={h}
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                  }}
                >
                  {h}
                </p>
              ))}
            </div>
            {[...points].reverse().map((p, i) => {
              const isAllTimeBest =
                p.bestWeight === Math.max(...points.map((pt) => pt.bestWeight));
              return (
                <div
                  key={i}
                  style={{
                    padding: "9px 14px",
                    borderBottom: "1px solid var(--border)",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: 4,
                    background: isAllTimeBest
                      ? "rgba(255,215,0,.04)"
                      : "transparent",
                  }}
                >
                  <p style={{ fontSize: 11, color: "var(--muted)" }}>
                    {p.date}
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: isAllTimeBest ? "var(--warn)" : "var(--text)",
                    }}
                  >
                    {p.bestWeight}lbs{isAllTimeBest ? "" : ""}
                  </p>
                  <p style={{ fontSize: 11, color: "var(--text)" }}>
                    {p.e1RM}lbs
                  </p>
                  <p style={{ fontSize: 11, color: "var(--muted)" }}>
                    {Math.round((p.totalVolume / 1000) * 10) / 10}k
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── BUILD FILTERED LIST ──
  const byMuscleGroup = {};
  exercises.forEach(([key, ex]) => {
    if (ex.points.length === 0) return;
    const group = ex.muscle || "Other";
    if (!byMuscleGroup[group]) byMuscleGroup[group] = [];
    byMuscleGroup[group].push([key, ex]);
  });

  // Sort within each group based on sortBy
  const sortFn = ([, a], [, b]) => {
    if (sortBy === "progression")
      return (computeROP(b.points) || 0) - (computeROP(a.points) || 0);
    if (sortBy === "weight")
      return (
        (b.points[b.points.length - 1]?.bestWeight || 0) -
        (a.points[a.points.length - 1]?.bestWeight || 0)
      );
    if (sortBy === "sessions") return b.points.length - a.points.length;
    if (sortBy === "recent")
      return (
        (b.points[b.points.length - 1]?.timestamp || 0) -
        (a.points[a.points.length - 1]?.timestamp || 0)
      );
    if (sortBy === "gain")
      return (
        (b.points[b.points.length - 1]?.bestWeight || 0) -
        (b.points[0]?.bestWeight || 0) -
        ((a.points[a.points.length - 1]?.bestWeight || 0) -
          (a.points[0]?.bestWeight || 0))
      );
    return 0;
  };

  Object.keys(byMuscleGroup).forEach((g) => byMuscleGroup[g].sort(sortFn));

  const availableMuscles = MUSCLE_GROUPS.filter(
    (g) => byMuscleGroup[g]?.length > 0
  );
  const availableExercises =
    filterMuscle === "All"
      ? exercises.filter(([, ex]) => ex.points.length > 0)
      : byMuscleGroup[filterMuscle] || [];

  const filteredGroups = (
    filterMuscle === "All" ? availableMuscles : [filterMuscle]
  )
    .filter((g) => byMuscleGroup[g]?.length > 0)
    .map((g) => {
      const groupExs = byMuscleGroup[g] || [];
      const filtered =
        filterExercise === "All"
          ? groupExs
          : groupExs.filter(([key]) => key === filterExercise);
      return { group: g, exercises: filtered };
    })
    .filter((g) => g.exercises.length > 0);

  const handleMuscleChange = (val) => {
    setFilterMuscle(val);
    setFilterExercise("All");
  };

  const selectStyle = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "9px 12px",
    color: "var(--text)",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "'DM Sans',sans-serif",
    flex: 1,
    appearance: "none",
    WebkitAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235e5e78' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    paddingRight: 30,
  };

  return (
    <div
      style={{ padding: 0, display: "flex", flexDirection: "column", gap: 16 }}
    >
      <div
        style={{
          background: "var(--surface)",
          borderBottom: "1px solid var(--border)",
          padding: "16px 16px 14px",
        }}
      >
        <p
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "var(--text)",
            letterSpacing: -0.4,
          }}
        >
          Strength Progress
        </p>
        <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
          {exercises.length > 0
            ? `${exercises.length} exercise${
                exercises.length !== 1 ? "s" : ""
              } tracked · tap any to view chart`
            : "Log workouts to track your progress"}
        </p>
      </div>

      <div
        style={{
          padding: "0 16px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {exercises.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  marginBottom: 5,
                }}
              >
                Muscle Group
              </p>
              <select
                value={filterMuscle}
                onChange={(e) => handleMuscleChange(e.target.value)}
                style={selectStyle}
              >
                <option value="All">
                  All Muscles ({availableMuscles.length})
                </option>
                {availableMuscles.map((g) => (
                  <option key={g} value={g}>
                    {g} ({byMuscleGroup[g]?.length || 0})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  marginBottom: 5,
                }}
              >
                Exercise
              </p>
              <select
                value={filterExercise}
                onChange={(e) => setFilterExercise(e.target.value)}
                style={selectStyle}
              >
                <option value="All">
                  All Exercises ({availableExercises.length})
                </option>
                {availableExercises.map(([key, ex]) => (
                  <option key={key} value={key}>
                    {ex.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  marginBottom: 5,
                }}
              >
                Sort By
              </p>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={selectStyle}
              >
                <option value="progression">Fastest Progression</option>
                <option value="weight">Heaviest Weight</option>
                <option value="gain">Biggest Total Gain</option>
                <option value="sessions">Most Sessions</option>
                <option value="recent">Most Recent</option>
              </select>
            </div>

            {(filterMuscle !== "All" || filterExercise !== "All") && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {filterMuscle !== "All" && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--accent)",
                      background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)",
                      border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
                      borderRadius: 20,
                      padding: "3px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {filterMuscle}
                    <button
                      onClick={() => handleMuscleChange("All")}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--accent)",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: 11,
                        lineHeight: 1,
                      }}
                    >
                      ✕
                    </button>
                  </span>
                )}
                {filterExercise !== "All" && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--info)",
                      background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)",
                      border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
                      borderRadius: 20,
                      padding: "3px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {exerciseMap[filterExercise]?.name}
                    <button
                      onClick={() => setFilterExercise("All")}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--info)",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: 11,
                        lineHeight: 1,
                      }}
                    >
                      ✕
                    </button>
                  </span>
                )}
                <button
                  onClick={() => handleMuscleChange("All")}
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {exercises.length === 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "56px 24px",
              gap: 16,
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
                border: "1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
              }}
            >
              <Ic.TrendUp s={22} c="currentColor" />
            </div>
            <div>
              <p
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "var(--text)",
                  marginBottom: 8,
                }}
              >
                No progress data yet
              </p>
              <p
                style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.7 }}
              >
                Log a few workouts and repeat them to see your strength progress
                over time
              </p>
            </div>
          </div>
        )}

        {exercises.length > 0 && filteredGroups.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "var(--muted)",
            }}
          >
            
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              No exercises found
            </p>
            <p style={{ fontSize: 11, marginTop: 6 }}>Try a different filter</p>
          </div>
        )}

        {filteredGroups.map(({ group, exercises: groupExs }) => {
          const color = GROUP_COLORS[group] || "var(--accent)";
          return (
            <div key={group}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: 4,
                    height: 18,
                    background: color,
                    borderRadius: 2,
                  }}
                />
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--text)",
                  }}
                >
                  {group}
                </p>
                <p style={{ fontSize: 11, color: "var(--muted)" }}>
                  {groupExs.length} exercise{groupExs.length !== 1 ? "s" : ""}
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {groupExs.map(([key, ex]) => {
                  const latest = ex.points[ex.points.length - 1];
                  const first = ex.points[0];
                  const gain = latest.bestWeight - first.bestWeight;
                  const exRop = computeROP(ex.points);
                  const exIsPR = isPR(ex.points);
                  const exIsPlat = isPlateaued(ex.points);
                  const range = dateRange(ex.points);
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedExercise(key)}
                      style={{
                        background: "var(--surface)",
                        border: `1px solid ${
                          exIsPR
                            ? "rgba(255,215,0,.4)"
                            : exIsPlat
                            ? "rgba(255,159,67,.35)"
                            : "var(--border)"
                        }`,
                        borderLeft: `3px solid ${
                          exIsPR
                            ? "var(--warn)"
                            : exIsPlat
                            ? "var(--orange)"
                            : color
                        }`,
                        borderRadius: 16,
                        padding: 16,
                        textAlign: "left",
                        cursor: "pointer",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexWrap: "wrap",
                            }}
                          >
                            <p
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: "var(--text)",
                              }}
                            >
                              {ex.name}
                            </p>
                            {exIsPR && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--warn)",
                                  background: "rgba(255,215,0,.12)",
                                  border: "1px solid rgba(255,215,0,.3)",
                                  borderRadius: 6,
                                  padding: "2px 6px",
                                  flexShrink: 0,
                                }}
                              >
                                PR
                              </span>
                            )}
                            {exIsPlat && !exIsPR && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--orange)",
                                  background: "rgba(255,159,67,.1)",
                                  border: "1px solid rgba(255,159,67,.3)",
                                  borderRadius: 6,
                                  padding: "2px 6px",
                                  flexShrink: 0,
                                }}
                              >
                                ⚠️ Plateau
                              </span>
                            )}
                          </div>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 2,
                            }}
                          >
                            {ex.points.length} session
                            {ex.points.length !== 1 ? "s" : ""}
                            {range ? ` · ${range}` : ""}
                          </p>
                        </div>
                        <div
                          style={{
                            textAlign: "right",
                            flexShrink: 0,
                            marginLeft: 12,
                          }}
                        >
                          <p
                            style={{
                              fontSize: 15,
                              fontWeight: 700,
                              color: exIsPR ? "var(--warn)" : color,
                            }}
                          >
                            {latest.bestWeight} lbs
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              color:
                                gain > 0
                                  ? "var(--success)"
                                  : gain < 0
                                  ? "var(--danger)"
                                  : "var(--muted)",
                              marginTop: 2,
                            }}
                          >
                            {gain > 0 ? "▲" : gain < 0 ? "▼" : "—"}{" "}
                            {Math.abs(gain)} lbs total
                          </p>
                        </div>
                      </div>
                      {exRop !== null && (
                        <div
                          style={{
                            marginTop: 8,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              height: 4,
                              background: "var(--surface2)",
                              borderRadius: 99,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(
                                  100,
                                  Math.abs(exRop) * 10
                                )}%`,
                                height: "100%",
                                borderRadius: 99,
                                background:
                                  exRop > 0
                                    ? "var(--success)"
                                    : "var(--danger)",
                              }}
                            />
                          </div>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {exRop > 0 ? "+" : ""}
                            {exRop} lbs/wk
                          </p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {exercises.length > 0 &&
          filterMuscle === "All" &&
          filterExercise === "All" && (
            <>
              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: 15, fontWeight: 700 }}>
                  Muscle Balance Analysis
                </p>
                <p
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}
                >
                  Based on your last 28 days — flags sub-muscles getting less
                  than 40% of your peak volume
                </p>
              </div>
              {MUSCLE_GROUPS.map((group) => (
                <MuscleBalanceAnalysis
                  key={group}
                  sessions={sessions}
                  muscleGroup={group}
                />
              ))}
            </>
          )}
      </div>
    </div>
  );
}

const FOOD_DB = [
  { name: "Chicken Breast", cal: 165, p: 31, c: 0, f: 3.6, serving: "100g" },
  { name: "Brown Rice", cal: 216, p: 5, c: 45, f: 1.8, serving: "1 cup" },
  { name: "Eggs", cal: 78, p: 6, c: 0.6, f: 5, serving: "1 large" },
  { name: "Greek Yogurt", cal: 100, p: 17, c: 6, f: 0.7, serving: "170g" },
  { name: "Oatmeal", cal: 150, p: 5, c: 27, f: 3, serving: "1 cup" },
  { name: "Salmon", cal: 208, p: 20, c: 0, f: 13, serving: "100g" },
  {
    name: "Sweet Potato",
    cal: 103,
    p: 2.3,
    c: 24,
    f: 0.1,
    serving: "1 medium",
  },
  { name: "Broccoli", cal: 55, p: 3.7, c: 11, f: 0.6, serving: "1 cup" },
  { name: "Almonds", cal: 164, p: 6, c: 6, f: 14, serving: "28g" },
  { name: "Banana", cal: 105, p: 1.3, c: 27, f: 0.4, serving: "1 medium" },
  { name: "Tuna", cal: 109, p: 24, c: 0, f: 1, serving: "100g" },
  { name: "Cottage Cheese", cal: 206, p: 28, c: 8, f: 9, serving: "1 cup" },
  { name: "Whole Milk", cal: 149, p: 8, c: 12, f: 8, serving: "1 cup" },
  { name: "Peanut Butter", cal: 188, p: 8, c: 6, f: 16, serving: "2 tbsp" },
  { name: "Whey Protein", cal: 120, p: 25, c: 3, f: 1.5, serving: "1 scoop" },
  { name: "White Rice", cal: 206, p: 4.3, c: 45, f: 0.4, serving: "1 cup" },
  { name: "Ground Beef 80%", cal: 287, p: 19, c: 0, f: 23, serving: "100g" },
  { name: "Turkey Breast", cal: 135, p: 30, c: 0, f: 1, serving: "100g" },
  { name: "Pasta", cal: 220, p: 8, c: 43, f: 1.3, serving: "1 cup" },
  { name: "Avocado", cal: 234, p: 2.9, c: 12, f: 21, serving: "1 medium" },
  { name: "Blueberries", cal: 84, p: 1.1, c: 21, f: 0.5, serving: "1 cup" },
  { name: "Apple", cal: 95, p: 0.5, c: 25, f: 0.3, serving: "1 medium" },
  { name: "Bread", cal: 79, p: 2.7, c: 15, f: 1, serving: "1 slice" },
  { name: "Cheddar Cheese", cal: 113, p: 7, c: 0.4, f: 9, serving: "28g" },
  { name: "Spinach", cal: 7, p: 0.9, c: 1.1, f: 0.1, serving: "1 cup" },
  { name: "Olive Oil", cal: 119, p: 0, c: 0, f: 13.5, serving: "1 tbsp" },
  { name: "Black Beans", cal: 227, p: 15, c: 41, f: 0.9, serving: "1 cup" },
  { name: "Quinoa", cal: 222, p: 8, c: 39, f: 3.5, serving: "1 cup" },
  { name: "Mixed Nuts", cal: 173, p: 5, c: 8, f: 16, serving: "28g" },
  { name: "Orange Juice", cal: 112, p: 1.7, c: 26, f: 0.5, serving: "1 cup" },
];

async function searchOpenFoodFacts(query) {
  const url = `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(
    query
  )}&search_simple=1&action=process&json=1&page_size=25&fields=product_name,brands,nutriments,serving_size,serving_quantity`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 6000); // 6 second timeout
  try {
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.products || [])
      .map((product) => {
        const n = product.nutriments || {};
        const cal = Math.round(
          n["energy-kcal_100g"] ||
            (n["energy_100g"] ? n["energy_100g"] / 4.184 : 0)
        );
        const p = Math.round((n["proteins_100g"] || 0) * 10) / 10;
        const c = Math.round((n["carbohydrates_100g"] || 0) * 10) / 10;
        const f = Math.round((n["fat_100g"] || 0) * 10) / 10;
        const name = product.product_name || "";
        const brand = product.brands || "";
        if (!name) return null;
        return { name, brand, cal, p, c, f };
      })
      .filter(Boolean)
      .filter((f) => f.cal > 0 || f.p > 0 || f.c > 0);
  } catch (e) {
    clearTimeout(timeout);
    throw e;
  }
}

function NutritionScreen({
  profile,
  todayMuscle,
  todayLog,
  onAddFood,
  onDeleteFood,
  favorites,
  onAddFavorite,
  onRemoveFavorite,
  onFinishDay,
  daySummary,
  settings,
}) {
  const fmtTime = (id) => {
    if (!id) return "";
    const d = new Date(id);
    return d.toLocaleTimeString(
      [],
      settings?.timeFormat === "24hr"
        ? { hour: "2-digit", minute: "2-digit", hour12: false }
        : { hour: "2-digit", minute: "2-digit", hour12: true }
    );
  };
  const [mode, setMode] = useState(null); // null | "search" | "manual" | "favorites" | "summary"
  const [mealType, setMealType] = useState("Lunch"); // current meal being logged
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedFood, setSelectedFood] = useState(null);
  const [servingUnit, setServingUnit] = useState("100g");
  const [servingAmount, setServingAmount] = useState("");
  const [manual, setManual] = useState({ name: "", p: "", c: "", f: "" });

  const MEAL_TYPES = ["Breakfast", "Lunch", "Dinner", "Snack"];
  const MEAL_ICONS = {
    Breakfast: "",
    Lunch: "",
    Dinner: "",
    Snack: <Ic.Food s={18} c="currentColor" />,
  };

  const manualCal = Math.round(
    (Number(manual.p) || 0) * 4 +
      (Number(manual.c) || 0) * 4 +
      (Number(manual.f) || 0) * 9
  );

  const getServingMultiplier = (amount, unit) => {
    const amt = parseFloat(amount) || 0;
    if (unit === "100g") return amt;
    if (unit === "1g") return amt / 100;
    if (unit === "oz") return (amt * 28.3495) / 100;
    return amt;
  };

  const bodyGoal = profile.bodyGoal || "recomp";
  const targets = calcMacros(profile, todayMuscle, settings?.calAdjust || 0);
  const totals = todayLog.reduce(
    (acc, e) => ({
      cal: acc.cal + e.cal,
      p: acc.p + e.p,
      c: acc.c + e.c,
      f: acc.f + e.f,
    }),
    { cal: 0, p: 0, c: 0, f: 0 }
  );
  const pct = (val, target) =>
    Math.min(100, Math.round((val / Math.max(target, 1)) * 100));
  const goalLabel =
    { bulk: "Bulk ↑", cut: "Cut ↓", recomp: "Recomp" }[bodyGoal] || "Recomp";

  const logByMeal = MEAL_TYPES.reduce((acc, m) => {
    acc[m] = todayLog.filter((e) => e.mealType === m);
    return acc;
  }, {});
  const uncategorized = todayLog.filter((e) => !e.mealType);

  useEffect(() => {
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      const local = FOOD_DB.filter((f) =>
        f.name.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(local);
      try {
        const live = await searchOpenFoodFacts(query);
        const combined = [
          ...live,
          ...local.filter(
            (l) =>
              !live.find((u) => u.name.toLowerCase() === l.name.toLowerCase())
          ),
        ];
        setSearchResults(combined.length > 0 ? combined : local);
        if (combined.length === 0 && local.length === 0) {
          setSearchError(
            "No matches found. Try manual entry or a different search term."
          );
        }
      } catch (e) {
        if (local.length === 0) {
          setSearchError(
            "Live search unavailable right now. Try manual entry for this food."
          );
        }
      }
      setSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  const logFood = (food, amount, unit) => {
    const mult = getServingMultiplier(amount, unit);
    onAddFood({
      name: food.brand ? `${food.name} (${food.brand})` : food.name,
      cal: Math.round(food.cal * mult),
      p: Math.round(food.p * mult * 10) / 10,
      c: Math.round(food.c * mult * 10) / 10,
      f: Math.round(food.f * mult * 10) / 10,
      mealType,
    });
    onAddFavorite({
      name: food.brand ? `${food.name} (${food.brand})` : food.name,
      cal: food.cal,
      p: food.p,
      c: food.c,
      f: food.f,
    });
    setMode(null);
    setQuery("");
    setSelectedFood(null);
    setServingAmount("");
    setServingUnit("100g");
  };

  const logManual = () => {
    if (!manual.name) return;
    const entry = {
      name: manual.name,
      cal: manualCal,
      p: Number(manual.p) || 0,
      c: Number(manual.c) || 0,
      f: Number(manual.f) || 0,
    };
    onAddFood({ ...entry, mealType });
    onAddFavorite(entry);
    setManual({ name: "", p: "", c: "", f: "" });
    setMode(null);
  };

  const logFavorite = (fav) => {
    onAddFood({ ...fav, mealType, id: Date.now() });
    setMode(null);
  };

  const generateSummary = () => {
    const calPct = pct(Math.round(totals.cal), targets.calories);
    const protPct = pct(Math.round(totals.p), targets.protein);
    const carbPct = pct(Math.round(totals.c), targets.carbs);
    const fatPct = pct(Math.round(totals.f), targets.fat);

    const grade = (p) => (p >= 90 ? "✅" : p >= 70 ? "⚠️" : "❌");
    const feedback = [];

    const calDiff = Math.round(totals.cal) - targets.calories;
    if (calDiff > 200)
      feedback.push({
        icon: <Ic.ArrowUp s={16} c="var(--success)" />,
        text: `${calDiff} kcal over target. Fine for a bulk, but monitor trends.`,
        type: "warn",
      });
    else if (calDiff < -300)
      feedback.push({
        icon: <Ic.ArrowDown s={16} c="var(--danger)" />,
        text: `${Math.abs(
          calDiff
        )} kcal under target. Make sure this is intentional.`,
        type: "warn",
      });
    else
      feedback.push({
        icon: null,
        text: `Calories on point — ${Math.round(totals.cal)} of ${
          targets.calories
        } kcal.`,
        type: "good",
      });

    if (protPct < 80)
      feedback.push({
        icon: <Ic.Meat s={22} c="currentColor" />,
        text: `Protein at ${Math.round(totals.p)}g — ${
          targets.protein - Math.round(totals.p)
        }g short of your target. Prioritize protein first tomorrow.`,
        type: "warn",
      });
    else
      feedback.push({
        icon: null,
        text: `Protein target hit — ${Math.round(totals.p)}g of ${
          targets.protein
        }g. Great for muscle preservation.`,
        type: "good",
      });

    if (todayMuscle !== "Rest" && carbPct < 70)
      feedback.push({
        icon: null,
        text: `Carbs low on a training day. Glycogen replenishment may be insufficient for recovery.`,
        type: "warn",
      });
    else if (todayMuscle === "Rest" && carbPct > 110)
      feedback.push({
        icon: "⚠️",
        text: `Carbs high on a rest day. Consider shifting some to tomorrow's training day.`,
        type: "warn",
      });
    else
      feedback.push({
        icon: null,
        text: `Carb intake appropriate for a ${
          todayMuscle === "Rest" ? "rest" : todayMuscle.toLowerCase()
        } day.`,
        type: "good",
      });

    const mealsLogged = MEAL_TYPES.filter(
      (m) => logByMeal[m]?.length > 0
    ).length;
    if (mealsLogged < 2)
      feedback.push({
        icon: <Ic.Clipboard s={22} c="currentColor" />,
        text: `Only ${mealsLogged} meal logged today. More frequent meals help sustain protein synthesis.`,
        type: "warn",
      });

    const summary = {
      date: new Date().toLocaleDateString(),
      totals: {
        cal: Math.round(totals.cal),
        p: Math.round(totals.p),
        c: Math.round(totals.c),
        f: Math.round(totals.f),
      },
      targets,
      pcts: { cal: calPct, p: protPct, c: carbPct, f: fatPct },
      feedback,
      muscle: todayMuscle,
    };
    onFinishDay(summary);
    setMode("summary");
  };

  if (mode === "summary" || daySummary) {
    const s = daySummary || {};
    const fb = s.feedback || [];
    return (
      <div
        style={{
          padding: "48px 16px 100px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 40 }}>
            <Ic.ChartBar s={22} c="currentColor" />
          </p>
          <p style={{ fontSize: 22, fontWeight: 700, marginTop: 8 }}>
            Day Complete!
          </p>
          <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
            {s.date} · {s.muscle || todayMuscle} day
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)",
            gap: 8,
          }}
        >
          {[
            {
              label: "Calories",
              val: s.totals?.cal || Math.round(totals.cal),
              target: targets.calories,
              unit: "kcal",
            },
            {
              label: "Protein",
              val: s.totals?.p || Math.round(totals.p),
              target: targets.protein,
              unit: "g",
            },
            {
              label: "Carbs",
              val: s.totals?.c || Math.round(totals.c),
              target: targets.carbs,
              unit: "g",
            },
            {
              label: "Fat",
              val: s.totals?.f || Math.round(totals.f),
              target: targets.fat,
              unit: "g",
            },
          ].map((m, i) => {
            const p = pct(m.val, m.target);
            const color =
              p >= 90
                ? "var(--success)"
                : p >= 70
                ? "var(--accent)"
                : "var(--danger)";
            return (
              <div
                key={i}
                className="stat-box"
                style={{ borderColor: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)` }}
              >
                <span
                  className="stat-num"
                  style={{ fontSize: 13, color: "var(--text)" }}
                >
                  <CountUp value={m.val} />
                  <span style={{ fontSize: 11 }}>{m.unit}</span>
                </span>
                <span className="stat-lbl">{m.label}</span>
                <span style={{ fontSize: 11, color: "var(--text)" }}>{p}%</span>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {fb.map((f, i) => (
            <div
              key={i}
              style={{
                background:
                  f.type === "good"
                    ? "rgba(61,220,132,.07)"
                    : "rgba(255,213,0,.07)",
                border: `1px solid ${
                  f.type === "good"
                    ? "rgba(61,220,132,.3)"
                    : "rgba(255,213,0,.3)"
                }`,
                borderRadius: 12,
                padding: "12px 14px",
                display: "flex",
                gap: 10,
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{f.icon}</span>
              <p
                style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.5 }}
              >
                {f.text}
              </p>
            </div>
          ))}
        </div>

        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: "14px 16px",
          }}
        >
          <p
            style={{
              fontSize: 11,
              color: "var(--accent)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Tomorrow
          </p>
          <p
            style={{
              fontSize: 13,
              color: "var(--text)",
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            {(s.totals?.cal || Math.round(totals.cal)) < targets.calories - 100
              ? `You were ${
                  targets.calories - (s.totals?.cal || Math.round(totals.cal))
                } kcal under today. Try to hit your target more consistently.`
              : `Good day. Stay consistent and keep protein high.`}
          </p>
        </div>

        <button className="btn-primary" onClick={() => setMode(null)}>
          ← Back to Log
        </button>
        <style>{CSS}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "48px 16px 100px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <ScreenHeader
        eyebrow={`${goalLabel} · ${
          todayMuscle === "Rest" ? "Rest Day" : `${todayMuscle} Day`
        }`}
        title="Nutrition"
      />

      <div className="cal-card">
        <div style={{ textAlign: "center" }}>
          <p
            style={{
              fontSize: 40,
              fontWeight: 700,
              fontFamily: "'Bebas Neue',sans-serif",
              color: "var(--accent)",
              lineHeight: 1,
            }}
          >
            {Math.round(totals.cal)}
          </p>
          <p style={{ fontSize: 11, color: "var(--muted)" }}>
            of {targets.calories} kcal
          </p>
          <p
            style={{
              fontSize: 11,
              color:
                totals.cal <= targets.calories
                  ? "var(--success)"
                  : "var(--danger)",
              marginTop: 4,
              fontWeight: 700,
            }}
          >
            {targets.calories - Math.round(totals.cal) >= 0
              ? `${targets.calories - Math.round(totals.cal)} kcal remaining`
              : `${Math.round(totals.cal) - targets.calories} kcal over`}
          </p>
        </div>
      </div>

      <div className="card" style={{ gap: 14 }}>
        {[
          {
            label: "Protein",
            val: Math.round(totals.p),
            target: targets.protein,
            color: "var(--success)",
          },
          {
            label: "Carbs",
            val: Math.round(totals.c),
            target: targets.carbs,
            color: "var(--accent)",
          },
          {
            label: "Fat",
            val: Math.round(totals.f),
            target: targets.fat,
            color: "var(--orange)",
          },
        ].map((m) => (
          <div key={m.label}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700 }}>{m.label}</span>
              <span style={{ fontSize: 13, color: "var(--muted)" }}>
                {m.val}g / {m.target}g
              </span>
            </div>
            <div
              style={{
                background: "var(--surface2)",
                borderRadius: 99,
                height: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${pct(m.val, m.target)}%`,
                  height: "100%",
                  background: m.color,
                  borderRadius: 99,
                  transition: "width .4s",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {targets.calorieFloorApplied && (
        // Shown rather than silently presenting the floor as a normal result.
        // Deliberately describes the settings, not the user's health, and makes
        // no claim that the floor value is an appropriate intake.
        <div
          style={{
            background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)",
            border: "1px solid var(--warn)",
            borderRadius: 16,
            padding: "12px 14px",
          }}
        >
          <p
            style={{
              fontSize: 11,
              color: "var(--warn)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 1,
              marginBottom: 4,
            }}
          >
            Target limited
          </p>
          <p style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6 }}>
            Your current settings would create an excessively low calorie
            target. OverLoad has limited the recommendation for safety. Review
            your activity level, body goal, or calorie adjustment.
          </p>
        </div>
      )}

      <div
        style={{
          background: "var(--surface2)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "12px 14px",
        }}
      >
        <p
          style={{
            fontSize: 11,
            color: "var(--accent)",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          Why these numbers?
        </p>
        <p
          style={{
            fontSize: 11,
            color: "var(--muted)",
            marginTop: 6,
            lineHeight: 1.6,
          }}
        >
          {/* This paragraph used to describe carbohydrate periodisation by
              muscle group — "Legs training causes the greatest glycogen
              depletion", "Rest day — carbs are lower", and so on. Step 1
              disabled GROUP_CARB_FACTOR because it altered total energy
              intake rather than redistributing it, which made every one of
              those sentences false. Replaced with a description of what the
              app actually does now. The factor table is still in the code for
              the later periodisation redesign. */}
          Protein is set at{" "}
          {(
            targets.protein / Math.max(Number(profile.weight) || 1, 1)
          ).toFixed(1)}
          g/lb
          for your {goalLabel} goal, fat at{" "}
          {Math.round(((targets.fat * 9) / Math.max(targets.calories, 1)) * 100)}%
          of your target. Carbohydrates fill the calories that remain.
        </p>
      </div>

      <div>
        <p
          style={{
            fontSize: 11,
            color: "var(--muted)",
            marginBottom: 8,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          Logging for
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          {MEAL_TYPES.map((m) => (
            <button
              key={m}
              className={`rest-chip ${mealType === m ? "active" : ""}`}
              style={{ flex: 1, fontSize: 11 }}
              onClick={() => setMealType(m)}
            >
              {MEAL_ICONS[m]} {m}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <p style={{ fontSize: 15, fontWeight: 700 }}>Today's Log</p>
          <p style={{ fontSize: 11, color: "var(--muted)" }}>
            {new Date().toLocaleDateString([], {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </p>
        </div>

        {todayLog.length === 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "40px 24px",
              gap: 14,
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(85,239,196,.08)",
                border: "1.5px solid rgba(85,239,196,.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
              }}
            >
              <Ic.Food s={22} c="currentColor" />
            </div>
            <div>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--text)",
                  marginBottom: 6,
                }}
              >
                Nothing logged today
              </p>
              <p
                style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}
              >
                Tap <strong style={{ color: "var(--success)" }}>Search</strong>,{" "}
                <strong style={{ color: "var(--success)" }}>Favorites</strong>,
                or <strong style={{ color: "var(--success)" }}>Manual</strong>{" "}
                below to log your first meal
              </p>
            </div>
          </div>
        )}

        {MEAL_TYPES.map((meal) => {
          const entries = logByMeal[meal] || [];
          if (entries.length === 0) return null;
          const mealTotals = entries.reduce(
            (a, e) => ({
              cal: a.cal + e.cal,
              p: a.p + e.p,
              c: a.c + e.c,
              f: a.f + e.f,
            }),
            { cal: 0, p: 0, c: 0, f: 0 }
          );
          return (
            <div
              key={meal}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid var(--border)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: "var(--text)",
                  }}
                >
                  {MEAL_ICONS[meal]} {meal}
                </p>
                <p style={{ fontSize: 11, color: "var(--muted)" }}>
                  {Math.round(mealTotals.cal)} kcal · P:
                  {Math.round(mealTotals.p)}g C:{Math.round(mealTotals.c)}g F:
                  {Math.round(mealTotals.f)}g
                </p>
              </div>
              {entries.map((entry, i) => (
                <div
                  key={i}
                  className="food-row"
                  style={{ padding: "10px 14px" }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <p style={{ fontSize: 13, fontWeight: 700 }}>
                        {entry.name}
                      </p>
                      <p style={{ fontSize: 11, color: "var(--muted)" }}>
                        {fmtTime(entry.id)}
                      </p>
                    </div>
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginTop: 2,
                      }}
                    >
                      {entry.cal} kcal · P:{entry.p}g · C:{entry.c}g · F:
                      {entry.f}g
                    </p>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 13,
                        cursor: "pointer",
                        color: favorites.find((f) => f.name === entry.name)
                          ? "var(--accent)"
                          : "var(--muted)",
                      }}
                      onClick={() =>
                        favorites.find((f) => f.name === entry.name)
                          ? onRemoveFavorite(entry.name)
                          : onAddFavorite({
                              name: entry.name,
                              cal: entry.cal,
                              p: entry.p,
                              c: entry.c,
                              f: entry.f,
                            })
                      }
                    >
                      ★
                    </button>
                    <button
                      className="del-btn"
                      onClick={() => onDeleteFood(entry.id)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        {uncategorized.map((entry, i) => (
          <div key={i} className="food-row">
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 700 }}>{entry.name}</p>
              <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                {entry.cal} kcal · P:{entry.p}g · C:{entry.c}g · F:{entry.f}g
              </p>
            </div>
            <button className="del-btn" onClick={() => onDeleteFood(entry.id)}>
              ✕
            </button>
          </div>
        ))}
      </div>

      {!mode && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              className="btn-add-exercise"
              style={{ flex: 1 }}
              onClick={() => setMode("search")}
            >
              Search
            </button>
            <button
              className="btn-add-exercise"
              style={{ flex: 1 }}
              onClick={() => setMode("favorites")}
            >
              ⭐ Favorites
            </button>
            <button
              className="btn-add-exercise"
              style={{ flex: 1 }}
              onClick={() => setMode("manual")}
            >
              <Ic.Pencil
                s={13}
                c="currentColor"
                style={{
                  display: "inline",
                  verticalAlign: "middle",
                  marginRight: 4,
                }}
              />
              Manual
            </button>
          </div>
          {todayLog.length > 0 && (
            <button
              onClick={generateSummary}
              style={{
                background: "rgba(61,220,132,.08)",
                border: "1px solid var(--success)",
                borderRadius: 16,
                padding: "16px",
                color: "var(--success)",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              ✓ Finish Day — See Summary
            </button>
          )}
        </div>
      )}

      {mode === "favorites" && (
        <div className="card" style={{ gap: 10 }}>
          <p style={{ fontWeight: 700, fontSize: 15 }}>⭐ Favorites</p>
          {favorites.length === 0 && (
            <p
              style={{
                fontSize: 13,
                color: "var(--muted)",
                textAlign: "center",
                padding: "16px 0",
              }}
            >
              No favorites yet. Foods you log get saved here automatically.
            </p>
          )}
          <div
            style={{
              maxHeight: 320,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {favorites.map((fav, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 13, fontWeight: 700 }}>{fav.name}</p>
                  <p style={{ fontSize: 11, color: "var(--muted)" }}>
                    {fav.cal} kcal · P:{fav.p}g C:{fav.c}g F:{fav.f}g per 100g
                  </p>
                </div>
                <button
                  className="btn-set-action normal"
                  style={{ padding: "6px 12px", fontSize: 11, flex: "none" }}
                  onClick={() =>
                    logFavorite({
                      ...fav,
                      cal: fav.cal,
                      p: fav.p,
                      c: fav.c,
                      f: fav.f,
                    })
                  }
                >
                  + Log
                </button>
                <button
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--danger)",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                  onClick={() => onRemoveFavorite(fav.name)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button className="btn-ghost" onClick={() => setMode(null)}>
            Cancel
          </button>
        </div>
      )}

      {mode === "search" && !selectedFood && (
        <div className="card" style={{ gap: 10 }}>
          <p style={{ fontWeight: 700, fontSize: 15 }}>Search Food</p>
          <p style={{ fontSize: 11, color: "var(--muted)" }}>
            Open Food Facts · 2.8 million foods · no account needed
          </p>
          <input
            className="big-input"
            placeholder="e.g. chicken breast, oats, banana..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ fontSize: 15, padding: "11px 14px" }}
          />
          {searching && (
            <p
              style={{
                fontSize: 13,
                color: "var(--muted)",
                textAlign: "center",
              }}
            >
              Searching...
            </p>
          )}
          {searchError && (
            <p style={{ fontSize: 13, color: "var(--danger)" }}>
              {searchError}
            </p>
          )}
          <div
            style={{
              maxHeight: 300,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {searchResults.map((food, i) => (
              <button
                key={i}
                className="ex-pick-btn"
                onClick={() => setSelectedFood(food)}
              >
                <div style={{ textAlign: "left", flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {food.name}
                  </p>
                  {food.brand && (
                    <p style={{ fontSize: 11, color: "var(--muted)" }}>
                      {food.brand}
                    </p>
                  )}
                  <p style={{ fontSize: 11, color: "var(--muted)" }}>
                    Per 100g: {food.cal}kcal · P:{food.p}g C:{food.c}g F:
                    {food.f}g
                  </p>
                </div>
                <span style={{ color: "var(--accent)", flexShrink: 0 }}>+</span>
              </button>
            ))}
          </div>
          <button
            className="btn-ghost"
            onClick={() => {
              setMode(null);
              setQuery("");
              setSearchResults([]);
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {mode === "search" && selectedFood && (
        <div className="card" style={{ gap: 12 }}>
          <p style={{ fontWeight: 700, fontSize: 15 }}>{selectedFood.name}</p>
          {selectedFood.brand && (
            <p style={{ fontSize: 11, color: "var(--muted)", marginTop: -8 }}>
              {selectedFood.brand}
            </p>
          )}
          <p style={{ fontSize: 11, color: "var(--muted)" }}>
            Per 100g: {selectedFood.cal} kcal · P:{selectedFood.p}g · C:
            {selectedFood.c}g · F:{selectedFood.f}g
          </p>
          <div>
            <label
              className="input-label"
              style={{ display: "block", marginBottom: 6 }}
            >
              Serving unit
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { key: "1g", label: "1 gram" },
                { key: "100g", label: "100g" },
                { key: "oz", label: "oz (weight)" },
              ].map((u) => (
                <button
                  key={u.key}
                  className={`rest-chip ${
                    servingUnit === u.key ? "active" : ""
                  }`}
                  style={{ flex: 1 }}
                  onClick={() => {
                    setServingUnit(u.key);
                    setServingAmount("");
                  }}
                >
                  {u.label}
                </button>
              ))}
            </div>
          </div>
          <div className="input-group">
            <label className="input-label">
              {servingUnit === "1g" && "Amount (grams)"}
              {servingUnit === "100g" && "Amount (× 100g, e.g. 1.5 = 150g)"}
              {servingUnit === "oz" && "Amount (weight oz)"}
            </label>
            <input
              className="num-input"
              type="number"
              inputMode="decimal"
              placeholder={
                servingUnit === "100g"
                  ? "1"
                  : servingUnit === "oz"
                  ? "4"
                  : "100"
              }
              value={servingAmount}
              onChange={(e) => setServingAmount(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>
          {servingAmount && (
            <div
              style={{
                background: "var(--surface2)",
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              <p style={{ fontSize: 11, color: "var(--muted)" }}>
                Total logged:
              </p>
              <p style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                {(() => {
                  const mult = getServingMultiplier(servingAmount, servingUnit);
                  return `${Math.round(
                    selectedFood.cal * mult
                  )} kcal · P:${Math.round(
                    selectedFood.p * mult
                  )}g · C:${Math.round(
                    selectedFood.c * mult
                  )}g · F:${Math.round(selectedFood.f * mult)}g`;
                })()}
              </p>
            </div>
          )}
          <button
            className="btn-primary"
            disabled={!servingAmount}
            onClick={() => logFood(selectedFood, servingAmount, servingUnit)}
          >
            Log to {mealType}
          </button>
          <button className="btn-ghost" onClick={() => setSelectedFood(null)}>
            ← Back to Search
          </button>
        </div>
      )}

      {mode === "manual" && (
        <div className="card" style={{ gap: 12 }}>
          <p style={{ fontWeight: 700, fontSize: 15 }}>Manual Entry</p>
          <p style={{ fontSize: 11, color: "var(--muted)", marginTop: -8 }}>
            Calories calculated automatically from macros.
          </p>
          <input
            className="big-input"
            placeholder="Food name"
            value={manual.name}
            onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
            style={{ fontSize: 15, padding: "11px 14px" }}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
            }}
          >
            {[
              { key: "p", label: "Protein (g)", color: "var(--success)" },
              { key: "c", label: "Carbs (g)", color: "var(--accent)" },
              { key: "f", label: "Fat (g)", color: "var(--orange)" },
            ].map((field) => (
              <div key={field.key} className="input-group">
                <label className="input-label" style={{ color: field.color }}>
                  {field.label}
                </label>
                <input
                  className="num-input"
                  type="number"
                  inputMode="decimal"
                  value={manual[field.key]}
                  onChange={(e) =>
                    setManual((m) => ({ ...m, [field.key]: e.target.value }))
                  }
                  style={{ marginTop: 4 }}
                />
              </div>
            ))}
          </div>
          <div
            style={{
              background: "var(--surface2)",
              borderRadius: 10,
              padding: "12px 14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <p style={{ fontSize: 11, color: "var(--muted)" }}>
                Calories (auto-calculated)
              </p>
              <p
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  fontFamily: "'Bebas Neue',sans-serif",
                  color: "var(--accent)",
                  lineHeight: 1.2,
                }}
              >
                {manualCal}{" "}
                <span style={{ fontSize: 13, color: "var(--muted)" }}>
                  kcal
                </span>
              </p>
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--muted)",
                textAlign: "right",
                lineHeight: 1.8,
              }}
            >
              <p>
                {Number(manual.p) || 0}g × 4 = {(Number(manual.p) || 0) * 4}
              </p>
              <p>
                {Number(manual.c) || 0}g × 4 = {(Number(manual.c) || 0) * 4}
              </p>
              <p>
                {Number(manual.f) || 0}g × 9 = {(Number(manual.f) || 0) * 9}
              </p>
            </div>
          </div>
          <button
            className="btn-primary"
            disabled={!manual.name || manualCal === 0}
            onClick={logManual}
          >
            Log to {mealType}
          </button>
          <button className="btn-ghost" onClick={() => setMode(null)}>
            Cancel
          </button>
        </div>
      )}

      <style>{CSS}</style>
    </div>
  );
}

function SettingsScreen({
  profile,
  settings,
  onUpdate,
  onSaveProfile,
  sessions,
}) {
  const [editGoal, setEditGoal] = useState(false);
  const [editBodyGoal, setEditBodyGoal] = useState(false);
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ ...profile });
  const [showExport, setShowExport] = useState(false);

  // ── CSV EXPORT ──
  // Two separate files, since they answer different questions:
  //  1. Workout Log   — one row per set, the raw training record
  //  2. Progression    — one row per exercise per session, the best set that
  //                       session, so progress over time is easy to chart
  // Both built from the SAME sessions data, just shaped differently for
  // their respective use cases (raw log vs trend analysis).
  const escapeCsv = (val) => {
    const s = String(val ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const [exportFallback, setExportFallback] = useState(null); // { filename, csv } when download can't be confirmed
  const [copiedExport, setCopiedExport] = useState(false);

  const downloadCsv = (filename, rows) => {
    const csv = rows.map((row) => row.map(escapeCsv).join(",")).join("\n");

    // Try the standard programmatic download first — works in real browsers.
    // Sandboxed/embedded preview environments often silently block this
    // (no error thrown, the click just does nothing), so we can't actually
    // detect success or failure directly. Instead: always also offer the
    // fallback view below the buttons, so there's never a dead end —
    // worst case, the person copies the data manually into a spreadsheet.
    try {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Fall through to showing the fallback regardless
    }

    setExportFallback({ filename, csv });
    setCopiedExport(false);
  };

  const copyExportToClipboard = async () => {
    if (!exportFallback) return;
    try {
      await navigator.clipboard.writeText(exportFallback.csv);
      setCopiedExport(true);
      setTimeout(() => setCopiedExport(false), 2500);
    } catch {
      // Clipboard API blocked too — select-all fallback handled by the textarea itself
    }
  };

  // ── FULL BACKUP / RESTORE (JSON) ──
  // Different job from the CSV exports above. CSV is a human-readable view of
  // sessions only — lossy, and it can't rebuild the app. This captures EVERY
  // key the app writes to localStorage, verbatim, so a backup can fully
  // restore onto another device, another browser, or another domain.
  //
  // Why this matters: localStorage is scoped per-origin. Moving the app to a
  // new address strands everyone's history at the old one. It's also wiped by
  // "clear browsing data", and iOS Safari evicts it after ~7 days of not
  // opening the site unless the app is installed to the home screen. This is
  // the only bridge across all of those.
  //
  // Keys are collected by PREFIX rather than a hardcoded list, so any new
  // wt_* key added to the app in future is backed up automatically with no
  // maintenance here.
  const BACKUP_PREFIX = "wt_";
  const PRE_IMPORT_KEY = "overload_pre_import_backup"; // deliberately NOT wt_*, so a restore can't wipe the safety net

  const [showBackup, setShowBackup] = useState(false);
  const [backupFallback, setBackupFallback] = useState(null);
  const [copiedBackup, setCopiedBackup] = useState(false);
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importDone, setImportDone] = useState(false);

  const collectBackup = () => {
    const keys = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(BACKUP_PREFIX)) keys[k] = localStorage.getItem(k);
      }
    } catch {
      // Storage unreadable (private mode, disabled cookies) — return an empty
      // envelope rather than throwing; the UI reports the empty case.
    }
    return {
      app: "overload",
      type: "backup",
      version: 1,
      exportedAt: new Date().toISOString(),
      keys,
    };
  };

  // Summarise a backup in the user's own terms (sessions, sets, dates) so they
  // can confirm it's the right file BEFORE it overwrites anything.
  const describeBackup = (payload) => {
    const readKey = (k) => {
      try {
        return JSON.parse(payload.keys[k]);
      } catch {
        return null;
      }
    };
    const raw = readKey("wt_sessions");
    const sess = Array.isArray(raw) ? raw : [];
    const sets = sess.reduce(
      (a, s) =>
        a +
        (s.exercises || []).reduce(
          (b, ex) => b + ((ex && ex.sets && ex.sets.length) || 0),
          0
        ),
      0
    );
    const dates = sess
      .map((s) => s && s.date)
      .filter(Boolean)
      .sort();
    const food = readKey("wt_food");
    return {
      sessions: sess.length,
      sets,
      first: dates[0] || null,
      last: dates[dates.length - 1] || null,
      foodDays:
        food && typeof food === "object" ? Object.keys(food).length : 0,
      keyCount: Object.keys(payload.keys || {}).length,
    };
  };

  const downloadBackup = () => {
    const payload = collectBackup();
    const json = JSON.stringify(payload);
    const filename = `overload-backup-${new Date()
      .toISOString()
      .slice(0, 10)}.json`;

    // Same reasoning as downloadCsv: programmatic downloads are silently
    // blocked in sandboxed/embedded previews, and we can't detect it. So we
    // always show the copy-to-clipboard fallback as well — that path is what
    // makes the move off the old host possible when downloads don't work.
    try {
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fall through to the fallback regardless
    }

    setBackupFallback({ filename, json });
    setCopiedBackup(false);
  };

  const copyBackupToClipboard = async () => {
    if (!backupFallback) return;
    try {
      await navigator.clipboard.writeText(backupFallback.json);
      setCopiedBackup(true);
      setTimeout(() => setCopiedBackup(false), 2500);
    } catch {
      // Select-all fallback handled by the textarea itself
    }
  };

  const stageImport = (raw) => {
    setImportError(null);
    setImportPreview(null);
    const text = (raw || "").trim();
    if (!text) {
      setImportError("Nothing to read — paste a backup or choose a file first.");
      return;
    }
    let payload;
    try {
      payload = JSON.parse(text);
    } catch {
      setImportError(
        "That isn't valid JSON. If you pasted it, make sure you copied the whole thing — from the first { to the last }."
      );
      return;
    }
    if (
      !payload ||
      payload.app !== "overload" ||
      !payload.keys ||
      typeof payload.keys !== "object"
    ) {
      setImportError(
        "That's readable, but it isn't an OverLoad backup. Use the file created by Download Backup."
      );
      return;
    }
    if (Object.keys(payload.keys).length === 0) {
      setImportError(
        "That backup is empty — there was no saved data on the device it came from."
      );
      return;
    }
    setImportPreview({ payload, summary: describeBackup(payload) });
  };

  const handleImportFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => stageImport(String(reader.result || ""));
    reader.onerror = () =>
      setImportError("Couldn't read that file. Try the paste box instead.");
    reader.readAsText(file);
    e.target.value = ""; // allow re-picking the same file after an error
  };

  // REPLACE, not merge. Merging risks duplicated sessions and wrong PRs, so
  // every existing wt_* key is cleared first and the backup written in whole.
  // Whatever was on the device is stashed under PRE_IMPORT_KEY first, so an
  // import onto the wrong device is recoverable rather than destructive.
  const applyImport = () => {
    if (!importPreview) return;
    try {
      localStorage.setItem(PRE_IMPORT_KEY, JSON.stringify(collectBackup()));

      const existing = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(BACKUP_PREFIX)) existing.push(k);
      }
      existing.forEach((k) => localStorage.removeItem(k));

      Object.entries(importPreview.payload.keys).forEach(([k, v]) => {
        if (k.startsWith(BACKUP_PREFIX) && typeof v === "string") {
          localStorage.setItem(k, v);
        }
      });

      setImportDone(true);
      // Reload so every screen re-reads storage from scratch rather than
      // holding stale React state from before the swap.
      setTimeout(() => window.location.reload(), 900);
    } catch {
      setImportError(
        "Couldn't save to this device. If you're browsing in Private mode, switch to a normal tab and try again."
      );
    }
  };

  const exportWorkoutLog = () => {
    const rows = [
      [
        "Date",
        "Muscle Group",
        "Exercise",
        "Set Number",
        "Weight (lbs)",
        "Reps",
        "Volume (lbs)",
        "Rest (sec)",
        "Set Type",
        "Session Note",
      ],
    ];
    [...sessions]
      .sort(
        (a, b) =>
          (a.id || new Date(a.date).getTime()) -
          (b.id || new Date(b.date).getTime())
      )
      .forEach((s) => {
        s.exercises.forEach((ex) => {
          ex.sets.forEach((set, si) => {
            rows.push([
              s.date,
              s.muscleGroup || "",
              ex.name || "",
              si + 1,
              set.weight ?? "",
              set.reps ?? "",
              Math.round((set.weight || 0) * (set.reps || 0)),
              set.rest ?? "",
              set.type === "super" ? "Superset" : "Working",
              s.note || "",
            ]);
          });
        });
      });
    downloadCsv("overload-workout-log.csv", rows);
  };

  const exportProgressionData = () => {
    const rows = [
      [
        "Date",
        "Muscle Group",
        "Exercise",
        "Best Weight (lbs)",
        "Best Reps",
        "Best Set Volume (lbs)",
        "Total Session Volume (lbs)",
        "Est. 1RM (lbs)",
        "Total Sets",
      ],
    ];
    [...sessions]
      .sort(
        (a, b) =>
          (a.id || new Date(a.date).getTime()) -
          (b.id || new Date(b.date).getTime())
      )
      .forEach((s) => {
        s.exercises.forEach((ex) => {
          if (!ex.sets || ex.sets.length === 0) return;
          const best = ex.sets.reduce((a, b) =>
            (b.weight || 0) * (1 + (b.reps || 0) / 30) >
            (a.weight || 0) * (1 + (a.reps || 0) / 30)
              ? b
              : a
          );
          const totalVolume = ex.sets.reduce(
            (sum, set) => sum + (set.weight || 0) * (set.reps || 0),
            0
          );
          const est1RM = Math.round(
            (best.weight || 0) * (1 + (best.reps || 0) / 30)
          );
          rows.push([
            s.date,
            s.muscleGroup || "",
            ex.name || "",
            best.weight ?? "",
            best.reps ?? "",
            Math.round((best.weight || 0) * (best.reps || 0)),
            totalVolume,
            est1RM,
            ex.sets.length,
          ]);
        });
      });
    downloadCsv("overload-progression.csv", rows);
  };

  // ── EXPORT SCREEN ──
  if (showBackup) {
    const live = describeBackup(collectBackup());
    const card = {
      background: "var(--surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--r-md)",
      padding: 18,
    };
    const primaryBtn = {
      width: "100%",
      background: "var(--accent)",
      border: "none",
      borderRadius: "var(--r-sm)",
      padding: "13px",
      fontSize: 13,
      fontWeight: 700,
      color: "var(--bg)",
      cursor: "pointer",
      fontFamily: "'DM Sans',sans-serif",
    };
    const ghostBtn = {
      ...primaryBtn,
      background: "var(--surface2)",
      border: "1px solid var(--border)",
      color: "var(--text)",
    };

    return (
      <div
        className="screen"
        style={{
                    display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <button
            className="btn-ghost sm"
            onClick={() => {
              setShowBackup(false);
              setBackupFallback(null);
              setImportPreview(null);
              setImportError(null);
              setImportText("");
            }}
          >
            ← Back
          </button>
        </div>

        <p
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "var(--text)",
            letterSpacing: -0.5,
          }}
        >
          Backup &amp; Restore
        </p>
        <p
          style={{
            fontSize: 13,
            color: "var(--muted)",
            marginTop: 6,
            lineHeight: 1.5,
            marginBottom: 24,
          }}
        >
          Your data is stored on this device only. A backup file lets you move
          it to a new phone, recover it if your browser clears its storage, or
          carry it across if the app moves to a new web address.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* ── BACKUP ── */}
          <div style={card}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 14,
              }}
            >
              <div>
                <p
                  style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}
                >
                  Download Backup
                </p>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    marginTop: 3,
                    lineHeight: 1.5,
                  }}
                >
                  Everything: workouts, food logs, cardio, check-ins,
                  favourites, profile and settings. Keep the file somewhere
                  safe.
                </p>
              </div>
              <span style={{ fontSize: 22, flexShrink: 0 }}>
                <Ic.Upload s={22} c="currentColor" />
              </span>
            </div>
            <p
              style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12 }}
            >
              {live.sessions} session{live.sessions !== 1 ? "s" : ""} ·{" "}
              {live.sets} sets
              {live.foodDays > 0
                ? ` · ${live.foodDays} day${
                    live.foodDays !== 1 ? "s" : ""
                  } of food`
                : ""}
            </p>
            <button onClick={downloadBackup} style={primaryBtn}>
              Download Backup (.json)
            </button>

            {backupFallback && (
              <div style={{ marginTop: 14 }}>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    lineHeight: 1.5,
                    marginBottom: 8,
                  }}
                >
                  If the download didn't start, copy the text below instead and
                  paste it into the Restore box on the other device.
                </p>
                <button onClick={copyBackupToClipboard} style={ghostBtn}>
                  {copiedBackup ? "✓ Copied to clipboard" : "Copy Backup Text"}
                </button>
                <textarea
                  readOnly
                  value={backupFallback.json}
                  onFocus={(e) => e.target.select()}
                  style={{
                    width: "100%",
                    height: 90,
                    marginTop: 10,
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--r-sm)",
                    color: "var(--muted)",
                    fontSize: 11,
                    fontFamily: "monospace",
                    padding: 10,
                    resize: "vertical",
                  }}
                />
              </div>
            )}
          </div>

          {/* ── RESTORE ── */}
          <div style={card}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 14,
              }}
            >
              <div>
                <p
                  style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}
                >
                  Restore From Backup
                </p>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    marginTop: 3,
                    lineHeight: 1.5,
                  }}
                >
                  Loads a backup file onto this device. You'll see what's in it
                  and confirm before anything is written.
                </p>
              </div>
              <span style={{ fontSize: 22, flexShrink: 0 }}>
                <Ic.Clipboard s={22} c="currentColor" />
              </span>
            </div>

            {importDone ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "18px 8px",
                  color: "var(--muted)",
                }}
              >
                <p style={{ fontSize: 28, marginBottom: 8 }}>✓</p>
                <p
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "var(--text)",
                  }}
                >
                  Restored
                </p>
                <p style={{ fontSize: 11, marginTop: 6 }}>Reloading…</p>
              </div>
            ) : importPreview ? (
              <div>
                <div
                  style={{
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--r-sm)",
                    padding: 14,
                    marginBottom: 12,
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                      marginBottom: 6,
                    }}
                  >
                    This backup contains
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      lineHeight: 1.6,
                    }}
                  >
                    {importPreview.summary.sessions} session
                    {importPreview.summary.sessions !== 1 ? "s" : ""} ·{" "}
                    {importPreview.summary.sets} sets
                    {importPreview.summary.foodDays > 0
                      ? ` · ${importPreview.summary.foodDays} day${
                          importPreview.summary.foodDays !== 1 ? "s" : ""
                        } of food`
                      : ""}
                    {importPreview.summary.first &&
                      ` · ${importPreview.summary.first} to ${importPreview.summary.last}`}
                  </p>
                  {importPreview.payload.exportedAt && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginTop: 6,
                      }}
                    >
                      Created{" "}
                      {new Date(
                        importPreview.payload.exportedAt
                      ).toLocaleString()}
                    </p>
                  )}
                </div>

                <p
                  style={{
                    fontSize: 11,
                    color: "var(--warn, #f0a020)",
                    lineHeight: 1.5,
                    marginBottom: 12,
                  }}
                >
                  This replaces everything currently on this device
                  {live.sessions > 0
                    ? ` (${live.sessions} session${
                        live.sessions !== 1 ? "s" : ""
                      }, ${live.sets} sets)`
                    : ""}
                  . A copy of the current data is saved first, so it can be
                  recovered if this was a mistake.
                </p>

                <button onClick={applyImport} style={primaryBtn}>
                  Replace My Data
                </button>
                <button
                  onClick={() => {
                    setImportPreview(null);
                    setImportText("");
                  }}
                  style={{ ...ghostBtn, marginTop: 8 }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div>
                <label
                  style={{
                    ...ghostBtn,
                    display: "block",
                    textAlign: "center",
                    boxSizing: "border-box",
                  }}
                >
                  Choose Backup File
                  <input
                    type="file"
                    accept="application/json,.json,text/plain"
                    onChange={handleImportFile}
                    style={{ display: "none" }}
                  />
                </label>

                <p
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    textAlign: "center",
                    margin: "12px 0 8px",
                  }}
                >
                  or paste the backup text
                </p>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder='{"app":"overload", ...}'
                  style={{
                    width: "100%",
                    height: 80,
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--r-sm)",
                    color: "var(--text)",
                    fontSize: 11,
                    fontFamily: "monospace",
                    padding: 10,
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                />
                <button
                  onClick={() => stageImport(importText)}
                  style={{ ...primaryBtn, marginTop: 10 }}
                >
                  Read Backup
                </button>
              </div>
            )}

            {importError && (
              <p
                style={{
                  fontSize: 11,
                  color: "var(--danger, #ff5a5a)",
                  lineHeight: 1.5,
                  marginTop: 12,
                }}
              >
                {importError}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (showExport) {
    const totalSets = sessions.reduce(
      (a, s) => a + s.exercises.reduce((b, ex) => b + ex.sets.length, 0),
      0
    );
    const exerciseRows = sessions.reduce(
      (a, s) => a + s.exercises.filter((ex) => ex.sets.length > 0).length,
      0
    );
    return (
      <div
        className="screen"
        style={{
                    display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <button
            className="btn-ghost sm"
            onClick={() => {
              setShowExport(false);
              setExportFallback(null);
            }}
          >
            ← Back
          </button>
        </div>

        <p
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "var(--text)",
            letterSpacing: -0.5,
          }}
        >
          Export Data
        </p>
        <p
          style={{
            fontSize: 13,
            color: "var(--muted)",
            marginTop: 6,
            lineHeight: 1.5,
            marginBottom: 24,
          }}
        >
          Download your training history as a CSV file — opens in Excel, Google
          Sheets, or Numbers. All-time data, every session you've ever logged.
        </p>

        {sessions.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "var(--muted)",
            }}
          >
            
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              Nothing to export yet
            </p>
            <p style={{ fontSize: 11, marginTop: 6 }}>
              Log a workout first, then come back here.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r-md)",
                padding: 18,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 14,
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--text)",
                    }}
                  >
                    Workout Log
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      marginTop: 3,
                      lineHeight: 1.5,
                    }}
                  >
                    Every set you've ever logged — date, exercise, weight, reps,
                    rest, and notes. One row per set.
                  </p>
                </div>
                <span style={{ fontSize: 22, flexShrink: 0 }}>
                  <Ic.Clipboard s={22} c="currentColor" />
                </span>
              </div>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginBottom: 12,
                }}
              >
                {sessions.length} session{sessions.length !== 1 ? "s" : ""} ·{" "}
                {totalSets} sets total
              </p>
              <button
                onClick={exportWorkoutLog}
                style={{
                  width: "100%",
                  background: "var(--accent)",
                  border: "none",
                  borderRadius: "var(--r-sm)",
                  padding: "13px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--bg)",
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                Download Workout Log (.csv)
              </button>
            </div>

            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r-md)",
                padding: 18,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 14,
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--text)",
                    }}
                  >
                    Progression Data
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      marginTop: 3,
                      lineHeight: 1.5,
                    }}
                  >
                    Your best set per exercise, per session — ready to chart
                    strength gains over time. One row per exercise per session.
                  </p>
                </div>
                <span style={{ fontSize: 22, flexShrink: 0 }}>
                  <Ic.TrendUp s={22} c="currentColor" />
                </span>
              </div>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginBottom: 12,
                }}
              >
                {exerciseRows} exercise log{exerciseRows !== 1 ? "s" : ""}{" "}
                across {sessions.length} session
                {sessions.length !== 1 ? "s" : ""}
              </p>
              <button
                onClick={exportProgressionData}
                style={{
                  width: "100%",
                  background: "var(--accent)",
                  border: "none",
                  borderRadius: "var(--r-sm)",
                  padding: "13px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--bg)",
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                Download Progression Data (.csv)
              </button>
            </div>

            {exportFallback && (
              <div
                style={{
                  background: "var(--surface)",
                  border: "1.5px solid var(--accent)",
                  borderRadius: "var(--r-md)",
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                    }}
                  >
                    {exportFallback.filename}
                  </p>
                  <button
                    onClick={() => setExportFallback(null)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--muted)",
                      fontSize: 13,
                      cursor: "pointer",
                      padding: 4,
                    }}
                  >
                    ✕
                  </button>
                </div>
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    lineHeight: 1.5,
                  }}
                >
                  If the download didn't start automatically (common when
                  viewing this inside an embedded preview), copy the data below
                  and paste it into a new spreadsheet, or save it as a .csv file
                  yourself.
                </p>
                <textarea
                  readOnly
                  value={exportFallback.csv}
                  onClick={(e) => e.target.select()}
                  style={{
                    width: "100%",
                    height: 140,
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--r-sm)",
                    padding: 10,
                    fontSize: 11,
                    fontFamily: "monospace",
                    color: "var(--text)",
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                />
                <button
                  onClick={copyExportToClipboard}
                  style={{
                    width: "100%",
                    background: copiedExport
                      ? "var(--success)"
                      : "var(--surface2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--r-sm)",
                    padding: "11px",
                    fontSize: 13,
                    fontWeight: 700,
                    color: copiedExport ? "var(--bg)" : "var(--text)",
                    cursor: "pointer",
                    fontFamily: "'DM Sans',sans-serif",
                    transition: "all .15s",
                  }}
                >
                  {copiedExport ? "✓ Copied to clipboard" : "Copy All Data"}
                </button>
              </div>
            )}

            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                textAlign: "center",
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              Files download directly to your device. Nothing is uploaded or
              sent anywhere.
            </p>
          </div>
        )}

        <style>{CSS}</style>
      </div>
    );
  }

  const calcBase = calcMacros(profile, "Rest");

  // ── TOGGLE COMPONENT ──
  const Toggle = ({ value, onChange }) => (
    <button
      onClick={() => onChange(!value)}
      style={{
        width: 46,
        height: 26,
        borderRadius: 13,
        border: "none",
        cursor: "pointer",
        background: value ? "var(--accent)" : "var(--border)",
        position: "relative",
        transition: "background .2s",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: value ? 23 : 3,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: value ? "var(--bg)" : "var(--surface)",
          transition: "left .2s",
          boxShadow: "0 1px 3px rgba(0,0,0,.3)",
        }}
      />
    </button>
  );

  // ── SECTION HEADER ──
  const SectionHeader = ({ icon, label }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 10,
        marginTop: 8,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)",
          border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
        }}
      >
        {icon}
      </div>
      <p
        style={{
          fontSize: 11,
          color: "var(--accent)",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 1.5,
        }}
      >
        {label}
      </p>
    </div>
  );

  // ── ROW COMPONENT ──
  const Row = ({ icon, label, sub, children, last, danger }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "13px 16px",
        borderBottom: last ? "none" : "1px solid var(--border)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flex: 1,
          minWidth: 0,
        }}
      >
        {icon && (
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              flexShrink: 0,
              background: danger ? "rgba(255,85,102,.1)" : "var(--surface2)",
              border: `1px solid ${
                danger ? "rgba(255,85,102,.2)" : "var(--border)"
              }`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
            }}
          >
            {icon}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: danger ? "var(--danger)" : "var(--text)",
            }}
          >
            {label}
          </p>
          {sub && (
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                marginTop: 2,
                lineHeight: 1.4,
              }}
            >
              {sub}
            </p>
          )}
        </div>
      </div>
      <div style={{ marginLeft: 12, flexShrink: 0 }}>{children}</div>
    </div>
  );

  // ── CARD WRAPPER ──
  const Card = ({ children }) => (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 16,
        overflow: "hidden",
        marginBottom: 24,
      }}
    >
      {children}
    </div>
  );

  const goalLabels = {
    bulk: "Build Muscle",
    cut: "Lose Fat",
    recomp: "Recomp",
  };

  return (
    <div
      style={{
        padding: "48px 16px 100px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ marginBottom: 28 }}>
        <p
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "var(--text)",
            letterSpacing: -0.5,
          }}
        >
          Settings
        </p>
        <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
          Customize your OverLoad experience
        </p>
      </div>

      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 20,
          padding: "20px 16px",
          marginBottom: 28,
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            flexShrink: 0,
            background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.12)",
            border: "1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            fontWeight: 700,
            color: "var(--accent)",
            fontFamily: "'Bebas Neue',sans-serif",
          }}
        >
          {profile.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>
            {profile.name}
          </p>
          <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
            {profile.weight} lbs · {profile.height || "—"} ·{" "}
            {profile.age || "—"} yrs
          </p>
          <div
            style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.12)",
                color: "var(--accent)",
                borderRadius: 6,
                padding: "3px 8px",
              }}
            >
              {GOALS[profile.goal]?.label || "—"}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                background: "var(--surface2)",
                color: "var(--muted)",
                borderRadius: 6,
                padding: "3px 8px",
              }}
            >
              {goalLabels[profile.bodyGoal] || "—"}
            </span>
          </div>
        </div>
        <button
          onClick={() => setEditProfile(true)}
          style={{
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            padding: "8px 14px",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--text)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          Edit
        </button>
      </div>

      {editProfile && (
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--accent)",
            borderRadius: 16,
            padding: 16,
            marginBottom: 24,
          }}
        >
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--accent)",
              marginBottom: 14,
            }}
          >
            Edit Profile
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { key: "weight", label: "Weight (70–700 lbs)", type: "number" },
              { key: "height", label: "Height (e.g. 5'10\")", type: "text" },
              { key: "age", label: "Age (18–120)", type: "number" },
            ].map(({ key, label, type }) => (
              <div key={key}>
                <label
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {label}
                </label>
                <input
                  type={type}
                  inputMode={type === "number" ? "decimal" : "text"}
                  value={profileForm[key] || ""}
                  onChange={(e) =>
                    setProfileForm((f) => ({ ...f, [key]: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    marginTop: 4,
                    padding: "10px 12px",
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    color: "var(--text)",
                    fontSize: 13,
                    boxSizing: "border-box",
                  }}
                />
              </div>
            ))}
            {!isValidWeight(profileForm.weight) && (
              <p style={{ fontSize: 11, color: "var(--danger)", lineHeight: 1.5 }}>
                Enter a valid weight between 70 and 700 lb.
              </p>
            )}
            {!isValidAge(profileForm.age) && (
              <p style={{ fontSize: 11, color: "var(--danger)", lineHeight: 1.5 }}>
                Enter a whole number between 18 and 120. OverLoad's nutrition
                recommendations are designed for adults 18 and older.
              </p>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button
                disabled={
                  !isValidAge(profileForm.age) ||
                  !isValidWeight(profileForm.weight)
                }
                onClick={() => {
                  // Age and weight are validated rather than coerced. Silently
                  // substituting a default is what produced the age-25 and
                  // weight-180 bugs — an invalid entry is refused, not
                  // replaced, so the previous valid value survives.
                  if (!isValidAge(profileForm.age)) return;
                  if (!isValidWeight(profileForm.weight)) return;
                  // Height is edited here as a free-text string. Saving it
                  // without re-deriving heightFt/heightIn left the raw fields
                  // stale, so the edit updated the display and had no effect
                  // on BMR. Re-parse so both stay in sync.
                  const parsed = resolveHeight({ height: profileForm.height });
                  onSaveProfile({
                    ...profileForm,
                    heightFt: String(parsed.heightFt),
                    heightIn: String(parsed.heightIn),
                  });
                  setEditProfile(false);
                }}
                style={{
                  flex: 1,
                  background: "var(--accent)",
                  border: "none",
                  borderRadius: 10,
                  padding: "11px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--bg)",
                  cursor: "pointer",
                }}
              >
                Save
              </button>
              <button
                onClick={() => setEditProfile(false)}
                style={{
                  flex: 1,
                  background: "var(--surface2)",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  padding: "11px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--muted)",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <SectionHeader icon={<Ic.Gear s={15} c="currentColor" />} label="Appearance" />
      <Card>
        <Row
          icon={<Ic.Moon s={15} c="currentColor" />}
          label="Dark Mode"
          sub="Switch between dark and light theme"
        >
          <Toggle
            value={settings.darkMode}
            onChange={(v) => onUpdate("darkMode", v)}
          />
        </Row>
        <Row
          icon={<Ic.Clock s={15} c="currentColor" />}
          label="Time Format"
          sub={
            settings.timeFormat === "12hr"
              ? "12-hour (3:45 PM)"
              : "24-hour (15:45)"
          }
          last
        >
          <div style={{ display: "flex", gap: 6 }}>
            {["12hr", "24hr"].map((f) => (
              <button
                key={f}
                onClick={() => onUpdate("timeFormat", f)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  background:
                    settings.timeFormat === f
                      ? "var(--accent)"
                      : "var(--surface2)",
                  color:
                    settings.timeFormat === f ? "var(--bg)" : "var(--muted)",
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </Row>
      </Card>

      <SectionHeader
        icon={<Ic.Barbell s={22} c="currentColor" />}
        label="Workout"
      />
      <Card>
        <Row
          icon={<Ic.Timer s={22} c="currentColor" />}
          label="Session Timer"
          sub="Running clock during workout"
        >
          <Toggle
            value={settings.autoTimer}
            onChange={(v) => onUpdate("autoTimer", v)}
          />
        </Row>
        <Row
          icon={<Ic.Pause s={20} c="currentColor" />}
          label="Rest Timer"
          sub="Auto-starts countdown after each set"
        >
          <Toggle
            value={settings.manualRest}
            onChange={(v) => onUpdate("manualRest", v)}
          />
        </Row>
        <Row
          icon={<Ic.Clipboard s={22} c="currentColor" />}
          label="Exercise Info"
          sub="Form guide and GIF for exercises"
        >
          <Toggle
            value={settings.showExerciseInfo}
            onChange={(v) => onUpdate("showExerciseInfo", v)}
          />
        </Row>
        <Row
          icon={<Ic.Calendar s={22} c="currentColor" />}
          label="Last Session Data"
          sub="Show previous sets during logging"
        >
          <Toggle
            value={settings.showLastSession !== false}
            onChange={(v) => onUpdate("showLastSession", v)}
          />
        </Row>
        <Row
          icon={<Ic.Target s={22} c="currentColor" />}
          label="PO Targets"
          sub="Show progressive overload target box"
        >
          <Toggle
            value={settings.showPOTargets !== false}
            onChange={(v) => onUpdate("showPOTargets", v)}
          />
        </Row>
        <Row
          icon={<Ic.Barbell s={15} c="currentColor" />}
          label="Live 1RM Estimate"
          sub="Estimated one-rep max as you log"
          last
        >
          <Toggle
            value={settings.show1RM !== false}
            onChange={(v) => onUpdate("show1RM", v)}
          />
        </Row>
      </Card>

      <SectionHeader
        icon={<Ic.Bell s={22} c="currentColor" />}
        label="Alerts"
      />
      <Card>
        <Row
          icon={<Ic.Bell s={22} c="currentColor" />}
          label="Vibration"
          sub="Buzz on countdown and rest complete"
          last
        >
          <Toggle
            value={settings.vibration !== false}
            onChange={(v) => onUpdate("vibration", v)}
          />
        </Row>
      </Card>

      <SectionHeader icon={<Ic.Target s={15} c="currentColor" />} label="Training Goals" />
      <Card>
        <Row
          icon={<Ic.Target s={22} c="currentColor" />}
          label="Training Goal"
          sub={GOALS[profile.goal]?.label || "—"}
        >
          {!editGoal ? (
            <button
              onClick={() => setEditGoal(true)}
              style={{
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "5px 12px",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text)",
                cursor: "pointer",
              }}
            >
              Change
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {Object.entries(GOALS).map(([key, g]) => (
                <button
                  key={key}
                  onClick={() => {
                    onSaveProfile({ ...profile, goal: key });
                    setEditGoal(false);
                  }}
                  style={{
                    padding: "7px 12px",
                    borderRadius: 8,
                    border: `1.5px solid ${
                      profile.goal === key ? "var(--accent)" : "var(--border)"
                    }`,
                    background:
                      profile.goal === key
                        ? "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)"
                        : "var(--surface2)",
                    color:
                      profile.goal === key ? "var(--accent)" : "var(--text)",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {g.label} · {g.minReps}–{g.maxReps} reps
                </button>
              ))}
              <button
                onClick={() => setEditGoal(false)}
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </Row>
        <Row
          icon={<Ic.Scale s={20} c="currentColor" />}
          label="Body Goal"
          sub={
            {
              bulk: "Build Muscle (Bulk)",
              cut: "Lose Fat (Cut)",
              recomp: "Maintain & Recomp",
            }[profile.bodyGoal] || "—"
          }
          last
        >
          {!editBodyGoal ? (
            <button
              onClick={() => setEditBodyGoal(true)}
              style={{
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "5px 12px",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text)",
                cursor: "pointer",
              }}
            >
              Change
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                { key: "bulk", label: "Build Muscle", sub: "Calorie surplus" },
                { key: "cut", label: "Lose Fat", sub: "Calorie deficit" },
                { key: "recomp", label: "Recomp", sub: "Maintain calories" },
              ].map((o) => (
                <button
                  key={o.key}
                  onClick={() => {
                    onSaveProfile({ ...profile, bodyGoal: o.key });
                    setEditBodyGoal(false);
                  }}
                  style={{
                    padding: "7px 12px",
                    borderRadius: 8,
                    border: `1.5px solid ${
                      profile.bodyGoal === o.key
                        ? "var(--accent)"
                        : "var(--border)"
                    }`,
                    background:
                      profile.bodyGoal === o.key
                        ? "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1)"
                        : "var(--surface2)",
                    color:
                      profile.bodyGoal === o.key
                        ? "var(--accent)"
                        : "var(--text)",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                    textAlign: "left",
                  }}
                >
                  {o.label}{" "}
                  <span style={{ fontWeight: 400, color: "var(--muted)" }}>
                    · {o.sub}
                  </span>
                </button>
              ))}
              <button
                onClick={() => setEditBodyGoal(false)}
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </Row>
      </Card>

      <SectionHeader
        icon={<Ic.Food s={22} c="currentColor" />}
        label="Nutrition"
      />
      <Card>
        <Row
          icon={<Ic.Apple s={15} c="currentColor" />}
          label="Calorie Adjustment"
          sub={(() => {
            // calcBase is computed with calAdjust 0, so its .calories is the
            // unadjusted target — which is exactly what "Base" should mean.
            const adjusted = calcMacros(profile, "Rest", settings.calAdjust);
            return adjusted.calorieFloorApplied
              ? `Base ${calcBase.calories} kcal · Limited to ${adjusted.calories} kcal for safety`
              : `Base ${calcBase.calories} kcal · Adjusted ${adjusted.calories} kcal`;
          })()}
          last
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              // The allowable decrement depends on the individual's TDEE and
              // body goal, so there is no universal minimum calAdjust. The
              // button is disabled once another step would take the target
              // below the floor — pressing it further would change a stored
              // number while the displayed target stayed put.
              disabled={
                calcMacros(profile, "Rest", settings.calAdjust - 250)
                  .calorieFloorApplied
              }
              onClick={() => {
                const next = settings.calAdjust - 250;
                if (!calcMacros(profile, "Rest", next).calorieFloorApplied) {
                  onUpdate("calAdjust", next);
                }
              }}
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px solid var(--border)",
                background: "var(--surface2)",
                color: "var(--text)",
                fontSize: 18,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              −
            </button>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                minWidth: 56,
                textAlign: "center",
                color:
                  settings.calAdjust === 0
                    ? "var(--muted)"
                    : settings.calAdjust > 0
                    ? "var(--success)"
                    : "var(--danger)",
              }}
            >
              {settings.calAdjust > 0
                ? `+${settings.calAdjust}`
                : settings.calAdjust === 0
                ? "None"
                : settings.calAdjust}
            </span>
            <button
              onClick={() => onUpdate("calAdjust", settings.calAdjust + 250)}
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px solid var(--border)",
                background: "var(--surface2)",
                color: "var(--text)",
                fontSize: 18,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              +
            </button>
          </div>
        </Row>
      </Card>

      <SectionHeader
        icon={<Ic.Run s={22} c="currentColor" />}
        label="Activity"
      />
      <Card>
        <Row
          icon={<Ic.ChartBar s={22} c="currentColor" />}
          label="Activity Level"
          sub={
            // Same meaning as onboarding: overall activity INCLUDING training.
            {
              sedentary: "Sedentary · incl. training",
              light: "Lightly Active · incl. training",
              moderate: "Moderately Active · incl. training",
              very: "Very Active · incl. training",
              extreme: "Extremely Active · incl. training",
            }[profile.activity] || "—"
          }
          last
        >
          <select
            value={profile.activity}
            onChange={(e) =>
              onSaveProfile({ ...profile, activity: e.target.value })
            }
            style={{
              background: "var(--surface2)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "6px 10px",
              color: "var(--text)",
              fontSize: 11,
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {[
              ["sedentary", "Sedentary"],
              ["light", "Lightly Active"],
              ["moderate", "Moderately Active"],
              ["very", "Very Active"],
              ["extreme", "Extremely Active"],
            ].map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </Row>
      </Card>

      <SectionHeader
        icon={<Ic.Upload s={22} c="currentColor" />}
        label="Data"
      />
      <Card>
        <Row
          icon={<Ic.Clipboard s={15} c="currentColor" />}
          label="Export Data"
          sub="Download your workout history as CSV"
        >
          <button
            onClick={() => setShowExport(true)}
            style={{
              background: "var(--surface2)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--text)",
              cursor: "pointer",
            }}
          >
            Export →
          </button>
        </Row>
        <Row
          icon={<Ic.Upload s={15} c="currentColor" />}
          label="Backup & Restore"
          sub="Save a full copy, or move your data to a new device"
          last
        >
          <button
            onClick={() => setShowBackup(true)}
            style={{
              background: "var(--surface2)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--text)",
              cursor: "pointer",
            }}
          >
            Open →
          </button>
        </Row>
      </Card>

      <div style={{ textAlign: "center", paddingTop: 8, paddingBottom: 8 }}>
        <p
          style={{
            fontSize: 22,
            fontWeight: 700,
            fontFamily: "'Bebas Neue',sans-serif",
            letterSpacing: 2,
            color: "var(--accent)",
          }}
        >
          OVERLOAD
        </p>
        <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
          Progressive Overload · Science-Based · Built for you
        </p>
      </div>

      <style>{CSS}</style>
    </div>
  );
}

const REST_TIPS = {};

const DEFAULT_REST_TIPS = [
  {
    icon: <Ic.Droplet s={22} c="currentColor" />,
    title: "Hydrate well",
    tip: "Drink at least 2-3L of water today. Muscles are 75% water — hydration directly impacts recovery speed.",
  },
  {
    icon: <Ic.Meat s={22} c="currentColor" />,
    title: "Hit your protein",
    tip: "Keep protein high on rest days. Your muscles are actively repairing — they need the amino acids regardless of training.",
  },
  {
    icon: <Ic.Moon s={22} c="currentColor" />,
    title: "Prioritize sleep",
    tip: "7-9 hours is when most muscle protein synthesis happens. Rest days without quality sleep slow recovery significantly.",
  },
  {
    icon: <Ic.Walk s={22} c="currentColor" />,
    title: "Light movement helps",
    tip: "A 20-minute walk or gentle stretching increases blood flow to sore muscles without adding stress. Active recovery beats total inactivity.",
  },
  {
    icon: <Ic.Yoga s={22} c="currentColor" />,
    title: "Manage stress",
    tip: "Cortisol from mental stress inhibits muscle repair just like overtraining does. Rest your mind too.",
  },
];

// One-time profile completion for users whose saved profile predates a field
// being collected. Asks only for what is actually missing — it is not an
// onboarding re-run, and it never touches any other stored value.
function ProfileCompletionScreen({ needAge, needWeight, onSave }) {
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const ok =
    (!needAge || isValidAge(age)) && (!needWeight || isValidWeight(weight));
  return (
    <div
      className="screen"
      style={{ justifyContent: "center", minHeight: "100vh" }}
    >
      <div style={{ marginBottom: 24 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            color: "var(--accent)",
            marginBottom: 6,
          }}
        >
          One quick update
        </p>
        <p style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.5 }}>
          {needAge && needWeight
            ? "A couple of details"
            : needWeight
            ? "What's your current weight?"
            : "What's your age?"}
        </p>
        <p
          style={{
            fontSize: 13,
            color: "var(--muted)",
            marginTop: 8,
            lineHeight: 1.6,
          }}
        >
          We need {needAge && needWeight ? "these" : "this"} to calculate your
          calorie and nutrition targets accurately. Your workouts and history
          are unchanged.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {needAge && (
          <WheelSelect
            value={age}
            onChange={setAge}
            options={AGE_OPTIONS}
            unit="yrs"
            placeholder="Age"
          />
        )}
        {needWeight && (
          <WheelSelect
            value={weight}
            onChange={setWeight}
            options={WEIGHT_OPTIONS}
            unit="lbs"
            placeholder="Weight"
          />
        )}
      </div>

      {needAge && (
        <p
          style={{
            fontSize: 11,
            color: "var(--muted)",
            marginTop: 10,
            lineHeight: 1.5,
          }}
        >
          OverLoad's nutrition recommendations are designed for adults 18 and
          older.
        </p>
      )}

      <div className="screen-action">
        <button
          className="btn-primary"
          disabled={!ok}
          onClick={() => {
            if (!ok) return;
            const patch = {};
            if (needAge) patch.age = String(age);
            if (needWeight) patch.weight = String(weight);
            onSave(patch);
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function RestDayScreen({
  profile,
  settings,
  recoveryStatus,
  sessions,
  restDays,
  todayKey,
  onLog,
  onBack,
}) {
  const alreadyLogged = restDays?.some((r) => r.date === todayKey);
  const [justLogged, setJustLogged] = useState(false);

  const handleLog = () => {
    onLog();
    setJustLogged(true);
  };
  const lastSession = [...sessions].reverse()[0];
  const lastGroup = lastSession?.muscleGroup?.split(" + ")[0] || null;
  const tips = REST_TIPS[lastGroup] || REST_TIPS.default || DEFAULT_REST_TIPS;

  const tomorrow = Object.entries(recoveryStatus || {})
    .filter(([g, r]) => r.status === "ready" || r.hoursRemaining <= 24)
    .sort((a, b) => (a[1].hoursRemaining || 0) - (b[1].hoursRemaining || 0))
    .map(([g]) => g);

  const nextSuggestion = tomorrow.find((g) => g !== lastGroup) || tomorrow[0];

  const restMacros = calcMacros(profile, "Rest", settings?.calAdjust || 0);

  return (
    <div className="screen" style={{ padding: 0 }}>
      <div className="log-header">
        <button className="btn-ghost sm" onClick={onBack}>
          ← Back
        </button>
        <p style={{ fontWeight: 700, fontSize: 18 }}>Rest Day</p>
        <div style={{ width: 60 }} />
      </div>

      <div
        style={{
          padding: "16px 16px 80px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          style={{
            background: "rgba(162,155,254,.08)",
            border: "1px solid rgba(162,155,254,.25)",
            borderRadius: 16,
            padding: "24px 20px",
            textAlign: "center",
          }}
        >
          <p style={{ fontSize: 40 }}>
            <Ic.Moon s={22} c="currentColor" />
          </p>
          <p
            style={{
              fontSize: 22,
              fontWeight: 700,
              marginTop: 12,
              color: "var(--purple)",
            }}
          >
            Rest is Training
          </p>
          <p
            style={{
              fontSize: 13,
              color: "var(--muted)",
              marginTop: 8,
              lineHeight: 1.6,
            }}
          >
            Muscles don't grow during the workout — they grow during recovery.
            Today is doing more work than you think.
          </p>
        </div>

        {!alreadyLogged && !justLogged ? (
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: 16,
            }}
          >
            <p
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--text)",
                marginBottom: 4,
              }}
            >
              Log today as a rest day?
            </p>
            <p
              style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12 }}
            >
              It will appear on your calendar so you can track your full week
              pattern.
            </p>
            <button
              onClick={handleLog}
              style={{
                width: "100%",
                background: "rgba(162,155,254,.12)",
                border: "1.5px solid rgba(162,155,254,.4)",
                borderRadius: 12,
                padding: "13px",
                fontSize: 13,
                fontWeight: 700,
                color: "var(--purple)",
                cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              <Ic.Moon s={22} c="currentColor" /> Yes, Log Rest Day
            </button>
          </div>
        ) : (
          <div
            style={{
              background: "rgba(61,220,132,.07)",
              border: "1px solid rgba(61,220,132,.3)",
              borderRadius: 16,
              padding: "14px 16px",
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 22 }}>✅</span>
            <div>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--success)",
                }}
              >
                Rest day logged
              </p>
              <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                Showing on your calendar today.
              </p>
            </div>
          </div>
        )}
        {recoveryStatus && (
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              padding: 16,
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--purple)",
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 12,
              }}
            >
              Current Recovery Status
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {MUSCLE_GROUPS.filter((g) => recoveryStatus[g]?.lastDate).map(
                (g) => {
                  const r = recoveryStatus[g];
                  const statusColor =
                    r.status === "ready"
                      ? "var(--success)"
                      : r.status === "almost"
                      ? "var(--accent)"
                      : "var(--muted)";
                  const label =
                    r.status === "ready"
                      ? "✓ Ready"
                      : `${r.hoursRemaining}h left`;
                  return (
                    <div
                      key={g}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "var(--text)",
                          width: 90,
                          flexShrink: 0,
                        }}
                      >
                        {g}
                      </span>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          background: "var(--surface2)",
                          borderRadius: 99,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${r.pctRecovered}%`,
                            height: "100%",
                            background: statusColor,
                            borderRadius: 99,
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          color: statusColor,
                          fontWeight: 700,
                          width: 65,
                          textAlign: "right",
                          flexShrink: 0,
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}

        <div>
          <p style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>
            {lastGroup ? `${lastGroup} Recovery Tips` : "Recovery Tips"}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tips.map((tip, i) => (
              <div
                key={i}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: 16,
                  padding: "14px 16px",
                  display: "flex",
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 22, flexShrink: 0 }}>{tip.icon}</span>
                <div>
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                    }}
                  >
                    {tip.title}
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    {tip.tip}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: 16,
          }}
        >
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--accent)",
              textTransform: "uppercase",
              letterSpacing: 1,
              marginBottom: 10,
            }}
          >
            Rest Day Nutrition
          </p>
          <p
            style={{
              fontSize: 11,
              color: "var(--muted)",
              lineHeight: 1.6,
              marginBottom: 12,
            }}
          >
            Carbs are lower today since you're not depleting glycogen. But keep
            protein high — your muscles are actively repairing right now.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 8,
            }}
          >
            {[
              {
                label: "Protein",
                val: `${restMacros.protein}g`,
                color: "var(--success)",
              },
              {
                label: "Carbs",
                val: `${restMacros.carbs}g`,
                color: "var(--accent)",
              },
              {
                label: "Calories",
                val: `${restMacros.calories}`,
                color: "var(--accent)",
              },
            ].map((m, i) => (
              <div
                key={i}
                className="stat-box"
                style={{ borderColor: "var(--border)" }}
              >
                <span
                  className="stat-num"
                  style={{ fontSize: 15, color: m.color }}
                >
                  <CountUp value={m.val} />
                </span>
                <span className="stat-lbl">{m.label}</span>
              </div>
            ))}
          </div>
        </div>

        {nextSuggestion && (
          <div
            style={{
              background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.06)",
              border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
              borderRadius: 16,
              padding: "14px 16px",
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--accent)",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Tomorrow
            </p>
            <p
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "var(--text)",
                marginTop: 6,
              }}
            >
              {nextSuggestion} will be ready
            </p>
            <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
              {recoveryStatus[nextSuggestion]?.status === "ready"
                ? "Fully recovered and ready to train."
                : `~${recoveryStatus[nextSuggestion]?.hoursRemaining}h left — will be ready by tomorrow.`}
            </p>
          </div>
        )}

        <button className="btn-ghost" onClick={onBack}>
          ← Back to Home
        </button>
      </div>
      <style>{CSS}</style>
    </div>
  );
}

function ShockWorkoutScreen({
  profile,
  sessions,
  recoveryStatus,
  onStart,
  onBack,
}) {
  const [generated, setGenerated] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [seed, setSeed] = useState(0); // increment to regenerate

  const generate = () => {
    setGenerating(true);
    setTimeout(() => {
      setGenerated(buildShockWorkout(profile, sessions, recoveryStatus, seed));
      setGenerating(false);
    }, 800); // slight delay for effect
  };

  useEffect(() => {
    generate();
  }, [seed]);

  const buildShockWorkout = (profile, sessions, recoveryStatus, seed) => {
    const goal = profile.goal || "hypertrophy";

    const recentGroups = sessions
      .slice(-3)
      .map((s) => s.muscleGroup?.split(" + ")[0]);
    const readyGroups = MUSCLE_GROUPS.filter((g) => {
      const r = recoveryStatus?.[g];
      return !r || r.status === "ready" || r.pctRecovered >= 80;
    });
    const preferred = readyGroups.filter((g) => !recentGroups.includes(g));
    const candidates =
      preferred.length > 0
        ? preferred
        : readyGroups.length > 0
        ? readyGroups
        : MUSCLE_GROUPS;
    const muscleGroup = candidates[seed % candidates.length];

    const groupSessions = sessions
      .filter((s) =>
        s.muscleGroup?.split(" + ").some((g) => g.trim() === muscleGroup)
      )
      .slice(-6);

    let avgReps = 10; // default
    if (groupSessions.length > 0) {
      const allReps = groupSessions
        .flatMap((s) => s.exercises.flatMap((ex) => ex.sets.map((s) => s.reps)))
        .filter(Boolean);
      if (allReps.length > 0)
        avgReps = Math.round(
          allReps.reduce((a, b) => a + b, 0) / allReps.length
        );
    }

    let shockRepMin, shockRepMax, shockType, shockReason;
    const rand = (seed * 7 + 3) % 2; // deterministic flip
    if (avgReps >= 10) {
      if (rand === 0) {
        shockRepMin = 4;
        shockRepMax = 6;
        shockType = "Heavy Overload";
        shockReason =
          "You typically train in the 8-12 range. Heavy sets of 4-6 reps apply maximum mechanical tension — a completely different stimulus for growth.";
      } else {
        shockRepMin = 15;
        shockRepMax = 20;
        shockType = "Metabolic Stress";
        shockReason =
          "High-rep sets of 15-20 create metabolic stress and muscle pump your muscles haven't experienced. This drives hypertrophy through a different pathway.";
      }
    } else if (avgReps <= 6) {
      shockRepMin = 12;
      shockRepMax = 15;
      shockType = "Volume Shock";
      shockReason =
        "You train heavy. Moderate-rep volume forces your muscles to sustain tension longer than they're used to — a powerful growth stimulus.";
    } else {
      shockRepMin = 15;
      shockRepMax = 20;
      shockType = "Metabolic Stress";
      shockReason =
        "High-rep metabolic sets flood the muscle with blood and create a growth stimulus through cellular swelling and metabolic byproduct accumulation.";
    }

    const recentExKeys = new Set(
      groupSessions.flatMap((s) =>
        s.exercises.map(
          (e) =>
            `${e.name.split(" (")[0]}__${
              e.name.match(/\(([^)]+)\)/)?.[1] || ""
            }`
        )
      )
    );

    const allExercises = EXERCISE_LIBRARY[muscleGroup] || [];

    const scored = allExercises
      .flatMap((ex) =>
        ex.equipment.map((eq) => {
          const key = `${ex.name}__${eq}`;
          const isRecent = recentExKeys.has(key);
          const subMuscle = getSubMuscle(ex.name) || "";
          return {
            name: `${ex.name} (${eq})`,
            key: `${ex.name}__${eq}`,
            exName: ex.name,
            subMuscle,
            isRecent,
            score: isRecent ? 0 : ((seed * 13 + ex.name.length) % 10) + 1,
          };
        })
      )
      .sort((a, b) => b.score - a.score);

    const picked = [];
    const usedSubMuscles = new Set();
    for (const candidate of scored) {
      if (picked.length >= 5) break;
      if (!usedSubMuscles.has(candidate.subMuscle) || picked.length < 3) {
        picked.push(candidate);
        usedSubMuscles.add(candidate.subMuscle);
      }
    }

    const exercises = picked.map((ex) => {
      const lastPerf = sessions
        .slice()
        .reverse()
        .find((s) =>
          s.exercises.find(
            (e) =>
              `${e.name.split(" (")[0]}__${
                e.name.match(/\(([^)]+)\)/)?.[1] || ""
              }` === ex.key || e.name.includes(ex.exName)
          )
        )
        ?.exercises.find((e) => e.name.includes(ex.exName));

      let targetWeight = null;
      let targetReps = Math.round((shockRepMin + shockRepMax) / 2);
      let reason = "";

      if (lastPerf?.sets?.length > 0) {
        const workingSets = lastPerf.sets;
        if (workingSets.length > 0) {
          const best = workingSets.reduce(
            (a, s) => (s.weight > a.weight ? s : a),
            workingSets[0]
          );
          const est1RM = Math.round(best.weight * (1 + best.reps / 30));
          const rawWeight = est1RM / (1 + targetReps / 30);
          targetWeight = Math.round(rawWeight / 5) * 5;
          reason = `Based on your ${best.weight}lbs × ${best.reps} → est. 1RM ${est1RM}lbs`;
        }
      }

      const sets = Array.from({ length: shockRepMin <= 6 ? 4 : 3 }, () => ({
        weight: targetWeight || 0,
        reps: targetReps,
        isTarget: true,
      }));

      return {
        name: ex.name,
        key: ex.key,
        sets: [],
        poTarget: targetWeight
          ? { weight: targetWeight, reps: targetReps, reason }
          : null,
        lastSets: lastPerf?.sets || [],
        shockReason: ex.isRecent
          ? "Adding variety — you've done this recently but it fits the shock plan"
          : `Fresh stimulus — not done recently`,
        targetSets: sets,
      };
    });

    return {
      muscleGroup,
      shockType,
      shockReason,
      shockRepMin,
      shockRepMax,
      exercises,
      generatedAt: Date.now(),
    };
  };

  const startWorkout = () => {
    if (!generated) return;
    const session = {
      id: Date.now(),
      date: new Date().toLocaleDateString(),
      muscleGroup: generated.muscleGroup,
      exercises: generated.exercises,
    };
    onStart(session);
  };

  const color = "var(--orange)";

  return (
    <div className="screen" style={{ padding: 0 }}>
      <div className="log-header">
        <button className="btn-ghost sm" onClick={onBack}>
          ← Back
        </button>
        <p style={{ fontWeight: 700, fontSize: 18 }}>
          <Ic.Dice s={22} c="currentColor" /> Build My Workout
        </p>
        <div style={{ width: 60 }} />
      </div>

      <div
        style={{
          padding: "16px 16px 80px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {generating && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <p style={{ fontSize: 40, marginBottom: 16 }}>⚡</p>
            <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
              Analyzing your training history...
            </p>
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 8 }}>
              Building your shock workout
            </p>
          </div>
        )}

        {!generating && generated && (
          <>
            <div
              style={{
                background: `linear-gradient(135deg, rgba(255,100,50,.12), rgba(255,50,150,.06))`,
                border: `1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
                borderRadius: 16,
                padding: "20px 18px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--text)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 2,
                    }}
                  >
                    Shock Protocol
                  </p>
                  <p
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: "var(--text)",
                      marginTop: 4,
                    }}
                  >
                    {generated.muscleGroup}
                  </p>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--text)",
                      fontWeight: 700,
                      marginTop: 2,
                    }}
                  >
                    {generated.shockType}
                  </p>
                </div>
                <div
                  style={{
                    background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.126)`,
                    border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
                    borderRadius: 12,
                    padding: "8px 12px",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      fontFamily: "'Bebas Neue',sans-serif",
                      color: "var(--text)",
                      lineHeight: 1,
                    }}
                  >
                    {generated.shockRepMin}–{generated.shockRepMax}
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--muted)",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                    }}
                  >
                    rep range
                  </p>
                </div>
              </div>
              <div
                style={{
                  marginTop: 12,
                  background: "rgba(0,0,0,.2)",
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--text)",
                    lineHeight: 1.6,
                  }}
                >
                  <Ic.Brain s={20} c="currentColor" /> {generated.shockReason}
                </p>
              </div>
            </div>

            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 10,
                }}
              >
                Your Shock Workout — {generated.exercises.length} Exercises
              </p>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {generated.exercises.map((ex, i) => (
                  <div
                    key={i}
                    style={{
                      background: "var(--surface)",
                      border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.2)`,
                      borderRadius: 16,
                      padding: 16,
                      borderLeft: `3px solid ${color}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "var(--text)",
                          }}
                        >
                          {i + 1}. {ex.name}
                        </p>
                        <p
                          style={{
                            fontSize: 11,
                            color: "var(--muted)",
                            marginTop: 2,
                          }}
                        >
                          {ex.shockReason}
                        </p>
                      </div>
                    </div>

                    {ex.poTarget && (
                      <div
                        style={{
                          marginTop: 10,
                          background: `rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.063)`,
                          border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.2)`,
                          borderRadius: 10,
                          padding: "10px 12px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--text)",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: 0.8,
                            }}
                          >
                            Target
                          </p>
                          <p
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: "var(--text)",
                              marginTop: 2,
                            }}
                          >
                            {ex.poTarget.weight} lbs × {generated.shockRepMin}–
                            {generated.shockRepMax} reps
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              marginTop: 2,
                            }}
                          >
                            {ex.poTarget.reason}
                          </p>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <p
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              fontFamily: "'Bebas Neue',sans-serif",
                              color: "var(--text)",
                            }}
                          >
                            {ex.targetSets.length}
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              color: "var(--muted)",
                              textTransform: "uppercase",
                            }}
                          >
                            sets
                          </p>
                        </div>
                      </div>
                    )}
                    {!ex.poTarget && (
                      <div
                        style={{
                          marginTop: 8,
                          background: "rgba(255,255,255,.04)",
                          borderRadius: 10,
                          padding: "8px 12px",
                        }}
                      >
                        <p style={{ fontSize: 11, color: "var(--muted)" }}>
                          No previous data — start light and build up. Target:{" "}
                          {generated.shockRepMin}–{generated.shockRepMax} reps
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "12px 14px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--text)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                Why This Works
              </p>
              <p
                style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.6 }}
              >
                Muscle confusion (Weider, 1951) works by changing the mechanical
                stimulus before full neural adaptation occurs. Varying rep
                ranges changes which muscle fiber types are primarily recruited
                — fast-twitch (heavy) vs slow-twitch (high rep) — forcing growth
                through a different pathway than your usual training.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={startWorkout}
                style={{
                  background: `linear-gradient(135deg, var(--orange), var(--danger))`,
                  border: "none",
                  borderRadius: 16,
                  padding: "18px",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "var(--text)",
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                  boxShadow: "0 4px 20px rgba(255,100,50,.3)",
                }}
              >
                ⚡ Start Shock Workout
              </button>
              <button
                onClick={() => setSeed((s) => s + 1)}
                style={{
                  background: "var(--surface)",
                  border: `1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),0.267)`,
                  borderRadius: 16,
                  padding: "16px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--text)",
                  cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                <Ic.Refresh s={22} c="currentColor" /> Generate Different
                Workout
              </button>
            </div>
          </>
        )}
      </div>
      <style>{CSS}</style>
    </div>
  );
}

const CARDIO_ACTIVITIES = [
  { name: "Incline Treadmill", icon: null, metFactor: 6.0 },
  { name: "Running", icon: <Ic.Run s={22} c="currentColor" />, metFactor: 9.8 },
  { name: "Stationary Bike", icon: null, metFactor: 7.0 },
  { name: "Jump Rope", icon: null, metFactor: 11.0 },
  { name: "Elliptical", icon: "⚙️", metFactor: 5.0 },
  { name: "Stairmaster", icon: null, metFactor: 8.0 },
  { name: "Rowing Machine", icon: null, metFactor: 7.0 },
  { name: "Swimming", icon: null, metFactor: 8.0 },
  {
    name: "Walking",
    icon: <Ic.Walk s={22} c="currentColor" />,
    metFactor: 3.5,
  },
  { name: "HIIT", icon: null, metFactor: 10.0 },
];

const CARDIO_DURATIONS = [15, 30, 45, 60];

function estimateCardioCalories(metFactor, weightLbs, durationMins) {
  const weightKg = weightLbs * 0.453592;
  return Math.round(metFactor * weightKg * (durationMins / 60));
}

function CardioScreen({ profile, onLog, onBack }) {
  const [phase, setPhase] = useState("pick");
  const [activity, setActivity] = useState(null);
  const [duration, setDuration] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null); // null = not started yet
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const endTimeRef = useRef(null);
  const intervalRef = useRef(null);

  // Defensive only — an invalid weight is intercepted before this screen is
  // reachable. The MET formula itself is unchanged.
  const weightLbs = Number(profile.weight) || 180;

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const startTimer = (dur) => {
    const endTime = Date.now() + dur * 60 * 1000;
    endTimeRef.current = endTime;
    pausedRef.current = false;
    setTimeLeft(dur * 60);
    setPhase("timer");

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (pausedRef.current) return;
      const remaining = Math.ceil((endTimeRef.current - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        setTimeLeft(0);
        setPhase("done");
      } else {
        setTimeLeft(remaining);
      }
    }, 500);
  };

  const togglePause = () => {
    if (!paused) {
      pausedRef.current = true;
      setPaused(true);
    } else {
      endTimeRef.current = Date.now() + timeLeft * 1000;
      pausedRef.current = false;
      setPaused(false);
    }
  };

  const stopEarly = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setPhase("done");
  };

  const fmtTime = (secs) => {
    if (!secs && secs !== 0) return "00:00";
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const totalSecs = (duration || 0) * 60;
  const elapsed = Math.max(0, totalSecs - (timeLeft ?? totalSecs));
  const pct = totalSecs > 0 ? Math.round((elapsed / totalSecs) * 100) : 0;
  const cal = estimateCardioCalories(
    activity?.metFactor || 6,
    weightLbs,
    duration || 0
  );

  if (phase === "pick") {
    return (
      <div className="screen" style={{ padding: 0 }}>
        <div className="log-header">
          <button className="btn-ghost sm" onClick={onBack}>
            ← Back
          </button>
          <p style={{ fontWeight: 700, fontSize: 18 }}>Cardio</p>
          <div style={{ width: 60 }} />
        </div>
        <div
          style={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <p style={{ fontSize: 13, color: "var(--muted)" }}>
            Choose your activity
          </p>
          {CARDIO_ACTIVITIES.map((a) => (
            <button
              key={a.name}
              onClick={() => {
                setActivity(a);
                setPhase("duration");
              }}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: "16px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <span style={{ fontSize: 28 }}>{a.icon}</span>
              <div>
                <p
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "var(--text)",
                  }}
                >
                  {a.name}
                </p>
                <p
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}
                >
                  ~{estimateCardioCalories(a.metFactor, weightLbs, 30)} kcal /
                  30 min
                </p>
              </div>
            </button>
          ))}
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  if (phase === "duration") {
    return (
      <div className="screen" style={{ padding: 0 }}>
        <div className="log-header">
          <button className="btn-ghost sm" onClick={() => setPhase("pick")}>
            ← Back
          </button>
          <p style={{ fontWeight: 700, fontSize: 15 }}>
            {activity.icon} {activity.name}
          </p>
          <div style={{ width: 60 }} />
        </div>
        <div
          style={{
            padding: "24px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <p style={{ fontSize: 13, color: "var(--muted)" }}>
            How long are you going?
          </p>
          {CARDIO_DURATIONS.map((d) => (
            <button
              key={d}
              onClick={() => {
                setDuration(d);
                startTimer(d);
              }}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: "20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    fontFamily: "'Bebas Neue',sans-serif",
                    color: "var(--text)",
                    letterSpacing: 1,
                  }}
                >
                  {d} min
                </p>
                <p
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}
                >
                  ~{estimateCardioCalories(activity.metFactor, weightLbs, d)}{" "}
                  kcal burned
                </p>
              </div>
              <span style={{ fontSize: 22 }}>→</span>
            </button>
          ))}
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  if (phase === "timer") {
    const radius = 90;
    const circ = 2 * Math.PI * radius;
    const dashOff = circ * (1 - pct / 100);
    return (
      <div
        className="screen"
        style={{
                    display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 24,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 22, fontWeight: 700 }}>
            {activity.icon} {activity.name}
          </p>
          <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
            {duration} min · ~{cal} kcal
          </p>
        </div>

        <div style={{ position: "relative", width: 220, height: 220 }}>
          <svg width="220" height="220" style={{ transform: "rotate(-90deg)" }}>
            <circle
              cx="110"
              cy="110"
              r={radius}
              fill="none"
              stroke="var(--border)"
              strokeWidth="12"
            />
            <circle
              cx="110"
              cy="110"
              r={radius}
              fill="none"
              stroke="var(--info)"
              strokeWidth="12"
              strokeDasharray={circ}
              strokeDashoffset={dashOff}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset .5s" }}
            />
          </svg>
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p
              style={{
                fontSize: 40,
                fontWeight: 700,
                fontFamily: "'Bebas Neue',sans-serif",
                color: paused ? "var(--muted)" : "var(--info)",
                lineHeight: 1,
              }}
            >
              {fmtTime(timeLeft)}
            </p>
            <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
              {paused ? "Paused" : "remaining"}
            </p>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 10,
            width: "100%",
          }}
        >
          {[
            { label: "Elapsed", val: fmtTime(duration * 60 - timeLeft) },
            { label: "% Done", val: `${pct}%` },
            {
              label: "Est. Cal",
              val: `~${Math.round((cal * pct) / 100)} kcal`,
            },
          ].map((s, i) => (
            <div
              key={i}
              className="stat-box"
              style={{ borderColor: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)" }}
            >
              <span
                className="stat-num"
                style={{ fontSize: 15, color: "var(--info)" }}
              >
                <CountUp value={s.val} />
              </span>
              <span className="stat-lbl">{s.label}</span>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, width: "100%" }}>
          <button
            onClick={togglePause}
            style={{
              flex: 1,
              background: paused ? "var(--info)" : "var(--surface2)",
              border: `1px solid ${paused ? "var(--info)" : "var(--border)"}`,
              borderRadius: 16,
              padding: "16px",
              fontSize: 15,
              fontWeight: 700,
              color: paused ? "var(--bg)" : "var(--text)",
              cursor: "pointer",
            }}
          >
            {paused ? "▶ Resume" : "⏸ Pause"}
          </button>
          <button
            onClick={stopEarly}
            style={{
              flex: 1,
              background: "rgba(255,85,102,.1)",
              border: "1px solid rgba(255,85,102,.4)",
              borderRadius: 16,
              padding: "16px",
              fontSize: 15,
              fontWeight: 700,
              color: "var(--danger)",
              cursor: "pointer",
            }}
          >
            Stop Early
          </button>
        </div>
        <style>{CSS}</style>
      </div>
    );
  }

  const actualMins =
    timeLeft === 0 ? duration : Math.max(1, Math.round(elapsed / 60));
  const actualCal = estimateCardioCalories(
    activity?.metFactor || 6,
    weightLbs,
    actualMins
  );

  return (
    <div
      className="screen"
      style={{
                display: "flex",
        flexDirection: "column",
        gap: 20,
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <div>
        
        <p style={{ fontSize: 28, fontWeight: 700, marginTop: 12 }}>
          {timeLeft === 0 ? "Cardio Complete!" : "Session Ended"}
        </p>
        <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 6 }}>
          {activity.icon} {activity.name} · {actualMins} min
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2,1fr)",
          gap: 12,
          width: "100%",
        }}
      >
        {[
          { label: "Duration", val: `${actualMins} min`, color: "var(--info)" },
          {
            label: "Calories",
            val: `~${actualCal} kcal`,
            color: "var(--danger)",
          },
        ].map((s, i) => (
          <div
            key={i}
            className="stat-box"
            style={{ borderColor: "var(--border)" }}
          >
            <span className="stat-num" style={{ color: s.color }}>
              <CountUp value={s.val} />
            </span>
            <span className="stat-lbl">{s.label}</span>
          </div>
        ))}
      </div>

      <div
        style={{
          background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07)",
          border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
          borderRadius: 16,
          padding: "14px 16px",
          width: "100%",
          textAlign: "left",
        }}
      >
        <p
          style={{
            fontSize: 11,
            color: "var(--info)",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          Calorie Estimate
        </p>
        <p
          style={{
            fontSize: 11,
            color: "var(--muted)",
            marginTop: 6,
            lineHeight: 1.6,
          }}
        >
          Based on MET value for {activity.name} × your body weight ({weightLbs}
          lbs) × {actualMins} min. Actual burn varies with intensity. Using
          Ainsworth et al. (2011) Compendium of Physical Activities.
        </p>
      </div>

      <button
        className="btn-primary"
        style={{ width: "100%", padding: 16 }}
        onClick={() => {
          onLog({
            activity: activity.name,
            duration: actualMins,
            caloriesBurned: actualCal,
          });
          onBack();
        }}
      >
        ✓ Save & Log Calories
      </button>
      <button className="btn-ghost" style={{ width: "100%" }} onClick={onBack}>
        Discard
      </button>
      <style>{CSS}</style>
    </div>
  );
}

function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState("in");

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("hold"), 700);
    const t2 = setTimeout(() => setPhase("out"), 2500);
    const t3 = setTimeout(() => onDone(), 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const visible = phase !== "in";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        opacity: phase === "out" ? 0 : 1,
        transition: phase === "out" ? "opacity .5s ease-in" : "none",
        userSelect: "none",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          width: 340,
          height: 340,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(var(--accent-r),var(--accent-g),var(--accent-b),.09) 0%, rgba(var(--accent-r),var(--accent-g),var(--accent-b),.03) 45%, transparent 70%)",
          opacity: visible ? 1 : 0,
          transition: "opacity 1.2s ease",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 28,
          opacity: visible ? 1 : 0,
          transform: visible
            ? "translateY(0) scale(1)"
            : "translateY(16px) scale(.93)",
          transition:
            "opacity .7s cubic-bezier(.32,.72,0,1), transform .7s cubic-bezier(.32,.72,0,1)",
        }}
      >
        <svg
          width="80"
          height="80"
          viewBox="0 0 80 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="40"
            cy="40"
            r="36"
            stroke="var(--accent)"
            strokeWidth="3"
            fill="none"
          />

          <circle
            cx="40"
            cy="40"
            r="26"
            stroke="var(--accent)"
            strokeWidth="1.5"
            fill="none"
            opacity=".45"
          />

          {[0, 60, 120, 180, 240, 300].map((angle) => {
            const rad = (angle * Math.PI) / 180;
            return (
              <line
                key={angle}
                x1={40 + Math.cos(rad) * 16}
                y1={40 + Math.sin(rad) * 16}
                x2={40 + Math.cos(rad) * 24}
                y2={40 + Math.sin(rad) * 24}
                stroke="var(--accent)"
                strokeWidth="2"
                opacity=".5"
                strokeLinecap="round"
              />
            );
          })}

          <circle
            cx="40"
            cy="40"
            r="7"
            stroke="var(--accent)"
            strokeWidth="2.5"
            fill="var(--bg)"
          />

          <circle cx="40" cy="40" r="2.5" fill="var(--accent)" />
        </svg>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
          }}
        >
          <p
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 40,
              letterSpacing: 10,
              lineHeight: 1,
              color: "var(--text)",
              textShadow: "0 0 60px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.12)",
              margin: 0,
            }}
          >
            OVER<span style={{ color: "var(--accent)" }}>LOAD</span>
          </p>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              opacity: visible ? 1 : 0,
              transition: "opacity .5s ease .35s",
            }}
          >
            <div
              style={{
                width: 36,
                height: 1,
                background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
              }}
            />
            <p
              style={{
                fontSize: 11,
                letterSpacing: 5,
                fontWeight: 700,
                color: "var(--text)",
                textTransform: "uppercase",
                fontFamily: "'DM Sans', sans-serif",
                margin: 0,
              }}
            >
              1 More Rep
            </p>
            <div
              style={{
                width: 36,
                height: 1,
                background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.3)",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 7,
            opacity: visible ? 1 : 0,
            transition: "opacity .4s ease .55s",
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: i === 1 ? "var(--accent)" : "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35)",
                animation: `splashDot 1s ease-in-out ${
                  i * 0.18
                }s infinite alternate`,
              }}
            />
          ))}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 52,
          // Was 0.28 here AND 50% alpha on the text below, which multiply out
          // to ~14% white on black — effectively invisible. The container now
          // only drives the fade-in; the text colour alone sets the contrast.
          opacity: visible ? 1 : 0,
          transition: "opacity .6s ease .5s",
        }}
      >
        <p
          style={{
            fontSize: 11, // 9px with 3px letter-spacing is below legible size on a phone
            letterSpacing: 3,
            color: "var(--muted)",
            textTransform: "uppercase",
            fontFamily: "'DM Sans', sans-serif",
            margin: 0,
          }}
        >
          Progressive Overload Tracker
        </p>
      </div>

      <style>{`
        @keyframes splashDot {
          from { opacity:.4; transform:translateY(0); }
          to   { opacity:1;  transform:translateY(-4px); }
        }
      `}</style>
    </div>
  );
}

function TourScreen({
  profile,
  settings,
  onUpdateSetting,
  onBulkUpdateSettings,
  onDone,
}) {
  const [step, setStep] = useState(0);
  const [level, setLevel] = useState(null);
  const [levelApplied, setLevelApplied] = useState(false);

  const applyLevel = (chosen) => {
    setLevel(chosen);
    const base = {
      autoTimer: true,
      manualRest: true,
      vibration: true,
      showLastSession: true,
      showPOTargets: true,
      darkMode: settings.darkMode, // preserve existing
      timeFormat: settings.timeFormat,
      calAdjust: settings.calAdjust,
    };
    const byLevel = {
      beginner: { ...base, showExerciseInfo: true, show1RM: false },
      intermediate: { ...base, showExerciseInfo: true, show1RM: true },
      expert: { ...base, showExerciseInfo: false, show1RM: true },
    };
    onBulkUpdateSettings(byLevel[chosen]);
    setLevelApplied(true);
  };

  const PhoneFrame = ({ children }) => (
    <div
      style={{
        background: "var(--surface)",
        border: "2px solid rgba(255,255,255,.12)",
        borderRadius: 24,
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,.5)",
        maxHeight: 280,
        position: "relative",
      }}
    >
      <div
        style={{
          background: "var(--bg)",
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid rgba(255,255,255,.06)",
        }}
      >
        <div
          style={{
            width: 60,
            height: 6,
            borderRadius: 99,
            background: "rgba(255,255,255,.15)",
          }}
        />
      </div>
      <div style={{ overflowY: "auto", maxHeight: 252 }}>{children}</div>
    </div>
  );

  const TOTAL_STEPS = 8;

  const steps = [
    {
      icon: "Barbell",
      title: `Welcome, ${profile.name}!`,
      subtitle: "Your profile is set up.",
      body: "OverLoad is a science-backed workout tracker built around progressive overload — the most proven principle in fitness. Let me show you how it works.",
      cta: "Let's Go →",
      bg: "linear-gradient(160deg, var(--bg) 0%, var(--surface2) 60%, var(--surface) 100%)",
      accent: "var(--accent)",
    },
    {
      icon: "Target",
      title: "Your Home Screen",
      subtitle: "Everything starts here.",
      body: "Tap Start Workout to log a session. Swipe the card below the buttons to see your last session, weekly stats, recovery, and more.",
      visual: (
        <PhoneFrame>
          <div
            style={{
              background: "linear-gradient(160deg,var(--bg),var(--surface2),var(--surface))",
              padding: "16px 14px 12px",
            }}
          >
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              Good morning,
            </p>
            <p style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>
              {profile.name}
            </p>
            <p
              style={{
                fontSize: 11,
                color: "var(--accent)",
                marginTop: 2,
                fontWeight: 700,
              }}
            >
              Muscle Growth mode
            </p>
          </div>
          <div
            style={{
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                background:
                  "linear-gradient(135deg,var(--accent),var(--accent))",
                borderRadius: 10,
                padding: "12px",
                textAlign: "center",
                fontWeight: 700,
                fontSize: 13,
                color: "var(--bg)",
              }}
            >
              <Ic.Barbell s={22} c="currentColor" /> Start Workout
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 6,
              }}
            >
              <div
                style={{
                  background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.2)",
                  border: "1px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.4)",
                  borderRadius: 8,
                  padding: "8px",
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--info)",
                }}
              >
                <Ic.Run s={22} c="currentColor" /> Cardio
              </div>
              <div
                style={{
                  background: "rgba(255,100,50,.2)",
                  border: "1px solid rgba(255,100,50,.4)",
                  borderRadius: 8,
                  padding: "8px",
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--orange)",
                }}
              >
                <Ic.Dice s={22} c="currentColor" /> Build Workout
              </div>
            </div>
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                padding: "10px 12px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Last Session — 5/22/2026
              </p>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--success)",
                }}
              >
                Back
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3,1fr)",
                  gap: 4,
                  marginTop: 6,
                }}
              >
                {[
                  ["17", "Sets"],
                  ["245", "Reps"],
                  ["22.9K", "Volume"],
                ].map(([v, l]) => (
                  <div
                    key={l}
                    style={{
                      background: "rgba(255,255,255,.04)",
                      borderRadius: 6,
                      padding: "5px 4px",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--success)",
                      }}
                    >
                      {v}
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        textTransform: "uppercase",
                      }}
                    >
                      {l}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </PhoneFrame>
      ),
      cta: "Next →",
      bg: "linear-gradient(160deg, var(--bg), var(--surface2))",
      accent: "var(--accent)",
    },
    {
      emoji: <Ic.TrendUp s={22} c="currentColor" />,
      title: "Progressive Overload",
      subtitle: "The engine behind your growth.",
      body: "Every time you repeat a workout, OverLoad analyzes your history and gives you a personal target to beat. This is the #1 driver of muscle growth.",
      visual: (
        <PhoneFrame>
          <div style={{ padding: "12px" }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text)",
                marginBottom: 8,
              }}
            >
              Row — Overhand (Machine)
            </p>
            <div
              style={{
                background: "rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)",
                border: "1px solid var(--accent)",
                borderRadius: 8,
                padding: "10px 12px",
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--accent)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  <Ic.Brain s={20} c="currentColor" /> Adaptive Target
                </p>
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    background: "rgba(255,255,255,.08)",
                    borderRadius: 4,
                    padding: "1px 5px",
                  }}
                >
                  personalized
                </span>
              </div>
              <p
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "var(--text)",
                  marginTop: 4,
                }}
              >
                95 lbs × 10 reps
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 2,
                }}
              >
                You're progressing at +5 lbs/wk
              </p>
            </div>
            <div
              style={{
                background: "rgba(255,255,255,.04)",
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 8,
                padding: "8px 10px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  marginBottom: 4,
                }}
              >
                Last Session
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {["90×14", "110×11", "120×10"].map((s) => (
                  <span
                    key={s}
                    style={{
                      fontSize: 11,
                      background: "rgba(255,255,255,.08)",
                      borderRadius: 6,
                      padding: "3px 7px",
                      color: "var(--text)",
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </PhoneFrame>
      ),
      cta: "Next →",
      bg: "linear-gradient(160deg, var(--surface), var(--bg))",
      accent: "var(--accent)",
    },
    {
      icon: "TrendUp",
      title: "Recovery Tracking",
      subtitle: "Know when your muscles are ready.",
      body: "After every workout, OverLoad tracks recovery time per muscle group based on scientific guidelines. The app recommends what to train based on what's most recovered.",
      visual: (
        <PhoneFrame>
          <div style={{ padding: "12px" }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--success)",
                marginBottom: 10,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Recovery Status
            </p>
            {[
              ["Chest", "100%", "var(--success)", "Ready ✓"],
              ["Back", "100%", "var(--success)", "Ready ✓"],
              ["Legs", "45%", "rgba(240,240,250,.3)", "38h left"],
              ["Shoulders", "20%", "rgba(255,85,102,.7)", "43h left"],
            ].map(([g, pct, color, label]) => (
              <div
                key={g}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    width: 68,
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text)",
                    flexShrink: 0,
                  }}
                >
                  {g}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 5,
                    background: "rgba(255,255,255,.08)",
                    borderRadius: 99,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: pct,
                      height: "100%",
                      background: color,
                      borderRadius: 99,
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--muted)",
                    fontWeight: 700,
                    flexShrink: 0,
                    width: 56,
                    textAlign: "right",
                  }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>
        </PhoneFrame>
      ),
      cta: "Next →",
      bg: "linear-gradient(160deg, var(--bg), var(--surface2))",
      accent: "var(--accent)",
    },
    {
      emoji: <Ic.Food s={22} c="currentColor" />,
      title: "Smart Nutrition",
      subtitle: "Targets that change with your training.",
      body: "Your daily targets automatically adjust based on what muscle group you trained. Leg day means more carbs. Rest day means fewer. Log meals in the Nutrition tab.",
      visual: (
        <PhoneFrame>
          <div style={{ padding: "12px" }}>
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <p
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: "var(--accent)",
                }}
              >
                2,200
              </p>
              <p style={{ fontSize: 11, color: "var(--muted)" }}>
                of 2,200 kcal · 2,200 kcal remaining
              </p>
            </div>
            {[
              ["Protein", "0g", "200g", "var(--success)"],
              ["Carbs", "0g", "270g", "var(--warn)"],
              ["Fat", "0g", "81g", "var(--orange)"],
            ].map(([label, curr, target, color]) => (
              <div key={label} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 3,
                  }}
                >
                  <span
                    style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}
                  >
                    {label}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--muted)" }}>
                    {curr} / {target}
                  </span>
                </div>
                <div
                  style={{
                    height: 4,
                    background: "rgba(255,255,255,.08)",
                    borderRadius: 99,
                  }}
                >
                  <div
                    style={{
                      width: "2%",
                      height: "100%",
                      background: color,
                      borderRadius: 99,
                    }}
                  />
                </div>
              </div>
            ))}
            <div
              style={{
                background: "rgba(255,255,255,.04)",
                borderRadius: 8,
                padding: "8px 10px",
                marginTop: 10,
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--accent)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                Why these numbers?
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 3,
                  lineHeight: 1.5,
                }}
              >
                Legs training causes the greatest glycogen depletion. Carbs
                adjusted higher for high-intensity training.
              </p>
            </div>
          </div>
        </PhoneFrame>
      ),
      cta: "Next →",
      bg: "linear-gradient(160deg, var(--surface), var(--bg))",
      accent: "var(--accent)",
    },
    {
      emoji: <Ic.Dice s={22} c="currentColor" />,
      title: "Build My Workout",
      subtitle: "Let OverLoad decide.",
      body: "Not sure what to train? OverLoad picks the most recovered muscle group, selects fresh exercises, and calculates weights from your 1RM history. A full workout in seconds.",
      visual: (
        <PhoneFrame>
          <div style={{ padding: "12px" }}>
            <p
              style={{
                fontSize: 11,
                color: "var(--muted)",
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 8,
              }}
            >
              <Ic.Dice s={22} c="currentColor" /> Build My Workout
            </p>
            <div
              style={{
                background:
                  "linear-gradient(135deg,rgba(255,100,50,.15),rgba(255,50,150,.08))",
                border: "1.5px solid rgba(255,100,50,.4)",
                borderRadius: 10,
                padding: "12px",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "var(--orange)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 2,
                  marginBottom: 4,
                }}
              >
                Shock Protocol — Back
              </p>
              <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>
                Metabolic Stress
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                Last trained 4 days ago — fully recovered. High-rep stimulus to
                break plateau.
              </p>
            </div>
            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexDirection: "column",
                gap: 5,
              }}
            >
              {[
                ["Lat Pulldown", "85 lbs × 12"],
                ["Row — Overhand", "95 lbs × 10"],
                ["Single Arm Row", "45 lbs × 12"],
              ].map(([ex, target]) => (
                <div
                  key={ex}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "5px 0",
                    borderBottom: "1px solid rgba(255,255,255,.06)",
                  }}
                >
                  <span style={{ fontSize: 11, color: "var(--text)" }}>
                    {ex}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--accent)",
                    }}
                  >
                    {target}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </PhoneFrame>
      ),
      cta: "Next →",
      bg: "linear-gradient(160deg, var(--surface), var(--bg))",
      accent: "var(--accent)",
    },
    {
      emoji: <Ic.Target s={22} c="currentColor" />,
      title: "Set Up Your Experience",
      subtitle: "We'll configure the app for you.",
      body: "Tell us where you are in your fitness journey. This configures your settings. You can change anything individually in Settings later.",
      isLevelPicker: true,
      cta: levelApplied ? "Next →" : null,
      bg: "linear-gradient(160deg, var(--bg), var(--surface2))",
      accent: "var(--accent)",
    },
    {
      icon: "Gear",
      title: "Your Settings",
      subtitle: "Fully customizable.",
      body: "Fine-tune anything in the Settings tab — Exercise Info guides, Live 1RM, Rest Timer vibration, PO targets, time format, and more.",
      visual: (
        <PhoneFrame>
          <div style={{ padding: "12px" }}>
            <p
              style={{
                fontSize: 11,
                color: "var(--purple)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 8,
              }}
            >
              Workout Features
            </p>
            {[
              {
                label: "Exercise Info",
                sub: "Form guides during logging",
                on: true,
              },
              {
                label: "Show Last Session",
                sub: "Previous sets per exercise",
                on: true,
              },
              {
                label: "PO Targets",
                sub: "Today's target for each exercise",
                on: true,
              },
              {
                label: "Live 1RM Estimate",
                sub: "Estimated max as you type",
                on: level !== "beginner",
              },
              {
                label: "Rest Timer Vibration",
                sub: "Buzz when countdown ends",
                on: true,
              },
            ].map((r, i, arr) => (
              <div
                key={r.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "7px 0",
                  borderBottom:
                    i < arr.length - 1
                      ? "1px solid rgba(255,255,255,.06)"
                      : "none",
                }}
              >
                <div>
                  <p
                    style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}
                  >
                    {r.label}
                  </p>
                  <p style={{ fontSize: 11, color: "var(--muted)" }}>
                    {r.sub}
                  </p>
                </div>
                <div
                  style={{
                    width: 30,
                    height: 16,
                    borderRadius: 8,
                    background: r.on ? "var(--purple)" : "rgba(255,255,255,.1)",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 2,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "var(--text)",
                      transition: "left .2s",
                      left: r.on ? 16 : 2,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </PhoneFrame>
      ),
      cta: "Start Training",
      bg: "linear-gradient(160deg, var(--bg), var(--surface2))",
      accent: "var(--accent)",
      isLast: true,
    },
  ];

  const current = steps[step];
  const StepIcon = Ic[current.icon];

  const LEVEL_OPTIONS = [
    {
      key: "beginner",
      emoji: <Ic.Leaf s={22} c="currentColor" />,
      label: "Beginner",
      desc: "New to working out",
      features: "Exercise guides on · 1RM off · All timers on",
      color: "var(--info)",
    },
    {
      key: "intermediate",
      icon: "Apple",
      label: "Intermediate",
      desc: "Some training experience",
      features: "All features on · Full data display",
      color: "var(--accent)",
    },
    {
      key: "expert",
      icon: "Battery",
      label: "Expert",
      desc: "Experienced lifter",
      features: "Exercise guides off · Clean interface · Full data",
      color: "var(--orange)",
    },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 500,
        background: current.bg,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        // The tour is a fixed full-screen takeover rendered from an early
        // return in App, so nothing above it mounts. Without this <style> tag
        // the tour ran with NO stylesheet at all: no body font-family (text
        // fell back to the browser's serif) and no :root custom properties, so
        // every var(--accent) / var(--text) / var(--bg) below resolved to
        // nothing and those colour rules were silently dropped — which is what
        // made text render black-on-black.
        overflowX: "hidden", // content wider than the viewport was scrolling sideways and clipping headings
        transition: "background .4s",
      }}
    >
      <style>{CSS}</style>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "rgba(255,255,255,.1)",
          zIndex: 501,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${((step + 1) / TOTAL_STEPS) * 100}%`,
            background: current.accent,
            transition: "width .4s",
            borderRadius: "0 2px 2px 0",
          }}
        />
      </div>

      {step < TOTAL_STEPS - 1 && (
        <button
          onClick={onDone}
          style={{
            position: "fixed",
            top: 18,
            right: 18,
            zIndex: 502,
            background: "rgba(255,255,255,.08)",
            border: "1px solid rgba(255,255,255,.15)",
            borderRadius: 20,
            padding: "6px 14px",
            fontSize: 11,
            color: "var(--muted)",
            cursor: "pointer",
          }}
        >
          Skip
        </button>
      )}

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "80px 28px 120px",
          maxWidth: 480,
          margin: "0 auto",
          width: "100%",
          // Explicit rather than relying on the global `* { box-sizing }` rule:
          // width:100% plus 28px of side padding overflows the viewport by 56px
          // under the default content-box, which is what clipped the headings.
          boxSizing: "border-box",
        }}
      >
        <div style={{ marginBottom: 16, lineHeight: 1, color: current.accent }}>
          {StepIcon ? <StepIcon s={40} c="currentColor" /> : null}
        </div>

        <p
          style={{
            fontSize: 11,
            color: "var(--accent)",
            textTransform: "uppercase",
            letterSpacing: 2,
            fontWeight: 700,
            marginBottom: 8,
          }}
        >
          {current.subtitle}
        </p>
        <p
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: "var(--text)",
            lineHeight: 1.2,
            letterSpacing: -0.5,
            marginBottom: 16,
          }}
        >
          {current.title}
        </p>
        <p
          style={{
            fontSize: 15,
            color: "var(--text)",
            lineHeight: 1.7,
            marginBottom: 24,
          }}
        >
          {current.body}
        </p>

        {current.visual && (
          <div style={{ marginBottom: 24 }}>{current.visual}</div>
        )}

        {current.isLevelPicker && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 24,
            }}
          >
            {LEVEL_OPTIONS.map((opt) => {
              const selected = level === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => applyLevel(opt.key)}
                  style={{
                    background: selected
                      ? "rgba(255,255,255,.10)"
                      : "rgba(255,255,255,.04)",
                    border: `2px solid ${
                      selected ? opt.color : "rgba(255,255,255,.1)"
                    }`,
                    borderRadius: 16,
                    padding: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all .2s",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <span style={{ fontSize: 28 }}>{opt.emoji}</span>
                    <div>
                      {/* Title stays light for readability on the dark card; the
                        accent color lives on the border + checkmark only, never
                        the text (which previously made it dark-on-dark). */}
                      <p
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "var(--text)",
                        }}
                      >
                        {opt.label}
                      </p>
                      <p
                        style={{
                          fontSize: 11,
                          color: "var(--muted)",
                          marginTop: 2,
                        }}
                      >
                        {opt.desc}
                      </p>
                    </div>
                    {selected && (
                      <span
                        style={{
                          marginLeft: "auto",
                          fontSize: 18,
                          color: opt.color,
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                  {selected && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--text)",
                        marginTop: 10,
                        paddingTop: 10,
                        borderTop: "1px solid rgba(255,255,255,.12)",
                      }}
                    >
                      {opt.features}
                    </p>
                  )}
                </button>
              );
            })}
            {levelApplied && (
              <p
                style={{
                  fontSize: 11,
                  color: "var(--muted)",
                  textAlign: "center",
                  marginTop: 4,
                }}
              >
                ✓ Settings configured — you can change them anytime in Settings
              </p>
            )}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 6,
            justifyContent: "center",
            marginBottom: 24,
          }}
        >
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 4,
                borderRadius: 99,
                transition: "all .3s",
                width: i === step ? 20 : 6,
                background:
                  i <= step ? current.accent : "rgba(255,255,255,.15)",
              }}
            />
          ))}
        </div>

        {current.cta && (
          <button
            onClick={() => (current.isLast ? onDone() : setStep((s) => s + 1))}
            style={{
              background: current.accent,
              border: "none",
              borderRadius: 16,
              padding: "18px",
              fontSize: 15,
              fontWeight: 700,
              color: "var(--bg)",
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
              boxShadow: `0 4px 24px rgba(0,0,0,.3)`,
              letterSpacing: 0.3,
              width: "100%",
            }}
          >
            {current.cta}
          </button>
        )}

        {step > 0 && (
          <button
            onClick={() => setStep((s) => s - 1)}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted)",
              fontSize: 13,
              cursor: "pointer",
              marginTop: 12,
              textAlign: "center",
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            ← Back
          </button>
        )}
      </div>
    </div>
  );
}

const CSS = `
 @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700;800&display=swap');

 * { box-sizing:border-box; margin:0; padding:0; }
 /* THE bug behind every "black text on black background" report.
    Only '.theme-light body' ever set a text colour, so in dark mode body had
    none — and any element that doesn't set 'color' itself inherited the
    browser default, which is BLACK. On a near-black background that text was
    invisible. It affected every heading and label that relied on inheriting.
    This is the base both themes now inherit from. */
 body { font-family:'DM Sans',sans-serif; background:var(--bg); color:var(--text); }
 /* Form controls and buttons do NOT inherit font-family by default — without
    this they fall back to the browser's system UI font (San Francisco on iOS,
    Roboto on Android), which is exactly why inputs/buttons looked like a
    different font than the DM Sans text beside them. Force inheritance so the
    whole app is visually consistent. Bebas Neue (display headers) is always
    applied explicitly where wanted, so this never overrides it. */
 input, select, textarea, button { font-family:inherit; }
 input[type=number]::-webkit-inner-spin-button,
 input[type=number]::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
 input[type=number] { -moz-appearance:textfield; }

 /* ── DESIGN TOKENS ───────────────────────────────────────────────
    ONE source of truth. This block previously existed THREE times
    (.theme-dark, :root, and .theme-light twice) with conflicting values
    for the same names — editing one and having another silently win
    further down the file is why past colour fixes appeared not to work.

    :root carries the dark values (the default). .theme-light overrides
    them and must stay AFTER :root — equal specificity, so source order
    decides. body gets .theme-dark / .theme-light set from JS.

    Contrast: every text colour clears 4.5:1 against --bg, --surface AND
    --surface2 in both themes. --subtle is BORDERS ONLY, never text.

    --info / --purple / --orange are kept as names because ~66 places
    still reference them. They now point at the accent and the neutral
    ramp instead of six competing hues, so the palette collapses to one
    accent without any of those places losing their colour. */
 :root {
 --bg:#0a0a0f; --surface:#141419; --surface2:#1c1c24; --surface3:#26262f;
 --text:#f2f4f8; --muted:#9a9ab2; --subtle:#3a3a48;
 --border:#26262f;
 --accent:#4ec8ff; --accent-r:78; --accent-g:200; --accent-b:255;
 --success:#3ddc84; --danger:#ff6b7a; --warn:#ffb84d;
 --info:var(--success); --purple:var(--muted); --orange:var(--warn);
 --surface-workout:#141419; --surface-nutrition:#141419;

 /* ── TYPE SCALE ── 7 sizes, nothing below 11px ── */
 --fs-micro:11px; --fs-sm:13px; --fs-body:15px; --fs-title:18px;
 --fs-head:22px; --fs-page:28px; --fs-hero:40px;

 /* ── RADIUS ── */
 --r-sm:10px; --r-md:14px; --r-lg:20px; --radius:14px;

 /* ── ELEVATION ── */
 --shadow-sm:0 1px 3px rgba(0,0,0,.3);
 --shadow-md:0 4px 16px rgba(0,0,0,.35);
 --shadow-lg:0 8px 32px rgba(0,0,0,.4);
 }

 .theme-light {
 --bg:#f7f8fa; --surface:#ffffff; --surface2:#eceef3; --surface3:#e2e5ec;
 --text:#14151a; --muted:#5a6070; --subtle:#c4c9d4;
 --border:#dfe3ea;
 --accent:#00679e; --accent-r:0; --accent-g:103; --accent-b:158;
 --success:#0b7742; --danger:#c62839; --warn:#8a5800;
 --surface-workout:#ffffff; --surface-nutrition:#ffffff;
 --shadow-sm:0 1px 3px rgba(0,0,0,.08);
 --shadow-md:0 4px 16px rgba(0,0,0,.1);
 --shadow-lg:0 8px 32px rgba(0,0,0,.12);
 }

 /* Light theme needs dark-on-accent inverted to light-on-accent, since
    the light accent is a deep blue rather than a pale one. */
 .theme-light .logo-box { background:var(--accent); }
 .theme-light .btn-primary { background:var(--accent); color:#fff; }
 .theme-light .btn-finish { background:var(--success); color:#fff; }
 .theme-light .tab.active { color:var(--accent); border-bottom-color:var(--accent); }
 .theme-light .nav-btn { color:var(--muted); }
 .theme-light .nav-btn.active { color:var(--accent); }
 .theme-light .nav-btn.active svg { stroke:var(--accent); }
 .theme-light .rest-chip.active { background:rgba(0,103,158,.1); border-color:var(--accent); color:var(--accent); }
 .theme-light .ex-card.open { border-color:var(--accent); box-shadow:0 2px 16px rgba(0,103,158,.12); }
 /* ── RESET ── */
 * { box-sizing:border-box; -webkit-font-smoothing:antialiased; }
 button { -webkit-tap-highlight-color:transparent; }

 /* ── SCREEN ── */
 .screen { display:flex; flex-direction:column; gap:0; padding:48px 20px 100px; min-height:100vh; overflow-y:auto; background:var(--bg); }

 /* ── ANIMATIONS ── */
 @keyframes slideInRight { from{transform:translateX(100%);opacity:.6} to{transform:translateX(0);opacity:1} }
 @keyframes slideOutLeft { from{transform:translateX(0);opacity:1} to{transform:translateX(-30%);opacity:0} }
 @keyframes slideInLeft { from{transform:translateX(-30%);opacity:.6} to{transform:translateX(0);opacity:1} }
 @keyframes slideOutRight { from{transform:translateX(0);opacity:1} to{transform:translateX(100%);opacity:0} }
 @keyframes fadeInTab { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
 @keyframes slideUpSheet { from{transform:translateY(100%);opacity:0} to{transform:translateY(0);opacity:1} }
 @keyframes pressDown { 0%{transform:scale(1)} 50%{transform:scale(.96)} 100%{transform:scale(1)} }
 @keyframes successPop { 0%{transform:scale(1)} 40%{transform:scale(1.06)} 100%{transform:scale(1)} }
 @keyframes slideUp { from{transform:translateY(6px);opacity:0} to{transform:translateY(0);opacity:1} }
 @keyframes fadeIn { from{opacity:0} to{opacity:1} }
 @keyframes workoutPulse { 0%,100%{box-shadow:0 4px 24px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35)} 50%{box-shadow:0 4px 36px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.6),0 0 0 6px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.08)} }
 @keyframes prFlash { 0%{background:rgba(255,215,0,.0)} 30%{background:rgba(255,215,0,.18)} 100%{background:rgba(255,215,0,.0)} }
 @keyframes setSlideIn { from{transform:translateX(-8px);opacity:0} to{transform:translateX(0);opacity:1} }
 @keyframes confettiFall { 0%{transform:translateY(0) rotate(0deg);opacity:.9} 100%{transform:translateY(320px) rotate(720deg);opacity:0} }
 @keyframes celebBounce { 0%{transform:scale(0) rotate(-10deg);opacity:0} 60%{transform:scale(1.2) rotate(5deg)} 100%{transform:scale(1) rotate(0deg);opacity:1} }
 @keyframes fadeSlideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
 @keyframes splashDot { 0%,80%,100%{transform:scale(0);opacity:.3} 40%{transform:scale(1);opacity:1} }

 .screen-push { animation:slideInRight .28s cubic-bezier(.32,.72,0,1) both; }
 .screen-pop  { animation:slideInLeft .25s cubic-bezier(.32,.72,0,1) both; }
 .screen-tab  { animation:fadeInTab .18s ease both; }

 /* ── TYPOGRAPHY SYSTEM ── 3 levels */
 .t-display { font-size:28px; font-weight:700; color:var(--text); letter-spacing:-.5px; line-height:1.1; }
 .t-title   { font-size:18px; font-weight:700; color:var(--text); letter-spacing:-.2px; }
 .t-body    { font-size:13px; font-weight:400; color:var(--text); line-height:1.5; }
 .t-caption { font-size:11px; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:.8px; }

 /* ── CARD ── elevation-based, minimal border */
 .card { background:var(--surface); border-radius:var(--r-md); padding:16px; display:flex; flex-direction:column; gap:12px; box-shadow:var(--shadow-sm); }
 .theme-dark .card { border:1px solid var(--border); }

 /* ── STAT BOX ── */
 .stat-box { background:var(--surface2); border-radius:var(--r-sm); padding:14px 10px; display:flex; flex-direction:column; align-items:center; gap:4px; }
 .stat-num { font-family:'Bebas Neue',sans-serif; font-size:22px; color:var(--text); line-height:1; text-align:center; word-break:break-all; }
 .stat-lbl { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:.8px; text-align:center; font-weight:700; }

 /* ── CHIP / TAG ── */
 .chip { background:var(--surface3); border-radius:var(--r-sm); padding:4px 10px; font-size:11px; color:var(--muted); font-weight:700; }

 /* ── BUTTONS ── */
 .btn-primary { background:var(--accent); color:var(--bg); border:none; border-radius:var(--r-md); padding:15px 24px; font-size:15px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; width:100%; letter-spacing:.2px; transition:transform .12s,opacity .12s; box-shadow:0 4px 16px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.25); }
 .btn-primary:active:not(:disabled) { transform:scale(.96); opacity:.9; }
 .btn-primary:disabled { opacity:.35; cursor:not-allowed; }
 .screen-action { position:sticky; bottom:0; z-index:5; display:flex; flex-direction:column; gap:10px; margin:auto -20px 0; padding:16px 20px calc(16px + env(safe-area-inset-bottom)); background:linear-gradient(to top, var(--bg) 62%, transparent); }

 .btn-ghost { background:none; border:1.5px solid var(--border); border-radius:var(--r-sm); padding:10px 16px; color:var(--muted); font-size:13px; cursor:pointer; font-family:'DM Sans',sans-serif; width:100%; transition:all .15s; }
 .btn-ghost:active { border-color:var(--accent); color:var(--accent); transform:scale(.97); }
 .btn-ghost.sm { padding:6px 12px; font-size:11px; width:auto; }

 .btn-finish { background:var(--success); color:var(--bg); border:none; border-radius:var(--r-sm); padding:8px 18px; font-size:13px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; letter-spacing:.2px; transition:transform .12s,box-shadow .12s; }
 .btn-finish:active { transform:scale(.93); box-shadow:0 0 0 4px rgba(61,220,132,.2); }

 .btn-add-set { background:var(--accent); color:var(--bg); border:none; border-radius:var(--r-sm); padding:13px 16px; font-size:13px; font-weight:700; cursor:pointer; white-space:nowrap; font-family:'DM Sans',sans-serif; align-self:flex-end; flex-shrink:0; transition:transform .12s; }
 .btn-add-set:active { transform:scale(.91); }
 .btn-add-set:disabled { opacity:.35; cursor:not-allowed; }

 .btn-add-exercise { background:var(--surface); border:1.5px dashed var(--subtle); border-radius:var(--r-md); padding:16px; color:var(--muted); font-size:13px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; width:100%; transition:all .15s; }
 .btn-add-exercise:active { border-color:var(--accent); color:var(--accent); transform:scale(.98); }

 .btn-finish-exercise { width:100%; background:rgba(61,220,132,.07); border:1.5px solid rgba(61,220,132,.3); border-radius:var(--r-sm); padding:13px; color:var(--success); font-size:13px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; letter-spacing:.2px; transition:all .15s; }
 .btn-finish-exercise:active { background:rgba(61,220,132,.15); transform:scale(.98); }

 .btn-set-action { flex:1; border:none; border-radius:var(--r-sm); padding:11px 6px; font-size:11px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; transition:transform .12s,opacity .12s; letter-spacing:.2px; }
 .btn-set-action:active:not(:disabled) { transform:scale(.91); opacity:.85; }
 .btn-set-action:disabled { opacity:.35; cursor:not-allowed; }
 .btn-set-action.normal { background:var(--accent); color:var(--bg); }
 .btn-set-action.super  { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.15); color:var(--purple); border:1.5px solid rgba(var(--accent-r),var(--accent-g),var(--accent-b),.4); }

 /* ── FORM INPUTS ── */
 .big-input { width:100%; background:var(--surface2); border:1.5px solid var(--border); border-radius:var(--r-sm); padding:14px 16px; color:var(--text); font-size:15px; font-family:'DM Sans',sans-serif; outline:none; box-sizing:border-box; transition:border-color .15s,box-shadow .15s; }
 .big-input:focus { border-color:var(--accent); box-shadow:0 0 0 3px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1); }

 .num-input { background:var(--surface2); border:1.5px solid var(--border); border-radius:var(--r-sm); padding:13px 10px; color:var(--text); font-size:18px; font-family:'DM Sans',sans-serif; outline:none; width:100%; font-weight:700; box-sizing:border-box; transition:border-color .15s,box-shadow .15s; }
 .num-input:focus { border-color:var(--accent); box-shadow:0 0 0 3px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1); }

 .input-row   { display:flex; gap:10px; align-items:flex-end; }
 .input-group { display:flex; flex-direction:column; gap:5px; flex:1; min-width:0; }
 .input-label { font-size:11px; color:var(--muted); font-weight:700; letter-spacing:.5px; text-transform:uppercase; }

 /* ── LOG HEADER ── */
 .log-header { display:flex; justify-content:space-between; align-items:center; padding:52px 16px 14px; background:var(--surface); border-bottom:1px solid var(--border); gap:8px; }

 /* ── TAB BAR ── */
 .tab-bar { display:flex; background:var(--surface); border-bottom:1px solid var(--border); }
 .tab { flex:1; padding:13px 12px; background:none; border:none; color:var(--muted); font-size:13px; font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; border-bottom:2px solid transparent; transition:color .15s,border-color .15s; letter-spacing:.2px; }
 .tab:active { opacity:.7; }
 .tab.active { color:var(--accent); border-bottom-color:var(--accent); }

 /* ── EXERCISE CARD ── spotlight effect for active */
 .ex-card { background:var(--surface); border:1.5px solid var(--border); border-radius:var(--r-md); overflow:hidden; transition:all .2s; }
 .ex-card.open { border-color:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.35); box-shadow:0 0 0 1px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1),var(--shadow-md); }
 .ex-card.finished { border-color:rgba(61,220,132,.25); background:var(--surface); }
 .ex-header { display:flex; justify-content:space-between; align-items:center; padding:14px 16px; cursor:pointer; user-select:none; gap:8px; transition:background .12s; }
 .ex-header:active { background:rgba(255,255,255,.03); }
 .ex-name { font-weight:700; font-size:15px; color:var(--text); line-height:1.3; }
 .ex-body { padding:0 16px 16px; display:flex; flex-direction:column; gap:12px; animation:slideUp .2s ease; }

 /* ── SET ROW ── with PR flash */
 .set-row { display:flex; align-items:center; gap:8px; padding:9px 0; border-bottom:1px solid var(--border); animation:setSlideIn .2s ease; }
 .set-row.pr-flash { animation:setSlideIn .2s ease, prFlash .8s .1s ease; }
 .set-num-label { font-size:11px; color:var(--muted); width:30px; flex-shrink:0; font-weight:700; }
 .set-detail { flex:1; font-size:13px; font-weight:700; color:var(--text); }
 .del-btn { background:none; border:none; color:var(--subtle); font-size:13px; cursor:pointer; padding:4px 8px; transition:opacity .15s,color .15s,transform .12s; }
 .del-btn:active { color:var(--danger); transform:scale(.85); }

 /* ── HINT / REC BOXES ── */
 .hint-box   { background:var(--surface2); border-radius:var(--r-sm); padding:10px 12px; }
 .hint-title { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:1px; font-weight:700; }
 .hint-chip  { font-size:11px; color:var(--text); background:var(--surface3); border-radius:6px; padding:3px 8px; font-weight:700; }

 .rec-card    { background:var(--surface); border:1.5px solid var(--border); border-radius:var(--r-md); padding:16px; display:flex; flex-direction:column; gap:10px; animation:slideUp .2s ease; }
 .rec-ex-name { font-size:15px; font-weight:700; color:var(--text); letter-spacing:-.2px; }
 .rec-option  { border-radius:var(--r-sm); padding:12px 14px; border:1.5px solid var(--border); transition:transform .12s; }
 .rec-option:active { transform:scale(.98); }
 .rec-option.primary  { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07); border-color:var(--accent); }
 .rec-option.secondary{ background:var(--surface2); }
 .rec-big  { font-family:'Bebas Neue',sans-serif; font-size:28px; color:var(--accent); line-height:1; }
 .rec-badge { font-size:11px; font-weight:700; background:var(--accent); color:var(--bg); border-radius:4px; padding:2px 6px; margin-left:4px; letter-spacing:.3px; }

 /* ── REST / CHIP ── */
 .rest-chip { background:var(--surface2); border:1.5px solid var(--border); border-radius:99px; padding:7px 16px; font-size:11px; font-weight:700; color:var(--muted); cursor:pointer; font-family:'DM Sans',sans-serif; transition:all .15s; }
 .rest-chip:active { transform:scale(.93); }
 .rest-chip.active { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1); border-color:var(--accent); color:var(--accent); }

 .tab-pill { background:var(--surface2); border:1.5px solid var(--border); border-radius:99px; padding:5px 14px; font-size:13px; cursor:pointer; color:var(--muted); font-weight:700; transition:all .15s; }
 .tab-pill:active { transform:scale(.93); }
 .tab-pill.active { background:var(--accent); color:var(--bg); border-color:var(--accent); }

 /* ── PICKER / MODAL ── */
 .overlay { position:fixed; inset:0; background:rgba(0,0,0,.65); display:flex; align-items:flex-end; justify-content:center; z-index:100; backdrop-filter:blur(6px); max-width:480px; margin:0 auto; animation:fadeIn .2s ease; }
 .picker-modal { background:var(--surface); border-radius:var(--r-lg) var(--r-lg) 0 0; padding:24px 20px 40px; width:100%; display:flex; flex-direction:column; gap:12px; border-top:1px solid var(--border); animation:slideUp .25s ease; }
 .picker-title { font-size:18px; font-weight:700; color:var(--text); letter-spacing:-.3px; }
 .picker-sub   { font-size:13px; color:var(--muted); margin-top:-6px; }

 .ex-pick-btn { background:var(--surface2); border-radius:var(--r-sm); padding:14px 16px; display:flex; justify-content:space-between; align-items:center; cursor:pointer; transition:all .12s; border:none; width:100%; }
 .ex-pick-btn:active { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07); transform:scale(.98); }
 .ex-pick-name  { font-size:13px; font-weight:700; color:var(--text); }
 .ex-pick-equip { display:flex; gap:4px; }
 .equip-tag { display:inline-flex; align-items:center; justify-content:center; width:22px; height:22px; border-radius:6px; background:var(--surface2); border:1px solid var(--border); }

 .equip-btn { background:var(--surface2); border-radius:var(--r-sm); padding:15px 16px; display:flex; align-items:center; gap:12px; cursor:pointer; transition:all .12s; border:none; width:100%; }
 .equip-btn:active { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07); transform:scale(.98); }
 .equip-icon  { display:flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:8px; background:var(--surface); border:1px solid var(--border); flex-shrink:0; }
 .equip-name  { font-size:13px; font-weight:700; color:var(--text); flex:1; text-align:left; }
 .equip-arrow { color:var(--accent); font-size:15px; }

 .confirm-box  { background:var(--surface2); border-radius:var(--r-md); padding:20px 16px; text-align:center; animation:successPop .3s ease; }
 .confirm-name  { font-size:18px; font-weight:700; color:var(--text); letter-spacing:-.3px; }
 .confirm-equip { font-size:13px; color:var(--muted); margin-top:6px; }

 /* ── GOAL / MUSCLE BUTTONS ── */
 .goal-btn { background:var(--surface2); border-radius:var(--r-sm); padding:14px 16px; text-align:left; cursor:pointer; transition:all .12s; display:flex; flex-direction:column; gap:4px; width:100%; border:none; }
 .goal-btn:active { transform:scale(.98); }
 .goal-btn.active { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07); outline:1.5px solid var(--accent); }
 .goal-label { color:var(--text); font-weight:700; font-size:15px; }
 .goal-sub   { color:var(--muted); font-size:11px; }

 .muscle-btn { background:var(--surface2); border-radius:var(--r-sm); padding:14px 16px; color:var(--text); font-size:13px; font-weight:700; cursor:pointer; transition:all .12s; border:none; }
 .muscle-btn:active { transform:scale(.97); background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07); }

 /* ── SET TYPE BADGE ── */
 .set-type-badge { font-size:11px; font-weight:700; border-radius:4px; padding:2px 6px; flex-shrink:0; letter-spacing:.5px; }
 .set-type-badge.super { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.2); color:var(--purple); }

 /* ── BOTTOM NAV ── redesigned with pill active state ── */
 .bottom-nav { display:flex; background:var(--surface); border-top:1px solid var(--border); position:sticky; bottom:0; z-index:50; padding-bottom:env(safe-area-inset-bottom,0); }
 .nav-btn { flex:1; display:flex; flex-direction:column; align-items:center; gap:3px; padding:10px 0 12px; background:none; border:none; cursor:pointer; font-family:'DM Sans',sans-serif; color:var(--muted); position:relative; transition:color .2s; }
 .nav-btn svg { transition:stroke .2s,transform .15s; stroke:var(--muted); }
 .nav-btn:active svg { transform:scale(.82); }
 .nav-btn.active { color:var(--accent); }
 .nav-btn.active svg { stroke:var(--accent); }
 .nav-btn.active .nav-icon-wrap { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.18); }
 .nav-btn.active .nav-icon-wrap svg { transform:scale(1.06); transition:transform .2s; }
 .nav-icon-wrap { width:42px; height:28px; border-radius:14px; display:flex; align-items:center; justify-content:center; transition:background .2s; }
 .nav-label { font-size:11px; font-weight:700; letter-spacing:.3px; }

 /* ── DOTS ROW (onboard) ── */
 .dots-row { display:flex; gap:6px; }
 .dot { width:8px; height:8px; border-radius:50%; background:var(--border); transition:all .3s; }
 .dot.active { background:var(--accent); width:22px; border-radius:4px; }
 .dot.done { background:var(--success); }
 .step-title { font-size:18px; font-weight:700; letter-spacing:-.3px; }

 /* ── CALENDAR ── */
 .cal-card { background:var(--surface); border-radius:var(--r-md); padding:20px 16px; display:flex; justify-content:center; }

 /* ── FOOD / NUTRITION ── */
 .food-row { display:flex; align-items:center; gap:10px; padding:11px 0; border-bottom:1px solid var(--border); transition:background .12s; }
 .food-row:active { background:var(--surface2); }

 /* ── LIGHT MODE OVERRIDES ── */
 .theme-light .btn-set-action.normal  { background:var(--accent); color:#fff; }
 .theme-light .tab-pill.active        { background:var(--accent); color:#fff; border-color:var(--accent); }
 .theme-light .rec-option.primary     { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07); border-color:var(--accent); }
 .theme-light .goal-btn.active        { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.07); outline-color:var(--accent); }
 .theme-light .rest-chip.active       { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1); border-color:var(--accent); color:var(--accent); }
 .theme-light .ex-card.open           { border-color:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.4); box-shadow:0 0 0 1px rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1),var(--shadow-md); }
 .theme-light .nav-btn.active .nav-icon-wrap { background:rgba(var(--accent-r),var(--accent-g),var(--accent-b),.1); }
`;
